import { isAbstention } from "./abstention.js";
import { AMBIGUOUS_OWNER_ERROR } from "./owner-routing.js";
import { envelopeText } from "./envelope-text.js";
import { EventEmitter } from "node:events";
import { AsyncLocalStorage } from "node:async_hooks";
import { classifyCommitFailure } from "./mls-commit-classify.js";
import { parseMentions } from "./room-protocol.js";
import { createServer, type Server, type IncomingMessage, type ServerResponse } from "node:http";
import { randomUUID, createHash } from "node:crypto";
import { readFileSync } from "node:fs";
import { homedir as osHomedir } from "node:os";
import { writeFile, mkdir } from "node:fs/promises";
import { join } from "node:path";
import { readFile } from "node:fs/promises";
import sodium from "libsodium-wrappers-sumo";
import WebSocket from "ws";
import {
  generateIdentityKeypair,
  generateEphemeralKeypair,
  computeFingerprint,
  createProofOfPossession,
  performX3DH,
  DoubleRatchet,
  decryptFile,
  encryptFile,
  computeFileDigest,
  ScanEngine,
  TelemetryReporter,
  MLSGroupManager,
  legacyHistoryEntryId,
  hashHistoryCode,
  mergeHistoryEntries,
  buildHistoryArchive,
} from "@agentvault/crypto";
import type { SerializedMLSGroupState } from "@agentvault/crypto";
import { orderDeliveryBatch } from "@agentvault/crypto";
// #1013: the pre-send epoch rule, shared with the browser (#996) and
// mirroring Hermes's `_server_epoch_hint`. Deliberately NOT re-implemented
// here — three copies of one rule is how the 1:1 path fell behind.
//
// Imported from the SUBPATH, not the package root: the root pulls in ts-mls and
// is wholesale-mocked by 26 suites, so a root import would make every one of
// them responsible for stubbing a pure function they do not care about.
import { noteObservedEpoch, needsPreSendResync } from "@agentvault/crypto/mls-presend-epoch";
import { saveMlsState, loadMlsState, deleteMlsState, hasPendingWelcome, savePendingKpBundle, loadPendingKpBundle, clearPendingWelcome, acquireA2ASyncLock, releaseA2ASyncLock, quarantineOrphanedMlsState} from "./mls-state.js";
import { joinWithFirstMatchingKp, type KpBundle } from "./mls-kp-pool.js";
import { buildClientVersion } from "./client-version.js";

import type {
  SecureChannelConfig,
  ChannelState,
  MessageMetadata,
  AttachmentData,
  PersistedState,
  LegacyPersistedState,
  HistoryEntry,
  SendOptions,
  OwnerAudience,
  DecisionRequest,
  DecisionResponse,
  HeartbeatStatus,
  StatusAlert,
  RoomMemberInfo,
  RoomConversationInfo,
  RoomInfo,
  RoomState,
  A2AChannel,
  A2AMessage,
  DeliveryTarget,
  DeliveryContent,
  DeliveryOptions,
  DeliveryReceipt,
  TargetInfo,
  CredentialGrantPayload,
  CredentialRevokePayload,
} from "./types.js";
import { CredentialStore } from "./credential-store.js";
import {
  hexToBytes,
  bytesToHex,
  base64ToBytes,
  bytesToBase64,
  encryptedMessageToTransport,
  transportToEncryptedMessage,
} from "./crypto-helpers.js";
import { saveState, loadState, clearState, backupState, restoreState } from "./state.js";
import {
  enrollDevice,
  pollDeviceStatus,
  activateDevice,
} from "./transport.js";
import { A2ALogGate } from "./a2a-log-gate.js";
import { mlsMembershipFromStatus } from "./mls-membership-probe.js";
import { HistoryBackupScheduler } from "./history-backup-scheduler.js";
import type { HistoryBackupClient } from "./history-backup-client.js";
import { loadHistoryRecoveryCode } from "./history-recovery-code.js";
import { decisionReplyFromEnvelope } from "./decision-reply.js";
import { WelcomeKeyPackageBackoff, type PendingWelcomeRow } from "./welcome-kp-backoff.js";

/** Message types that should be forwarded to the agent in room contexts.
 * Internal protocol messages (thinking, status, heartbeat, typing, ack, etc.) are dropped. */
const ROOM_AGENT_TYPES = new Set([
  "message", "text", "decision_request", "decision_response", "artifact_share",
]);

/** Credential protocol message types — intercepted by plugin, never forwarded to agent. */
const CREDENTIAL_MESSAGE_TYPES = new Set([
  "credential_grant", "credential_revoke", "credential_request",
]);

const CATCHUP_MESSAGE_TYPES = new Set([
  "history_catchup_request", "history_catchup_response",
]);

const POLL_INTERVAL_MS = 6_000; // 6s — safely below the 1/5sec server-side rate limit
const RECONNECT_BASE_MS = 1_000;
/** #1226: the only shape the server accepts in `member_device_ids`. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RECONNECT_MAX_MS = 30_000;
const PENDING_POLL_INTERVAL_MS = 15_000;
const HISTORY_RESTORE_TIMEOUT_MS = 10_000;
const HISTORY_UPLOAD_TIMEOUT_MS = 10_000;
const CANONICAL_TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

// Outbound idempotency guard (#475): collapse exact-duplicate content to the
// same destination+thread within a SLIDING window to a single send. A buggy
// agent runtime that emits the same reply many times in a burst (observed on
// prod: 23× in ~5s at ~4/sec) is collapsed to one delivery; distinct content,
// distinct topic/conversation, distinct destination, or a repeat after the
// window all pass untouched.
//
// Safety invariants (post-review #476):
//  - A key is recorded ONLY after a send SUCCEEDS, so a failed first send can
//    never suppress the retries that follow it (no silent message loss).
//  - Suppression of any one key is capped in absolute time, so a legitimately
//    periodic identical message can never be suppressed forever.
//  - Side-effecting content types (decision_request/action_confirmation) are
//    never content-deduped — their identity is not captured by a content hash.
const OUTBOUND_DEDUPE_WINDOW_MS = 3_000; // sliding: gap between dup emits must exceed this to pass
const OUTBOUND_DEDUPE_MAX_SUPPRESS_MS = 15_000; // absolute cap: never suppress one key longer than this
const OUTBOUND_DEDUPE_MAX = 256; // prune stale keys once the map grows past this
// Only these content types are eligible for content-hash dedupe. Side-effecting
// types carry an identity a content hash cannot stand in for.
const OUTBOUND_DEDUPE_TYPES = new Set(["text", "status_alert", "artifact", "attachment"]);

interface OutboundDedupeEntry {
  firstTs: number; // start of the current suppression run (for the absolute cap)
  lastTs: number; // most recent send/suppress of this key (for the sliding window)
  suppressed: number;
}

// #732: a message refused with `unknown group` is resent once the conversation
// →group map has been reconciled. The window between the frame reaching the
// socket and the server's refusal is ~50ms, so this TTL is generous by two
// orders of magnitude; it exists to stop a reply resurfacing long after the
// owner has moved on, where an out-of-order message is worse than a missing one.
const RETRY_CANDIDATE_TTL_MS = 60_000;
const RETRY_CANDIDATE_MAX = 32; // bound the map; oldest evicted first

/**
 * #1184: a commit of ours awaiting the server's verdict (see `_commitInFlight`).
 */
interface CommitInFlight {
  /** `mls_commit` frames the socket delivered for this group meanwhile; replayed after the verdict. */
  frames: any[];
  /**
   * Our commits in send order: the epoch each produced, its digest, whether the
   * server POSITIVELY confirmed it (our digest seen at its head), and the state
   * to return to if it — or anything after it — turns out not to hold.
   */
  sent: Array<{ epoch: number; digest: string; verified: boolean; stateBefore: any }>;
  /**
   * The server's refusal, once one arrives (`_noteCommitRefusal`): its detail
   * and the index in `sent` of the FIRST commit of ours it shows did not land.
   * Honoured whenever it arrives — during the confirm window or after it, up
   * to the fulfil POST.
   */
  refusal?: { detail: string; from: number };
  /** Installed by the confirm loop while it waits; a refusal calls it to end the wait early. */
  wake?: () => void;
}

interface RetryCandidate {
  plaintext: string;
  options?: SendOptions;
  ts: number;
}

/**
 * #1418 — the conversation whose turn is running, for this async call chain.
 *
 * Set by `SecureChannel.runForConversation()` around the work an inbound 1:1
 * message triggers (openclaw-entry does it for every owner DM turn), so an
 * UNTARGETED send made inside that work — the agent's `message` tool, a
 * decision request, an artifact — goes to the person who asked, even when a
 * second person's message has arrived in the meantime. Module-level because a
 * turn may reach the channel through code that never sees the instance; each
 * channel only honours a conversation it actually has.
 */
const _turnConversation = new AsyncLocalStorage<TurnStore>();

/**
 * #1418 — what a running turn knows about the message that started it.
 *
 * `groupId` is the 1:1 group the inbound message actually DECRYPTED under —
 * attested by the decrypt, so it beats the local conversation→group map, which
 * can be stale (a conversation recreated before the next reconcile). `ended`
 * is set when the turn returns: async work the turn started (a timer, a
 * background job, a dispatch orphaned by the turn budget) inherits this store.
 * That work is still FOR this turn's person — for `PERSON_INVOLVED_WINDOW_MS`
 * after the end; after that it is proactive (the primary person). It never
 * becomes someone else's turn or last message.
 */
interface TurnStore {
  convId: string | undefined;
  groupId: string | undefined;
  ended: boolean;
  /** When the turn returned. Work it left running keeps its person this long. */
  endedAt?: number;
}

/** #1418 — an owner 1:1 message that decrypted, and who it attests to. */
interface AttestedInbound {
  convId: string | undefined;
  groupId: string | undefined;
  senderDeviceId: string | undefined;
  /** When it was SENT (server `created_at`, never later than receipt). */
  at: number;
}

/**
 * #1418 — how long an inbound 1:1 message keeps its person "involved" for
 * untargeted content that carries no turn context (a skill calling the local
 * HTTP `/send` or `/decision`, a bridge on an older build). After this the
 * content counts as proactive and goes to the PRIMARY person instead. Covers a
 * 120 s turn budget plus queueing behind another turn.
 */
const PERSON_INVOLVED_WINDOW_MS = 5 * 60_000;

/**
 * #1418 — where an owner-bound helper (decision, alert, artifact, attachment…)
 * sends. A `conversationId` names the person; without one it goes to the
 * person involved, and `audience: "everyone"` is the only way to reach all.
 */
export interface OwnerRouting {
  conversationId?: string;
  audience?: OwnerAudience;
}

/** #1418 — the person an untargeted owner message is for, and why. */
interface InvolvedPerson {
  /** A conversation of the person (the one that triggered the content). */
  convId: string | undefined;
  /** The person's 1:1 group, when known — every browser of theirs shares it. */
  groupId: string | undefined;
  reason: "target" | "turn" | "outlived-turn" | "active-turn" | "last-inbound" | "primary";
}

/** #1418 — at most this many attested inbound pairs are remembered. */
const ATTESTED_INBOUND_MAX = 64;

/** #1418 — an inbound on an unmapped group re-resolves the map at most this often. */
const UNMAPPED_RECONCILE_COOLDOWN_MS = 60_000;

/**
 * Internal-only extension of the public `SendOptions`.
 *
 * Kept out of `types.ts` deliberately: `isResend` is a private re-entry flag
 * for #732, not part of the contract offered to agent runtimes.
 */
interface InternalSendOptions extends SendOptions {
  /**
   * This send is a resend of a message already recorded in history (#732).
   * Suppresses the history append so LLM context replay does not show the
   * agent saying the same thing twice.
   */
  isResend?: boolean;
  /**
   * Skip the #952 MLS hold for this send. Set only when releasing an already
   * held message — without it, a release re-enters the hold and loops forever.
   */
  bypassMlsHold?: boolean;
}

/**
 * Build the outbound-dedupe key for a delivery, or null to skip deduping.
 * Returns null for side-effecting content types. Prefers a runtime-supplied
 * idempotency id when present; otherwise keys on destination + conversation +
 * topic + priority + message_type + a hash of the exact content, so ONLY an
 * exact-duplicate to the same place/thread/priority collapses. Pure (no `this`).
 */
function outboundDedupeKey(
  resolved: { kind: string; id?: string },
  content: DeliveryContent,
  options?: DeliveryOptions,
): string | null {
  if (!OUTBOUND_DEDUPE_TYPES.has(content.type)) return null;
  const base = [resolved.kind, resolved.id ?? "owner", options?.conversationId ?? "", options?.topicId ?? ""];
  // If the runtime stamps a stable per-logical-message id (repeated on re-emit),
  // dedupe on THAT — it distinguishes a re-emit from a legit identical message.
  const explicitId = (options?.metadata?.idempotency_key ?? options?.metadata?.message_id) as string | undefined;
  if (explicitId) return [...base, `id:${explicitId}`].join("|");
  const contentHash = createHash("sha256").update(JSON.stringify(content)).digest("hex").slice(0, 32);
  const priority = options?.priority ?? "";
  const messageType = (options?.metadata?.message_type as string | undefined) ?? "";
  return [...base, `p:${priority}`, `mt:${messageType}`, contentHash].join("|");
}

/** Drop dedupe entries older than the window once the map grows past the cap. */
function pruneRecentDeliveries(map: Map<string, OutboundDedupeEntry>, now: number): void {
  if (map.size <= OUTBOUND_DEDUPE_MAX) return;
  for (const [key, entry] of map) {
    if (now - entry.lastTs >= OUTBOUND_DEDUPE_WINDOW_MS) map.delete(key);
  }
}

type LegacyHistoryEntry = Omit<HistoryEntry, "id"> & { id?: string };

/**
 * Emergency migration ID for persisted rows that predate archive validation
 * and cannot be represented as canonical UTF-8 (notably lone UTF-16
 * surrogates). The source strings are hashed as length-prefixed UTF-16LE code
 * units, so no replacement character is introduced. The SHA-256 digest is
 * encoded as a deterministic UUIDv4-shaped ID accepted by the archive ID
 * grammar; the row itself remains unchanged and may still be excluded by a
 * later archive validation step.
 */
function legacyHistoryFallbackId(entry: Omit<HistoryEntry, "id">): string {
  const hash = createHash("sha256");
  hash.update("agentvault-history-entry-utf16-fallback-v1\0", "utf8");
  const addUtf16 = (value: string) => {
    const bytes = Buffer.allocUnsafe(4 + value.length * 2);
    bytes.writeUInt32LE(value.length, 0);
    for (let index = 0; index < value.length; index += 1) {
      bytes.writeUInt16LE(value.charCodeAt(index), 4 + index * 2);
    }
    hash.update(bytes);
  };
  addUtf16(entry.sender);
  addUtf16(entry.text);
  addUtf16(entry.ts);
  hash.update(entry.topicId === undefined ? Uint8Array.of(0) : Uint8Array.of(1));
  if (entry.topicId !== undefined) addUtf16(entry.topicId);
  const digest = hash.digest().subarray(0, 16);
  digest[6] = (digest[6] & 0x0f) | 0x40;
  digest[8] = (digest[8] & 0x3f) | 0x80;
  const hex = digest.toString("hex");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function migrateHistoryEntries(history: LegacyHistoryEntry[]): HistoryEntry[] {
  return history.map((entry) => {
    if (typeof entry.id === "string") return entry as HistoryEntry;
    const legacyEntry = {
      sender: entry.sender,
      text: entry.text,
      ts: entry.ts,
      ...(entry.topicId === undefined ? {} : { topicId: entry.topicId }),
    };
    let id: string;
    try {
      id = legacyHistoryEntryId(legacyEntry);
    } catch {
      id = legacyHistoryFallbackId(legacyEntry);
    }
    return { id, ...legacyEntry };
  });
}

/**
 * Migrates legacy single-session persisted state to the new
 * multi-session format. If the state is already in the new format,
 * returns it as-is.
 */
/**
 * #1416: state written before per-group topics holds ONE `defaultTopicId`,
 * the primary group's (it was always read off the primary activation entry).
 * Seed the per-group map with it so a single-group agent addresses exactly
 * the topic it did before. Every other group starts unknown and is filled by
 * activation / the conversation reconcile — never borrowed from the primary.
 */
/** #1416: topic ids are server UUIDs; anything else is treated as absent. */
const TOPIC_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isTopicId(value: unknown): value is string {
  return typeof value === "string" && TOPIC_ID_RE.test(value);
}

export function migrateDefaultTopics(state: PersistedState): PersistedState {
  if (!state.defaultTopicId || !state.groupId) return state;
  if (state.defaultTopicIds?.[state.groupId]) return state;
  return {
    ...state,
    defaultTopicIds: { ...(state.defaultTopicIds ?? {}), [state.groupId]: state.defaultTopicId },
  };
}

export function migratePersistedState(raw: any): PersistedState {
  // New format: has `sessions` and `primaryConversationId`
  if (raw.sessions && raw.primaryConversationId) {
    const history = raw.messageHistory as LegacyHistoryEntry[] | undefined;
    if (!history?.some((entry) => typeof entry.id !== "string")) {
      return migrateDefaultTopics(raw as PersistedState);
    }
    return migrateDefaultTopics({
      ...raw,
      messageHistory: migrateHistoryEntries(history),
    } as PersistedState);
  }

  // Legacy format: single conversationId + ratchetState
  const legacy = raw as LegacyPersistedState;
  return {
    deviceId: legacy.deviceId,
    deviceJwt: legacy.deviceJwt,
    primaryConversationId: legacy.conversationId,
    sessions: {
      [legacy.conversationId]: {
        ownerDeviceId: "",
        ratchetState: legacy.ratchetState,
      },
    },
    identityKeypair: legacy.identityKeypair,
    ephemeralKeypair: legacy.ephemeralKeypair,
    fingerprint: legacy.fingerprint,
    lastMessageTimestamp: legacy.lastMessageTimestamp,
    messageHistory: migrateHistoryEntries((raw.messageHistory ?? []) as LegacyHistoryEntry[]),
  };
}

/** True when `target` addresses this agent's OWN hub address (#499, A2 parity
 * for OpenClaw). Deterministic self-reference guard for A2A: an agent must
 * never open (or send on) an A2A channel to itself — that is the self-name
 * bug's dead-room outcome. Normalized (trim + lowercase) EXACT match on the
 * full hub address, so a genuinely different peer with a similar name is
 * unaffected. Returns false when own address is unknown (never guard on an
 * empty self). Mirrors Hermes' find_agent self-sentinel. */
export function _isSelfHubAddress(ownHubAddress: string, target: string): boolean {
  const own = (ownHubAddress ?? "").trim().toLowerCase();
  const tgt = (target ?? "").trim().toLowerCase();
  return own.length > 0 && own === tgt;
}

/**
 * Minimum gap between re-admitting the SAME device to the SAME room (#1171).
 *
 * Mirrors the web client's `FULFILL_COOLDOWN_MS`, which this path never had.
 * Long enough that a device which actually joined has finished doing so; short
 * enough that a genuine second attempt is not blocked for long.
 */
const ROOM_FULFIL_COOLDOWN_MS = 60_000;

/**
 * #1459 — carry a member's `tenantId` across a roster replacement whose payload
 * omits it (a payload from a server older than `member_device_tenants`, or one
 * built on a path that did not run it). A device's account never changes, so
 * one already learned is kept rather than dropped. A payload that does carry a
 * value always wins.
 */
export function keepKnownMemberTenants(
  previous: readonly RoomMemberInfo[],
  next: readonly RoomMemberInfo[],
): RoomMemberInfo[] {
  const known = new Map<string, string>();
  for (const m of previous) if (m.tenantId) known.set(m.deviceId, m.tenantId);
  return next.map((m) => {
    const tenantId = m.tenantId ? undefined : known.get(m.deviceId);
    return tenantId ? { ...m, tenantId } : m;
  });
}

export class SecureChannel extends EventEmitter {
  private _state: ChannelState = "idle";
  private _deviceId: string | null = null;
  private _fingerprint: string | null = null;
  private _primaryConversationId: string = "";
  private _deviceJwt: string | null = null;
  private _sessions: Map<
    string,
    { ownerDeviceId: string; ratchet: DoubleRatchet; activated: boolean; epoch?: number }
  > = new Map();
  private _ws: WebSocket | null = null;
  private _pollTimer: ReturnType<typeof setTimeout> | null = null;
  private _reconnectAttempt = 0;
  private _reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _rapidDisconnects = 0;
  private _lastWsOpenTime = 0;
  private _pingTimer: ReturnType<typeof setInterval> | null = null;
  private _lastServerMessage = 0;
  private _pendingAcks: string[] = [];
  private _ackTimer: ReturnType<typeof setTimeout> | null = null;
  private _stopped = false;
  private _persisted: PersistedState | null = null;
  // AgentVault display_name, resolved from GET /hub/identities (#499, A1).
  // hub_address alone isn't enough — its random suffix (wren-8789ee6d…) differs
  // from what the owner types (Wren). Empty until resolved; the DM preamble is
  // then omitted rather than asserting a wrong name. Re-resolved on every
  // connect (picks up renames; retries after a failed fetch).
  private _ownDisplayName = "";
  // Dedupes concurrent resolves (connect-trigger + lazy DM-trigger) so a burst
  // of DMs can't stampede /hub/identities.
  private _resolvingOwnDisplayName: Promise<void> | null = null;
  private _httpServer: Server | null = null;
  private _mcpServer: import("./mcp-server.js").AgentVaultMcpServer | null = null;
  private _pollFallbackTimer: ReturnType<typeof setInterval> | null = null;
  private _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private _heartbeatCallback: (() => HeartbeatStatus) | null = null;
  private _heartbeatIntervalSeconds: number = 0;
  /** #798: report the A2A channel set only when it CHANGES. This line polls
   *  every 30s and produced 90.2% of bridge.log (90,841/100,702 lines) and a
   *  27.9MB gateway.log on wren, every one reading `channels=0`. Not deleted —
   *  #794's A2A watch needs the transitions. */
  private _a2aLogGate = new A2ALogGate();
  private _wakeDetectorTimer: ReturnType<typeof setInterval> | null = null;
  private _lastWakeTick: number = Date.now();
  private _trustToken: string | null = null;
  private _trustTier: string | null = null;
  private _trustTokenExpiresAt: string | null = null;
  private _trustTokenInterval: ReturnType<typeof setInterval> | null = null;
  private _trustTokenUnavailableUntil = 0;
  private _pendingPollTimer: ReturnType<typeof setInterval> | null = null;
  private _syncMessageIds: Set<string> | null = null;
  private _deliveryHeartbeat: ReturnType<typeof setInterval> | null = null;
  /**
   * How often to re-check the KeyPackage pool against the server (#826).
   *
   * KeyPackages expire after 7 days and, before this, only a reconnect
   * republished them — so an agent that simply stayed up ran to zero usable
   * and silently stopped accepting new MLS 1:1 conversations while looking
   * perfectly healthy. 6h gives ~28 attempts inside one lifetime, so a run of
   * transient failures cannot age the pool out. Cost is one GET per interval.
   */
  private static readonly _KP_REPLENISH_INTERVAL_MS = 6 * 60 * 60 * 1000;
  private _kpReplenishTimer: ReturnType<typeof setInterval> | null = null;
  private _a2aReconcileTimer: ReturnType<typeof setInterval> | null = null;
  private _deliveryPulling = false;
  private _drDeliveryPulling = false;

  /** MLS group managers per room/conversation (roomId or conv:conversationId or 1to1-group:groupId -> MLSGroupManager) */
  private _mlsGroups: Map<string, MLSGroupManager> = new Map();
  /** `${mlsGroupId}:${deviceId}` -> last re-admit ms (#1171). */
  private _roomFulfilCooldown: Map<string, number> = new Map();
  /** #1428: per (group, device) KeyPackage backoff for the pending-Welcome pulls. */
  private _welcomeKpBackoff = new WelcomeKeyPackageBackoff();

  /** Maps conversationId -> conversation_group_id for shared 1:1 MLS groups */
  private _sessionGroupIds: Map<string, string> = new Map();
  /** Consecutive MLS decrypt failure count per group key for epoch recovery. */
  private _mlsDecryptFailCounts: Map<string, number> = new Map();
  /** #474: per-group consecutive processCommit failures. Mirrors the decrypt
   *  counter above — a commit that fails is divergence, and divergence must
   *  self-heal rather than be logged and dropped. */
  private _mlsCommitFailCounts: Map<string, number> = new Map();
  private static readonly MAX_MLS_DECRYPT_FAILS = 3;
  /**
   * #1084: consecutive resyncs that found NOTHING to apply, per group.
   *
   * "No new commits" means the server holds no commit past our epoch — we are
   * NOT BEHIND. Treating that as corruption and re-keying is what drove the
   * epoch churn (see `_resyncOnDivergence`). But a state that is genuinely
   * broken while its epoch counter happens to match must still recover, so the
   * suppression is bounded rather than absolute.
   */
  private _resyncNoOpStrikes: Map<string, number> = new Map();
  private static readonly MAX_RESYNC_NOOPS = 3;

  /** Cached MLS KeyPackage bundle for this device (regenerated on each connect). */
  private _mlsKeyPackage: Awaited<ReturnType<MLSGroupManager["generateKeyPackage"]>> | null = null;
  /** Pending KeyPackage bundle from request-Welcome flow (used by _handleMlsWelcome). */
  private _pendingMlsKpBundle: Awaited<ReturnType<MLSGroupManager["generateKeyPackage"]>> | undefined;
  /**
   * Pool of pending KeyPackage bundles this device has published but not yet
   * consumed by a Welcome. The backend consumes published KeyPackages newest-first (#508),
   * so a given room's creator may have been served ANY of these — `_handleMlsWelcome`
   * trial-decrypts the incoming Welcome against every bundle in the pool (plus the
   * connect KP and the persisted per-group bundle). Capped at `_KP_POOL_TARGET`;
   * a consumed bundle is removed on a successful join. (KP-pool design 2026-06-18.)
   */
  private _pendingKpBundles: KpBundle[] = [];
  /** Pool target: keep this many unconsumed KeyPackages published; backend caps to match. */
  private static readonly _KP_POOL_TARGET = 10;
  /**
   * Local retention bound for `_pendingKpBundles` (#363). Deliberately ABOVE
   * `_KP_POOL_TARGET`, because the two counts are in DIFFERENT UNITS: the backend
   * keeps up to `_KP_POOL_TARGET` **unconsumed** KeyPackages, while this array
   * holds every bundle published since the last join — consumed ones are removed
   * only by `_removeKpFromPool` on a successful join.
   *
   * Capping local retention AT the backend's target therefore evicts bundles the
   * backend can still serve, and their private material cannot be rebuilt (#341:
   * `mls_rs_uniffi` exposes no KeyPackage repository), so any Welcome minted
   * against an evicted bundle is PERMANENTLY unjoinable.
   *
   * Same defect and same remedy as #511, where the web store capped 6 against a
   * backend pool of 10 and was raised to 12. Retaining extra bundles is cheap and
   * purely protective — `_handleMlsWelcome` trial-decrypts against the whole pool,
   * so a surplus only ever adds chances to match.
   */
  private static readonly _KP_POOL_RETENTION = 20;
  /** Reentrancy guard so concurrent pool top-ups don't double-publish past target. */
  private _kpPoolFilling = false;

  /** Buffer for MLS commits received before Welcome (keyed by groupId, sorted by epoch). */
  private _pendingMlsCommits: Map<string, Array<{ epoch: number; data: any }>> = new Map();

  /**
   * #1013: the highest group epoch any inbound frame has reported, per MLS
   * group id. Every inbound frame carries the epoch the group was at when it
   * was produced, so each is a LOWER BOUND on the group's current epoch — the
   * hint is monotonic and only ever rises.
   */
  private _observedGroupEpochs: Map<string, number> = new Map();

  /** In-memory credential store for renter-provided credentials (never persisted). */
  private _credentialStore = new CredentialStore();

  /** Rooms whose roster has been refreshed from the backend at least once in
   * this process (GH #366). Cleared on (re)join so the next first message
   * re-fetches the authoritative roster. Prevents per-message refetch storms. */
  private _roomRosterRefreshed = new Set<string>();
  /**
   * #1181: the last attachment saved per room, so the NEXT room turn can name
   * it. wren saved an un-mentioned file (no reply, #1107), was asked "@wren can
   * you read this file?" 14 s later, and answered "I don't see a file
   * attached" — the note lived only in the prior history line. Hermes defers
   * un-mentioned attachments and fetches on the next addressed turn (#1162);
   * this is the plugin's equivalent surface. In-memory: a restart forgets it,
   * which is the right default for "this file".
   */
  private _recentRoomAttachments = new Map<string, { filePath: string; name: string; at: number; senderDeviceId?: string }>();
  private static readonly RECENT_ROOM_ATTACHMENT_MS = 10 * 60 * 1000;

  /** #1181: prefix a room turn that carries no attachment with the one saved in this room recently.
   *  `from` is who sent that file (#1459), so the turn can be marked as carrying their content. */
  private _withRecentRoomAttachmentNote(roomId: string, text: string): { text: string; from?: string | null } {
    const recent = this._recentRoomAttachments.get(roomId);
    if (!recent) return { text };
    const ageMs = Date.now() - recent.at;
    if (ageMs > SecureChannel.RECENT_ROOM_ATTACHMENT_MS) return { text };
    return {
      text:
        `[Most recent file in this room: ${recent.filePath} (${recent.name}, received ${Math.round(ageMs / 1000)}s ago). ` +
        `If this message refers to "the file" or "this file", it means that one — use your Read tool on it.]\n\n${text}`,
      from: recent.senderDeviceId ?? null,
    };
  }
  /**
   * #1161: room attachments whose caption addressed nobody, NOT yet fetched.
   * The reply gate lives in every consumer of `room_message` (openclaw-entry
   * and the three native bridges), but its first clause is shared and needs no
   * identity: a caption with no mention token owes nobody a reply (#1107), so
   * nobody needs the file yet. Kept per room, newest last, and fetched on the
   * room's next ADDRESSED turn — the owner attaches, then asks (#1159/#1181).
   * Hermes does the same (`_defer_room_attachment`).
   */
  private _deferredRoomAttachments = new Map<string, { info: any; at: number; senderDeviceId?: string }[]>();
  private static readonly DEFERRED_ROOM_ATTACHMENT_MAX = 5;
  private static readonly DEFERRED_ROOM_ATTACHMENT_MS = 60 * 60 * 1000;

  private _deferRoomAttachment(roomId: string, info: any, senderDeviceId?: string): void {
    const list = this._deferredRoomAttachments.get(roomId) ?? [];
    list.push({ info, at: Date.now(), senderDeviceId });
    while (list.length > SecureChannel.DEFERRED_ROOM_ATTACHMENT_MAX) list.shift();
    this._deferredRoomAttachments.set(roomId, list);
    console.log(
      `[SecureChannel] room ${roomId.slice(0, 8)}: attachment ${String(info?.filename ?? "file")} deferred — ` +
        `caption addresses nobody, fetch on the next @-addressed turn (#1161)`,
    );
  }

  /** Drain the room's deferred attachments that are still fresh (oldest first), with who sent each. */
  private _takeDeferredRoomAttachments(roomId: string): { info: any; senderDeviceId?: string }[] {
    const list = this._deferredRoomAttachments.get(roomId);
    if (!list?.length) return [];
    this._deferredRoomAttachments.delete(roomId);
    const cutoff = Date.now() - SecureChannel.DEFERRED_ROOM_ATTACHMENT_MS;
    return list.filter((d) => d.at >= cutoff).map((d) => ({ info: d.info, senderDeviceId: d.senderDeviceId }));
  }

  /** In-flight roster-refresh promises (per room) — dedup concurrent refreshes. */
  private _roomRosterRefreshInFlight = new Map<string, Promise<void>>();

  /** Dedup buffer for A2A message IDs (prevents double-delivery via direct + Redis) */
  private _a2aSeenMessageIds = new Set<string>();
  private static readonly A2A_SEEN_MAX = 500;
  /** Dedup buffer for regular message IDs (prevents double-decrypt via direct WS + Redis pub/sub) */
  private _seenMessageIds = new Set<string>();
  private static readonly SEEN_MSG_MAX = 500;
  /**
   * Outbound idempotency guard (#475): last send time per (destination+topic+content)
   * key. Collapses an exact-duplicate burst from a misbehaving agent runtime to a
   * single delivery. In-memory only (short window; no cross-restart persistence needed).
   */
  private _recentDeliveries = new Map<string, OutboundDedupeEntry>();
  /**
   * Last outbound plaintext per MLS group id (#732), so a message refused with
   * `unknown group` can be resent once the map is reconciled.
   *
   * PLAINTEXT, not the frame: the refused frame's `payload` is ciphertext
   * encrypted to a group that no longer exists, so replaying those bytes into
   * the replacement group would store something nobody can decrypt. The retry
   * has to encrypt again, which means keeping what was encrypted.
   *
   * In-memory only, TTL-bounded, and consumed on first use — this exists for
   * the ~50ms between a frame reaching the socket and the server refusing it,
   * not as a durable outbox (that is #688's dead-letter).
   */
  private _retryCandidates = new Map<string, RetryCandidate>();
  private _scanEngine: ScanEngine | null = null;
  private _scanRuleSetVersion = 0;
  private _telemetryReporter: TelemetryReporter | null = null;

  /**
   * #1416: topic of the most recent inbound message, PER conversation group —
   * the fallback for a reply that names no topic. Keyed by `_topicScope()`:
   * `g:<group>` normally, `c:<conversation>` for a conversation whose group is
   * not known yet, `*` for a legacy agent that knows no groups at all. It used
   * to be one value for the whole agent, so a broadcast into person B's group
   * carried the topic of person A's last message. In memory only, as before.
   */
  private _lastIncomingTopics: Map<string, string> = new Map();

  /** Room ID from the most recent inbound room message — used as fallback for HTTP /send replies. */
  private _lastInboundRoomId: string | undefined;

  /**
   * #1418: the most recent owner 1:1 message that reached the agent — the
   * person involved in whatever the agent says next without naming a
   * conversation (see `_involvedPerson`). In memory only: after a restart
   * there is no trigger, so untargeted content goes to the primary person.
   */
  private _lastInboundDm: { convId: string | undefined; groupId: string | undefined; at: number } | undefined;

  /** #1418: turns in progress (`runForConversation`). */
  private _activeTurns: Set<TurnStore> = new Set();

  /**
   * #1418: recent owner 1:1 messages that decrypted, keyed by
   * conversation|group — evidence of which conversation, group and browser
   * belong together (one person). In memory only, bounded.
   */
  private _attestedInbound: Map<string, AttestedInbound> = new Map();

  /** #1418: per UNMAPPED group (or conversation), the last time it asked for a reconcile. */
  private _unmappedReconcileAt: Map<string, number> = new Map();

  /**
   * #1418: 1:1 groups joined by a Welcome IN THIS PROCESS while no
   * conversation mapped them — a person who has not been learned yet, counted
   * as one until the next successful reconcile. Persisted husks never are.
   */
  private _welcomedUnmapped: Map<string, number> = new Map();

  /** #1418: what the last SUCCESSFUL reconcile reported live, and when it started. */
  private _lastReconcile: { at: number; convs: Set<string>; groups: Set<string> } | undefined;

  /** Rate-limit: last resync_request timestamp per conversation (5-min cooldown). */
  private _lastResyncRequest: Map<string, number> = new Map();

  /** Dedup: last room/group welcome self-heal request timestamp per group (30s window). */
  private _welcomeSelfHealInflight: Map<string, number> = new Map();

  /** History-only backup work is intentionally independent of ratchet persistence. */
  private _historyBackupScheduler: HistoryBackupScheduler;
  private _historyBackupClient: HistoryBackupClient | null = null;
  private _historyRestoreController: AbortController | null = null;
  private _historyUploadController: AbortController | null = null;
  private _historyKeyCacheIdentity: string | null = null;
  /** Serializes every atomic credential publication so snapshots cannot overtake each other. */
  private _stateSaveQueue: Promise<void> = Promise.resolve();
  /** Collapses reconnect/manual flush races into one ordered queue consumer. */
  private _outboundFlushPromise: Promise<void> | null = null;
  private _lifecycleGeneration = 0;

  // Reactive device-JWT reissue guard. Reset at the start of every _connect
  // so each WS session gets exactly one reissue attempt on a 401, preventing
  // an authedFetch loop when the reissued JWT is itself rejected.
  private _reissueAttempted: boolean = false;

  // Set to true when an `auth_failed` event has been emitted in the current
  // session (either via proactive reissue in `_maybeReissueDeviceJwt` or via
  // reactive reissue in `_authedFetch`). Used by the rapid-disconnect
  // classifier in `_scheduleReconnect` to distinguish auth failure from
  // session-collision so we emit the correct cause and stop reconnecting.
  // Reset at the start of every `_connect`.
  private _authFailedThisSession: boolean = false;

  // Last observed WebSocket close code, captured by the ws.on("close", ...)
  // handler. Used by the rapid-disconnect classifier: 4001 = server rejected
  // the credentials (auth_failed); 4000 = reserved "session_replaced".
  private _lastCloseCode: number | null = null;

  // True while a once("ready") listener is waiting to honour config.httpPort,
  // so repeated start() calls do not stack listeners.
  private _httpPortListenerArmed = false;

  // Liveness detection: server sends app-level {"event":"ping"} every 30s.
  // We check every 30s; if no data received in 90s (3 missed pings), connection is dead.
  private static readonly PING_INTERVAL_MS = 30_000;
  private static readonly SILENCE_TIMEOUT_MS = 90_000;
  private static readonly POLL_FALLBACK_INTERVAL_MS = 30_000;  // 30s when messages found
  static readonly TRUST_TOKEN_UNAVAILABLE_BACKOFF_MS = 6 * 60 * 60 * 1000;
  private static readonly POLL_FALLBACK_IDLE_MS = 60_000;      // 60s when idle

  /**
   * When true, start() ignores any persisted credentials/backup in dataDir
   * and goes straight to the fresh-enrollment path. Surface for setup --force
   * to recover from expired/corrupt device JWTs.
   *
   * Spec: docs/superpowers/specs/2026-05-25-device-credential-lifecycle-design.md §8.2
   */
  private _forceReEnroll: boolean;

  constructor(private config: SecureChannelConfig) {
    super();
    this._forceReEnroll = config.forceReEnroll ?? false;
    this._historyBackupScheduler = this._createHistoryBackupScheduler();
  }

  private _createHistoryBackupScheduler(): HistoryBackupScheduler {
    return new HistoryBackupScheduler({ upload: async () => this._uploadHistorySnapshot() });
  }

  /**
   * Decode the `exp` claim from a JWT without verifying the signature. The
   * plugin only uses this to decide whether to proactively reissue a device
   * credential; the backend always re-verifies signatures.
   */
  private _decodeJwtExp(jwt: string): number {
    try {
      const payload = jwt.split(".")[1];
      if (!payload) return 0;
      const body = JSON.parse(Buffer.from(payload, "base64url").toString("utf-8"));
      return typeof body.exp === "number" ? body.exp : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Notify the AV backend that device credentials are catastrophically broken
   * (the plugin's own auth is what failed, so this endpoint is intentionally
   * unauthenticated). Writes a single row into ``devices.last_auth_failure``
   * which the AV web UI surfaces as a re-enroll banner (Task 25).
   *
   * Best-effort: a transport failure here MUST NOT propagate — we still want
   * the in-process ``auth_failed`` event to fire so the CLI/UI can prompt.
   *
   * Spec: docs/superpowers/specs/2026-05-25-device-credential-lifecycle-design.md §8.1
   */
  private async _postAuthFailedToBackend(
    reason: "device_jwt_expired" | "device_revoked" | "device_deleted",
  ): Promise<void> {
    const deviceId = this._persisted?.deviceId;
    const apiUrl = this.config?.apiUrl;
    if (!deviceId || !apiUrl) return;
    try {
      await fetch(`${apiUrl}/api/v1/devices/${deviceId}/health`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind: "auth_failed",
          reason,
          action_required: "re_enroll",
          ts: new Date().toISOString(),
        }),
      });
    } catch (err) {
      console.warn("[SecureChannel] failed to post auth_failed:", err);
    }
  }

  /**
   * Ask the PUBLIC status endpoint what this device is right now. Used to
   * CONFIRM a credential rejection before acting on it — a 403 on its own
   * cannot tell "revoked" from "paused by its owner" (both are non-ACTIVE to
   * get_device_auth), and treating a pause as a revoke is what removed
   * loopita's launchd service on 2026-10-25.
   *
   * Only two answers are terminal: REVOKED, and a deleted row (404 whose
   * JSON `detail` is exactly get_device_or_404's "Device not found" — any
   * other 404 is something in the way, not proof). Everything unreadable or inconclusive (network, 5xx, 429,
   * PENDING, an ACTIVE device answering 403) is `unknown`, which callers MUST
   * treat as transient: never terminal, never an uninstall, never a banner.
   */
  private async _readDeviceStanding(): Promise<
    | { kind: "active" }
    | { kind: "suspended" }
    | { kind: "gone"; reason: "device_revoked" | "device_deleted" }
    | { kind: "unknown"; why: string }
  > {
    const deviceId = this._deviceId ?? this._persisted?.deviceId;
    if (!deviceId) return { kind: "unknown", why: "no device id" };
    try {
      const st = await pollDeviceStatus(this.config.apiUrl, deviceId);
      if (!st || st.rateLimited) return { kind: "unknown", why: "rate limited" };
      if (st.status === "SUSPENDED") return { kind: "suspended" };
      if (st.status === "REVOKED") return { kind: "gone", reason: "device_revoked" };
      if (st.status === "ACTIVE" || st.status === "APPROVED") return { kind: "active" };
      return { kind: "unknown", why: `status ${String(st.status)}` };
    } catch (err) {
      const e = err as { httpStatus?: number; body?: string };
      if (e?.httpStatus === 404) {
        try {
          const parsed = JSON.parse(e.body ?? "") as { detail?: unknown };
          // EXACTLY get_device_or_404's detail. FastAPI's own unknown-route
          // 404 is also JSON with a `detail` ("Not Found") — a wrong URL or a
          // proxy in the way must never read as "this device was deleted".
          if (parsed?.detail === SecureChannel.DEVICE_DELETED_DETAIL) {
            return { kind: "gone", reason: "device_deleted" };
          }
        } catch {
          /* not the backend's 404 — inconclusive */
        }
      }
      return { kind: "unknown", why: err instanceof Error ? err.message : String(err) };
    }
  }

  /**
   * A credential path answered 403 (today: /auth/reissue). Confirm before
   * concluding anything:
   *   - REVOKED / deleted  -> terminal: re_enroll health report + `auth_failed`
   *                           carrying `deviceGone: true` (the ONLY verdict the
   *                           bridges' self-uninstall acts on).
   *   - SUSPENDED          -> suspended-wait (idle, keep polling, resume).
   *   - anything else      -> transient; nothing is emitted or reported.
   * Already paused => stay paused without another status read: the
   * suspended-wait poll owns that question and will see a later revoke.
   */
  private async _confirmForbidden(source: string): Promise<"gone" | "suspended" | "transient"> {
    if (this._suspended) return "suspended";
    const standing = await this._readDeviceStanding();
    if (standing.kind === "gone") {
      console.warn(
        `[SecureChannel] ${source}: 403 confirmed by the status endpoint (${standing.reason}) — terminal`,
      );
      this._authFailedThisSession = true;
      await this._postAuthFailedToBackend(standing.reason);
      this.emit("auth_failed", { reason: standing.reason, deviceGone: true });
      return "gone";
    }
    if (standing.kind === "suspended") {
      this._enterSuspendedWait(source);
      return "suspended";
    }
    console.warn(
      `[SecureChannel] ${source}: 403 but the device is not confirmed revoked (${
        standing.kind === "unknown" ? standing.why : standing.kind
      }) — treating as transient`,
    );
    return "transient";
  }

  /** The backend's exact detail for an inactive (paused/revoked/deleted) device. */
  static readonly INACTIVE_DEVICE_DETAIL = "Device not found or not active";
  /** get_device_or_404's exact detail — the only 404 that means "row deleted". */
  static readonly DEVICE_DELETED_DETAIL = "Device not found";
  /**
   * After a TRANSIENT inactive-403 verdict, how long before another 403 may
   * re-read the status endpoint. Long enough that a burst cannot trip the
   * 1-per-5s limiter (which would itself read as "unknown"); short enough that
   * a revoke behind an outage is still noticed within the session (dr-evil Q1).
   */
  static readonly INACTIVE_403_RECHECK_MS = 30_000;

  /** Read a FastAPI error `detail` without consuming the caller's body. */
  private async _errorDetail(resp: Response): Promise<string | null> {
    try {
      const body = (await resp.clone().json()) as { detail?: unknown };
      return typeof body?.detail === "string" ? body.detail : null;
    } catch {
      return null;
    }
  }

  // One confirmation per WS session, shared by every caller: a burst of 403s
  // (the open handler fires several REST calls at once) must not turn into a
  // burst of status reads against a 1-per-5s rate limit. Reset in _connect.
  private _inactive403Check: Promise<"gone" | "suspended" | "transient"> | null = null;
  private _inactive403RetryAt = 0;

  /**
   * A REST call answered 403 "Device not found or not active". With the
   * companion backend change this is how EVERY inactive-device auth failure
   * arrives (401 now means a bad token only, so `_authedFetch`'s reissue never
   * runs for it). Same rule as the reissue path: confirm, then act.
   */
  private _confirmInactive403(source: string): Promise<"gone" | "suspended" | "transient"> {
    if (this._inactive403Check) return this._inactive403Check;
    if (Date.now() < this._inactive403RetryAt) return Promise.resolve("transient");
    const check = this._confirmForbidden(source).then((verdict) => {
      // A conclusive verdict stands for the session; a transient one is
      // re-asked after the back-off (a first "unknown" must not hide a revoke
      // for the rest of the session).
      if (verdict === "transient" && this._inactive403Check === check) {
        this._inactive403Check = null;
        this._inactive403RetryAt = Date.now() + SecureChannel.INACTIVE_403_RECHECK_MS;
      }
      return verdict;
    });
    this._inactive403Check = check;
    return check;
  }

  /**
   * Proactively reissue the persisted device JWT when its remaining lifetime
   * is under 30 days. Called before each WebSocket connect attempt so the
   * credential is refreshed during routine reconnect cycles rather than
   * forcing the customer to re-enroll once it expires.
   *
   * Non-fatal: a transient network error or 5xx keeps the current JWT and
   * relies on the next reconnect. Terminal failures (401/403) surface via
   * the `auth_failed` event so callers can prompt the user to recover.
   *
   * Returns TRUE when the credentials are terminally dead. #743: the caller
   * MUST NOT go on to open a WebSocket in that case — the server has already
   * told us this credential can never be served, and connecting anyway is what
   * produced `ws_guard action=denylist` on repeat in prod (device 0e87ff50, a
   * hard-deleted device, ~12/hour). The verdict is returned explicitly rather
   * than read back off `_authFailedThisSession`, whose lifetime is reset per
   * connect and is easy to get subtly wrong.
   */
  //
  // 2026-10-25: a TRUE return now also covers "paused by its owner" (a 403
  // confirmed SUSPENDED). The caller tells the two apart by `_suspended`.
  private async _maybeReissueDeviceJwt(): Promise<boolean> {
    const jwt = this._persisted?.deviceJwt ?? this._deviceJwt;
    if (!jwt) return false;

    const exp = this._decodeJwtExp(jwt);
    const nowSec = Math.floor(Date.now() / 1000);
    if (exp - nowSec >= 30 * 86400) return false; // > 30 days left, skip

    try {
      const resp = await fetch(`${this.config.apiUrl}/api/v1/auth/reissue`, {
        method: "POST",
        headers: { Authorization: `Bearer ${jwt}` },
      });

      if (resp.status === 200) {
        const body = (await resp.json()) as { device_jwt?: string };
        if (body.device_jwt) {
          // Always update in-memory if we got a fresh token, so this connect
          // uses it even if persisted state is somehow missing.
          this._deviceJwt = body.device_jwt;
          if (this._persisted) {
            this._persisted.deviceJwt = body.device_jwt;
            await this._persistState();
            console.log("[SecureChannel] device_jwt reissued (5y)");
          } else {
            console.log("[SecureChannel] device_jwt reissued (5y) — in memory only (no persisted state)");
          }
        } else {
          console.warn("[SecureChannel] reissue returned 200 but no device_jwt in response");
        }
        return false;
      }

      if (resp.status === 403) {
        // 403 = "device not found or not active" — which includes PAUSED.
        // Never conclude "revoked" from it alone (2026-10-25): confirm. A
        // confirmed revoke/delete or a pause both abort this connect (the WS
        // would only be rejected); a transient answer lets it proceed, and the
        // server's own connection_rejected decides from there.
        console.warn("[SecureChannel] reissue rejected (403); confirming device status");
        const verdict = await this._confirmForbidden("reissue_forbidden");
        return verdict !== "transient";
      }

      if (resp.status === 401) {
        // Terminal — the JWT itself is no longer accepted (the device may well
        // be alive, so this is never `deviceGone`). Surface via the event
        // surface so callers (CLI, UI) can show a recovery prompt.
        console.warn(`[SecureChannel] reissue rejected (${resp.status}); credentials need attention`);
        this._authFailedThisSession = true;
        // Task 24 — notify backend (best-effort) BEFORE emitting locally so
        // the AV web UI banner can render even if listeners are slow to react.
        await this._postAuthFailedToBackend("device_jwt_expired");
        this.emit("auth_failed", { reason: "device_jwt_expired", deviceGone: false });
        return true;
      }

      // 5xx / other — non-fatal. Retry on next connect.
      console.warn(`[SecureChannel] reissue ${resp.status}; keeping current jwt`);
    } catch (err) {
      console.warn("[SecureChannel] reissue network error; keeping current jwt:", err);
    }
    return false;
  }

  /**
   * Wraps `fetch` for authenticated AV REST endpoints with reactive device-JWT
   * reissue on 401. On the first 401 of a WS session, calls /auth/reissue
   * once; if it returns a fresh JWT, adopts it (in-memory + persisted) and
   * retries the original request with the new bearer. The `_reissueAttempted`
   * guard ensures we never loop — a second 401 (or a 401/403 from the reissue
   * itself) emits `auth_failed` and the caller sees the original 401.
   *
   * Existing fetch sites can opt in by replacing `fetch(url, init)` with
   * `this._authedFetch(url, init)`. The wrapper sets the `Authorization`
   * header itself, so callers can omit it.
   */
  private async _authedFetch(url: string, init?: RequestInit): Promise<Response> {
    const jwt = this._deviceJwt ?? this._persisted?.deviceJwt ?? "";
    const baseHeaders = (init?.headers as Record<string, string> | undefined) ?? {};
    const headers: Record<string, string> = {
      ...baseHeaders,
      Authorization: `Bearer ${jwt}`,
    };
    let resp = await fetch(url, { ...init, headers });

    if (resp.status === 401 && !this._reissueAttempted) {
      this._reissueAttempted = true;
      let reissueResp: Response;
      try {
        reissueResp = await fetch(`${this.config.apiUrl}/api/v1/auth/reissue`, {
          method: "POST",
          headers: { Authorization: `Bearer ${jwt}` },
        });
      } catch (err) {
        // Network error on reissue — leave the original 401 for the caller.
        console.warn("[SecureChannel] authedFetch reissue network error:", err);
        return resp;
      }

      if (reissueResp.status === 200) {
        const body = (await reissueResp.json()) as { device_jwt?: string };
        if (body.device_jwt) {
          this._deviceJwt = body.device_jwt;
          if (this._persisted) {
            this._persisted.deviceJwt = body.device_jwt;
            try {
              await this._persistState();
            } catch (err) {
              // Non-fatal — fresh JWT is in-memory regardless.
              console.warn("[SecureChannel] authedFetch persist after reissue failed:", err);
            }
          }
          const retryHeaders: Record<string, string> = {
            ...baseHeaders,
            Authorization: `Bearer ${body.device_jwt}`,
          };
          resp = await fetch(url, { ...init, headers: retryHeaders });
        }
      } else if (reissueResp.status === 403) {
        // 2026-10-25: a PAUSED device lands here too (telemetry auth 401s any
        // non-ACTIVE device, then get_device_auth 403s the reissue). Mapping
        // this straight to "device_revoked" is what made loopita uninstall its
        // own service. Confirm against the status endpoint; only a confirmed
        // revoke/delete is terminal. Either way the caller gets the original
        // 401 — no silent retry.
        await this._confirmForbidden("reissue_forbidden");
      } else if (reissueResp.status === 401) {
        // Terminal — the JWT itself is rejected. Surface to callers (CLI / UI)
        // so they can prompt the user to re-enroll. Leave `resp` as the
        // original 401 so the caller sees an auth failure rather than
        // misinterpreting a silent retry. The device may be alive, so this is
        // never `deviceGone`.
        this._authFailedThisSession = true;
        // Task 24 — notify backend (best-effort) BEFORE emitting locally so
        // the AV web UI banner can render even if listeners are slow to react.
        await this._postAuthFailedToBackend("device_jwt_expired");
        this.emit("auth_failed", { reason: "device_jwt_expired", deviceGone: false });
      }
      // Other reissue statuses (5xx etc.) fall through: caller gets the original 401.
    }
    // Direct 403 for an inactive device (companion backend change). Confirm via
    // the status endpoint: REVOKED/deleted -> terminal (deviceGone), SUSPENDED
    // -> suspended-wait, anything else -> nothing. Any OTHER 403 is an
    // authorization answer about the resource, not the device, and concludes
    // nothing here. The caller still gets the 403 either way.
    if (
      resp.status === 403 &&
      (await this._errorDetail(resp)) === SecureChannel.INACTIVE_DEVICE_DETAIL
    ) {
      await this._confirmInactive403("rest_forbidden");
    }
    return resp;
  }

  get state(): ChannelState {
    return this._state;
  }

  get deviceId(): string | null {
    return this._deviceId;
  }

  /** This agent's AgentVault display_name (#499, A1). "" until resolved. */
  get ownDisplayName(): string {
    return this._ownDisplayName;
  }

  /** This agent's AgentVault hub_address (#499, A1). "" until known. */
  get ownHubAddress(): string {
    return this._persisted?.hubAddress ?? "";
  }

  /** This agent's AgentVault hub_id. "" until enrolment persists it.
   *
   * Lives in the credential store, NOT in the channel config — which is where
   * the memory-recall helper was reading it from. `channel.config.hubId` is
   * unset for every agent enrolled through the normal flow, so
   * `channel.config.hubId || ""` produced `/api/v1/agents//memory/search`, a
   * 404 on every inbound message, swallowed by `if (!res.ok) return ""`.
   * Memory recall was silently dead while the hub_id sat right here.
   */
  get ownHubId(): string {
    return this._persisted?.hubId ?? "";
  }

  /** Public best-effort trigger to (re)resolve this agent's display_name.
   * Called lazily from the 1:1 DM path when the name is still unknown, so a
   * resolve that failed at connect (or a fresh agent that hadn't registered
   * yet) is retried without waiting for the next WS reconnect (#499, A1). */
  ensureOwnIdentityResolved(): Promise<void> {
    return this._resolveOwnDisplayName();
  }

  /** Resolve this agent's own display_name from GET /api/v1/hub/identities
   * (matches its own device_id). Deduped — concurrent callers share one
   * in-flight fetch. Refreshes the cached value on success (picks up a rename);
   * on failure leaves the prior value intact. Best-effort — never throws.
   * Mirrors Hermes RestClient.get_own_identity (#499, Component 0). */
  private _resolveOwnDisplayName(): Promise<void> {
    if (this._resolvingOwnDisplayName) return this._resolvingOwnDisplayName;
    const p = (async () => {
      const jwt = this._persisted?.deviceJwt ?? this._deviceJwt;
      const deviceId = this._persisted?.deviceId ?? this._deviceId;
      if (!jwt || !deviceId) return;
      try {
        const res = await fetch(`${this.config.apiUrl}/api/v1/hub/identities`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (!res.ok) return;
        const list = await res.json();
        if (!Array.isArray(list)) return;
        const mine = list.find((x: any) => x?.device_id === deviceId);
        if (mine?.display_name) this._ownDisplayName = String(mine.display_name);
      } catch {
        /* best-effort — retried on next connect (hub_identity_sync) or next DM */
      }
    })();
    this._resolvingOwnDisplayName = p;
    void p.finally(() => {
      if (this._resolvingOwnDisplayName === p) this._resolvingOwnDisplayName = null;
    });
    return p;
  }

  get fingerprint(): string | null {
    return this._fingerprint;
  }

  /** Returns the primary conversation ID (backward-compatible). */
  get conversationId(): string | null {
    return this._primaryConversationId || null;
  }

  /** Returns all active conversation IDs. */
  get conversationIds(): string[] {
    return Array.from(this._sessions.keys());
  }

  /** Returns the number of active sessions. */
  get sessionCount(): number {
    return this._sessions.size;
  }

  /** Room ID from the most recent inbound room message (for HTTP /send fallback). */
  get lastInboundRoomId(): string | undefined {
    return this._lastInboundRoomId;
  }

  /** Returns all persisted room IDs and names (for outbound target registration). */
  get roomIds(): Array<{ roomId: string; name: string }> {
    if (!this._persisted?.rooms) return [];
    return Object.values(this._persisted.rooms).map((r) => ({ roomId: r.roomId, name: r.name }));
  }

  /** Returns hub addresses of all persisted A2A peer participants. */
  get a2aPeerAddresses(): string[] {
    if (!this._persisted?.a2aChannels) return [];
    const addrs: string[] = [];
    for (const ch of Object.values(this._persisted.a2aChannels)) {
      for (const p of ch.participants ?? []) {
        if (p.status === "active" || p.status === "invited") {
          addrs.push(p.hubAddress);
        }
      }
    }
    return [...new Set(addrs)];
  }

  /** Resolves an A2A channel ID to the first active peer's hub address, or null if not found. */
  resolveA2AChannelHub(channelId: string): string | null {
    const entry = this._persisted?.a2aChannels?.[channelId];
    if (!entry?.participants?.length) return null;
    const active = entry.participants.find(p => p.status === "active");
    return active?.hubAddress ?? entry.participants[0]?.hubAddress ?? null;
  }

  /** Returns the TelemetryReporter instance (available after WebSocket connect). */
  get telemetry(): TelemetryReporter | null {
    return this._telemetryReporter;
  }

  /**
   * Check if a skill is in shadow mode. Returns the shadow config if active, undefined otherwise.
   */
  getShadowConfig(skillName: string): { sessionId: string; autonomyLevel: string; decisionClass: string } | undefined {
    return this._persisted?.shadowSkills?.[skillName];
  }

  async start(): Promise<void> {
    // A caller may reuse this instance immediately after a previous lifecycle.
    // Never hydrate an older disk snapshot while an earlier history publication
    // is still queued behind the atomic state writer.
    await this._stateSaveQueue;
    this._stopped = false;
    this._clearSuspendedWait();
    this._lifecycleGeneration += 1;
    this._historyBackupScheduler.stop();
    this._historyBackupScheduler = this._createHistoryBackupScheduler();
    await sodium.ready;

    // Honour the documented `httpPort` config: open the local send server once
    // the channel is usable (the same point the OpenClaw entries start it).
    // startHttpServer is idempotent, so an explicit call elsewhere is harmless.
    const httpPort = this.config.httpPort;
    if (typeof httpPort === "number" && !this._httpPortListenerArmed) {
      this._httpPortListenerArmed = true;
      this.once("ready", () => {
        this._httpPortListenerArmed = false;
        if (!this._stopped) this.startHttpServer(httpPort);
      });
    }

    // setup --force path: wipe any cached credentials so the enrollment
    // flow below creates fresh ones from the invite token. Without this
    // clear, a partially-written backup or legacy state file could
    // resurface on the next start() and we'd be back to the silent
    // "Already enrolled" failure mode this flag exists to break.
    //
    // Spec: docs/superpowers/specs/2026-05-25-device-credential-lifecycle-design.md §8.2
    if (this._forceReEnroll) {
      console.log("[SecureChannel] forceReEnroll set — clearing cached credentials, will enroll fresh");
      await clearState(this.config.dataDir);
      this._persisted = null;
      await this._enroll();
      return;
    }

    // Check for persisted state (may be legacy or new format)
    const raw = await loadState(this.config.dataDir);
    if (raw) {
      await this._hydrateFromPersisted(raw);
      await this._initializeHistoryRecovery();
      this._connect();
      return;
    }

    // Try restoring from backup before enrolling
    const restored = await restoreState(this.config.dataDir);
    if (restored) {
      console.log("[SecureChannel] Restored state from backup");
      const restoredRaw = await loadState(this.config.dataDir);
      if (restoredRaw) {
        await this._hydrateFromPersisted(restoredRaw);
        await this._initializeHistoryRecovery();
        this._connect();
        return;
      }
    }

    // Full lifecycle: enroll -> poll -> activate -> connect
    await this._enroll();
  }

  /**
   * Hydrate all in-memory state from persisted creds. The single source of
   * truth for both the primary boot and the .bak-restore boot, so the two can
   * never drift (the drift is what caused #416: the backup branch used to skip
   * MLS group loading and dropped owner MLS DMs). Callers invoke _connect()
   * after this resolves.
   */
  private async _hydrateFromPersisted(raw: unknown): Promise<void> {
    await backupState(this.config.dataDir);
    this._persisted = migratePersistedState(raw);
    if (!this._persisted.messageHistory) {
      this._persisted.messageHistory = [];
    }
    this._deviceId = this._persisted.deviceId;
    this._deviceJwt = this._persisted.deviceJwt;
    this._primaryConversationId = this._persisted.primaryConversationId;
    this._fingerprint = this._persisted.fingerprint;

    // Restore sticky room context for proactive send routing
    this._lastInboundRoomId = this._persisted.lastInboundRoomId;

    // Restore dedup sets from persisted state
    if (this._persisted.seenMessageIds) {
      this._seenMessageIds = new Set(this._persisted.seenMessageIds.slice(-SecureChannel.SEEN_MSG_MAX));
    }
    if (this._persisted.seenA2AMessageIds) {
      this._a2aSeenMessageIds = new Set(this._persisted.seenA2AMessageIds.slice(-SecureChannel.A2A_SEEN_MAX));
    }

    // Clean up stale DR room sessions (rooms are MLS-only now)
    if (this._persisted?.rooms) {
      const roomConvIds = new Set<string>();
      for (const room of Object.values(this._persisted.rooms)) {
        for (const cid of room.conversationIds || []) {
          roomConvIds.add(cid);
        }
      }
      let cleanedCount = 0;
      for (const cid of roomConvIds) {
        if (this._persisted.sessions[cid]) {
          delete this._persisted.sessions[cid];
          cleanedCount++;
        }
      }
      if (cleanedCount > 0) {
        console.log(`[SecureChannel] Cleaned ${cleanedCount} stale DR room sessions`);
      }
    }

    // Restore all ratchet sessions
    for (const [convId, sessionData] of Object.entries(
      this._persisted.sessions,
    )) {
      if (sessionData.ratchetState) {
        const ratchet = DoubleRatchet.deserialize(sessionData.ratchetState);
        this._sessions.set(convId, {
          ownerDeviceId: sessionData.ownerDeviceId,
          ratchet,
          activated: sessionData.activated ?? false,
          epoch: sessionData.epoch,
        });
      }
    }

    // Rebuild _sessionGroupIds from persisted conversationGroupIds
    if (this._persisted.conversationGroupIds) {
      this._sessionGroupIds = new Map(Object.entries(this._persisted.conversationGroupIds));
      console.log(`[SecureChannel] Restored ${this._sessionGroupIds.size} conversation→group mappings`);
    }

    // Load MLS group states for rooms
    if (this._persisted.rooms) {
      for (const [roomId, room] of Object.entries(this._persisted.rooms)) {
        const mlsGroupId = room.mlsGroupId;
        if (mlsGroupId) {
          try {
            const mlsState = await loadMlsState(this.config.dataDir, mlsGroupId);
            if (mlsState) {
              const mgr = new MLSGroupManager();
              mgr.importState(JSON.parse(mlsState) as SerializedMLSGroupState);
              this._mlsGroups.set(roomId, mgr);
            }
          } catch (mlsErr) {
            console.warn(`[SecureChannel] Failed to restore MLS state for room ${roomId.slice(0, 8)}:`, mlsErr);
          }
        }
      }
    }

    // Load MLS group states for 1:1 conversations
    for (const [convId, entry] of Object.entries(this._persisted.mlsConversations ?? {})) {
      if (entry.mlsGroupId) {
        try {
          const mlsState = await loadMlsState(this.config.dataDir, entry.mlsGroupId);
          if (mlsState) {
            const mgr = new MLSGroupManager();
            mgr.importState(JSON.parse(mlsState) as SerializedMLSGroupState);
            this._mlsGroups.set(`conv:${convId}`, mgr);
          }
        } catch (mlsErr) {
          console.warn(`[SecureChannel] Failed to restore MLS state for conv ${convId.slice(0, 8)}:`, mlsErr);
        }
      }
    }

    // Load shared 1:1 MLS groups
    for (const [gid, entry] of Object.entries(this._persisted.mlsGroups ?? {})) {
      if (!entry.mlsGroupId) continue;
      try {
        const stateJson = await loadMlsState(this.config.dataDir, entry.mlsGroupId);
        if (stateJson) {
          const mgr = new MLSGroupManager();
          mgr.importState(JSON.parse(stateJson) as SerializedMLSGroupState);
          this._mlsGroups.set(`1to1-group:${gid}`, mgr);
          console.log(`[SecureChannel] Loaded shared MLS group for ${gid.slice(0, 8)} (epoch=${mgr.epoch})`);
        }
      } catch (loadErr) {
        console.warn(`[SecureChannel] Failed to load shared MLS group ${gid.slice(0, 8)}:`, loadErr);
      }
    }

    // Load MLS group states for A2A channels
    for (const [channelId, entry] of Object.entries(this._persisted.a2aChannels ?? {})) {
      if (entry.mlsGroupId) {
        try {
          const mlsState = await loadMlsState(this.config.dataDir, entry.mlsGroupId);
          if (mlsState) {
            const mgr = new MLSGroupManager();
            mgr.importState(JSON.parse(mlsState) as SerializedMLSGroupState);
            this._mlsGroups.set(`a2a:${channelId}`, mgr);
          }
        } catch (mlsErr) {
          console.warn(`[SecureChannel] Failed to restore MLS state for A2A ${channelId.slice(0, 8)}:`, mlsErr);
        }
      }
    }
  }

  /**
   * Fetch scan rules from the server and load them into the ScanEngine.
   */
  private async _fetchScanRules(): Promise<void> {
    if (!this._scanEngine) return;
    try {
      const resp = await fetch(`${this.config.apiUrl}/api/v1/scan-rules`, {
        headers: { Authorization: `Bearer ${this._persisted?.deviceJwt}` },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      if (data.rule_set_version !== this._scanRuleSetVersion) {
        this._scanEngine.loadRules(data.rules);
        this._scanRuleSetVersion = data.rule_set_version;
      }
    } catch (err) {
      console.error("[SecureChannel] Failed to fetch scan rules:", err);
    }
  }

  /**
   * Activate all sibling 1:1 sessions when any session receives an owner message.
   * Ensures agent replies fan out to ALL owner devices, not just the active one.
   */
  private _activateSiblings(triggerConvId: string): void {
    const roomConvIds = new Set<string>();
    if (this._persisted?.rooms) {
      for (const room of Object.values(this._persisted.rooms)) {
        for (const cid of room.conversationIds) {
          roomConvIds.add(cid);
        }
      }
    }
    for (const [sibConvId, sibSession] of this._sessions) {
      if (sibConvId === triggerConvId) continue;
      if (roomConvIds.has(sibConvId)) continue;
      if (!sibSession.activated) {
        sibSession.activated = true;
        console.log(`[SecureChannel] Sibling session ${sibConvId.slice(0, 8)}... activated (multi-device fan-out)`);
        if (this._persisted?.sessions[sibConvId]) {
          this._persisted.sessions[sibConvId].activated = true;
        }
      }
    }
  }

  /**
   * Append a message to persistent history for cross-device replay.
   */
  private async _appendHistory(
    sender: "owner" | "agent",
    text: string,
    topicId?: string,
    transportTimestamp?: string,
    stableId?: string,
    persist = true,
  ): Promise<boolean> {
    if (!this._persisted) return false;
    if (!this._persisted.messageHistory) {
      this._persisted.messageHistory = [];
    }

    if (stableId && this._persisted.messageHistory.some((entry) => entry.id === stableId)) {
      // A previous publication may have failed after the in-memory append.
      // Retrying the same stable delivery must retry durability without
      // creating a duplicate or another remote-backup revision.
      if (persist) await this._saveHistoryState();
      return false;
    }

    const maxSize = this.config.maxHistorySize ?? 500;
    const entry: HistoryEntry = {
      id: stableId ?? randomUUID(),
      sender,
      text,
      ts: transportTimestamp && Number.isFinite(Date.parse(transportTimestamp))
        ? new Date(transportTimestamp).toISOString()
        : new Date().toISOString(),
    };
    if (topicId) {
      entry.topicId = topicId;
    }
    this._persisted.messageHistory.push(entry);

    // Evict oldest entries if over limit
    if (this._persisted.messageHistory.length > maxSize) {
      this._persisted.messageHistory = this._persisted.messageHistory.slice(-maxSize);
    }
    // The remote revision represents the successful application mutation, not
    // the outcome of its local disk write. A failed write is retried by stable
    // ID without incrementing the revision a second time.
    this._markHistoryDirty();
    if (persist) await this._saveHistoryState();
    return true;
  }

  /** Produce the canonical routing context stored in HistoryEntry.topicId. */
  private _historyContext(kind: "dm" | "room" | "a2a", id?: string): string {
    if ((kind === "room" || kind === "a2a") && !id) {
      throw new Error(`History ${kind} context requires an ID`);
    }
    return `${kind}:${id ?? ""}`;
  }

  /*
   * #1416 — DM history is partitioned by conversation GROUP.
   *
   * Since #1399 every person has their own 1:1 group with the agent, but the
   * agent kept one DM history pool: a catch-up request or a session_init
   * replay from person B's group was answered with person A's plaintext too.
   * A DM entry's context now names its group:
   *
   *   dm:g:<groupId>:<topicId>   — the group is known
   *   dm:c:<convId>:<topicId>    — the conversation's group is not known yet
   *   dm:<topicId>               — legacy (pre-#1416): the PRIMARY group's
   *
   * Every form keeps the `dm:` prefix the archive, the web client and Hermes
   * test for, and the archive format (`topicId` is a free string) is unchanged.
   * An agent that knows at most one group selects exactly what it did before.
   */

  /** The DM history context for a message in `gid` (or `convId`'s group). */
  private _dmHistoryContext(
    topicId: string | undefined,
    gid: string | undefined,
    convId: string | undefined,
  ): string {
    const g = gid ?? (convId ? this._sessionGroupIds.get(convId) : undefined);
    if (g) return `dm:g:${g}:${topicId ?? ""}`;
    // An agent that knows no groups at all has one person: keep the legacy
    // form, byte for byte. Otherwise an unmapped conversation is tagged by id.
    if (convId && this._knownDmGroups().size > 0) return `dm:c:${convId}:${topicId ?? ""}`;
    // A topic shaped like a tag would forge another group's entry once the
    // legacy form is parsed; it never reaches the context.
    const legacyTopic = topicId && /^[gc]:/.test(topicId) ? "" : topicId;
    return this._historyContext("dm", legacyTopic);
  }

  /** Every conversation group this agent knows of. */
  private _knownDmGroups(): Set<string> {
    const known = new Set<string>(this._sessionGroupIds.values());
    if (this._persisted?.groupId) known.add(this._persisted.groupId);
    return known;
  }

  // --- #1418: only the person involved ----------------------------------

  /**
   * Run `fn` as the work an inbound 1:1 message from `conversationId`
   * triggered. Untargeted owner content sent while it runs — from inside its
   * async call chain, or from anywhere while it is the only turn in progress —
   * goes to that conversation's person only.
   *
   * `opts.groupId` is the group that message DECRYPTED under
   * (`metadata.conversationGroupId`). Pass it: it outranks the local
   * conversation→group map, which can be stale, and it is all there is for a
   * frame whose conversation is not mapped yet. With neither an id nor a
   * group, `fn` runs unchanged.
   */
  async runForConversation<T>(
    conversationId: string | undefined,
    fn: () => Promise<T>,
    opts?: { groupId?: string },
  ): Promise<T> {
    const groupId = typeof opts?.groupId === "string" && opts.groupId ? opts.groupId : undefined;
    if (!conversationId && !groupId) return fn();
    const store: TurnStore = { convId: conversationId || undefined, groupId, ended: false };
    this._activeTurns.add(store);
    try {
      return await _turnConversation.run(store, fn);
    } finally {
      // Work the turn started (a timer, a background job) inherits the store;
      // once the turn is over it no longer pins anyone.
      store.ended = true;
      store.endedAt = Date.now();
      this._activeTurns.delete(store);
    }
  }

  /**
   * The turn this call chain belongs to (#1418) — running, or ended but
   * still within the window in which work it left behind is for its person.
   * `"expired"` once that window is over: the work is proactive now.
   */
  private _currentTurn(): TurnStore | "expired" | undefined {
    const store = _turnConversation.getStore();
    if (!store) return undefined;
    if (!store.ended) return store;
    return Date.now() - (store.endedAt ?? 0) < PERSON_INVOLVED_WINDOW_MS ? store : "expired";
  }

  /**
   * Record an owner 1:1 message that just decrypted (#1418).
   *
   * Always kept as evidence of which conversation, group and browser belong
   * together (see `_dmPeople`). It makes its person "the person involved" only
   * when it is RECENT by the server's `createdAt`: a frame replayed from
   * backlog on reconnect is not something anyone is waiting on an answer to.
   */
  private _noteInboundDm(
    convId: string | undefined,
    groupId: string | undefined,
    opts: { createdAt?: string; senderDeviceId?: string } = {},
  ): void {
    if (!convId && !groupId) return;
    const now = Date.now();
    const sentAt = opts.createdAt ? Date.parse(opts.createdAt) : NaN;
    // A server clock slightly ahead of ours must not stretch the window.
    // A frame replayed from backlog keeps its old `at`, so it is never recent
    // (`_involvedPerson` checks the window against it) and can never make a
    // new person (`_dmPeople`).
    const at = Number.isFinite(sentAt) ? Math.min(now, sentAt) : now;

    const sender = opts.senderDeviceId && opts.senderDeviceId !== this._deviceId ? opts.senderDeviceId : undefined;
    const key = `${convId ?? ""}|${groupId ?? ""}|${sender ?? ""}`;
    const prior = this._attestedInbound.get(key);
    this._attestedInbound.delete(key); // re-insert: Map order = recency
    this._attestedInbound.set(key, { convId, groupId, senderDeviceId: sender, at: Math.max(prior?.at ?? 0, at) });
    while (this._attestedInbound.size > ATTESTED_INBOUND_MAX) {
      const oldest = this._attestedInbound.keys().next().value;
      if (oldest === undefined) break;
      this._attestedInbound.delete(oldest);
    }

    if (this._lastInboundDm && this._lastInboundDm.at > at) return; // an older frame arriving late
    this._lastInboundDm = {
      convId,
      groupId: groupId ?? (convId ? this._sessionGroupIds.get(convId) : undefined),
      at,
    };
  }

  /** The group `convId`'s most recent inbound decrypted under, if any. */
  private _attestedGroupOf(convId: string | undefined): string | undefined {
    if (!convId) return undefined;
    let gid: string | undefined;
    for (const a of this._attestedInbound.values()) {
      if (a.convId === convId && a.groupId) gid = a.groupId; // last one wins: newest
    }
    return gid;
  }

  /** Does some mapped conversation point at `groupId`? */
  private _isMappedGroup(groupId: string): boolean {
    for (const gid of this._sessionGroupIds.values()) if (gid === groupId) return true;
    return false;
  }

  /**
   * An owner message decrypted under a group no mapped conversation points at
   * (#1418): the map is stale — a Welcome landed before its conversation was
   * learned, or a conversation was recreated. Re-resolve it from the server.
   * Fire-and-forget and rate-limited per group: never awaited on the receive
   * or Welcome path, so it can never hold up MLS processing.
   */
  private _reconcileForUnmappedGroup(groupId: string | undefined): void {
    if (!groupId || this._isMappedGroup(groupId)) return;
    this._reconcileSoon(`g:${groupId}`);
  }

  /** The same for a Double Ratchet message on a conversation the map does not know. */
  private _reconcileForUnmappedConversation(convId: string | undefined): void {
    // An agent that maps nothing has nothing to correct (it behaves as before #1418).
    if (!convId || this._sessionGroupIds.size === 0 || this._sessionGroupIds.has(convId)) return;
    this._reconcileSoon(`c:${convId}`);
  }

  private _reconcileSoon(key: string): void {
    const now = Date.now();
    if (now - (this._unmappedReconcileAt.get(key) ?? 0) < UNMAPPED_RECONCILE_COOLDOWN_MS) return;
    this._unmappedReconcileAt.set(key, now);
    void Promise.resolve()
      .then(() => this._reconcileConversationGroups())
      .catch((err) => console.warn("[SecureChannel] Conversation-group reconcile (unmapped group) failed:", err));
  }

  /** A conversation of `groupId`, for an MLS frame that named only its group. */
  private _conversationOfGroup(groupId: string | undefined): string | undefined {
    if (!groupId) return undefined;
    if (this._primaryConversationId && this._sessionGroupIds.get(this._primaryConversationId) === groupId) {
      return this._primaryConversationId;
    }
    for (const [convId, gid] of this._sessionGroupIds) {
      if (gid === groupId) return convId;
    }
    return undefined;
  }

  /** Is `convId` one of this agent's own 1:1 conversations (never a room's or an A2A channel's)? */
  private _isOwnDmConversation(convId: string): boolean {
    if (this._isNonDmConversation(convId)) return false;
    return this._sessions.has(convId)
      || this._sessionGroupIds.has(convId)
      || !!this._persisted?.mlsConversations?.[convId]
      || convId === this._primaryConversationId;
  }

  /**
   * #1418 — the PEOPLE this agent has a 1:1 with, or `undefined` when it
   * cannot tell (no conversation is mapped: then it behaves as before #1418).
   *
   * People, not groups. Since #1399 one person has one group, but an agent
   * also keeps joined groups that are nobody's any more — husks of deleted or
   * recreated conversations. Measured 2026-10-24: wren held 13 joined groups
   * for 1 mapped, loopita 2 for 1, wowzer 2 for 1. Counting joined groups made
   * all three single-person agents look like several people, and their
   * replies could go only to a dead group.
   *
   * So a person is built from the conversations the server mapped, joined by
   * evidence that two things are the same person:
   *  - the same group (a legacy group shared by several people cannot be told
   *    apart, so it stays one — exactly the pre-#1418 behaviour);
   *  - the same owner browser (a browser belongs to exactly one person);
   *  - a message that decrypted under a group, from a conversation and browser.
   * A joined group nothing maps and nobody has spoken in is not a person. A
   * message on an unmapped group from a browser nobody knows IS someone —
   * that fails closed, toward "only them".
   */
  private _dmPeople(): { count: number; keyOf(convId?: string, groupId?: string): string | undefined } | undefined {
    if (this._sessionGroupIds.size === 0) return undefined;
    const parent = new Map<string, string>();
    const add = (x: string): void => { if (!parent.has(x)) parent.set(x, x); };
    const find = (x: string): string => {
      let root = x;
      while (parent.get(root) !== root) root = parent.get(root)!;
      return root;
    };
    const union = (a: string | undefined, b: string | undefined): void => {
      if (!a || !b) return;
      add(a); add(b);
      const ra = find(a), rb = find(b);
      if (ra !== rb) parent.set(ra, rb);
    };
    const ownerOf = (convId: string): string | undefined => {
      const owner = this._sessions.get(convId)?.ownerDeviceId ?? this._persisted?.sessions?.[convId]?.ownerDeviceId;
      return owner ? `d:${owner}` : undefined;
    };

    for (const [convId, gid] of this._sessionGroupIds) {
      add(`c:${convId}`);
      union(`c:${convId}`, `g:${gid}`);
      union(`c:${convId}`, ownerOf(convId));
    }
    // The primary conversation and the primary group are one person — but only
    // as a link between things already known: on its own it is nobody new.
    const primary = this._primaryConversationId || this._persisted?.primaryConversationId;
    const primaryGid = this._persisted?.groupId;
    if (primary && primaryGid && (parent.has(`c:${primary}`) || parent.has(`g:${primaryGid}`))) {
      union(`c:${primary}`, `g:${primaryGid}`);
    }
    // A group a Welcome joined in this process before any conversation mapped
    // it: a person not learned yet (until the next successful reconcile).
    for (const gid of this._welcomedUnmapped.keys()) add(`g:${gid}`);

    // Inbound evidence LINKS what is already known. It may create a NEW person
    // only when it is recent and the last successful reconcile did not already
    // rule its conversation and group out — otherwise one stray backlog frame
    // on a dead conversation would make a one-person agent two people forever.
    const records = [...this._attestedInbound.values()].map((a) => ({
      a,
      nodes: [
        a.convId ? `c:${a.convId}` : undefined,
        a.groupId ? `g:${a.groupId}` : undefined,
        a.senderDeviceId ? `d:${a.senderDeviceId}` : undefined,
      ].filter((n): n is string => !!n),
    }));
    const placed = new Set<number>();
    const place = (i: number): void => {
      const { a, nodes } = records[i];
      for (const n of nodes) add(n);
      for (const n of nodes.slice(1)) union(nodes[0], n);
      if (a.convId && this._sessionGroupIds.has(a.convId)) union(`c:${a.convId}`, ownerOf(a.convId));
      placed.add(i);
    };
    const linkKnown = (): void => {
      for (let progress = true; progress;) {
        progress = false;
        records.forEach((r, i) => {
          if (!placed.has(i) && r.nodes.some((n) => parent.has(n))) { place(i); progress = true; }
        });
      }
    };
    linkKnown();
    records.forEach((r, i) => { if (!placed.has(i) && this._mayBeNewPerson(r.a)) place(i); });
    linkKnown();

    const roots = new Set<string>();
    for (const node of parent.keys()) {
      if (!node.startsWith("d:")) roots.add(find(node));
    }
    return {
      count: roots.size,
      keyOf: (convId?: string, groupId?: string) => {
        if (convId && parent.has(`c:${convId}`)) return find(`c:${convId}`);
        if (groupId && parent.has(`g:${groupId}`)) return find(`g:${groupId}`);
        return undefined;
      },
    };
  }

  /**
   * May this inbound evidence stand for a person nobody knows yet (#1418)?
   * Only if it was sent recently, and the last successful reconcile — when it
   * started after the message was sent — listed its conversation or group.
   */
  private _mayBeNewPerson(a: AttestedInbound): boolean {
    if (Date.now() - a.at >= PERSON_INVOLVED_WINDOW_MS) return false;
    const r = this._lastReconcile;
    if (r && a.at <= r.at
      && !(a.convId && r.convs.has(a.convId))
      && !(a.groupId && r.groups.has(a.groupId))) return false;
    return true;
  }

  /** More than one person has a 1:1 with this agent (#1418). Unknown counts as one: the old behaviour. */
  private _isMultiPerson(): boolean {
    return (this._dmPeople()?.count ?? 0) > 1;
  }

  /**
   * The group a turn's message decrypted under, for placing the turn when its
   * conversation is not mapped (a mapped conversation already names its person).
   */
  private _turnGroup(turn: TurnStore): string | undefined {
    return turn.groupId ?? this._attestedGroupOf(turn.convId);
  }

  /** The people with a 1:1 turn in progress, one turn each (#1418). */
  private _activeTurnPeople(): Map<string, TurnStore> {
    const people = this._dmPeople();
    const out = new Map<string, TurnStore>();
    for (const turn of this._activeTurns) { // a turn leaves this set the moment it ends
      // A room's or an A2A channel's turn is nobody's 1:1. A turn that brought
      // an attested group is a 1:1 even when its conversation is not known yet.
      if (turn.convId && (turn.groupId ? this._isNonDmConversation(turn.convId) : !this._isOwnDmConversation(turn.convId))) continue;
      const gid = this._turnGroup(turn);
      const key = people?.keyOf(turn.convId, gid) ?? (gid ? `g:${gid}` : `c:${turn.convId}`);
      if (!out.has(key)) out.set(key, turn);
    }
    return out;
  }

  /**
   * The person an untargeted owner message is for (#1418), in order:
   *
   *  1. the turn this call chain belongs to — always, and only, that
   *     conversation, reached on the group its message decrypted under. A turn
   *     never borrows another conversation's turn: one it cannot place fails
   *     closed to its own conversation. Work the turn left running (a timer, a
   *     dispatch orphaned by the turn budget) stays with that person for
   *     `PERSON_INVOLVED_WINDOW_MS` after the turn, then is proactive (4);
   *  2. with no turn context (a skill's HTTP call), the one person with a turn
   *     in progress;
   *  3. the last inbound 1:1 message, if it is recent — a reply produced in
   *     response to it;
   *  4. otherwise the content is proactive: the PRIMARY conversation's person.
   */
  private _involvedPerson(): InvolvedPerson {
    const person = (convId: string | undefined, groupId: string | undefined, reason: InvolvedPerson["reason"]): InvolvedPerson => ({
      convId,
      groupId: groupId
        ?? this._attestedGroupOf(convId)
        ?? (convId ? this._sessionGroupIds.get(convId) : undefined),
      reason,
    });

    const turn = this._currentTurn();
    if (turn && turn !== "expired") {
      return person(turn.convId ?? this._conversationOfGroup(turn.groupId), turn.groupId, turn.ended ? "outlived-turn" : "turn");
    }
    // Work a turn left behind, after the window: proactive — never another
    // person's turn or last message.
    if (turn === "expired") return this._primaryPerson();

    const active = this._activeTurnPeople();
    if (active.size === 1) {
      const [only] = active.values();
      return person(only.convId ?? this._conversationOfGroup(only.groupId), only.groupId, "active-turn");
    }

    const last = this._lastInboundDm;
    if (active.size === 0 && last && Date.now() - last.at < PERSON_INVOLVED_WINDOW_MS) {
      return person(last.convId ?? this._conversationOfGroup(last.groupId), last.groupId, "last-inbound");
    }

    return this._primaryPerson();
  }

  /** The PRIMARY conversation's person — where proactive content goes. */
  private _primaryPerson(): InvolvedPerson {
    const primary = this._primaryConversationId || this._persisted?.primaryConversationId || undefined;
    const primaryGroup = (primary ? this._sessionGroupIds.get(primary) : undefined) ?? this._persisted?.groupId;
    return {
      convId: primary,
      groupId: primaryGroup ?? this._attestedGroupOf(primary),
      reason: "primary",
    };
  }

  /**
   * #1418 — would an untargeted owner send right now have to GUESS between
   * people? True only with no turn context while two or more people have a
   * turn in progress. The local HTTP API refuses such a request (409) and
   * asks for a conversation instead of picking one.
   */
  isUntargetedOwnerAmbiguous(options?: OwnerRouting): boolean {
    if (options?.conversationId !== undefined || options?.audience === "everyone") return false;
    if (this._currentTurn()) return false; // a turn's own work (or its expired leftovers) is never a guess
    if (!this._isMultiPerson()) return false;
    return this._activeTurnPeople().size > 1;
  }

  /**
   * Who an UNTARGETED owner send reaches, or `null` for "every session, as
   * before". `null` when the caller chose `audience: "everyone"`, and when the
   * agent has one person (or cannot tell how many) — nothing to choose
   * between, so its recipients are exactly what they always were.
   */
  private _untargetedAudience(options?: { conversationId?: string; audience?: string }): InvolvedPerson | null {
    if (options?.conversationId !== undefined) return null;
    if (options?.audience === "everyone") return null;
    if (!this._isMultiPerson()) return null;
    return this._involvedPerson();
  }

  /** A stable name for who an untargeted owner send reaches right now, or undefined when that is "everyone, as before". */
  private _audienceTag(options?: { conversationId?: string; audience?: string }): string | undefined {
    if (options?.audience === "everyone") return "everyone";
    const person = this._untargetedAudience(options);
    return person ? `person:${person.groupId ?? person.convId ?? ""}` : undefined;
  }

  /** Does conversation `convId` belong to `person`? A conversation nothing ties to them fails closed. */
  private _isPersonsConversation(person: InvolvedPerson, convId: string): boolean {
    if (convId === person.convId) return true;
    const gid = this._sessionGroupIds.get(convId);
    if (gid !== undefined && gid === person.groupId) return true;
    const people = this._dmPeople();
    const theirs = people?.keyOf(person.convId, person.groupId);
    return theirs !== undefined && people!.keyOf(convId, gid) === theirs;
  }

  /**
   * #1418: the Double Ratchet sessions an owner-bound FILE (attachment or
   * artifact) goes to, bucketed by the conversation its blob is uploaded
   * under. The blob is uploaded under a conversation of the person who gets
   * it — the backend only lets that conversation's participants download it —
   * so an explicit "everyone" uploads once per person.
   */
  private _ownerFileRecipients(routing?: OwnerRouting): Array<{ uploadConvId: string; convIds: string[] }> {
    const roomConvIds = new Set<string>();
    for (const room of Object.values(this._persisted?.rooms ?? {})) {
      for (const cid of room.conversationIds ?? []) roomConvIds.add(cid);
    }
    const live = [...this._sessions]
      .filter(([convId, session]) => session.activated && !roomConvIds.has(convId))
      .map(([convId]) => convId);

    const person: InvolvedPerson | null = routing?.conversationId !== undefined
      ? { convId: routing.conversationId, groupId: this._sessionGroupIds.get(routing.conversationId), reason: "target" }
      : this._untargetedAudience(routing);
    if (person) {
      const convIds = live.filter((convId) => this._isPersonsConversation(person, convId));
      const uploadConvId = person.convId ?? convIds[0];
      return uploadConvId && convIds.length > 0 ? [{ uploadConvId, convIds }] : [];
    }

    if (live.length === 0) return [];
    // One person (or no way to tell): one blob under the primary conversation
    // for every session — exactly as before #1418.
    if (!this._isMultiPerson()) {
      return [{ uploadConvId: this._primaryConversationId, convIds: live }];
    }
    // An explicit "everyone": one blob per person.
    const people = this._dmPeople();
    const buckets = new Map<string, string[]>();
    for (const convId of live) {
      const gid = this._sessionGroupIds.get(convId);
      const key = people?.keyOf(convId, gid) ?? gid ?? `c:${convId}`;
      buckets.set(key, [...(buckets.get(key) ?? []), convId]);
    }
    return [...buckets.values()].map((convIds) => ({
      uploadConvId: convIds.includes(this._primaryConversationId) ? this._primaryConversationId : convIds[0],
      convIds,
    }));
  }

  /** The group a DM history entry belongs to (see the forms above). */
  private _dmEntryScope(entry: HistoryEntry, known: Set<string>): string | undefined {
    const t = entry.topicId ?? "";
    let scope: string | undefined;
    if (t.startsWith("dm:g:")) {
      scope = t.slice("dm:g:".length).split(":")[0];
    } else if (t.startsWith("dm:c:")) {
      const conv = t.slice("dm:c:".length).split(":")[0];
      scope = this._sessionGroupIds.get(conv) ?? `c:${conv}`;
    } else {
      scope = this._persisted?.groupId; // legacy: the primary group's
    }
    // One known group: everything is that group's, exactly as before.
    if ((scope === undefined || scope.startsWith("c:")) && known.size === 1) {
      return [...known][0];
    }
    return scope;
  }

  /**
   * The group a DM history request is answered from, or `null` for "every DM
   * entry" (an agent that knows no groups at all — the old behaviour). A
   * request whose group cannot be told fails closed to its own conversation.
   */
  private _dmRequestScope(gid: string | undefined, convId: string | undefined, known: Set<string>): string | null {
    if (known.size === 0) return null;
    const g = gid ?? (convId ? this._sessionGroupIds.get(convId) : undefined);
    if (g) return g;
    // The one-group shortcut is only for a request that is not an UNMAPPED
    // other conversation: right after an upgrade, another person's
    // conversation can be unmapped until the reconcile lands, and must not be
    // answered with the primary person's history.
    const isPrimaryConv = convId === undefined || convId === this._primaryConversationId;
    if (known.size === 1 && isPrimaryConv) return [...known][0];
    if (convId && convId === this._primaryConversationId && this._persisted?.groupId) {
      return this._persisted.groupId;
    }
    return `c:${convId ?? ""}`;
  }

  /**
   * Normalize one accepted application envelope before it enters plaintext
   * history. Protocol/control types and empty attachment-only envelopes are
   * never archived, and attachment key material can never become the fallback
   * text.
   */
  private async _captureHistoryApplication(
    sender: "owner" | "agent",
    rawPlaintext: string,
    kind: "dm" | "room" | "a2a",
    id?: string,
    transportTimestamp?: string,
    declaredType = "message",
    dmScope?: { groupId?: string; conversationId?: string },
  ): Promise<string | null> {
    let messageType = declaredType;
    let text = rawPlaintext;
    try {
      const parsed = JSON.parse(rawPlaintext);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        if (typeof parsed.type === "string") messageType = parsed.type;
        text = envelopeText(parsed, rawPlaintext);
      }
    } catch {
      // Legacy plaintext is an application message when its transport type is.
    }
    if (!ROOM_AGENT_TYPES.has(messageType) || text.length === 0) return null;
    await this._appendHistory(
      sender,
      text,
      kind === "dm"
        ? this._dmHistoryContext(id, dmScope?.groupId, dmScope?.conversationId)
        : this._historyContext(kind, id),
      transportTimestamp,
    );
    return text;
  }

  /** Select a context before applying the optional lower bound and final cap. */
  private _historyForCatchup(
    kind: "dm" | "room",
    id: string | undefined,
    sinceTs: unknown,
    dmScope?: { groupId?: string; conversationId?: string },
  ): HistoryEntry[] {
    let filtered = this._selectHistoryContext(kind, id, dmScope);
    if (sinceTs !== undefined && sinceTs !== null) {
      if (
        typeof sinceTs !== "string" ||
        !CANONICAL_TIMESTAMP_RE.test(sinceTs) ||
        !Number.isFinite(Date.parse(sinceTs)) ||
        new Date(Date.parse(sinceTs)).toISOString() !== sinceTs
      ) {
        throw new Error("Invalid history catch-up timestamp");
      }
      filtered = filtered.filter((entry) => entry.ts > sinceTs);
    }
    return filtered
      .sort((a, b) => Date.parse(a.ts) - Date.parse(b.ts) || a.id.localeCompare(b.id))
      .slice(-100);
  }

  /** Shared exact routing selector for catch-up and legacy DR replay. */
  private _selectHistoryContext(
    kind: "dm" | "room",
    id?: string,
    dmScope?: { groupId?: string; conversationId?: string },
  ): HistoryEntry[] {
    const history = this._persisted?.messageHistory ?? [];
    if (kind === "room") {
      return history.filter((entry) => entry.topicId === this._historyContext("room", id));
    }
    const dm = history.filter((entry) =>
      entry.topicId?.startsWith("dm:") === true ||
      (!entry.topicId?.startsWith("room:") && !entry.topicId?.startsWith("a2a:"))
    );
    // #1416: only the requesting group's entries — never another person's.
    const known = this._knownDmGroups();
    const want = this._dmRequestScope(dmScope?.groupId, dmScope?.conversationId, known);
    return want === null ? dm : dm.filter((entry) => this._dmEntryScope(entry, known) === want);
  }

  private _markHistoryDirty(): void {
    this._historyBackupScheduler.markDirty();
  }

  /** Deliver one application frame only while the socket remains OPEN. */
  private _sendApplicationFrame(frame: string): boolean {
    const ws = this._ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(frame);
      // ws.send() may return without throwing as the socket transitions to
      // CLOSING/CLOSED. Do not count or archive that ambiguous write.
      return ws.readyState === WebSocket.OPEN;
    } catch {
      return false;
    }
  }

  private async _uploadHistorySnapshot(): Promise<void> {
    const state = this._persisted;
    if (!state?.hubId) return;
    const lifecycle = this._lifecycleGeneration;
    const controller = new AbortController();
    this._historyUploadController?.abort(new DOMException("History upload superseded", "AbortError"));
    this._historyUploadController = controller;
    const timeout = setTimeout(
      () => controller.abort(new DOMException("History upload timed out", "TimeoutError")),
      HISTORY_UPLOAD_TIMEOUT_MS,
    );
    const checkpoint = () => {
      controller.signal.throwIfAborted();
      if (
        this._stopped ||
        lifecycle !== this._lifecycleGeneration ||
        this._historyUploadController !== controller
      ) {
        throw new DOMException("History upload cancelled", "AbortError");
      }
    };
    const step = async <T>(promise: Promise<T>): Promise<T> => {
      checkpoint();
      let onAbort: (() => void) | undefined;
      try {
        const aborted = new Promise<never>((_resolve, reject) => {
          onAbort = () => reject(
            controller.signal.reason ?? new DOMException("History upload cancelled", "AbortError"),
          );
          controller.signal.addEventListener("abort", onAbort, { once: true });
        });
        const result = await Promise.race([promise, aborted]);
        checkpoint();
        return result;
      } finally {
        if (onAbort) controller.signal.removeEventListener("abort", onAbort);
      }
    };
    try {
      checkpoint();
      const tenantId = this._decodeTenantIdForRecovery(state.deviceJwt);
      if (!tenantId) return;
      const code = await step(this._loadHistoryRecoveryCode(tenantId));
      if (!code) return;
      checkpoint();
      const codeHashHex = hashHistoryCode(code);
      checkpoint();
      const client = this._historyBackupClient ?? await step(
        this._getHistoryBackupClient({ controller, lifecycle }),
      );
      let salt: Uint8Array;
      if (state.historyBackupSalt !== undefined) {
        if (!/^[0-9a-f]{32}$/.test(state.historyBackupSalt)) {
          throw new Error("Invalid history backup salt");
        }
        salt = new Uint8Array(Buffer.from(state.historyBackupSalt, "hex"));
      } else {
        await step(sodium.ready);
        salt = sodium.randombytes_buf(16);
      }
      checkpoint();
      const history = state.messageHistory ?? [];
      if (!Array.isArray(history)) throw new Error("entries must be an array");
      const snapshotEntries = new Map<string, HistoryEntry>();
      for (let index = 0; index < history.length; index += 1) {
        if (!(index in history)) throw new Error("entries must not be sparse");
        const entry = history[index];
        // Validate every row before deduplication and derive missing legacy IDs.
        const legacyId = legacyHistoryEntryId(entry);
        const id = entry.id ?? legacyId;
        const previous = snapshotEntries.get(id);
        if (previous) {
          if (
            previous.sender !== entry.sender || previous.text !== entry.text ||
            previous.ts !== entry.ts || previous.topicId !== entry.topicId
          ) {
            throw new Error("History archive contains duplicate entry IDs");
          }
          continue;
        }
        snapshotEntries.set(id, { ...entry, id });
      }
      const archive = buildHistoryArchive(state.hubId, [...snapshotEntries.values()]);
      const requestedSaltHex = Buffer.from(salt).toString("hex");
      const cacheIdentity = `${state.hubId}:${codeHashHex}:${requestedSaltHex}`;
      checkpoint();
      if (this._historyKeyCacheIdentity && this._historyKeyCacheIdentity !== cacheIdentity) {
        client.clearHistoryKeyCache();
      }
      this._historyKeyCacheIdentity = cacheIdentity;
      const { blob, saltHex } = await step(client.encryptArchive({ archive, code, codeHashHex, salt }));
      checkpoint();
      if (state.historyBackupSalt !== saltHex) {
        state.historyBackupSalt = saltHex;
        await step(this._saveHistoryState(state, {
          controller,
          lifecycle,
          current: () => this._historyUploadController === controller,
        }));
      }
      checkpoint();
      const uploaded = await step(client.upload({
        codeHashHex,
        blob,
        entryCount: archive.entries.length,
        signal: controller.signal,
      }));
      checkpoint();
      this.emit("history_backup", {
        state: "uploaded",
        entryCount: archive.entries.length,
        generationId: uploaded.generationId,
      });
    } catch (error) {
      if (!this._stopped && lifecycle === this._lifecycleGeneration && this._historyUploadController === controller) {
        this.emit("history_backup", { state: "error", reason: historyErrorReason(error) });
      }
      throw error;
    } finally {
      clearTimeout(timeout);
      if (this._historyUploadController === controller) this._historyUploadController = null;
    }
  }

  private async _initializeHistoryRecovery(): Promise<void> {
    if (!this._persisted) return;
    const needsRestore =
      (this._persisted.messageHistory?.length ?? 0) === 0 ||
      this._persisted.historyRecoveryState === "pending";
    if (!needsRestore) {
      this._historyBackupScheduler.scheduleStartupBackfill();
      return;
    }
    await this._restoreHistoryBeforeConnect();
  }

  private async _saveHistoryState(
    state: PersistedState | null = this._persisted,
    guard?: { controller: AbortController; lifecycle: number; current?: () => boolean },
  ): Promise<void> {
    if (!state) return;
    await this._queueStateSave(state, guard ? {
      signal: guard.controller.signal,
      commitGuard: () =>
        !this._stopped &&
        guard.lifecycle === this._lifecycleGeneration &&
        (guard.current?.() ?? this._historyRestoreController === guard.controller),
    } : undefined);
  }

  /** Queue one immutable state snapshot behind every earlier publication. */
  private _queueStateSave(
    state: PersistedState,
    options?: Parameters<typeof saveState>[2],
  ): Promise<void> {
    const snapshot = JSON.parse(JSON.stringify(state)) as PersistedState;
    const write = this._stateSaveQueue
      .catch(() => {})
      .then(() => saveState(this.config.dataDir, snapshot, options));
    this._stateSaveQueue = write.catch(() => {});
    return write;
  }

  private async _getHistoryBackupClient(
    guard?: { controller: AbortController; lifecycle: number },
  ): Promise<HistoryBackupClient> {
    const assertCurrent = () => {
      if (
        guard &&
        (guard.controller.signal.aborted || this._stopped || guard.lifecycle !== this._lifecycleGeneration)
      ) {
        throw guard.controller.signal.reason ?? new DOMException("History operation cancelled", "AbortError");
      }
    };
    assertCurrent();
    if (!this._historyBackupClient) {
      const { HistoryBackupClient } = await import("./history-backup-client.js");
      assertCurrent();
      this._historyBackupClient = new HistoryBackupClient(
        this.config.apiUrl,
        () => this._persisted?.deviceJwt ?? this._deviceJwt ?? "",
      );
    }
    assertCurrent();
    return this._historyBackupClient;
  }

  private _decodeTenantIdForRecovery(jwt: string): string | null {
    try {
      const payload = jwt.split(".")[1];
      if (!payload) return null;
      const claims = JSON.parse(Buffer.from(payload, "base64url").toString("utf8"));
      return typeof claims?.tid === "string" && UUID_RE.test(claims.tid)
        ? claims.tid.toLowerCase()
        : null;
    } catch {
      return null;
    }
  }

  private _loadHistoryRecoveryCode(tenantId: string): Promise<string | null> {
    return loadHistoryRecoveryCode(tenantId);
  }

  private async _restoreHistoryBeforeConnect(): Promise<void> {
    const state = this._persisted;
    if (!state) return;
    const lifecycle = this._lifecycleGeneration;
    state.historyRecoveryState = "pending";
    this.emit("history_recovery", { state: "pending" });
    await this._saveHistoryState(state);
    if (this._stopped || lifecycle !== this._lifecycleGeneration) return;

    const tenantId = this._decodeTenantIdForRecovery(state.deviceJwt);
    if (!tenantId) return;
    const code = await this._loadHistoryRecoveryCode(tenantId);
    if (!code || this._stopped || lifecycle !== this._lifecycleGeneration) return;

    const codeHashHex = hashHistoryCode(code);
    const controller = new AbortController();
    this._historyRestoreController?.abort();
    this._historyRestoreController = controller;
    const timeout = setTimeout(
      () => controller.abort(new DOMException("History restore timed out", "TimeoutError")),
      HISTORY_RESTORE_TIMEOUT_MS,
    );
    let rejectOnAbort: (() => void) | null = null;
    const isCurrent = () =>
      !controller.signal.aborted &&
      !this._stopped &&
      lifecycle === this._lifecycleGeneration &&
      this._historyRestoreController === controller;
    const discardLateClient = (client: HistoryBackupClient) => {
      if (isCurrent()) return;
      if (this._historyBackupClient === client) {
        this._historyBackupClient = null;
        this._historyKeyCacheIdentity = null;
      }
      // The late acquisition owns this client even when a restarted lifecycle
      // has already installed a different one. Zeroize only the late client;
      // never erase the replacement lifecycle's cache identity.
      client.dispose();
    };
    try {
      const abort = new Promise<never>((_resolve, reject) => {
        rejectOnAbort = () => reject(
          controller.signal.reason ?? new DOMException("History restore aborted", "AbortError"),
        );
        controller.signal.addEventListener("abort", rejectOnAbort, { once: true });
      });
      const clientAcquisition = this._getHistoryBackupClient({ controller, lifecycle });
      // If an injected/import implementation ignores cancellation, its late
      // client must still be zeroized and detached from the stopped lifecycle.
      void clientAcquisition.then(discardLateClient, () => {});
      const client = await Promise.race([clientAcquisition, abort]);
      if (!isCurrent()) {
        discardLateClient(client);
        return;
      }
      const cachedIdentity = this._historyKeyCacheIdentity?.split(":");
      if (
        cachedIdentity &&
        (cachedIdentity[1] !== codeHashHex || (state.hubId && cachedIdentity[0] !== state.hubId))
      ) {
        client.clearHistoryKeyCache();
        this._historyKeyCacheIdentity = null;
      }
      if (!isCurrent()) {
        discardLateClient(client);
        return;
      }
      const restored = await Promise.race([
        client.restore({ code, codeHashHex, signal: controller.signal }),
        abort,
      ]);
      if (!restored || !isCurrent()) {
        return;
      }
      if (state.hubId && state.hubId !== restored.hubId) {
        throw new Error("History restore hub mismatch");
      }

      const previousIdentity = this._historyKeyCacheIdentity;
      const nextIdentity = `${restored.hubId}:${codeHashHex}:${restored.saltHex}`;
      if (previousIdentity && previousIdentity !== nextIdentity) {
        client.clearHistoryKeyCache();
      }
      this._historyKeyCacheIdentity = nextIdentity;
      const mergedHistory = mergeHistoryEntries(restored.entries, state.messageHistory ?? []);
      const restoredState: PersistedState = {
        ...state,
        messageHistory: mergedHistory,
        hubId: state.hubId ?? restored.hubId,
        historyBackupSalt: restored.saltHex,
        restoredHistoryGenerationId: restored.generationId,
        historyRecoveryState: "complete",
      };
      if (!isCurrent()) return;
      await this._saveHistoryState(restoredState, { controller, lifecycle });
      if (!isCurrent()) return;
      this._persisted = restoredState;
      this.emit("history_recovery", {
        state: "restored",
        count: mergedHistory.length,
        generationId: restored.generationId,
        createdAt: restored.generationCreatedAt,
      });
    } catch {
      // Recovery is best effort for startup. The persisted pending marker makes
      // missing codes, corrupt generations, and transport failures retryable.
    } finally {
      clearTimeout(timeout);
      if (rejectOnAbort) controller.signal.removeEventListener("abort", rejectOnAbort);
      if (this._historyRestoreController === controller) this._historyRestoreController = null;
    }
  }

  /**
   * Resolve the room a conversation belongs to (if any) from persisted room
   * membership. A room-backed conversation must never surface to the native
   * worker tool-gate as a no-roomId 1:1 `message` — the gate treats that as an
   * authenticated owner DM (tool-enabled), bypassing arming. Shared by the
   * real-time DR path (which stamps roomId on the emitted message) and the DR
   * poll fallback (which SKIPS legacy room messages, matching the queue/sync
   * fallbacks). Resolution is from LOCAL persisted room state; the sync path
   * additionally honors the server's `room_id`. Defense-in-depth: DR is
   * 1:1-only and rooms use MLS today, so this is inert now, but closes the
   * residual gap if a room-backed conversation ever rides a DR session.
   */
  private _roomIdForConversation(convId: string): string | undefined {
    if (!this._persisted?.rooms) return undefined;
    for (const [rid, room] of Object.entries(this._persisted.rooms)) {
      if (room.conversationIds?.includes(convId)) return rid;
    }
    return undefined;
  }

  /**
   * True when a conversation is NOT a genuine 1:1 owner DM — i.e. it belongs to a
   * room OR an A2A channel — and therefore must NEVER surface to the native worker
   * tool-gate as a no-roomId `message` (the gate treats that as an authenticated
   * owner DM, tool-enabled, bypassing arming). This is the predicate every fallback
   * emit guard uses (MLS 1:1, DR-delivery queue, HTTP poll).
   *
   * Resolves from LOCAL persisted state — BOTH `_persisted.rooms[*].conversationIds`
   * and `_persisted.a2aChannels[*].conversationId` — and accepts EITHER the
   * conversation id or the conversation-group id, so a convGroupId-only room/A2A
   * payload is still caught while a genuine convGroupId-only 1:1 shared-group DM
   * (present in neither map) still returns false and emits.
   *
   * Some fallback guards additionally honor an AUTHORITATIVE server routing field as
   * a backstop for the cold-local-state window — but WHICH field each carries differs
   * per endpoint, so do NOT assume a blanket "backend always populates room_id/
   * a2a_channel_id" at a new call site:
   *   - HTTP poll (`/devices/{id}/messages`): server `room_id` populated for room-backed
   *     convs (conv_room_map). No `a2a_channel_id` on this endpoint → A2A caught locally.
   *   - DR-delivery (`/dr/delivery` -> dr_delivery_service.pull_pending): server `room_id`
   *     populated (from the conversation); NO `a2a_channel_id` (DrDeliveryQueue is
   *     1:1-only, never A2A). room_id is null in practice today but real for any future
   *     room-over-DR row.
   *   - MLS 1:1 (`_handleMessageMLS`): room/A2A are routed away UPSTREAM at the
   *     MLS-delivery dispatcher (authoritative `room_id`/`a2a_channel_id`) before this
   *     handler; the fields are absent on `data` here, so this predicate is the belt.
   * The MLS-delivery *queue* endpoint (mls_delivery_service.pull_pending) is the one that
   * returns all of room_id/a2a_channel_id/conversation_group_id — but that feeds the
   * dispatcher, not these emit guards directly.
   *
   * No fail-closed drop-unknown guard is used: that would risk dropping a legitimate
   * 1:1 owner DM that arrives before room/A2A hydration (a North-Star violation). The
   * reachable residual (truly-cold local state AND the server omitting its routing field
   * on a room/A2A row) is empty today because the only paths that carry room/A2A rows
   * (poll + MLS dispatcher) always populate the field; DR-delivery and sync are
   * structurally 1:1-only.
   */
  private _isNonDmConversation(convId?: string, convGroupId?: string): boolean {
    const ids = [convId, convGroupId].filter((x): x is string => !!x);
    if (ids.length === 0) return false;
    if (this._persisted?.rooms) {
      for (const room of Object.values(this._persisted.rooms)) {
        if (room.conversationIds?.some((c) => ids.includes(c))) return true;
      }
    }
    if (this._persisted?.a2aChannels) {
      for (const ch of Object.values(this._persisted.a2aChannels)) {
        if (ch.conversationId && ids.includes(ch.conversationId)) return true;
      }
    }
    return false;
  }

  /**
   * Get recent message history for a specific room, for LLM context injection.
   * Returns the last N messages tagged with `room:{roomId}`.
   */
  getRoomHistory(roomId: string, maxMessages = 20): HistoryEntry[] {
    const history = this._persisted?.messageHistory ?? [];
    const tag = `room:${roomId}`;
    return history.filter((e) => e.topicId === tag).slice(-maxMessages);
  }

  /**
   * Encrypt and send a message to ALL owner devices (fanout).
   * Each session gets the same plaintext encrypted independently.
   */
  /**
   * #952 — sends held because MLS is expected for this conversation but the
   * group is not joined yet.
   *
   * ⚠️ IN MEMORY ONLY. These entries hold PLAINTEXT. `_persisted` is written to
   * disk, and the product's entire premise is that plaintext does not rest
   * where it does not have to; a crash losing a held greeting is the correct
   * trade. This is why the existing `outboundQueue` could not be reused — that
   * one is persisted, and holds already-DR-encrypted bytes, which is exactly
   * the encryption we are trying not to perform yet.
   */
  private _mlsHold = new Map<
    string,
    { items: Array<{ convId: string; plaintext: string; options?: InternalSendOptions }>; timer: NodeJS.Timeout }
  >();

  /** How long a send waits for the Welcome. The two measured bursts were each
   *  confined to a single minute, with the group already existing server-side
   *  in the same second, so this is generous rather than tight. */
  private static readonly MLS_HOLD_MS = 30_000;

  /** Bound on held sends per group — see `_holdForMls`. */
  private static readonly MLS_HOLD_MAX = 50;

  /**
   * Should this 1:1 send wait for MLS rather than go out over Double Ratchet?
   *
   * The discriminator exists at the moment of the bug: `_sessionGroupIds`
   * already carries the conversation's group id — the server told us the group
   * exists — while `_mlsGroups` has no usable manager for it. Group expected,
   * group not ready.
   *
   * The negative case is load-bearing. A conversation with NO group id is
   * legitimately Double Ratchet, and holding it would break owner-to-agent
   * messaging outright, which is far worse than the bug this fixes. "Present"
   * is also not "ready": a manager parked at epoch 0 has not joined (#1092),
   * and reading presence as readiness is how #991 shipped.
   */
  private _shouldHoldForMls(
    convGroupId: string | undefined | null,
    mlsGroup: { isInitialized?: boolean; epoch?: unknown } | undefined | null,
    mlsGroupId: string | undefined | null,
  ): boolean {
    if (!convGroupId) return false;
    const ready = Boolean(mlsGroup?.isInitialized) && Boolean(mlsGroupId) && Number(mlsGroup?.epoch) > 0;
    return !ready;
  }

  /** Park a send until the Welcome lands, or until the hold expires. */
  private _holdForMls(
    convGroupId: string,
    convId: string,
    plaintext: string,
    options?: InternalSendOptions,
  ): void {
    let entry = this._mlsHold.get(convGroupId);
    if (!entry) {
      const timer = setTimeout(() => {
        void this._releaseHeldOverDr(convGroupId);
      }, SecureChannel.MLS_HOLD_MS);
      // A diagnostic timer that prevents a daemon from exiting would be a
      // worse bug than the one being fixed.
      timer.unref?.();
      entry = { items: [], timer };
      this._mlsHold.set(convGroupId, entry);
    }
    if (entry.items.length >= SecureChannel.MLS_HOLD_MAX) {
      entry.items.shift();
      console.warn(
        `[SecureChannel] MLS hold full for group ${convGroupId.slice(0, 8)} — dropped the oldest held message`,
      );
    }
    entry.items.push({ convId, plaintext, options });
    console.log(
      `[SecureChannel] Holding send for group ${convGroupId.slice(0, 8)} until its Welcome lands (held=${entry.items.length})`,
    );
  }

  private _takeHold(convGroupId: string) {
    const entry = this._mlsHold.get(convGroupId);
    if (!entry) return null;
    clearTimeout(entry.timer);
    this._mlsHold.delete(convGroupId);
    return entry;
  }

  /** Re-drive one held send through the normal path, now that MLS is ready. */
  private async _resendHeld(item: { convId: string; plaintext: string; options?: InternalSendOptions }): Promise<void> {
    // `conversationId` — NOT an invented field. `send()` derives its
    // single-conversation target from `options.conversationId`, so a new name
    // here would simply be ignored and the release would fan out to every
    // conversation. The unit test stubs this method, so nothing but reading the
    // send path would have caught that.
    await this.send(item.plaintext, {
      ...(item.options ?? {}),
      conversationId: item.convId,
      bypassMlsHold: true,
    });
  }

  /** The Welcome landed — release everything held for this group, in order. */
  private async _flushMlsHold(convGroupId: string): Promise<void> {
    const entry = this._takeHold(convGroupId);
    if (!entry) return;
    console.log(
      `[SecureChannel] Welcome landed for group ${convGroupId.slice(0, 8)} — releasing ${entry.items.length} held message(s) over MLS`,
    );
    for (const item of entry.items) {
      try {
        await this._resendHeld(item);
      } catch (err) {
        console.warn("[SecureChannel] Held message failed to send after Welcome:", err);
      }
    }
  }

  /**
   * The hold expired. Send over Double Ratchet exactly as before.
   *
   * This is deliberately NOT a drop. #853 and #831 are Welcomes that never
   * arrive; under those an unbounded hold turns a lost minute into an agent
   * that never speaks again. The hold removes the race — it must not invent a
   * new failure mode. The warning is the part that is new: today the loss is
   * silent.
   */
  private async _releaseHeldOverDr(convGroupId: string): Promise<void> {
    const entry = this._takeHold(convGroupId);
    if (!entry) return;
    console.warn(
      `[SecureChannel] No Welcome for group ${convGroupId.slice(0, 8)} within ` +
        `${SecureChannel.MLS_HOLD_MS}ms — releasing ${entry.items.length} message(s) over Double Ratchet. ` +
        "The recipient may not hold a ratchet with this device, in which case they are undeliverable (#952).",
    );
    for (const item of entry.items) {
      try {
        await this._resendHeld(item);
      } catch (err) {
        console.warn("[SecureChannel] Held message failed on Double Ratchet release:", err);
      }
    }
  }

  async send(plaintext: string, options?: InternalSendOptions): Promise<void> {
    // #1106/#1107: the agent declined to speak. Drop it BEFORE every other
    // check — there is nothing to send, so readiness, sessions and scan rules
    // are all irrelevant, and a no-op must never fail. Placed above
    // `_appendHistory` too: the sentinel in the agent's own history would
    // teach it to emit the token again.
    if (isAbstention(plaintext)) return;

    // Permanent failures — still throw
    if (this._state === "error" || this._state === "idle") {
      throw new Error("Channel is not ready");
    }
    if (this._sessions.size === 0) {
      throw new Error("No active sessions");
    }

    // Wake agent proactively before sending (fire-and-forget, best-effort)
    import("./openclaw-compat.js")
      .then(({ requestHeartbeatNow }) => requestHeartbeatNow({ reason: "channel-send" }))
      .catch(() => {});

    const targetConvId = options?.conversationId;
    // #1418: an untargeted send goes to the person involved, not to everyone.
    // `null` = every session (an explicit `audience: "everyone"`, or an agent
    // with only one person, whose recipients are exactly as before).
    const audience = this._untargetedAudience(options);
    // #1416: no single topic for the whole send — every frame takes the topic
    // of ITS OWN group (`_topicForFrame`). `historyContext` is the one the
    // local history entry is filed under: the first frame written.
    const explicitTopicId = options?.topicId;
    const targeted = targetConvId !== undefined;
    const frameHistoryContexts: string[] = [];
    const messageType = options?.messageType ?? "text";
    const priority = options?.priority ?? "normal";
    const parentSpanId = options?.parentSpanId;
    const envelopeMetadata = options?.metadata;

    // Outbound scan — before encryption
    let scanStatus: string | undefined;
    if (this._scanEngine) {
      const scanResult = this._scanEngine.scanOutbound(plaintext);
      if (scanResult.status === "blocked") {
        this.emit("scan_blocked", { direction: "outbound", violations: scanResult.violations });
        throw new Error(`Message blocked by scan rule: ${scanResult.violations[0]?.rule_name}`);
      }
      scanStatus = scanResult.status; // "clean" or "flagged"
    }

    // Build set of room conversation IDs to exclude from 1:1 send
    const roomConvIds = new Set<string>();
    if (this._persisted?.rooms) {
      for (const room of Object.values(this._persisted.rooms)) {
        for (const cid of room.conversationIds) {
          roomConvIds.add(cid);
        }
      }
    }

    const messageGroupId = randomUUID();
    const clientMessageId = randomUUID();
    let sentCount = 0;
    const pendingWsSends: string[] = [];
    const sentSharedGroupIds = new Set<string>();
    // #732: every MLS group this send addressed. Recorded as retry candidates
    // only once the frames actually reach the socket, so a send that never
    // dispatched leaves nothing behind to be resent.
    const addressedMlsGroupIds: string[] = [];

    // --- Shared 1:1 MLS groups first (encrypt once per group) ---
    // #1043: name the precondition that failed. Skipping this block silently is
    // how a reply that was never encrypted still reported `result=sent`.
    if (!(this._persisted?.mlsGroups && this._state === "ready" && this._ws)) {
      console.warn(
        `[SecureChannel] send(): shared-MLS block SKIPPED — ` +
          `mlsGroups=${this._persisted?.mlsGroups ? Object.keys(this._persisted.mlsGroups).length : "none"} ` +
          `state=${this._state} ws=${this._ws ? "open" : "null"}`,
      );
    }
    if (this._persisted?.mlsGroups && this._state === "ready" && this._ws) {
      // #460: honour `SendOptions.conversationId` here too.
      //
      // types.ts documents it as "Target a specific conversation instead of
      // broadcasting to all sessions", and both per-conversation loops below
      // already respect it (see the `targetConvId` guards further down). This
      // loop did not — it fanned out to EVERY persisted shared 1:1 group, so a
      // single targeted reply cost N encrypts and N WS frames and was delivered
      // into every DM the agent had, including counterparties who never asked.
      // Observed live: wren burst-sending one reply to ~10 groups.
      //
      // When no conversation is targeted, #1418 decides: the person involved's
      // group only, or every group for an explicit `audience: "everyone"` (and
      // for an agent with a single group, which has nobody else to reach).
      //
      // If the target maps to no shared group, this sends to NONE of them
      // rather than falling back to a broadcast: the per-conversation path below
      // owns that case, and silently widening an explicit target is the bug, not
      // a sensible default.
      const targetSharedGid = targetConvId
        ? this._sessionGroupIds?.get(targetConvId)
        : undefined;

      console.log(
        `[SecureChannel] send(): considering ${Object.keys(this._persisted.mlsGroups).length} shared group(s); ` +
          `targetConvId=${targetConvId ? targetConvId.slice(0, 8) : audience ? `(none — person involved: ${audience.reason})` : "(none — everyone)"} ` +
          `targetSharedGid=${targetSharedGid ? targetSharedGid.slice(0, 8) : audience?.groupId ? audience.groupId.slice(0, 8) : "(none)"}`,
      );
      const skipped: string[] = [];
      for (const [gid, entry] of Object.entries(this._persisted.mlsGroups)) {
        if (targetConvId && gid !== targetSharedGid) { skipped.push(`${gid.slice(0, 8)}:not-target`); continue; }
        if (audience && gid !== audience.groupId) { skipped.push(`${gid.slice(0, 8)}:not-involved`); continue; }
        if (!entry.mlsGroupId) { skipped.push(`${gid.slice(0, 8)}:no-mlsGroupId`); continue; }
        const mlsGroup = this._mlsGroups.get(`1to1-group:${gid}`);
        if (!mlsGroup?.isInitialized) { skipped.push(`${gid.slice(0, 8)}:not-in-memory`); continue; }
        if (Number(mlsGroup.epoch) <= 0) { skipped.push(`${gid.slice(0, 8)}:epoch<=0`); continue; }

        try {
          const plaintextBytes = new TextEncoder().encode(plaintext);
          const cipherBytes = await this._encryptWithCatchUp(
            mlsGroup, `1to1-group:${gid}`, entry.mlsGroupId,
            `1:1 group ${gid.slice(0, 8)}`, plaintextBytes,
          );
          await saveMlsState(this.config.dataDir, entry.mlsGroupId, JSON.stringify(mlsGroup.exportState()));

          const frameTopicId = this._topicForFrame(gid, targetConvId, explicitTopicId, targeted);
          const payload: Record<string, unknown> = {
            group_id: entry.mlsGroupId,
            epoch: Number(mlsGroup.epoch),
            payload: Buffer.from(cipherBytes).toString("hex"),
            topic_id: frameTopicId,
            client_message_id: clientMessageId,
            message_type: messageType,
            priority: priority,
            parent_span_id: parentSpanId,
            metadata: scanStatus
              ? { ...(envelopeMetadata ?? {}), scan_status: scanStatus }
              : envelopeMetadata,
          };
          if (this._persisted?.hubAddress) payload.hub_address = this._persisted.hubAddress;
          if (this._persisted?.hubId) payload.sender_hub_id = this._persisted.hubId;
          pendingWsSends.push(JSON.stringify({ event: "message_mls", data: payload }));
          frameHistoryContexts.push(this._dmHistoryContext(frameTopicId, gid, targetConvId));
          addressedMlsGroupIds.push(entry.mlsGroupId);
          sentSharedGroupIds.add(gid);
          sentCount++;
          console.log(`[SecureChannel] Shared MLS group send for group ${gid.slice(0, 8)} (${entry.mlsGroupId.slice(0, 8)})`);
        } catch (err) {
          console.error(`[SecureChannel] Shared MLS group send failed for ${gid.slice(0, 8)}:`, err);
          skipped.push(`${gid.slice(0, 8)}:encrypt-threw`);
        }
      }
      if (skipped.length > 0) {
        console.log(`[SecureChannel] send(): skipped ${skipped.length} group(s) — ${skipped.join(", ")}`);
      }
    }

    for (const [convId, session] of this._sessions) {
      if (!session.activated) continue;
      // Skip room pairwise sessions — those use sendToRoom()
      if (roomConvIds.has(convId)) continue;
      // If a specific conversation is targeted, skip all others
      if (targetConvId && convId !== targetConvId) continue;
      // #1418: untargeted — only the person involved's conversations.
      if (audience && !this._isPersonsConversation(audience, convId)) continue;

      // Skip if this conversation's group was already sent via shared MLS group above
      const convGroupId = this._sessionGroupIds?.get(convId);
      if (convGroupId && sentSharedGroupIds.has(convGroupId)) {
        sentCount++;
        continue;
      }

      try {
        const topicId = this._topicForFrame(convGroupId, convId, explicitTopicId, targeted);
        // MLS send path — prefer shared 1:1 group, fall back to per-conversation
        const mlsGroup = (convGroupId ? this._mlsGroups.get(`1to1-group:${convGroupId}`) : null)
          ?? this._mlsGroups.get(`conv:${convId}`);
        const mlsGroupId = (convGroupId ? this._persisted?.mlsGroups?.[convGroupId]?.mlsGroupId : null)
          ?? this._persisted?.mlsConversations?.[convId]?.mlsGroupId;

        // Skip if we already sent via this shared group (another conv in same group)
        if (convGroupId && mlsGroupId && sentSharedGroupIds.has(convGroupId)) {
          sentCount++;
          continue;
        }

        if (mlsGroup?.isInitialized && mlsGroupId && (Number(mlsGroup.epoch) > 0) && this._state === "ready" && this._ws) {
          const plaintextBytes = new TextEncoder().encode(plaintext);
          const sessionGroupKey = convGroupId && this._mlsGroups.has(`1to1-group:${convGroupId}`)
            ? `1to1-group:${convGroupId}`
            : `conv:${convId}`;
          const cipherBytes = await this._encryptWithCatchUp(
            mlsGroup, sessionGroupKey, mlsGroupId,
            `conversation ${convId.slice(0, 8)}`, plaintextBytes,
          );
          await saveMlsState(this.config.dataDir, mlsGroupId, JSON.stringify(mlsGroup.exportState()));

          const payload: Record<string, unknown> = {
            conversation_id: convId,
            group_id: mlsGroupId,
            epoch: Number(mlsGroup.epoch),
            payload: Buffer.from(cipherBytes).toString("hex"),
            topic_id: topicId,
            client_message_id: clientMessageId,
            message_type: messageType,
            priority: priority,
            parent_span_id: parentSpanId,
            metadata: scanStatus
              ? { ...(envelopeMetadata ?? {}), scan_status: scanStatus }
              : envelopeMetadata,
          };
          if (this._persisted?.hubAddress) {
            payload.hub_address = this._persisted.hubAddress;
          }
          if (this._persisted?.hubId) {
            payload.sender_hub_id = this._persisted.hubId;
          }
          pendingWsSends.push(
            JSON.stringify({
              event: "message_mls",
              data: payload,
            }),
          );
          frameHistoryContexts.push(this._dmHistoryContext(topicId, convGroupId, convId));
          addressedMlsGroupIds.push(mlsGroupId);
          if (convGroupId) sentSharedGroupIds.add(convGroupId);
        } else if (!options?.bypassMlsHold && this._shouldHoldForMls(convGroupId, mlsGroup, mlsGroupId)) {
          // #952 — the server has told us this conversation has an MLS group,
          // but its Welcome has not landed. Going out over Double Ratchet now
          // reaches a recipient who holds no ratchet with this device, so the
          // bytes are delivered to nobody. Hold instead, and release when the
          // Welcome arrives (or over DR if it never does).
          this._holdForMls(convGroupId!, convId, plaintext, options);
          sentCount++;
          continue;
        } else {
          // Double Ratchet fallback
          const encrypted = session.ratchet.encrypt(plaintext);
          const transport = encryptedMessageToTransport(encrypted);

          const msg = {
            convId,
            headerBlob: transport.header_blob,
            ciphertext: transport.ciphertext,
            messageGroupId,
            topicId,
            ...(!options?.isResend && messageType === "text" ? {
              history: {
                id: clientMessageId,
                text: plaintext,
                topicId: this._dmHistoryContext(topicId, convGroupId, convId),
              },
            } : {}),
          };

          if (this._state === "ready" && this._ws) {
            const payload: Record<string, unknown> = {
              conversation_id: msg.convId,
              header_blob: msg.headerBlob,
              ciphertext: msg.ciphertext,
              message_group_id: msg.messageGroupId,
              topic_id: msg.topicId,
              client_message_id: clientMessageId,
              message_type: messageType,
              priority: priority,
              parent_span_id: parentSpanId,
              metadata: scanStatus
                ? { ...(envelopeMetadata ?? {}), scan_status: scanStatus }
                : envelopeMetadata,
            };
            if (this._persisted?.hubAddress) {
              payload.hub_address = this._persisted.hubAddress;
            }
            if (this._persisted?.hubId) {
              payload.sender_hub_id = this._persisted.hubId;
            }
            pendingWsSends.push(
              JSON.stringify({
                event: "message",
                data: payload,
              }),
            );
            frameHistoryContexts.push(this._dmHistoryContext(topicId, convGroupId, convId));
          } else {
            // Queue for later (DR only — MLS requires live WS)
            if (!this._persisted!.outboundQueue) {
              this._persisted!.outboundQueue = [];
            }
            if (this._persisted!.outboundQueue.length >= 50) {
              this._persisted!.outboundQueue.shift();
              console.warn("[SecureChannel] Outbound queue full, dropping oldest message");
            }
            this._persisted!.outboundQueue.push(msg);
            console.log(`[SecureChannel] Message queued (state=${this._state}, queue=${this._persisted!.outboundQueue.length})`);
          }
        }
        sentCount++;
      } catch (err) {
        // Per-session error — log and continue to next session so one
        // stale/broken session doesn't prevent delivery to healthy ones
        console.error(`[SecureChannel] send() failed for conv ${convId.slice(0, 8)}...:`, err);
      }
    }

    // MLS-only send path: for conversations with MLS groups but no DR session.
    // This handles 1:1 chats where the message arrived via MLS from a device
    // that may not have a corresponding DR session in the agent's session map.
    if (this._persisted?.mlsConversations && this._state === "ready" && this._ws) {
      for (const [mlsConvId, entry] of Object.entries(this._persisted.mlsConversations)) {
        if (targetConvId && mlsConvId !== targetConvId) continue;
        if (audience && !this._isPersonsConversation(audience, mlsConvId)) continue; // #1418
        if (this._sessions.has(mlsConvId)) continue; // already handled above
        if (roomConvIds.has(mlsConvId)) continue;
        // Prefer shared 1:1 group, fall back to per-conversation
        const mlsOnlyConvGroupId = this._sessionGroupIds?.get(mlsConvId);
        const mlsGroup = (mlsOnlyConvGroupId ? this._mlsGroups.get(`1to1-group:${mlsOnlyConvGroupId}`) : null)
          ?? this._mlsGroups.get(`conv:${mlsConvId}`);
        const resolvedMlsGroupId = (mlsOnlyConvGroupId ? this._persisted?.mlsGroups?.[mlsOnlyConvGroupId]?.mlsGroupId : null)
          ?? entry.mlsGroupId;
        if (!mlsGroup?.isInitialized || !resolvedMlsGroupId || Number(mlsGroup.epoch) <= 0) continue;
        // Skip if we already sent via this shared group
        if (mlsOnlyConvGroupId && sentSharedGroupIds.has(mlsOnlyConvGroupId)) {
          sentCount++;
          continue;
        }

        try {
          const topicId = this._topicForFrame(mlsOnlyConvGroupId, mlsConvId, explicitTopicId, targeted);
          const plaintextBytes = new TextEncoder().encode(plaintext);
          const mlsOnlyGroupKey = mlsOnlyConvGroupId && this._mlsGroups.has(`1to1-group:${mlsOnlyConvGroupId}`)
            ? `1to1-group:${mlsOnlyConvGroupId}`
            : `conv:${mlsConvId}`;
          const cipherBytes = await this._encryptWithCatchUp(
            mlsGroup, mlsOnlyGroupKey, resolvedMlsGroupId,
            `conversation ${mlsConvId.slice(0, 8)}`, plaintextBytes,
          );
          await saveMlsState(this.config.dataDir, resolvedMlsGroupId, JSON.stringify(mlsGroup.exportState()));

          const payload: Record<string, unknown> = {
            conversation_id: mlsConvId,
            group_id: resolvedMlsGroupId,
            epoch: Number(mlsGroup.epoch),
            payload: Buffer.from(cipherBytes).toString("hex"),
            topic_id: topicId,
            client_message_id: clientMessageId,
            message_type: messageType,
            priority: priority,
            parent_span_id: parentSpanId,
            metadata: envelopeMetadata,
          };
          if (this._persisted?.hubAddress) payload.hub_address = this._persisted.hubAddress;
          if (this._persisted?.hubId) payload.sender_hub_id = this._persisted.hubId;
          pendingWsSends.push(JSON.stringify({ event: "message_mls", data: payload }));
          frameHistoryContexts.push(this._dmHistoryContext(topicId, mlsOnlyConvGroupId, mlsConvId));
          addressedMlsGroupIds.push(resolvedMlsGroupId);
          sentCount++;
          if (mlsOnlyConvGroupId) sentSharedGroupIds.add(mlsOnlyConvGroupId);
          console.log(`[SecureChannel] MLS-only send for conv ${mlsConvId.slice(0, 8)} (no DR session)`);
        } catch (err) {
          console.error(`[SecureChannel] MLS-only send failed for conv ${mlsConvId.slice(0, 8)}:`, err);
        }
      }
    }

    // #1043: this used to be gated on `this._sessions.size > 0`. An agent that
    // uses shared 1:1 MLS groups has NO per-conversation sessions, so the gate
    // was false and the warning never fired — send() returned normally having
    // encrypted nothing, and deliver() reported `result=sent`.
    //
    // Measured on wren 2026-08-24: `[deliver] target=owner result=sent` at
    // 14:54:44 with ZERO messages reaching the server and not one send line in
    // the log. Reporting success without evidence is why four separate
    // diagnoses of this agent were each plausible and each wrong.
    if (sentCount === 0) {
      console.warn(
        `[SecureChannel] send() delivered to 0 destinations — nothing was ` +
          `encrypted or queued (sessions=${this._sessions.size}, ` +
          `sharedGroups=${this._persisted?.mlsGroups ? Object.keys(this._persisted.mlsGroups).length : 0})`,
      );
    }

    // Persist ratchet states BEFORE sending over WS to avoid desync on crash
    await this._persistState();

    // #1043: a frame count and a readyState check. `ws.send()` on a CLOSING or
    // CLOSED socket returns without throwing, so writing here proved nothing.
    if (pendingWsSends.length > 0 && this._ws?.readyState !== WebSocket.OPEN) {
      console.warn(
        `[SecureChannel] send(): ${pendingWsSends.length} frame(s) NOT written — ` +
          `socket readyState=${this._ws?.readyState ?? "null"} (1=OPEN)`,
      );
    }
    let writtenFrames = 0;
    let historyContext: string | undefined;
    for (const [i, frame] of pendingWsSends.entries()) {
      if (this._sendApplicationFrame(frame)) {
        if (writtenFrames === 0) historyContext = frameHistoryContexts[i];
        writtenFrames += 1;
      }
    }
    // Record only application text that was actually relayed. Offline DR
    // entries remain deferred on their queue records until flush succeeds.
    // Handshakes/controls and failed sends never enter the plaintext archive.
    if (
      !options?.isResend &&
      messageType === "text" &&
      writtenFrames > 0
    ) {
      await this._appendHistory("agent", plaintext, historyContext ?? this._historyContext("dm"), undefined, clientMessageId);
    }
    if (writtenFrames > 0) {
      console.log(
        `[SecureChannel] send(): wrote ${writtenFrames} frame(s) to the socket ` +
          `(sentCount=${sentCount}, readyState=${this._ws?.readyState ?? "null"})`,
      );
    }

    // #732: retain what we just encrypted, keyed by the group it went to, so a
    // refusal arriving ~50ms from now can be answered with a resend instead of
    // a silently dropped reply. A resend is never itself retained — one attempt
    // is the whole bound, and re-arming here would make a persistent refusal
    // loop forever.
    if (!options?.isResend && writtenFrames > 0) {
      for (const mlsGroupId of addressedMlsGroupIds) {
        this._rememberRetryCandidate(mlsGroupId, plaintext, options);
      }
    }
  }

  /**
   * Retain one outbound plaintext against the MLS group it was sent to (#732).
   * Evicts expired entries, then the oldest, so the map cannot grow unbounded
   * on an agent that sends steadily and is never refused.
   */
  private _rememberRetryCandidate(
    mlsGroupId: string,
    plaintext: string,
    options?: SendOptions,
  ): void {
    const now = Date.now();
    for (const [k, v] of this._retryCandidates) {
      if (now - v.ts > RETRY_CANDIDATE_TTL_MS) this._retryCandidates.delete(k);
    }
    this._retryCandidates.set(mlsGroupId, { plaintext, options, ts: now });
    while (this._retryCandidates.size > RETRY_CANDIDATE_MAX) {
      const oldest = this._retryCandidates.keys().next().value;
      if (!oldest) break;
      this._retryCandidates.delete(oldest);
    }
  }

  /**
   * Send a typing indicator to all owner devices.
   * Ephemeral (unencrypted metadata), no ratchet advancement.
   */
  sendTyping(): void {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return;
    for (const convId of this._sessions.keys()) {
      this._ws.send(
        JSON.stringify({
          event: "typing",
          data: { conversation_id: convId },
        }),
      );
    }
  }

  /**
   * Send an activity span to all owner devices via WS.
   * Ephemeral (unencrypted metadata, like typing), no ratchet advancement.
   *
   * Includes a `resource` block so the backend can persist the span with
   * the same resource attributes that TelemetryReporter spans get
   * (av.plugin.version, service.version, gen_ai.agent.*). Without this,
   * legacy activity_span paths produce resource-less span rows.
   */
  sendActivitySpan(spanData: Record<string, unknown>): void {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return;
    const pluginVersion =
      typeof __AV_VERSION__ !== "undefined" ? __AV_VERSION__ : "0.0.0-dev";
    const agentName = this.config.agentName ?? "Agent";
    const resource: Record<string, string> = {
      "service.name": "agentvault-agent",
      "service.version": pluginVersion,
      "av.plugin.version": pluginVersion,
      "gen_ai.agent.name": agentName,
      "gen_ai.agent.version": this.config.agentVersion ?? "0.0.0",
    };
    if (this._persisted?.hubId) {
      resource["gen_ai.agent.id"] = this._persisted.hubId;
    }
    this._ws.send(
      JSON.stringify({
        event: "activity_span",
        data: {
          ...spanData,
          agent_name: agentName,
          resource,
        },
      }),
    );
  }

  /**
   * Send a worker-capability heartbeat to the backend (Slice 2 Plan C).
   * Reports whether this device can run the native tool-capable worker and
   * which rooms it currently considers armed, so the backend's arming
   * record stays in sync with the actual bridge/worker state.
   */
  sendWorkerHeartbeat(h: {
    workerCapable: boolean;
    armedRooms: string[];
    hostTrusted?: boolean;
    gatesEffective?: boolean;
  }): void {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return;
    this._ws.send(
      JSON.stringify({
        event: "worker_heartbeat",
        data: {
          worker_capable: h.workerCapable,
          armed_rooms: h.armedRooms,
          // #20: OPTIONAL on the way in so an older bridge against a newer
          // plugin still emits a shape the backend can read. `=== true` means
          // absent/malformed becomes false — the fail-closed direction, and it
          // never emits `undefined` (which would drop the key entirely).
          host_trusted: h.hostTrusted === true,
          gates_effective: h.gatesEffective === true,
        },
      }),
    );
  }

  /**
   * Send a decision request to the owner.
   * Builds a structured envelope with decision metadata and sends it
   * as a high-priority message. Returns the generated decision_id.
   */
  async sendDecisionRequest(request: DecisionRequest, routing?: OwnerRouting): Promise<string> {
    const decision_id = `dec_${randomUUID().replace(/-/g, "").slice(0, 16)}`;

    const payload = JSON.stringify({
      type: "message",
      text: `\u{1F4CB} ${request.title}`,
      decision: {
        decision_id,
        ...request,
      },
    });

    await this.send(payload, {
      conversationId: routing?.conversationId,
      audience: routing?.audience,
      messageType: "decision_request",
      priority: "high",
      metadata: {
        decision_id,
        title: request.title,
        description: request.description,
        options: request.options,
        context_refs: request.context_refs,
        deadline: request.deadline,
        auto_action: request.auto_action,
      },
    });

    return decision_id;
  }

  /**
   * Wait for a decision response matching the given decisionId.
   * Listens on the "message" event. Resolves when either:
   *   - metadata.decision carries an answer for this decisionId — what every
   *     receive path now lifts out of the app's envelope (an option selected,
   *     or action "defer" / "cancel"); or
   *   - legacy: metadata.messageType === "decision_response" and the
   *     plaintext is the JSON envelope with a matching decision.decision_id.
   * Optional timeout rejects with an Error.
   */
  waitForDecision(decisionId: string, timeoutMs?: number): Promise<DecisionResponse> {
    return new Promise<DecisionResponse>((resolve, reject) => {
      let timer: ReturnType<typeof setTimeout> | null = null;

      const handler = (plaintext: string, metadata: MessageMetadata) => {
        // Only the owner's own 1:1 conversation can answer a decision. A room
        // carries other agents and other owners; a "decision answer" posted
        // there must never resolve this agent's pending decision.
        if (metadata?.roomId) return;
        const reply = metadata?.decision;
        if (reply && reply.decision_id === decisionId) {
          if (timer) clearTimeout(timer);
          this.removeListener("message", handler);
          resolve(reply);
          return;
        }
        if (metadata?.messageType !== "decision_response") return;

        try {
          const parsed = JSON.parse(plaintext);
          if (parsed.decision?.decision_id === decisionId) {
            if (timer) clearTimeout(timer);
            this.removeListener("message", handler);
            resolve({
              decision_id: parsed.decision.decision_id,
              selected_option_id: parsed.decision.selected_option_id,
              resolved_at: parsed.decision.resolved_at,
              action: parsed.decision.action,
              defer_until: parsed.decision.defer_until,
              defer_reason: parsed.decision.defer_reason,
            });
          }
        } catch {
          // Not valid JSON, ignore
        }
      };

      this.on("message", handler);

      if (timeoutMs !== undefined) {
        timer = setTimeout(() => {
          this.removeListener("message", handler);
          reject(new Error(`Decision ${decisionId} timed out after ${timeoutMs}ms`));
        }, timeoutMs);
      }
    });
  }

  // --- Multi-agent room methods ---

  /**
   * Join a room by collecting pairwise conversation IDs involving this device.
   * Encryption is handled by MLS group operations, not per-member DR sessions.
   */
  async joinRoom(roomData: {
    roomId: string;
    name: string;
    members: RoomMemberInfo[];
    conversations: RoomConversationInfo[];
    forceRekey?: boolean;
  }): Promise<void> {
    if (!this._persisted) {
      throw new Error("Channel not initialized");
    }

    await sodium.ready;

    const myDeviceId = this._deviceId!;
    const conversationIds: string[] = [];

    for (const conv of roomData.conversations) {
      if (conv.participantA !== myDeviceId && conv.participantB !== myDeviceId) {
        continue;
      }
      conversationIds.push(conv.id);
    }

    // Store room state — preserve MLS fields from prior session
    if (!this._persisted.rooms) {
      this._persisted.rooms = {};
    }
    const existingMlsGroupId = this._persisted.rooms[roomData.roomId]?.mlsGroupId;
    const existingMlsTs = (this._persisted.rooms[roomData.roomId] as any)?.lastMlsMessageTs;
    // #1459 — keep account ids learned from this agent's own view.
    const members = keepKnownMemberTenants(
      this._persisted.rooms[roomData.roomId]?.members ?? [],
      roomData.members,
    );
    this._persisted.rooms[roomData.roomId] = {
      roomId: roomData.roomId,
      name: roomData.name,
      conversationIds,
      members,
      ...(existingMlsGroupId && { mlsGroupId: existingMlsGroupId }),
      ...(existingMlsTs && { lastMlsMessageTs: existingMlsTs }),
    };

    // --- MLS group initialization ---
    const mlsRoom = this._persisted.rooms![roomData.roomId];

    // Check if we already have MLS state for this room
    if (mlsRoom.mlsGroupId && !roomData.forceRekey) {
      const existingState = await loadMlsState(this.config.dataDir, mlsRoom.mlsGroupId);
      if (existingState) {
        try {
          const mgr = new MLSGroupManager();
          mgr.importState(JSON.parse(existingState) as SerializedMLSGroupState);
          this._mlsGroups.set(roomData.roomId, mgr);
          console.log(`[SecureChannel] Loaded MLS group for room ${roomData.roomId.slice(0, 8)} (epoch=${mgr.epoch})`);
        } catch (mlsLoadErr) {
          console.warn(`[SecureChannel] Failed to load MLS state for room ${roomData.roomId.slice(0, 8)}:`, mlsLoadErr);
        }
      }
    } else if (roomData.forceRekey && mlsRoom.mlsGroupId) {
      // Force rekey: clear MLS state
      await deleteMlsState(this.config.dataDir, mlsRoom.mlsGroupId);
      this._mlsGroups.delete(roomData.roomId);
      delete mlsRoom.mlsGroupId;
    }

    // Register with MLS group on backend (agent never creates — waits for Welcome from owner)
    if (!mlsRoom.mlsGroupId) {
      try {
        const initRes = await fetch(`${this.config.apiUrl}/api/v1/mls/rooms/${roomData.roomId}/init`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this._deviceJwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            member_device_ids: roomData.members.map(m => m.deviceId),
          }),
        });

        if (initRes.ok) {
          const groupData = await initRes.json();
          mlsRoom.mlsGroupId = groupData.group_id;
          // Agent does NOT create the group — owner will send Welcome via mls_welcome event.
          // The _handleMlsWelcome handler will call joinFromWelcome and set up the MLS group.
          console.log(`[SecureChannel] MLS room ${roomData.roomId.slice(0, 8)} registered (groupId=${groupData.group_id.slice(0, 8)}, awaiting Welcome from owner)`);
        }
      } catch (mlsInitErr) {
        console.error(`[SecureChannel] MLS room init failed:`, mlsInitErr);
      }
    }

    // GH #366: a fresh (re)join may carry a stale/partial roster snapshot, so
    // force the next inbound room message to re-fetch the authoritative roster.
    this._roomRosterRefreshed.delete(roomData.roomId);

    await this._persistState();
    this.emit("room_joined", { roomId: roomData.roomId, name: roomData.name });
  }

  /**
   * Send an encrypted message to all members of a room.
   * Uses MLS (preferred) or pairwise fan-out as fallback.
   */
  async sendToRoom(
    roomId: string,
    plaintext: string,
    opts?: {
      messageType?: string;
      priority?: string;
      metadata?: Record<string, unknown>;
    },
  ): Promise<void> {
    // #1106/#1107: see `send()`. Above the room lookup as well — an abstention
    // for a room we do not know about is still a no-op, never an error.
    if (isAbstention(plaintext)) return;

    if (!this._persisted?.rooms?.[roomId]) {
      throw new Error(`Room ${roomId} not found`);
    }

    // Wake agent proactively before sending (fire-and-forget, best-effort)
    import("./openclaw-compat.js")
      .then(({ requestHeartbeatNow }) => requestHeartbeatNow({ reason: "channel-sendToRoom" }))
      .catch(() => {});

    const room = this._persisted.rooms[roomId];
    const messageType = opts?.messageType ?? "text";

    // ── MLS path (preferred) ──
    if (room.mlsGroupId) {
      const mlsGroup = this._mlsGroups.get(roomId);
      if (mlsGroup?.isInitialized) {
        try {
          const plaintextBytes = new TextEncoder().encode(plaintext);
          const ciphertext = await this._encryptWithCatchUp(
            mlsGroup, roomId, room.mlsGroupId,
            `room ${roomId.slice(0, 8)}`, plaintextBytes,
          );

          // Persist MLS state BEFORE sending
          await saveMlsState(this.config.dataDir, room.mlsGroupId, JSON.stringify(mlsGroup.exportState()));

          if (this._state === "ready" && this._ws) {
            const delivered = this._sendApplicationFrame(JSON.stringify({
              event: "room_message_mls",
              data: {
                room_id: roomId,
                group_id: room.mlsGroupId,
                epoch: Number(mlsGroup.epoch),
                payload: Buffer.from(ciphertext).toString("hex"),
                message_type: messageType,
                priority: opts?.priority ?? "normal",
                metadata: opts?.metadata,
              },
            }));
            if (!delivered) {
              throw new Error("relay socket is not open");
            }
            if (messageType === "text" && plaintext) {
              await this._appendHistory(
                "agent",
                plaintext,
                this._historyContext("room", roomId),
                undefined,
                randomUUID(),
              );
            }
          }
          return;
        } catch (mlsErr) {
          throw new Error(
            `MLS encrypt failed for room ${roomId.slice(0, 8)}... — ` +
            `MLS group must be initialized before sending. Error: ${mlsErr}`,
          );
        }
      }
    }

    // No MLS group — error (DR fallback removed)
    throw new Error(
      `Room ${roomId.slice(0, 8)}... has no initialized MLS group. ` +
      `MLS initialization must complete before messages can be sent.`,
    );
  }

  /**
   * Leave a room: remove sessions and persisted room state.
   */
  async leaveRoom(roomId: string): Promise<void> {
    if (!this._persisted?.rooms?.[roomId]) {
      return; // Already left or never joined
    }

    const room = this._persisted.rooms[roomId];

    // Remove sessions for this room's conversations
    for (const convId of room.conversationIds) {
      this._sessions.delete(convId);
      delete this._persisted.sessions[convId];
    }

    // #629: prune the room's MLS state too. Leaving without this orphaned the
    // mls-<groupId>.json file (and the in-memory group, keyed by roomId), which
    // then replayed dead commit history on every reconnect.
    if (room.mlsGroupId) {
      try {
        await deleteMlsState(this.config.dataDir, room.mlsGroupId);
      } catch {
        /* best-effort: the file may already be gone */
      }
    }
    this._mlsGroups.delete(roomId);

    // Remove room state
    delete this._persisted.rooms[roomId];

    await this._persistState();
    this.emit("room_left", { roomId });
  }

  /**
   * #629 recurrence fix — reconcile local room state against the server on
   * connect.
   *
   * On disband the server marks each member ``"left"`` and sets
   * ``room.status="disbanded"`` but sends NO WS event to the agent's bridge, so a
   * disbanded/left room lingers in local state and replays dead MLS commit
   * history on every reconnect ("Desired gen in the past" / "invalid ghash tag").
   * ``GET /rooms`` returns ONLY active rooms the caller is a member of, so any
   * locally-persisted room ABSENT from it is terminal — prune its MLS state
   * (file + in-memory group, keyed by roomId), its sessions, and the room entry.
   *
   * Fail-safe: a failed fetch / non-ok / non-array response prunes NOTHING, so a
   * transient error can never over-prune a live room.
   */
  private async _reconcileRoomsWithServer(): Promise<void> {
    const local = this._persisted?.rooms;
    if (!local || Object.keys(local).length === 0) return;
    if (!this._deviceJwt) return;

    let activeIds: Set<string>;
    try {
      const res = await fetch(`${this.config.apiUrl}/api/v1/rooms`, {
        headers: { Authorization: `Bearer ${this._deviceJwt}` },
      });
      if (!res.ok) return; // fail-safe: don't prune on a bad response
      const list = (await res.json()) as unknown;
      if (!Array.isArray(list)) return; // fail-safe
      activeIds = new Set(
        list.map((r: { id?: string }) => r?.id).filter((id): id is string => !!id),
      );
    } catch (err) {
      console.warn(
        `[SecureChannel] room reconcile skipped (fetch failed, pruning nothing): ${err instanceof Error ? err.message : String(err)}`,
      );
      return; // fail-safe — never prune on a transient error
    }

    const stale = Object.keys(local).filter((rid) => !activeIds.has(rid));
    if (stale.length === 0) return;

    for (const rid of stale) {
      const room = local[rid];
      if (room?.mlsGroupId) {
        try {
          await deleteMlsState(this.config.dataDir, room.mlsGroupId);
        } catch {
          /* best-effort: the file may already be gone */
        }
      }
      this._mlsGroups.delete(rid); // room groups are keyed by roomId
      for (const convId of room?.conversationIds ?? []) {
        this._sessions.delete(convId);
        if (this._persisted?.sessions) delete this._persisted.sessions[convId];
      }
      delete local[rid];
    }
    await this._persistState();
    console.log(
      `[SecureChannel] Room reconcile: pruned ${stale.length} disbanded/left room(s) from local state (${stale.map((r) => r.slice(0, 8)).join(", ")})`,
    );
    this.emit("rooms_reconciled", { pruned: stale });
  }

  /**
   * #629 — quarantine MLS state for groups this device is no longer in.
   *
   * `_reconcileRoomsWithServer` above covers ROOMS: anything absent from
   * `GET /rooms` is pruned. It cannot see a group that was never a room — a 1:1
   * conversation group or an A2A channel group — so that state lingered with
   * nothing to reconcile it. Measured on loopita 2026-08-08: one group-state
   * file whose group had no server-side row at all.
   *
   * `quarantineOrphanedMlsState` was written for exactly this and shipped with
   * 11 passing tests, but nothing ever called it. This is that call site.
   *
   * Runs AFTER the room reconcile so rooms are already gone and this only sees
   * genuine non-room orphans.
   *
   * Quarantine RENAMES to `.orphaned` rather than deleting: if the verdict is
   * ever wrong the state is recoverable by hand, which deletion would not be.
   */
  private async _quarantineOrphanedMlsGroups(): Promise<void> {
    if (!this._deviceJwt) return;

    // A "not a member" 403 is only believed while the DEVICE is confirmed
    // ACTIVE — read once per pass, and only if some group actually says so.
    // Suspended, revoked or unreadable standing => leave every file alone:
    // quarantining on a wrong verdict is how a working agent loses its history.
    let deviceActive: Promise<boolean> | null = null;
    const confirmDeviceActive = (): Promise<boolean> =>
      (deviceActive ??= this._readDeviceStanding().then((st) => st.kind === "active"));

    const quarantined = await quarantineOrphanedMlsState(
      this.config.dataDir,
      async (groupId: string) => {
        try {
          // A paused device is refused everywhere; nothing it hears is a
          // membership answer. Checked PER PROBE, so a pause noticed mid-pass
          // (by an earlier probe's inactive-403) stops the rest of the pass.
          if (this._suspended) return null;
          const res = await fetch(
            `${this.config.apiUrl}/api/v1/mls/groups/${encodeURIComponent(groupId)}`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          const detail = res.status === 403 ? await this._errorDetail(res) : null;
          if (detail === SecureChannel.INACTIVE_DEVICE_DETAIL) {
            // The device, not the membership: route it to the same
            // confirmation as every other inactive-403 (one read per session).
            await this._confirmInactive403("mls_probe");
            return null;
          }
          const verdict = mlsMembershipFromStatus(res.status, detail);
          if (verdict === false && res.status === 403) {
            if (!(await confirmDeviceActive()) || this._suspended) return null;
          }
          return verdict;
        } catch {
          // Network failure is NOT a removal. `null` leaves the state alone —
          // see mls-membership-probe.ts for why this direction is load-bearing.
          return null;
        }
      },
    );

    if (quarantined.length > 0) {
      console.log(
        `[SecureChannel] Quarantined ${quarantined.length} orphaned MLS group state file(s): ` +
          quarantined.map((g) => g.slice(0, 8)).join(", "),
      );
      this.emit("mls_state_quarantined", { groupIds: quarantined });
    }
  }

  /**
   * Handle an in-band `{event:"error"}` frame from the server.
   *
   * This is an APPLICATION-level error about one resource — it is NOT a
   * transport fault, and must never be treated as one. When the error names a
   * dead resource, the correct response is to PRUNE that resource; recycling
   * the connection would just replay the same doomed write.
   *
   * `conversation deleted` (#460/#481) is the server refusing a write to a
   * shared 1:1 MLS group whose conversations are all closed/deleted. It carries
   * `group_id` for exactly this purpose. Without the prune, `_persisted.mlsGroups`
   * is append-only and the 1:1 send path fans out to every entry, so the dead
   * group takes a rejected write on every message for the life of the agent
   * (loopita: group c1b1e11a, closed 2026-07-22, still bouncing 3 days later).
   *
   * Deliberately NARROW — it prunes only on this exact detail AND only when
   * `group_id` matches a persisted shared 1:1 group. Unknown ids and other
   * details prune nothing, so a server-side wording change or a room/A2A group
   * can never cost us live MLS state. Safe because `closed`/`deleted` are
   * terminal server-side, and a genuinely new group re-arrives via Welcome.
   *
   * Mirrors the #629 room prune: MLS state file, in-memory group, persisted
   * entry, then persist.
   */
  private async _handleServerError(payload: {
    detail?: string;
    group_id?: string;
  }): Promise<void> {
    const detail = payload?.detail;
    if (detail !== "conversation deleted" && detail !== "unknown group") return;

    const mlsGroupId = payload.group_id;
    if (!mlsGroupId) return;

    const groups = this._persisted?.mlsGroups;
    if (!groups) return;

    const gid = Object.keys(groups).find((k) => groups[k]?.mlsGroupId === mlsGroupId);
    if (!gid) return; // not one of ours (room / A2A / already pruned) — leave it alone

    // `unknown group` (#719) means the server has NO mls_groups row for this id,
    // so our conversation→group map is stale. Unlike `conversation deleted`,
    // this is NOT self-evidently terminal, so we do not prune on the error
    // string — we re-resolve from the server and act only on what it returns.
    if (detail === "unknown group") {
      const changed = await this._reconcileConversationGroups(gid, mlsGroupId);
      // #732: resend the refused message, but ONLY when the reconcile actually
      // repointed the primary group. A reconcile that returns the same mapping
      // means the rejection was not about staleness. The server dedupes per
      // (group, sender device, client_message_id) since #1407, but the resend
      // goes to a DIFFERENT group — a new key it cannot match — and receivers
      // do not dedupe, so an unneeded resend would show the owner a duplicate.
      // This gate is still the entire idempotency argument for the resend.
      if (changed) await this._retryAfterReconcile(mlsGroupId);
      return;
    }

    // `conversation deleted` is terminal — there is no group to retry into, and
    // the right behaviour is to surface the loss, not invent a destination.
    this._retryCandidates.delete(mlsGroupId);

    try {
      await deleteMlsState(this.config.dataDir, mlsGroupId);
    } catch {
      /* best-effort: the file may already be gone */
    }
    this._mlsGroups.delete(`1to1-group:${gid}`);
    delete groups[gid];
    await this._persistState();

    console.log(
      `[SecureChannel] Pruned dead shared 1:1 group ${gid.slice(0, 8)} ` +
        `(${mlsGroupId.slice(0, 8)}) — server reports its conversation deleted`,
    );
    this.emit("dm_group_pruned", { conversationGroupId: gid, mlsGroupId });
  }

  /**
   * Re-resolve the conversation→group map from the server (#719).
   *
   * `_persisted.conversationGroupIds` and `_persisted.groupId` are built ONCE,
   * in `_activate()`, from the activation response. `_activate` runs at
   * enrollment and never again, so when the owner deletes a conversation and a
   * new one is created the agent keeps addressing the OLD group forever.
   * Nothing reconciled it — not restart (the stale map is reloaded from disk),
   * not reconnect, not the heartbeat.
   *
   * wren, 2026-07-31: `groupId` 53798dd2 had no `mls_groups` row while three
   * ACTIVE conversations sat on 99faf707. Inbound worked, every reply was
   * refused `unknown group` and dropped. Silent since 2026-07-02 — the #460
   * fan-out was spraying all 12 known groups, so a live one always got hit.
   *
   * Best-effort and conservative, mirroring `_refreshRoomRoster`: on any
   * failure the existing map is kept. The prune is NARROW — only the group the
   * server named, and only once the server has positively reported an active
   * conversation on some other group. Absence of data is never treated as
   * evidence of deletion.
   *
   * Returns TRUE only when the PRIMARY group pointer actually moved, which is
   * the signal #732's resend is gated on. Deliberately narrower than "anything
   * in the map changed": if some other group went stale we cannot tell which
   * conversation replaced it, and delivering a reply to the wrong counterparty
   * is strictly worse than losing it.
   */
  private async _reconcileConversationGroups(
    staleGid?: string,
    staleMlsGroupId?: string,
  ): Promise<boolean> {
    const jwt = this._deviceJwt ?? this._persisted?.deviceJwt;
    if (!jwt || !this._persisted) return false;

    let rows: unknown;
    const reconcileStartedAt = Date.now();
    try {
      const res = await fetch(`${this.config.apiUrl}/api/v1/conversations`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) return false;
      rows = await res.json();
    } catch (err) {
      console.warn("[SecureChannel] Conversation re-resolve failed:", err);
      return false;
    }
    if (!Array.isArray(rows)) return false;

    // The endpoint is TENANT-wide and returns `closed` rows too, so filter to
    // conversations this device can actually be addressed on.
    // #1076: only `direct` rows are this agent's 1:1 channels. Room-pairwise
    // rows (`conversation_type: "room"`) used to be listed too, and being newer
    // they were adopted as the primary — the agent then answered into a shadow
    // group as well as the real one. Migration 0178 removed the type; this
    // filter is the belt — an exclusion, so an unfamiliar label fails open.
    const mine = rows.filter(
      (r: any) =>
        r?.agent_device_id === this._deviceId &&
        r?.status === "active" &&
        r?.conversation_type !== "room" &&
        typeof r?.group_id === "string" &&
        typeof r?.id === "string",
    ) as Array<{ id: string; group_id: string; default_topic_id?: string | null }>;

    // An empty result is not evidence of deletion — it is equally consistent
    // with a filter/permissions/shape change. Keep what we have.
    if (mine.length === 0) return false;

    const before = this._persisted.groupId;
    const primaryBefore = this._persisted.primaryConversationId;
    // #1416: the groups known BEFORE this reconcile, for the history retag below.
    const knownBefore = this._knownDmGroups();
    // #1416 security review: the browsers that owned a conversation of the old
    // group. A browser belongs to exactly one person, so a new primary owned by
    // one of them is provably the same person's chat.
    const ownersBefore = new Set<string>();
    if (before) {
      for (const [convId, gid] of this._sessionGroupIds) {
        if (gid !== before) continue;
        const owner = this._sessions.get(convId)?.ownerDeviceId
          ?? this._persisted.sessions?.[convId]?.ownerDeviceId;
        if (owner) ownersBefore.add(owner);
      }
    }
    this._sessionGroupIds = new Map(mine.map((r) => [r.id, r.group_id]));
    this._persisted.conversationGroupIds = Object.fromEntries(this._sessionGroupIds);

    // #1418: the server just said which conversations and groups are live.
    // Inbound evidence from before this fetch that names neither is about a
    // dead conversation or a husk — drop it, so it cannot keep standing for a
    // person; and the groups a Welcome joined unmapped are now either mapped
    // or nobody's.
    {
      const convs = new Set(mine.map((r) => r.id));
      const groups = new Set(mine.map((r) => r.group_id));
      this._lastReconcile = { at: reconcileStartedAt, convs, groups };
      for (const [key, a] of this._attestedInbound) {
        if (a.at > reconcileStartedAt) continue;
        if ((a.convId && convs.has(a.convId)) || (a.groupId && groups.has(a.groupId))) continue;
        this._attestedInbound.delete(key);
      }
      for (const [gid, at] of this._welcomedUnmapped) {
        if (at <= reconcileStartedAt) this._welcomedUnmapped.delete(gid);
      }
    }

    // Prefer the existing primary if the server still reports it; otherwise the
    // agent's primary conversation is gone and we must adopt an active one.
    //
    // #1042: that adoption used to be `mine[0]` — the FIRST active row the
    // endpoint happened to return. Measured live on wren 2026-08-24: she had
    // SIX active conversations, five of them empty duplicates created in one
    // day with no messages and no MLS group. The reconcile adopted
    // a7547625 (an empty one), and her very next reply went there:
    //
    //   14:54:44  [deliver] target=owner content=text result=sent
    //
    // …while ZERO wren messages reached the server. She answered correctly,
    // into a thread nobody was reading. `result=sent` is a local statement
    // about dispatch, not about arrival.
    //
    // So prefer a conversation we have actually JOINED — one whose group we
    // hold MLS state for. That is not a proxy standing in for membership; it
    // IS membership, locally attested. An empty duplicate has no group and no
    // state, so it can never win.
    //
    // Order: keep the existing primary → else a joined conversation → else
    // fall back to the old behaviour so a first-run agent with no MLS state
    // yet still gets a primary rather than none.
    const joined = mine.find((r) => this._persisted!.mlsGroups?.[r.group_id]);
    const primary =
      mine.find((r) => r.id === this._persisted!.primaryConversationId) ??
      joined ??
      mine[0];
    if (!mine.some((r) => r.id === this._persisted!.primaryConversationId)) {
      console.log(
        `[SecureChannel] Adopting new primary conversation ${primary.id.slice(0, 8)} ` +
          `(group ${primary.group_id.slice(0, 8)}) — ` +
          `${joined ? "we hold MLS state for it" : "no joined conversation found, first active row"}`,
      );
    }
    this._persisted.groupId = primary.group_id;
    this._persisted.primaryConversationId = primary.id;

    // #1416: every listed conversation reports its own group's default topic.
    // Record each under its own group; the legacy `defaultTopicId` follows the
    // primary group, so a primary that moved no longer keeps the old group's
    // topic (it used to: only `groupId` was updated here).
    for (const r of mine) {
      if (typeof r.default_topic_id === "string") this._recordDefaultTopic(r.group_id, r.default_topic_id);
    }
    const primaryDefault = this._persisted.defaultTopicIds?.[primary.group_id];
    if (primaryDefault) this._persisted.defaultTopicId = primaryDefault;

    // #1416: a single-person agent whose ONLY group was replaced (#719/#1042 —
    // wren's 99faf707) keeps its own history: entries filed under the old
    // group move to the new primary group. Only when exactly that one group
    // was known — with more than one, the old group may be a person who left,
    // and moving it would hand their history to someone else.
    //
    // #1416 security review: "only one group was known" is not proof the new
    // primary is the SAME person (in a team account, A may have left and B's
    // chat become primary). Retag only when the new primary is the same
    // conversation, or is owned by a browser that owned a conversation of the
    // old group. Otherwise fail closed: the old entries keep their own scope.
    const primaryOwner = (primary as { owner_device_id?: unknown }).owner_device_id;
    const samePerson =
      primary.id === primaryBefore ||
      (typeof primaryOwner === "string" && ownersBefore.has(primaryOwner));
    let retagged = false;
    if (before && primary.group_id !== before && knownBefore.size === 1 && knownBefore.has(before) && samePerson) {
      const oldPrefix = `dm:g:${before}:`;
      for (const entry of this._persisted.messageHistory ?? []) {
        if (entry.topicId?.startsWith(oldPrefix)) {
          entry.topicId = `dm:g:${primary.group_id}:${entry.topicId.slice(oldPrefix.length)}`;
          retagged = true;
        }
      }
    }

    // Prune the named group ONLY if the server did not just report it as
    // carrying an active conversation (i.e. the error was not transient).
    // #1034: a PROACTIVE reconcile names no stale group, so there is nothing to
    // prune — it re-resolves the map and stops. Pruning on "the server didn't
    // mention it" would be exactly the absence-as-evidence mistake this
    // function's own docstring warns against.
    const liveGroupIds = new Set(mine.map((r) => r.group_id));
    if (staleGid && staleMlsGroupId && !liveGroupIds.has(staleGid)) {
      try {
        await deleteMlsState(this.config.dataDir, staleMlsGroupId);
      } catch {
        /* best-effort: the file may already be gone */
      }
      this._mlsGroups.delete(`1to1-group:${staleGid}`);
      delete this._persisted.mlsGroups?.[staleGid];
      this.emit("dm_group_pruned", {
        conversationGroupId: staleGid,
        mlsGroupId: staleMlsGroupId,
      });
    }

    await this._persistState();
    if (retagged) {
      this._markHistoryDirty();
      await this._saveHistoryState();
    }

    console.log(
      `[SecureChannel] Re-resolved conversation groups ` +
        `${staleMlsGroupId ? `after 'unknown group' ${staleMlsGroupId.slice(0, 8)}` : "on connect (#1034)"}: ` +
        `primary group ${String(before).slice(0, 8)} → ` +
        `${this._persisted.groupId.slice(0, 8)} (${mine.length} active conversation(s))`,
    );

    // The primary moved, AND it moved off the group the server just refused —
    // so the refused message has an unambiguous new home. Both halves matter:
    // without the second, a primary that changed for an unrelated reason would
    // send the reply to a counterparty it was never addressed to.
    // The #732 resend gate needs BOTH halves: the primary moved, AND it moved
    // off the group the server just refused. A proactive reconcile refused
    // nothing, so it never authorises a resend.
    return (
      staleGid !== undefined &&
      this._persisted.groupId !== before &&
      before === staleGid
    );
  }

  /**
   * Resend the message that a now-reconciled `unknown group` refusal killed (#732).
   *
   * Called ONLY after `_reconcileConversationGroups` reported that the primary
   * group pointer moved off the refused group, so there is exactly one sensible
   * destination: the conversation the reconcile settled on.
   *
   * The candidate is consumed BEFORE the resend, not after — one attempt is the
   * bound, and that must hold even if the resend itself throws. A retry that
   * re-armed on failure would turn a persistent refusal into an infinite loop,
   * which is a worse failure than the dropped message it set out to fix.
   *
   * The resend is always TARGETED, never a broadcast: the original send may
   * have fanned out to several groups of which only one was refused, and
   * re-broadcasting would duplicate the message in all the others.
   */
  private async _retryAfterReconcile(staleMlsGroupId: string): Promise<void> {
    const candidate = this._retryCandidates.get(staleMlsGroupId);
    this._retryCandidates.delete(staleMlsGroupId);
    if (!candidate) return;

    if (Date.now() - candidate.ts > RETRY_CANDIDATE_TTL_MS) {
      console.warn(
        `[deliver] delivery_result=LOST group=${staleMlsGroupId.slice(0, 8)} ` +
          `reason=retry-window-expired`,
      );
      return;
    }

    const conversationId = this._persisted?.primaryConversationId;
    if (!conversationId) return;

    try {
      await this.send(candidate.plaintext, {
        ...candidate.options,
        conversationId,
        isResend: true,
      });
      console.log(
        `[deliver] delivery_result=RESENT group=${staleMlsGroupId.slice(0, 8)} → ` +
          `${String(this._persisted?.groupId).slice(0, 8)} conv=${conversationId.slice(0, 8)}`,
      );
    } catch (err) {
      // #718's honesty rule: a failed retry must read as a loss, not silence.
      console.error(
        `[deliver] delivery_result=LOST group=${staleMlsGroupId.slice(0, 8)} ` +
          `reason=resend-failed detail=${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  /**
   * Return info for all joined rooms.
   */
  getRooms(): RoomInfo[] {
    if (!this._persisted?.rooms) return [];
    return Object.values(this._persisted.rooms).map((rs) => ({
      roomId: rs.roomId,
      name: rs.name,
      members: rs.members,
      conversationIds: rs.conversationIds,
    }));
  }

  /**
   * Refresh a room's member roster from the authoritative backend roster
   * (GET /rooms/{roomId}) and merge it into the persisted state (GH #366).
   *
   * The local roster is snapshotted at join-time (joinRoom) and only refreshed
   * by a later `room_joined` WS re-broadcast. When members join concurrently,
   * the first inbound message can arrive BEFORE that re-broadcast, so the agent
   * processes a message with an incomplete roster (e.g. self + owner but not the
   * other agent) and frames a group room as a 1:1 DM. Fetching on demand here
   * makes the roster complete before the message is surfaced.
   *
   * Best-effort: on any error (offline, 404, auth) the existing roster is kept.
   * Concurrent calls for the same room are deduped via _roomRosterRefreshInFlight.
   */
  private async _refreshRoomRoster(roomId: string): Promise<void> {
    const existing = this._roomRosterRefreshInFlight.get(roomId);
    if (existing) return existing;

    const run = (async () => {
      const jwt = this._deviceJwt ?? this._persisted?.deviceJwt;
      if (!jwt || !this._persisted?.rooms?.[roomId]) return;
      try {
        const res = await fetch(`${this.config.apiUrl}/api/v1/rooms/${roomId}`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (!res.ok) return;
        const room = await res.json();
        const members: RoomMemberInfo[] = (room.members || [])
          // Active members only — drop members who have left.
          .filter((m: any) => !m.left_at && (m.status === undefined || m.status === "active"))
          .map((m: any) => ({
            deviceId: m.device_id,
            entityType: m.entity_type,
            displayName: m.display_name,
            identityPublicKey: m.identity_public_key,
            ephemeralPublicKey: m.ephemeral_public_key,
            ...(typeof m.tenant_id === "string" ? { tenantId: m.tenant_id } : {}),
          }));
        if (members.length > 0) {
          const roomState = this._persisted.rooms[roomId];
          const before = roomState.members?.length ?? 0;
          roomState.members = members;
          this._roomRosterRefreshed.add(roomId);
          if (members.length !== before) {
            console.log(
              `[SecureChannel] Roster refreshed for room ${roomId.slice(0, 8)}: ${before} → ${members.length} members`,
            );
            await this._persistState();
          }
        }
      } catch (err) {
        console.warn(`[SecureChannel] Roster refresh failed for room ${roomId.slice(0, 8)}:`, err);
      }
    })();

    this._roomRosterRefreshInFlight.set(roomId, run);
    try {
      await run;
    } finally {
      this._roomRosterRefreshInFlight.delete(roomId);
    }
  }

  // --- Heartbeat and status methods ---

  startHeartbeat(
    intervalSeconds: number,
    statusCallback: () => HeartbeatStatus,
  ): void {
    this.stopHeartbeat();
    this._heartbeatCallback = statusCallback;
    this._heartbeatIntervalSeconds = intervalSeconds;
    this._sendHeartbeat();
    this._heartbeatTimer = setInterval(() => {
      this._sendHeartbeat();
    }, intervalSeconds * 1000);
  }

  async stopHeartbeat(): Promise<void> {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
    if (this._heartbeatCallback && this._state === "ready") {
      try {
        await this.send(
          JSON.stringify({
            agent_status: "shutting_down",
            current_task: "",
            timestamp: new Date().toISOString(),
          }),
          {
            messageType: "heartbeat",
            metadata: { next_heartbeat_seconds: this._heartbeatIntervalSeconds },
          },
        );
      } catch { /* best-effort shutdown heartbeat */ }
    }
    this._heartbeatCallback = null;
  }

  async sendStatusAlert(alert: StatusAlert, routing?: OwnerRouting): Promise<void> {
    const priority = alert.severity === "error" || alert.severity === "critical"
      ? "high" : "normal";
    const envelope: Record<string, unknown> = {
      title: alert.title,
      message: alert.message,
      severity: alert.severity,
      timestamp: new Date().toISOString(),
    };
    if (alert.detail !== undefined) envelope.detail = alert.detail;
    if (alert.detailFormat !== undefined) envelope.detail_format = alert.detailFormat;
    if (alert.category !== undefined) envelope.category = alert.category;
    await this.send(
      JSON.stringify(envelope),
      {
        conversationId: routing?.conversationId,
        audience: routing?.audience,
        messageType: "status_alert",
        priority,
        metadata: { severity: alert.severity },
      },
    );
  }

  async sendArtifact(artifact: {
    filePath: string;
    filename: string;
    mimeType: string;
    description?: string;
  }, routing?: OwnerRouting): Promise<void> {
    if (this._state !== "ready" || this._sessions.size === 0 || !this._ws) {
      throw new Error("Channel is not ready");
    }

    const messageGroupId = randomUUID();
    const pendingWsSends: string[] = [];

    // #1418: only the person involved (or each person, for an explicit
    // "everyone"), with the blob uploaded under that person's conversation.
    const buckets = this._ownerFileRecipients(routing);
    if (buckets.length === 0) {
      // #1418: reaching nobody is a failure, not a quiet success — deliver()
      // turns this into ok:false so the caller knows the file did not go.
      console.warn("[SecureChannel] sendArtifact(): no live session for the person involved — nothing sent");
      throw new Error("No conversation is ready to receive this file");
    }
    for (const bucket of buckets) {
      // Upload encrypted file via existing attachment infrastructure
      const attachMeta = await this._uploadAttachment(artifact.filePath, bucket.uploadConvId);

      // Build artifact_share envelope
      const envelope = JSON.stringify({
        type: "artifact",
        blob_id: attachMeta.blobId,
        blob_url: attachMeta.blobUrl,
        filename: artifact.filename,
        mime_type: artifact.mimeType,
        size_bytes: attachMeta.size,
        description: artifact.description,
        attachment: attachMeta,
      });

      for (const convId of bucket.convIds) {
        const session = this._sessions.get(convId)!;
        const encrypted = session.ratchet.encrypt(envelope);
        const transport = encryptedMessageToTransport(encrypted);
        pendingWsSends.push(
          JSON.stringify({
            event: "message",
            data: {
              conversation_id: convId,
              header_blob: transport.header_blob,
              ciphertext: transport.ciphertext,
              message_group_id: messageGroupId,
              message_type: "artifact_share",
            },
          }),
        );
      }
    }

    // Persist ratchet states BEFORE sending over WS to avoid desync on crash
    await this._persistState();

    for (const frame of pendingWsSends) {
      this._ws.send(frame);
    }
  }

  async sendActionConfirmation(confirmation: {
    action: string;
    status: "completed" | "failed" | "partial";
    decisionId?: string;
    detail?: string;
  }, routing?: OwnerRouting): Promise<void> {
    const envelope: Record<string, unknown> = {
      type: "action_confirmation",
      action: confirmation.action,
      status: confirmation.status,
    };
    if (confirmation.decisionId !== undefined) envelope.decision_id = confirmation.decisionId;
    if (confirmation.detail !== undefined) envelope.detail = confirmation.detail;

    await this.send(
      JSON.stringify(envelope),
      {
        conversationId: routing?.conversationId,
        audience: routing?.audience,
        messageType: "action_confirmation",
        metadata: { status: confirmation.status },
      },
    );
  }

  async sendActionConfirmationToRoom(
    roomId: string,
    confirmation: {
      action: string;
      status: "completed" | "failed" | "partial";
      decisionId?: string;
      detail?: string;
      estimated_cost?: number;
    },
  ): Promise<void> {
    const envelope: Record<string, unknown> = {
      type: "action_confirmation",
      action: confirmation.action,
      status: confirmation.status,
    };
    if (confirmation.decisionId !== undefined) envelope.decision_id = confirmation.decisionId;
    if (confirmation.detail !== undefined) envelope.detail = confirmation.detail;

    const metadata: Record<string, unknown> = {
      action: confirmation.action,
      status: confirmation.status,
    };
    if (confirmation.estimated_cost !== undefined) {
      metadata.estimated_cost = confirmation.estimated_cost;
    }

    await this.sendToRoom(roomId, JSON.stringify(envelope), {
      messageType: "action_confirmation",
      priority: "high",
      metadata,
    });
  }

  // --- Unified Delivery Protocol ---

  /**
   * Canonical message dispatcher. ALL outbound messages should flow through this method.
   * Routes based on explicit target — never silently falls back to a room.
   */
  async deliver(
    target: DeliveryTarget,
    content: DeliveryContent,
    options?: DeliveryOptions,
  ): Promise<DeliveryReceipt> {
    const ts = new Date().toISOString();

    // Validate channel state
    if (this._state === "idle" || this._state === "error") {
      return {
        ok: false,
        destination: { kind: "owner" },
        error: `Channel is in ${this._state} state`,
        timestamp: ts,
      };
    }

    // Resolve target
    let resolved: { kind: "owner" | "room" | "a2a"; id?: string };
    if (target.kind === "context") {
      if (this._lastInboundRoomId) {
        resolved = { kind: "room", id: this._lastInboundRoomId };
        console.log(`[deliver] target=context→room:${this._lastInboundRoomId.slice(0, 8)}...`);
      } else {
        resolved = { kind: "owner" };
        console.log(`[deliver] target=context→owner`);
      }
    } else if (target.kind === "owner") {
      resolved = { kind: "owner" };
    } else if (target.kind === "room") {
      if (!this._persisted?.rooms?.[target.roomId]) {
        const err = `Room ${target.roomId} not found`;
        console.log(`[deliver] target=room:${target.roomId.slice(0, 8)}... content=${content.type} result=FAIL: ${err}`);
        return { ok: false, destination: { kind: "room", id: target.roomId }, error: err, timestamp: ts };
      }
      resolved = { kind: "room", id: target.roomId };
    } else if (target.kind === "a2a") {
      // Look up channel by hub address in participants — try persisted state first
      const a2aEntries = this._persisted?.a2aChannels ? Object.values(this._persisted.a2aChannels) : [];
      let channelEntry = a2aEntries.find((ch) =>
        ch.participants?.some(p => p.hubAddress === target.hubAddress && (p.status === "active" || p.status === "invited")),
      );

      // If not found, fetch from API and send directly (bypasses persisted state race)
      if (!channelEntry && content.type === "text" && this._ws && this._state === "ready") {
        try {
          console.log(`[deliver] A2A local miss for ${target.hubAddress}, trying direct API send...`);
          const channels = await this.listA2AChannels();
          const match = channels.find((ch) =>
            (ch.status === "active" || ch.status === "approved") &&
            ch.participants?.some(p => p.hub_address === target.hubAddress),
          );
          if (match?.conversationId) {
            // Re-read after sync updated persisted state
            channelEntry = Object.values(this._persisted?.a2aChannels ?? {}).find(
              (ch) => ch.participants?.some(p => p.hubAddress === target.hubAddress),
            );
          }
        } catch (syncErr) {
          console.warn("[deliver] A2A direct send failed:", syncErr);
        }
      }

      if (!channelEntry) {
        const storedAddresses = a2aEntries.flatMap(ch => ch.participants?.map(p => p.hubAddress) ?? []).join(", ") || "(none)";
        const err = `No A2A channel found for hub address: ${target.hubAddress} (stored: ${storedAddresses})`;
        console.log(`[deliver] target=a2a:${target.hubAddress} content=${content.type} result=FAIL: ${err}`);
        return { ok: false, destination: { kind: "a2a", id: target.hubAddress }, error: err, timestamp: ts };
      }
      resolved = { kind: "a2a", id: target.hubAddress };
    } else if (target.kind === "a2a_channel") {
      // Direct channel ID routing — for multi-agent A2A channels
      const channelEntry = this._persisted?.a2aChannels?.[target.channelId];
      if (!channelEntry) {
        // Try syncing first
        try {
          await this.listA2AChannels();
        } catch { /* ignore */ }
        const entry = this._persisted?.a2aChannels?.[target.channelId];
        if (!entry) {
          const err = `A2A channel ${target.channelId.slice(0, 8)} not found`;
          console.log(`[deliver] target=channel:${target.channelId.slice(0, 8)} content=${content.type} result=FAIL: ${err}`);
          return { ok: false, destination: { kind: "a2a", id: target.channelId }, error: err, timestamp: ts };
        }
      }
      resolved = { kind: "a2a", id: target.channelId };
    } else {
      return { ok: false, destination: { kind: "owner" }, error: "Unknown target kind", timestamp: ts };
    }

    // #1418: a caller that cannot say whose message this is (the local HTTP
    // API) may ask not to have it GUESSED. Checked on the RESOLVED target, so
    // `target: "context"` that lands on the owner is covered too. Opt-in only:
    // heartbeats, schedules and alerts keep going to the primary person.
    if (
      resolved.kind === "owner"
      && options?.refuseIfAmbiguous
      && this.isUntargetedOwnerAmbiguous(options)
    ) {
      console.log(`[deliver] target=owner content=${content.type} result=REFUSED: ambiguous (several people mid-turn)`);
      return { ok: false, ambiguous: true, destination: resolved, error: AMBIGUOUS_OWNER_ERROR, timestamp: ts };
    }

    // Outbound idempotency guard (#475): a misbehaving agent runtime can hand us
    // the SAME message for the same destination many times in a burst (prod: 23×
    // in ~5s). Collapse an exact-duplicate of a message we ALREADY sent to the
    // same destination/thread within the sliding window (and under the absolute
    // cap) to a single send, so the owner never receives N copies. The key is
    // recorded only AFTER a successful dispatch (below), so a failed send never
    // suppresses its retries. Fail-open: an error here must never block delivery.
    let dedupeKey: string | null = null;
    try {
      // #1418: an untargeted owner delivery is addressed to a person resolved
      // NOW. The same text to two different people is not a duplicate, so the
      // key names that person (unchanged for a one-person agent: no tag). A
      // failure here only drops the tag — the guard itself keeps working.
      let audienceTag: string | undefined;
      try {
        audienceTag = resolved.kind === "owner" && options?.conversationId === undefined
          ? this._audienceTag(options)
          : undefined;
      } catch {
        audienceTag = undefined;
      }
      dedupeKey = outboundDedupeKey(
        resolved,
        content,
        audienceTag ? { ...options, conversationId: audienceTag } : options,
      );
      if (dedupeKey) {
        const now = Date.now();
        const recent = (this._recentDeliveries ??= new Map());
        const prior = recent.get(dedupeKey);
        if (
          prior &&
          now - prior.lastTs < OUTBOUND_DEDUPE_WINDOW_MS &&
          now - prior.firstTs < OUTBOUND_DEDUPE_MAX_SUPPRESS_MS
        ) {
          prior.lastTs = now; // slide: keep a steady burst collapsed to the first send
          prior.suppressed += 1;
          console.warn(
            `[deliver] suppressed duplicate ${content.type} → ${resolved.kind}:${(resolved.id ?? "owner").slice(0, 8)} ` +
            `(x${prior.suppressed} within ${OUTBOUND_DEDUPE_WINDOW_MS}ms)`,
          );
          return { ok: true, destination: resolved, deduped: true, timestamp: ts };
        }
      }
    } catch (dedupeErr) {
      dedupeKey = null;
      console.warn("[deliver] dedupe guard error (ignored):", dedupeErr);
    }

    // Dispatch based on content type + resolved target
    try {
      let decisionId: string | undefined;
      // #1418: who an owner-bound delivery is for. No conversation → the
      // person involved; `audience: "everyone"` only when the caller said so.
      const ownerRouting: OwnerRouting = {
        conversationId: options?.conversationId,
        audience: options?.audience,
      };

      if (content.type === "text") {
        if (resolved.kind === "room") {
          await this.sendToRoom(resolved.id!, content.text, {
            messageType: options?.metadata?.message_type as string | undefined,
            priority: options?.priority,
            metadata: options?.metadata,
          });
        } else if (resolved.kind === "a2a") {
          // resolved.id is hub address for kind=a2a, channel ID for kind=a2a_channel
          if (target.kind === "a2a_channel") {
            await this.sendToA2AChannel(resolved.id!, content.text);
          } else {
            await this.sendToAgent(resolved.id!, content.text, {
              parentSpanId: options?.parentSpanId,
            });
          }
        } else {
          await this.send(content.text, {
            conversationId: options?.conversationId,
            audience: options?.audience,
            topicId: options?.topicId,
            messageType: options?.metadata?.message_type as string | undefined,
            priority: options?.priority,
            metadata: options?.metadata,
          });
        }
      } else if (content.type === "decision_request") {
        if (resolved.kind !== "owner") {
          const err = "Decision requests can only be sent to owner";
          console.log(`[deliver] target=${resolved.kind}:${resolved.id} content=decision_request result=FAIL: ${err}`);
          return { ok: false, destination: resolved, error: err, timestamp: ts };
        }
        decisionId = await this.sendDecisionRequest(content.request, ownerRouting);
      } else if (content.type === "status_alert") {
        if (resolved.kind === "room") {
          const envelope: Record<string, unknown> = {
            title: content.alert.title,
            message: content.alert.message,
            severity: content.alert.severity,
            timestamp: ts,
          };
          if (content.alert.detail !== undefined) envelope.detail = content.alert.detail;
          if (content.alert.detailFormat !== undefined) envelope.detail_format = content.alert.detailFormat;
          if (content.alert.category !== undefined) envelope.category = content.alert.category;
          await this.sendToRoom(resolved.id!, JSON.stringify(envelope), {
            messageType: "status_alert",
            priority: content.alert.severity === "error" || content.alert.severity === "critical" ? "high" : "normal",
            metadata: { severity: content.alert.severity },
          });
        } else if (resolved.kind === "owner") {
          await this.sendStatusAlert(content.alert, ownerRouting);
        } else {
          return { ok: false, destination: resolved, error: "Status alerts cannot be sent to A2A targets", timestamp: ts };
        }
      } else if (content.type === "action_confirmation") {
        if (resolved.kind === "room") {
          await this.sendActionConfirmationToRoom(resolved.id!, content.confirmation);
        } else if (resolved.kind === "owner") {
          await this.sendActionConfirmation(content.confirmation, ownerRouting);
        } else {
          return { ok: false, destination: resolved, error: "Action confirmations cannot be sent to A2A targets", timestamp: ts };
        }
      } else if (content.type === "artifact") {
        if (resolved.kind !== "owner") {
          return { ok: false, destination: resolved, error: "Artifacts can only be sent to owner", timestamp: ts };
        }
        await this.sendArtifact(content.artifact, ownerRouting);
      } else if (content.type === "attachment") {
        if (resolved.kind !== "owner") {
          return { ok: false, destination: resolved, error: "Attachments can only be sent to owner", timestamp: ts };
        }
        await this.sendWithAttachment(content.text, content.filePath, {
          topicId: options?.topicId,
          ...ownerRouting,
        });
      }

      const targetLabel = resolved.kind === "owner" ? "owner" : `${resolved.kind}:${resolved.id?.slice(0, 8)}...`;
      // #718: `sent`, NOT `ok`. The MLS/WS path is fire-and-forget — this line
      // is written once the frame reaches the socket, roughly 50ms BEFORE the
      // server can accept or refuse it. Calling that `ok` made a TOTAL delivery
      // failure byte-identical to a success: wren logged `result=ok` beside 707
      // `unknown group` rejections for a month and the owner received none of
      // those messages. The real outcome, when the server reports one, arrives
      // on the correlated `[deliver] delivery_result=` line from the WS error
      // handler. Absence of that line is not proof of delivery — it is the
      // absence of a rejection, which is the most this transport can tell us.
      console.log(`[deliver] target=${targetLabel} content=${content.type} result=sent`);

      // Record-after-success (#475/#476): only now that the send has succeeded do
      // we arm the dedupe window for this key, so a failed send never suppresses
      // its own retries. A continuing run keeps its firstTs (for the absolute cap);
      // a fresh run (no prior, window expired, or cap exceeded) starts a new one.
      if (dedupeKey) {
        try {
          const now = Date.now();
          const recent = (this._recentDeliveries ??= new Map());
          const prior = recent.get(dedupeKey);
          const continuing =
            !!prior &&
            now - prior.lastTs < OUTBOUND_DEDUPE_WINDOW_MS &&
            now - prior.firstTs < OUTBOUND_DEDUPE_MAX_SUPPRESS_MS;
          recent.set(dedupeKey, {
            firstTs: continuing ? prior!.firstTs : now,
            lastTs: now,
            suppressed: continuing ? prior!.suppressed : 0,
          });
          pruneRecentDeliveries(recent, now);
        } catch (recordErr) {
          console.warn("[deliver] dedupe record error (ignored):", recordErr);
        }
      }

      return {
        ok: true,
        destination: resolved,
        queued: this._state !== "ready",
        decisionId,
        timestamp: ts,
      };
    } catch (err) {
      const targetLabel = resolved.kind === "owner" ? "owner" : `${resolved.kind}:${resolved.id?.slice(0, 8)}...`;
      const errMsg = err instanceof Error ? err.message : String(err);
      console.log(`[deliver] target=${targetLabel} content=${content.type} result=FAIL: ${errMsg}`);
      return { ok: false, destination: resolved, error: errMsg, timestamp: ts };
    }
  }

  /**
   * Returns all available delivery destinations with availability status.
   */
  listTargets(): TargetInfo[] {
    const targets: TargetInfo[] = [
      {
        kind: "owner",
        id: "owner",
        label: "Owner (direct)",
        available: this._state === "ready" && this._sessions.size > 0,
      },
    ];

    // Add rooms
    if (this._persisted?.rooms) {
      for (const room of Object.values(this._persisted.rooms)) {
        targets.push({
          kind: "room",
          id: room.roomId,
          label: room.name || `Room ${room.roomId.slice(0, 8)}`,
          // #1076: a room is reachable when it has an MLS group — which is what
          // `sendToRoom` actually needs. `conversationIds` was the pairwise
          // Double Ratchet list and is empty for every room since 0178.
          available: this._state === "ready" && !!room.mlsGroupId,
        });
      }
    }

    // Add A2A peers (each active participant is a target)
    if (this._persisted?.a2aChannels) {
      for (const ch of Object.values(this._persisted.a2aChannels)) {
        for (const p of ch.participants ?? []) {
          if (p.status !== "active" && p.status !== "invited") continue;
          targets.push({
            kind: "a2a",
            id: p.hubAddress,
            label: `A2A: ${p.hubAddress}`,
            available: this._state === "ready" && (
              !!ch.mlsGroupId && this._mlsGroups.has(`a2a:${ch.channelId}`)
            ),
          });
        }
      }
    }

    return targets;
  }

  private _sendHeartbeat(): void {
    if (this._state !== "ready" || !this._heartbeatCallback) return;
    const status = this._heartbeatCallback();
    this.send(
      JSON.stringify({
        agent_status: status.agent_status,
        current_task: status.current_task,
        timestamp: new Date().toISOString(),
      }),
      {
        messageType: "heartbeat",
        metadata: { next_heartbeat_seconds: this._heartbeatIntervalSeconds },
      },
    ).catch((err) => {
      this.emit("error", new Error(`Heartbeat send failed: ${err}`));
    });
  }

  // --- Trust Gate token management ---

  getTrustHeaders(): Record<string, string> {
    if (!this._trustToken) return {};
    return { "AgentVault-Trust": this._trustToken };
  }

  get trustToken(): string | null {
    return this._trustToken;
  }

  get trustTier(): string | null {
    return this._trustTier;
  }

  get trustTokenExpiresAt(): string | null {
    return this._trustTokenExpiresAt;
  }

  async refreshTrustToken(): Promise<void> {
    // The server answers 503 ("Trust Gate not configured"), 404 (no hub
    // identity) or 403 (unverified agent) for as long as that stays true, so
    // asking again every 12 minutes only fills the server log. Back off.
    if (Date.now() < this._trustTokenUnavailableUntil) return;
    try {
      const res = await fetch(`${this.config.apiUrl}/api/v1/gate/token`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this._deviceJwt}`,
          "Content-Type": "application/json",
        },
      });
      if (res.ok) {
        const data = await res.json();
        this._trustToken = data.token;
        this._trustTier = data.tier;
        this._trustTokenExpiresAt = data.expires_at;
      } else if (res.status === 503 || res.status === 404 || res.status === 403) {
        this._trustTokenUnavailableUntil = Date.now() + SecureChannel.TRUST_TOKEN_UNAVAILABLE_BACKOFF_MS;
      }
    } catch (err) {
      // Best-effort — keep existing token if available
      console.warn("[AgentVault] Trust token refresh failed:", err);
    }
  }

  startTrustTokenRefresh(): void {
    // Called on every (re)connect. Without this stop, each reconnect added
    // another 12-minute interval: measured 2026-10-23, 355 token requests in
    // 10 minutes against 7 reconnects across the fleet.
    this.stopTrustTokenRefresh();
    // Refresh every 12 minutes (80% of 15-min TTL)
    this.refreshTrustToken();
    this._trustTokenInterval = setInterval(
      () => this.refreshTrustToken(),
      12 * 60 * 1000,
    );
  }

  stopTrustTokenRefresh(): void {
    if (this._trustTokenInterval) {
      clearInterval(this._trustTokenInterval);
      this._trustTokenInterval = null;
    }
  }

  async stop(): Promise<void> {
    this._stopped = true;
    this._lifecycleGeneration += 1;
    this._historyRestoreController?.abort(new DOMException("Channel stopped", "AbortError"));
    this._historyRestoreController = null;
    this._historyUploadController?.abort(new DOMException("Channel stopped", "AbortError"));
    this._historyUploadController = null;
    this._historyBackupScheduler.stop();
    this._historyBackupClient?.dispose();
    this._historyBackupClient = null;
    this._historyKeyCacheIdentity = null;
    // A flush may be between a successful relay write and its atomic
    // history+remaining-queue publication. Let that single consumer settle
    // before draining the state writer below.
    await this._outboundFlushPromise?.catch(() => {});
    // Successful history handlers await their own publication, but stop can
    // race a handler owned by another caller. Do not report the lifecycle
    // stopped until every already-enqueued atomic publication has settled.
    await this._stateSaveQueue;
    await this.stopHeartbeat();
    this.stopTrustTokenRefresh();
    this._flushAcks(); // Send any pending ACKs before stopping
    this._stopPing();
    this._stopWakeDetector();
    this._stopPendingPoll();
    this._stopPollFallback();
    this._stopHttpServer();
    if (this._ackTimer) {
      clearTimeout(this._ackTimer);
      this._ackTimer = null;
    }
    if (this._pollTimer) {
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
    }
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._clearSuspendedWait();
    // Flush and clean up telemetry reporter before closing WebSocket
    if (this._telemetryReporter) {
      this._telemetryReporter.stopAutoFlush();
      await this._telemetryReporter.flush();
      this._telemetryReporter = null;
    }
    // removeAllListeners() below means the "close" handler won't fire, so clear
    // interval timers here explicitly (otherwise they leak across stop()).
    this._stopA2aReconcileTimer();
    if (this._ws) {
      this._ws.removeAllListeners();
      this._ws.close();
      this._ws = null;
    }
    this._setState("disconnected");
  }

  // --- Local HTTP server for proactive sends ---

  startHttpServer(port: number): void {
    if (this._httpServer) return;

    // Lazy import to avoid circular dependency at module load time
    let _handlers: typeof import("./http-handlers.js") | null = null;
    const getHandlers = async () => {
      if (!_handlers) _handlers = await import("./http-handlers.js");
      return _handlers;
    };

    this._httpServer = createServer(async (req: IncomingMessage, res: ServerResponse) => {
      // Only accept local connections
      const remote = req.socket.remoteAddress;
      if (remote !== "127.0.0.1" && remote !== "::1" && remote !== "::ffff:127.0.0.1") {
        res.writeHead(403, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ ok: false, error: "Forbidden" }));
        return;
      }

      const handlers = await getHandlers();

      if (req.method === "POST" && req.url === "/send") {
        let body = "";
        req.on("data", (chunk: Buffer) => { body += chunk.toString(); });
        req.on("end", async () => {
          try {
            const parsed = JSON.parse(body);
            const result = await handlers.handleSendRequest(parsed, this);
            res.writeHead(result.status, { "Content-Type": "application/json" });
            res.end(JSON.stringify(result.body));
          } catch (err) {
            res.writeHead(500, { "Content-Type": "application/json" });
            res.end(JSON.stringify({ ok: false, error: String(err) }));
          }
        });
      } else if (req.method === "POST" && req.url === "/decision") {
        let body = "";
        req.on("data", (chunk: Buffer) => { body += chunk.toString(); });
        req.on("end", async () => {
          try {
            const parsed = JSON.parse(body);
            const result = await handlers.handleDecisionRequest(parsed, this);
            res.writeHead(result.status, { "Content-Type": "application/json" });
            res.end(JSON.stringify(result.body));
          } catch (err) {
            res.writeHead(500, { "Content-Type": "application/json" });
            res.end(JSON.stringify({ ok: false, error: String(err) }));
          }
        });
      } else if (req.method === "POST" && req.url === "/action") {
        let body = "";
        req.on("data", (chunk: Buffer) => { body += chunk.toString(); });
        req.on("end", async () => {
          try {
            const parsed = JSON.parse(body);
            const result = await handlers.handleActionRequest(parsed, this);
            res.writeHead(result.status, { "Content-Type": "application/json" });
            res.end(JSON.stringify(result.body));
          } catch (err) {
            res.writeHead(500, { "Content-Type": "application/json" });
            res.end(JSON.stringify({ ok: false, error: String(err) }));
          }
        });
      } else if (req.method === "GET" && req.url === "/status") {
        const result = handlers.handleStatusRequest(this);
        res.writeHead(result.status, { "Content-Type": "application/json" });
        res.end(JSON.stringify(result.body));
      } else if (req.method === "GET" && req.url === "/targets") {
        const result = handlers.handleTargetsRequest(this);
        res.writeHead(result.status, { "Content-Type": "application/json" });
        res.end(JSON.stringify(result.body));
      } else if (req.url === "/mcp" && (req.method === "POST" || req.method === "GET" || req.method === "DELETE")) {
        // MCP Streamable HTTP endpoint — delegate to attached MCP server
        if (!this._mcpServer) {
          res.writeHead(503, { "Content-Type": "application/json" });
          res.end(JSON.stringify({ ok: false, error: "MCP server not initialized" }));
          return;
        }
        this._mcpServer.handleRequest(req, res).catch((err: unknown) => {
          console.error("[MCP] Request handling error:", err);
          if (!res.headersSent) {
            res.writeHead(500, { "Content-Type": "application/json" });
            res.end(JSON.stringify({ ok: false, error: "Internal MCP error" }));
          }
        });
      } else if (req.method === "GET" && req.url === "/trust") {
        const result = handlers.handleTrustRequest(this);
        res.writeHead(result.status, { "Content-Type": "application/json" });
        res.end(JSON.stringify(result.body));
      } else if (req.method === "GET" && req.url === "/mcp-config") {
        const agentName = this.config.agentName ?? "agent";
        const mcpSkillCount = this._mcpServer?.skillCount ?? 0;
        const result = handlers.handleMcpConfigRequest(agentName, port, mcpSkillCount);
        res.writeHead(result.status, { "Content-Type": "application/json" });
        res.end(JSON.stringify(result.body));
      } else {
        res.writeHead(404, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ ok: false, error: "Not found. Use POST /send, POST /decision, POST /action, GET /status, GET /targets, or /mcp" }));
      }
    });

    // A bind failure (e.g. EADDRINUSE) must not crash the agent process: an
    // 'error' on a server with no listener is thrown. Log it and allow a
    // later startHttpServer() to retry.
    const server = this._httpServer;
    server.on("error", (err: Error) => {
      console.error(`[SecureChannel] local HTTP server on 127.0.0.1:${port} failed: ${err.message}`);
      if (this._httpServer === server) this._httpServer = null;
    });
    server.listen(port, "127.0.0.1", () => {
      this.emit("http-ready", port);
    });
  }

  private _stopHttpServer(): void {
    if (this._httpServer) {
      this._httpServer.close();
      this._httpServer = null;
    }
  }

  /**
   * Attach an MCP server instance to this channel.
   * The MCP server will be served at /mcp on the local HTTP server.
   */
  setMcpServer(mcpServer: import("./mcp-server.js").AgentVaultMcpServer): void {
    this._mcpServer = mcpServer;
  }

  /** The attached MCP server, if any. */
  get mcpServer(): import("./mcp-server.js").AgentVaultMcpServer | null {
    return this._mcpServer;
  }

  // --- Topic management ---

  /*
   * #1416 — topics are per conversation GROUP.
   *
   * Since #1399 every person has their own 1:1 group with the agent, and a
   * topic belongs to exactly one group (`topics.group_id`). The agent used to
   * keep ONE default topic (the primary group's) and ONE last-incoming topic,
   * and stamped both on every frame — so a reply into person B's group could
   * carry person A's topic, and B's web client (which filters live messages by
   * the topic it is viewing) dropped it until a reload.
   *
   * The rule now: a frame's topic comes from THAT frame's group. A group with
   * no known topic gets none — the server then files the message under that
   * conversation's own default topic (`ws.py`, lazily created) — rather than
   * borrowing another group's.
   */

  /** Scope key for per-group topic state; see `_lastIncomingTopics`. */
  private _topicScope(gid: string | undefined, convId: string | undefined): string | undefined {
    if (gid) return `g:${gid}`;
    // A legacy agent that knows no groups at all is one group by definition —
    // keep its single shared slot so it behaves exactly as before.
    if (!this._persisted?.groupId && this._sessionGroupIds.size === 0) return "*";
    return convId ? `c:${convId}` : undefined;
  }

  /** Record the topic of an inbound 1:1 message for its own group (#1416). */
  private _rememberIncomingTopic(
    convId: string | undefined,
    gid: string | undefined,
    topicId: string | undefined,
  ): void {
    const scope = this._topicScope(gid ?? (convId ? this._sessionGroupIds.get(convId) : undefined), convId);
    if (!scope) return;
    // A topic that is not a server UUID is ignored outright — it could never
    // be stored, and must not steer later frames.
    if (topicId && !isTopicId(topicId)) return;
    // Mirrors the old single slot: a message without a topic clears it, so the
    // next reply falls back to the group's default rather than a stale topic.
    if (topicId) this._lastIncomingTopics.set(scope, topicId);
    else this._lastIncomingTopics.delete(scope);
  }

  /**
   * Record a group's default topic (#1416). The legacy `defaultTopicId` keeps
   * mirroring the PRIMARY group's entry, for older readers and backups.
   */
  private _recordDefaultTopic(gid: string | undefined, topicId: string | undefined): void {
    if (!gid || !topicId || !this._persisted) return;
    (this._persisted.defaultTopicIds ??= {})[gid] = topicId;
    if (gid === this._persisted.groupId) this._persisted.defaultTopicId = topicId;
  }

  /** The group a topic is known to belong to, if any. */
  private _groupOfTopic(topicId: string): string | undefined {
    const p = this._persisted;
    for (const [gid, tid] of Object.entries(p?.defaultTopicIds ?? {})) {
      if (tid === topicId) return gid;
    }
    if (p?.defaultTopicId === topicId && p.groupId) return p.groupId;
    for (const t of p?.topics ?? []) {
      if (t.id === topicId) return t.groupId ?? p?.groupId;
    }
    for (const [scope, tid] of this._lastIncomingTopics) {
      if (tid === topicId && scope.startsWith("g:")) return scope.slice(2);
    }
    return undefined;
  }

  /**
   * The topic one outgoing 1:1 frame carries (#1416), or `undefined` to send
   * none. `gid` is the frame's conversation group (looked up from `convId`
   * when not given); `explicit` is the caller's `topicId`; `targeted` is true
   * when the caller addressed one conversation.
   */
  private _topicForFrame(
    gid: string | undefined,
    convId: string | undefined,
    explicit: string | undefined,
    targeted: boolean,
    useLastIncoming = true,
  ): string | undefined {
    const p = this._persisted;
    const g = gid ?? (convId ? this._sessionGroupIds.get(convId) : undefined);
    // The frame belongs to the primary group (or, with no group known, to the
    // primary conversation / a legacy agent that knows no groups).
    const primaryScope = g !== undefined
      ? g === p?.groupId
      : convId === this._primaryConversationId || !p?.groupId;

    if (isTopicId(explicit)) {
      const owner = this._groupOfTopic(explicit);
      // Known owner: honour it only in its own group. Unknown owner: honour it
      // where it was aimed — the targeted conversation, or the primary group
      // of a broadcast (where a lone `defaultTopicId` always pointed).
      if (owner !== undefined ? owner === g : targeted || primaryScope) return explicit;
    }

    const scope = this._topicScope(g, convId);
    const last = scope && useLastIncoming ? this._lastIncomingTopics.get(scope) : undefined;
    if (last) return last;
    if (g && p?.defaultTopicIds?.[g]) return p.defaultTopicIds[g];
    if (primaryScope && p?.defaultTopicId) return p.defaultTopicId;
    return undefined;
  }

  /**
   * The conversation group a topic call addresses (#1416): the named
   * conversation's own group, else the primary group. A named conversation
   * whose group is unknown is an error — falling back to the primary group
   * would create/list topics in somebody else's chat.
   */
  private _topicGroupFor(conversationId: string | undefined): string {
    if (conversationId) {
      const gid = this._sessionGroupIds.get(conversationId);
      if (!gid) throw new Error("Conversation group unknown for topic call");
      return gid;
    }
    if (!this._persisted?.groupId) {
      throw new Error("Channel not initialized or groupId unknown");
    }
    return this._persisted.groupId;
  }

  /**
   * Create a new topic within a conversation group — the given conversation's
   * own group (#1416), or the primary group when none is named.
   * Requires the channel to be initialized with a groupId (from activation).
   */
  async createTopic(
    name: string,
    options?: { conversationId?: string },
  ): Promise<{ id: string; name: string; isDefault: boolean }> {
    const groupId = this._topicGroupFor(options?.conversationId);
    if (!this._deviceJwt || !this._persisted) {
      throw new Error("Channel not authenticated");
    }

    const res = await fetch(`${this.config.apiUrl}/api/v1/topics`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this._deviceJwt}`,
      },
      body: JSON.stringify({
        group_id: groupId,
        name,
        creator_device_id: this._persisted.deviceId,
      }),
    });

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`Create topic failed (${res.status}): ${detail}`);
    }

    const resp = await res.json();
    const topic = { id: resp.id, name: resp.name, isDefault: resp.is_default };

    if (!this._persisted.topics) {
      this._persisted.topics = [];
    }
    this._persisted.topics.push({ ...topic, groupId });
    if (topic.isDefault) this._recordDefaultTopic(groupId, topic.id);
    await this._persistState();

    return topic;
  }

  /**
   * List all topics in a conversation group — the given conversation's own
   * group (#1416), or the primary group when none is named.
   * Requires the channel to be initialized with a groupId (from activation).
   */
  async listTopics(
    options?: { conversationId?: string },
  ): Promise<Array<{ id: string; name: string; isDefault: boolean }>> {
    const groupId = this._topicGroupFor(options?.conversationId);
    if (!this._deviceJwt || !this._persisted) {
      throw new Error("Channel not authenticated");
    }

    const res = await fetch(
      `${this.config.apiUrl}/api/v1/topics?group_id=${encodeURIComponent(groupId)}`,
      {
        headers: {
          Authorization: `Bearer ${this._deviceJwt}`,
        },
      },
    );

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`List topics failed (${res.status}): ${detail}`);
    }

    const resp: any[] = await res.json();
    const topics = resp.map((t) => ({ id: t.id, name: t.name, isDefault: t.is_default }));

    // Replace only THIS group's cached topics; other groups' stay. Entries
    // without a groupId predate #1416 and belong to the primary group.
    const primaryGid = this._persisted.groupId;
    this._persisted.topics = [
      ...(this._persisted.topics ?? []).filter((t) => (t.groupId ?? primaryGid) !== groupId),
      ...topics.map((t) => ({ ...t, groupId })),
    ];
    const def = topics.find((t) => t.isDefault);
    if (def) this._recordDefaultTopic(groupId, def.id);
    await this._persistState();

    return topics;
  }

  // --- A2A Channel methods (agent-to-agent communication) ---

  /**
   * Request a new A2A channel with another agent by their hub address.
   * Returns the channel_id from the server response.
   */
  async requestA2AChannel(responderHubAddress: string): Promise<string> {
    if (!this._persisted?.hubAddress) {
      throw new Error("This agent does not have a hub address assigned");
    }
    // #499 A2 (deterministic backstop): never open an A2A channel to our OWN
    // hub address. When the owner addresses the agent by its own name, the
    // model can try to "find and channel" itself → a stuck dead room. Refuse at
    // the creation chokepoint so no self-A2A channel can ever be created; the
    // error surfaces to the model, which then replies to the owner directly.
    if (_isSelfHubAddress(this._persisted.hubAddress, responderHubAddress)) {
      throw new Error(
        "Refusing to open an A2A channel to this agent's own hub address " +
        "(self-reference). The owner is addressing you by your own name — " +
        "reply to them directly instead of opening a channel to yourself.",
      );
    }
    if (!this._deviceJwt) {
      throw new Error("Channel not authenticated");
    }

    const res = await fetch(`${this.config.apiUrl}/api/v1/a2a/channels/request`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this._deviceJwt}`,
      },
      body: JSON.stringify({
        initiator_hub_address: this._persisted.hubAddress,
        responder_hub_address: responderHubAddress,
      }),
    });

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`A2A channel request failed (${res.status}): ${detail}`);
    }

    const resp = await res.json();
    const channelId = resp.channel_id || resp.id;

    // Store channel info in persisted state with participants array
    if (!this._persisted.a2aChannels) {
      this._persisted.a2aChannels = {};
    }
    this._persisted.a2aChannels[channelId] = {
      channelId,
      conversationId: resp.conversation_id || "",
      role: "creator",
      participants: [
        {
          hubId: "",
          hubAddress: responderHubAddress,
          status: "invited",
        },
      ],
    };
    await this._persistState();

    return channelId;
  }

  /**
   * Invite another agent to an existing A2A channel by their hub address.
   */
  async inviteToA2AChannel(channelId: string, hubAddress: string): Promise<void> {
    if (!this._deviceJwt) {
      throw new Error("Channel not authenticated");
    }

    const res = await fetch(`${this.config.apiUrl}/api/v1/a2a/channels/${channelId}/invite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this._deviceJwt}`,
      },
      body: JSON.stringify({
        hub_address: hubAddress,
      }),
    });

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`A2A channel invite failed (${res.status}): ${detail}`);
    }

    // Add participant to local state
    const entry = this._persisted?.a2aChannels?.[channelId];
    if (entry) {
      if (!entry.participants) entry.participants = [];
      // Avoid duplicates
      if (!entry.participants.some(p => p.hubAddress === hubAddress)) {
        entry.participants.push({ hubId: "", hubAddress, status: "invited" });
        await this._persistState();
      }
    }
  }

  /**
   * Send a message to another agent via an active A2A channel.
   * Looks up the A2A conversation by hub address and sends via WS.
   *
   * If the channel has an established E2E session, the message is encrypted
   * with the Double Ratchet. If the responder hasn't received the initiator's
   * first message yet (ratchet not activated), the message is queued locally
   * and flushed when the first inbound message arrives.
   *
   * Falls back to plaintext for channels without a session (legacy/pre-encryption).
   */
  async sendToAgent(hubAddress: string, text: string, opts?: { parentSpanId?: string }): Promise<void> {
    // #499 A2 (defense-in-depth): never send an A2A message to our own address.
    if (_isSelfHubAddress(this._persisted?.hubAddress ?? "", hubAddress)) {
      throw new Error(
        "Refusing to send an A2A message to this agent's own hub address " +
        "(self-reference) — reply to the owner directly.",
      );
    }
    if (!this._persisted?.a2aChannels) {
      // Try re-syncing before giving up
      try {
        await this.listA2AChannels();
      } catch { /* ignore */ }
      if (!this._persisted?.a2aChannels) {
        throw new Error("No A2A channels established");
      }
    }

    // Find the channel for this hub address by searching participants.
    const a2a = this._persisted.a2aChannels;
    let allMatches = Object.values(a2a).filter(
      (ch) => ch.participants?.some(p => p.hubAddress === hubAddress && (p.status === "active" || p.status === "invited")),
    );
    let channelEntry =
      allMatches.find((ch) => ch.conversationId) ?? allMatches[0];

    // On-demand re-sync from server if channel not found locally
    if (!channelEntry) {
      try {
        console.log(`[sendToAgent] Channel not found for ${hubAddress}, re-syncing from server...`);
        await this.listA2AChannels();
        allMatches = Object.values(this._persisted.a2aChannels ?? {}).filter(
          (ch) => ch.participants?.some(p => p.hubAddress === hubAddress && (p.status === "active" || p.status === "invited")),
        );
        channelEntry = allMatches.find((ch) => ch.conversationId) ?? allMatches[0];
      } catch (syncErr) {
        console.warn("[sendToAgent] A2A re-sync failed:", syncErr);
      }
    }

    if (!channelEntry) {
      throw new Error(`No A2A channel found for hub address: ${hubAddress}`);
    }
    if (!channelEntry.conversationId) {
      throw new Error(`A2A channel ${channelEntry.channelId} does not have a conversation yet (not activated)`);
    }

    if (this._state !== "ready" || !this._ws) {
      throw new Error("Channel is not connected");
    }

    // --- MLS path (preferred over Double Ratchet) ---
    if (channelEntry.mlsGroupId) {
      const mlsGroup = this._mlsGroups.get(`a2a:${channelEntry.channelId}`);
      if (mlsGroup?.isInitialized) {
        const plaintextBytes = new TextEncoder().encode(text);
        const ciphertext = await this._encryptWithCatchUp(
          mlsGroup, `a2a:${channelEntry.channelId}`, channelEntry.mlsGroupId,
          `A2A ${channelEntry.channelId.slice(0, 8)}`, plaintextBytes,
        );

        // Persist MLS state BEFORE sending
        await saveMlsState(
          this.config.dataDir,
          channelEntry.mlsGroupId,
          JSON.stringify(mlsGroup.exportState()),
        );

        const delivered = this._sendApplicationFrame(JSON.stringify({
          event: "a2a_message_mls",
          data: {
            channel_id: channelEntry.channelId,
            group_id: channelEntry.mlsGroupId,
            epoch: Number(mlsGroup.epoch),
            payload: Buffer.from(ciphertext).toString("hex"),
            hub_address: this._persisted!.hubAddress,
          },
        }));
        if (!delivered) throw new Error("A2A relay socket is not open");
        await this._appendHistory(
          "agent", text, this._historyContext("a2a", channelEntry.channelId), undefined, randomUUID(),
        );
        return;
      }
    }

    // No MLS group — channel not yet initialized
    throw new Error(
      `A2A channel ${channelEntry.channelId.slice(0, 8)}... has no MLS group. ` +
      `MLS initialization must complete before messages can be sent.`,
    );
  }

  /**
   * Send a message to a specific A2A channel by channel ID.
   * Used for multi-agent channels where hub address routing is ambiguous.
   */
  async sendToA2AChannel(channelId: string, text: string): Promise<void> {
    if (!this._persisted?.a2aChannels) {
      try { await this.listA2AChannels(); } catch { /* ignore */ }
    }

    const entry = this._persisted?.a2aChannels?.[channelId];
    if (!entry) {
      throw new Error(`A2A channel ${channelId.slice(0, 8)} not found in persisted state`);
    }
    if (!entry.conversationId) {
      throw new Error(`A2A channel ${channelId.slice(0, 8)} has no conversation (not activated)`);
    }
    if (this._state !== "ready" || !this._ws) {
      throw new Error("Channel is not connected");
    }

    if (entry.mlsGroupId) {
      const mlsGroup = this._mlsGroups.get(`a2a:${channelId}`);
      if (mlsGroup?.isInitialized) {
        const plaintextBytes = new TextEncoder().encode(text);
        const ciphertext = await this._encryptWithCatchUp(
          mlsGroup, `a2a:${channelId}`, entry.mlsGroupId,
          `A2A ${channelId.slice(0, 8)}`, plaintextBytes,
        );
        await saveMlsState(this.config.dataDir, entry.mlsGroupId, JSON.stringify(mlsGroup.exportState()));
        const delivered = this._sendApplicationFrame(JSON.stringify({
          event: "a2a_message_mls",
          data: {
            channel_id: channelId,
            group_id: entry.mlsGroupId,
            epoch: Number(mlsGroup.epoch),
            payload: Buffer.from(ciphertext).toString("hex"),
            hub_address: this._persisted!.hubAddress,
          },
        }));
        if (!delivered) throw new Error("A2A relay socket is not open");
        await this._appendHistory("agent", text, this._historyContext("a2a", channelId), undefined, randomUUID());
        return;
      }
    }

    throw new Error(`A2A channel ${channelId.slice(0, 8)} has no initialized MLS group`);
  }

  /**
   * Start the periodic A2A reconcile timer (30s), mirroring `_deliveryHeartbeat`.
   *
   * A2A join-triggers (`a2a_channel_approved` etc.) are delivered best-effort by
   * the backend's local-only `send_to_device`; if this agent is briefly
   * disconnected when a channel is created, the push is silently dropped and the
   * channel hangs until the next connect. Re-running `listA2AChannels()` on a
   * timer re-fires the idempotent member sync-fallback (publish KeyPackage +
   * request Welcome) so the channel self-heals while staying connected.
   */
  private _startA2aReconcileTimer(): void {
    if (this._a2aReconcileTimer) clearInterval(this._a2aReconcileTimer);
    this._a2aReconcileTimer = setInterval(() => {
      this.listA2AChannels().catch(() => {});
    }, 30_000);
  }

  /** Stop the periodic A2A reconcile timer (idempotent). */
  private _stopA2aReconcileTimer(): void {
    if (this._a2aReconcileTimer) {
      clearInterval(this._a2aReconcileTimer);
      this._a2aReconcileTimer = null;
    }
  }

  /**
   * List all A2A channels for this agent.
   * Fetches from the server and updates local persisted state.
   */
  async listA2AChannels(): Promise<A2AChannel[]> {
    if (!this._deviceJwt) {
      throw new Error("Channel not authenticated");
    }

    // Uses authedFetch so a 401 (e.g. JWT expired since last connect) triggers
    // exactly one reissue + retry per WS session. See _authedFetch for the
    // guard semantics.
    const res = await this._authedFetch(`${this.config.apiUrl}/api/v1/a2a/channels`);

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`List A2A channels failed (${res.status}): ${detail}`);
    }

    const resp: any[] = await res.json();
    const channels: A2AChannel[] = resp.map((ch) => ({
      channelId: ch.channel_id || ch.id,
      creator_hub_id: ch.creator_hub_id || "",
      conversationId: ch.conversation_id,
      status: ch.status,
      createdAt: ch.created_at,
      max_participants: ch.max_participants ?? 10,
      participant_count: ch.participant_count ?? (ch.participants?.length ?? 0),
      participants: (ch.participants ?? []).map((p: any) => ({
        id: p.id,
        hub_id: p.hub_id,
        hub_address: p.hub_address,
        agent_name: p.agent_name,
        tenant_id: p.tenant_id,
        device_id: p.device_id,
        role: p.role,
        status: p.status,
        observer_enabled: p.observer_enabled ?? false,
        joined_at: p.joined_at,
        left_at: p.left_at,
      })),
      topic: ch.topic,
    }));

    // Update persisted a2aChannels map with latest server data
    if (this._persisted) {
      if (!this._persisted.a2aChannels) {
        this._persisted.a2aChannels = {};
      }
      const myHub = this._persisted.hubAddress || "";
      const myHubId = this._persisted.hubId || "";
      if (this._a2aLogGate.shouldReport(channels)) {
        console.log(`[listA2AChannels] myHub=${myHub}, channels=${channels.length}, raw=${JSON.stringify(channels.map(c => ({ id: c.channelId?.slice(0,8), status: c.status, participants: c.participant_count })))}`);
      }
      for (const ch of channels) {
        if (ch.status === "active" || ch.status === "approved") {
          // Determine our role from participants list
          const myParticipant = ch.participants?.find(
            p => p.hub_id === myHubId || p.hub_address === myHub,
          );
          const role: "creator" | "member" = myParticipant?.role === "creator" ? "creator" : "member";

          // Build local participants array (peers only — exclude self)
          // Carries device_id through for the same reason as the
          // a2a_channel_approved handler — sync-fallback may end up
          // invoking _foundA2AGroup, which needs deviceId for Welcome
          // dispatch (backend ws.py mls_welcome reads target_device_id).
          const localParticipants = (ch.participants ?? [])
            .filter(p => p.hub_id !== myHubId && p.hub_address !== myHub)
            .map(p => ({
              hubId: p.hub_id || "",
              hubAddress: p.hub_address || "",
              status: p.status as "invited" | "active" | "left" | "removed",
              deviceId: p.device_id || null,
            }));

          const existing = this._persisted.a2aChannels[ch.channelId];
          if (existing) {
            // Merge: update participants/conversation but preserve MLS state
            existing.participants = localParticipants;
            existing.conversationId = ch.conversationId || existing.conversationId;
            if (!existing.role) {
              existing.role = role;
            }
          } else {
            // New channel discovered from server
            this._persisted.a2aChannels[ch.channelId] = {
              channelId: ch.channelId,
              conversationId: ch.conversationId || "",
              role,
              participants: localParticipants,
            };
            console.log(
              `[SecureChannel] Discovered A2A channel ${ch.channelId.slice(0, 8)}... role=${role} peers=${localParticipants.length}`,
            );
          }
        }
      }
      // Remove revoked channels from persisted state
      const activeIds = new Set(
        channels.filter((c) => c.status === "active" || c.status === "approved").map((c) => c.channelId),
      );
      for (const id of Object.keys(this._persisted.a2aChannels)) {
        if (!activeIds.has(id)) {
          delete this._persisted.a2aChannels[id];
        }
      }
      await this._persistState();

      // Sync fallback: if this agent is the creator of an active channel
      // but hasn't founded the MLS group yet, do it now.
      // This handles the case where the creator was offline when a2a_channel_approved was sent.
      for (const [chId, chState] of Object.entries(this._persisted?.a2aChannels ?? {})) {
        if (
          chState.role === "creator" &&
          !chState.mlsGroupId &&
          chState.participants.some(p => p.status === "active")
        ) {
          const activeParticipants = chState.participants.filter(p => p.status === "active");
          if (activeParticipants.length >= 1) {
            console.log(`[AgentVault] Sync fallback: founding MLS group for channel ${chId.slice(0, 8)}`);
            try {
              await this._foundA2AGroup(chId, chState.conversationId, activeParticipants);
            } catch (err) {
              console.warn(`[AgentVault] Sync fallback MLS founding failed for ${chId.slice(0, 8)}:`, err);
            }
          }
        }

        // Sync fallback for members: if we're a member without a usable MLS group,
        // look up the group, publish a KeyPackage, and request a Welcome.
        // This handles: (a) member missed a2a_channel_approved WS event,
        // (b) mlsGroupId was stored but Welcome was never processed (e.g. distribute 422).
        const memberMlsGroup = chState.mlsGroupId ? this._mlsGroups.get(`a2a:${chId}`) : undefined;
        if (
          chState.role === "member" &&
          !memberMlsGroup?.isInitialized
        ) {
          // Acquire channel-level lock — skip if another instance is handling this channel
          if (!await acquireA2ASyncLock(this.config.dataDir, chId)) {
            console.log(`[SecureChannel] A2A ${chId.slice(0, 8)} sync: skipping — another instance holds the lock`);
            continue;
          }
          try {
            // Look up the MLS group for this channel
            let groupId = chState.mlsGroupId;
            let memberIds: string[] = [];
            if (!groupId) {
              const groupRes = await fetch(
                `${this.config.apiUrl}/api/v1/mls/a2a/${chId}/group`,
                { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
              );
              if (!groupRes.ok) {
                // Group not yet created by creator — nothing to join yet
                await releaseA2ASyncLock(this.config.dataDir, chId);
                continue;
              }
              const groupData = await groupRes.json();
              groupId = groupData.group_id;
              if (!groupId) {
                await releaseA2ASyncLock(this.config.dataDir, chId);
                continue;
              }
              memberIds = groupData.member_device_ids || [];

              // Store the groupId so delivery pull can restore MLS state later
              chState.mlsGroupId = groupId;
              await this._persistState();
            } else {
              // Already have groupId — fetch current membership to check
              try {
                const groupRes = await fetch(
                  `${this.config.apiUrl}/api/v1/mls/groups/${groupId}`,
                  { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
                );
                if (groupRes.ok) {
                  const groupData = await groupRes.json();
                  memberIds = groupData.member_device_ids || [];
                }
              } catch { /* proceed with empty memberIds */ }
            }

            // Check if we're already in the MLS group on the server
            if (memberIds.includes(this._persisted!.deviceId)) {
              console.log(`[SecureChannel] A2A ${chId.slice(0, 8)} member in server membership but no local MLS state — requesting Welcome`);
              // Fall through to request Welcome (we lost local state)
            }

            // Defense-in-depth: check KP-level lock (may exist from prior partial run)
            if (await hasPendingWelcome(this.config.dataDir, groupId!)) {
              console.log(`[SecureChannel] A2A ${chId.slice(0, 8)} sync fallback: skipping — Welcome already pending for group=${groupId!.slice(0, 8)}`);
              // Do NOT release channel lock — pending Welcome still needs to resolve
              continue;
            }

            // Publish a fresh KeyPackage and persist to disk (shared across agent instances)
            const mgr = new MLSGroupManager();
            const identity = new TextEncoder().encode(this._persisted!.deviceId);
            const kp = await mgr.generateKeyPackage(identity);
            this._recordPublishedKp(kp);
            await savePendingKpBundle(this.config.dataDir, groupId!, kp, MLSGroupManager.serializeKeyPackage);
            const kpBytes = MLSGroupManager.serializeKeyPackage(kp.publicPackage);
            await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${this._deviceJwt}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                key_package: Buffer.from(kpBytes).toString("hex"),
                device_id: this._persisted!.deviceId,
              }),
            });

            // Request Welcome from the creator
            await fetch(`${this.config.apiUrl}/api/v1/mls/groups/${groupId}/request-welcome`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${this._deviceJwt}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ device_id: this._persisted!.deviceId }),
            });
            console.log(
              `[SecureChannel] A2A ${chId.slice(0, 8)} member sync fallback: published KeyPackage + requested Welcome (group=${groupId!.slice(0, 8)})`,
            );
            // Lock is NOT released here — held until Welcome is processed in _handleMlsWelcome
          } catch (err) {
            // Release lock on error so next sync cycle can retry
            await releaseA2ASyncLock(this.config.dataDir, chId).catch(() => {});
            console.warn(`[AgentVault] Sync fallback member MLS join failed for ${chId.slice(0, 8)}:`, err);
          }
        }
      }
    }

    return channels;
  }

  // --- Internal lifecycle ---

  private async _enroll(): Promise<void> {
    this._setState("enrolling");

    try {
      const identity = await generateIdentityKeypair();
      const ephemeral = await generateEphemeralKeypair();
      const fingerprint = computeFingerprint(identity.publicKey);
      const proof = createProofOfPossession(
        identity.privateKey,
        identity.publicKey,
      );

      const result = await enrollDevice(
        this.config.apiUrl,
        this.config.inviteToken,
        bytesToHex(identity.publicKey),
        bytesToHex(ephemeral.publicKey),
        bytesToHex(proof),
        this.config.platform,
        this.config.expectedFramework,
      );

      // The owner matches the terminal's fingerprint against the app's before
      // approving. That check only means something if the terminal value comes
      // from OUR key: echoing the server's value would let a server that swapped
      // the key show matching codes on both screens. So we show the locally
      // computed fingerprint and refuse to continue if the server disagrees.
      if (result.fingerprint && result.fingerprint !== fingerprint) {
        throw new Error(
          `Enrollment fingerprint mismatch: this agent's key is ${fingerprint} but the server reported ${result.fingerprint}. Not continuing — do not approve this device.`,
        );
      }
      this._deviceId = result.device_id;
      this._fingerprint = fingerprint;

      // Store keypairs temporarily for activation (not persisted yet -- no JWT)
      this._persisted = {
        deviceId: result.device_id,
        deviceJwt: "", // set after activation
        primaryConversationId: "", // set after activation
        sessions: {}, // populated after activation
        identityKeypair: {
          publicKey: bytesToHex(identity.publicKey),
          privateKey: bytesToHex(identity.privateKey),
        },
        ephemeralKeypair: {
          publicKey: bytesToHex(ephemeral.publicKey),
          privateKey: bytesToHex(ephemeral.privateKey),
        },
        fingerprint,
        messageHistory: [],
      };

      this._poll();
    } catch (err) {
      this._handleError(err as Error);
    }
  }

  private _poll(): void {
    if (this._stopped) return;
    this._setState("polling");

    const doPoll = async () => {
      if (this._stopped) return;
      try {
        const status = await pollDeviceStatus(
          this.config.apiUrl,
          this._deviceId!,
        );

        if ((status as any).rateLimited) {
          // Server rate-limited this poll — back off 2x before retrying
          this._pollTimer = setTimeout(doPoll, POLL_INTERVAL_MS * 2);
          return;
        }

        if (status.status === "APPROVED") {
          await this._activate();
          return;
        }

        if (status.status === "REVOKED") {
          this._handleError(new Error("Device was revoked"));
          return;
        }

        // Still PENDING -- poll again
        this._pollTimer = setTimeout(doPoll, POLL_INTERVAL_MS);
      } catch (err) {
        this._handleError(err as Error);
      }
    };

    this._pollTimer = setTimeout(doPoll, POLL_INTERVAL_MS);
  }

  private async _activate(): Promise<void> {
    this._setState("activating");

    try {
      // #1331: sign with the identity key this device ENROLLED with. It is
      // only ever in memory here (set by _enroll in this same process): no
      // pre-activation state is persisted, so a restart before activation
      // re-enrols with a fresh key instead of resuming this device.
      const result = await activateDevice(
        this.config.apiUrl,
        this._deviceId!,
        hexToBytes(this._persisted!.identityKeypair.privateKey),
      );

      // Support both old (single conversation_id) and new (conversations array) response
      const conversations: Array<{
        conversation_id: string;
        owner_device_id: string;
        is_primary: boolean;
        owner_identity_public_key?: string;
        owner_ephemeral_public_key?: string;
        group_id?: string;
        default_topic_id?: string;
      }> = (result as any).conversations || [
        {
          conversation_id: result.conversation_id,
          owner_device_id: "",
          is_primary: true,
        },
      ];

      const primary =
        conversations.find((c) => c.is_primary) || conversations[0];
      this._primaryConversationId = primary.conversation_id;
      this._deviceJwt = result.device_jwt;

      const identity = this._persisted!.identityKeypair;
      const ephemeral = this._persisted!.ephemeralKeypair;
      const sessions: Record<
        string,
        { ownerDeviceId: string; ratchetState: ReturnType<DoubleRatchet["serialize"]>; epoch?: number }
      > = {};

      // Initialize X3DH + ratchet for EVERY conversation (not just primary)
      for (const conv of conversations) {
        // Use per-conversation owner keys if available, fallback to primary keys
        const ownerIdentityKey =
          conv.owner_identity_public_key || result.owner_identity_public_key;
        const ownerEphemeralKey =
          conv.owner_ephemeral_public_key ||
          result.owner_ephemeral_public_key ||
          ownerIdentityKey;

        const sharedSecret = performX3DH({
          myIdentityPrivate: hexToBytes(identity.privateKey),
          myEphemeralPrivate: hexToBytes(ephemeral.privateKey),
          theirIdentityPublic: hexToBytes(ownerIdentityKey),
          theirEphemeralPublic: hexToBytes(ownerEphemeralKey),
          isInitiator: false,
        });

        const ratchet = DoubleRatchet.initReceiver(sharedSecret, {
          publicKey: hexToBytes(identity.publicKey),
          privateKey: hexToBytes(identity.privateKey),
          keyType: "ed25519",
        }, hexToBytes(ownerIdentityKey));

        this._sessions.set(conv.conversation_id, {
          ownerDeviceId: conv.owner_device_id,
          ratchet,
          activated: false, // Wait for owner's first message before sending to this session
          epoch: 1,
        });

        sessions[conv.conversation_id] = {
          ownerDeviceId: conv.owner_device_id,
          ratchetState: ratchet.serialize(),
          epoch: 1,
        };

        console.log(
          `[SecureChannel] Session initialized for conv ${conv.conversation_id.slice(0, 8)}... (owner ${conv.owner_device_id.slice(0, 8)}..., primary=${conv.is_primary})`,
        );
      }

      // Persist full state (preserve existing messageHistory)
      this._persisted = {
        ...this._persisted!,
        deviceJwt: result.device_jwt,
        primaryConversationId: primary.conversation_id,
        sessions,
        messageHistory: this._persisted!.messageHistory ?? [],
      };

      // Store group_id and default_topic_id for topic API calls.
      // Read these off `primary`, not `conversations[0]` (#719): the primary
      // conversation is already resolved above via `is_primary`, and it is not
      // necessarily the first element. While every conversation shares one
      // group this is invisible; the moment they don't, the agent's primary
      // group becomes whichever row the server happened to list first.
      // `primary` falls back to `conversations[0]`, so this is a strict
      // improvement, never a behaviour change when no row is flagged primary.
      if (primary) {
        if (primary.group_id) {
          this._persisted.groupId = primary.group_id;
        }
        if (primary.default_topic_id) {
          this._persisted.defaultTopicId = primary.default_topic_id;
        }
      }
      // #1416: and EVERY entry's own default topic, keyed by its own group —
      // since #1415 each person's group carries its own `default_topic_id`.
      for (const conv of conversations) {
        this._recordDefaultTopic(conv.group_id, conv.default_topic_id);
      }

      // Build conversation -> group_id map for shared MLS groups (+ persist for reconnect)
      this._sessionGroupIds = new Map<string, string>();
      for (const conv of conversations) {
        if (conv.group_id) {
          this._sessionGroupIds.set(conv.conversation_id, conv.group_id);
        }
      }
      this._persisted.conversationGroupIds = Object.fromEntries(this._sessionGroupIds);

      await this._queueStateSave(this._persisted);

      // Register webhook if configured
      if (this.config.webhookUrl) {
        try {
          const webhookResp = await fetch(
            `${this.config.apiUrl}/api/v1/devices/self/webhook`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${this._deviceJwt}`,
              },
              body: JSON.stringify({ webhook_url: this.config.webhookUrl }),
            }
          );
          if (webhookResp.ok) {
            const webhookData = await webhookResp.json();
            console.log(
              `[SecureChannel] Webhook registered: ${this.config.webhookUrl} (secret: ${webhookData.webhook_secret?.slice(0, 8)}...)`,
            );
            this.emit("webhook_registered", {
              url: this.config.webhookUrl,
              secret: webhookData.webhook_secret,
            });
          } else {
            console.warn(`[SecureChannel] Webhook registration failed: ${webhookResp.status}`);
          }
        } catch (err) {
          console.warn(`[SecureChannel] Webhook registration error: ${err}`);
        }
      }

      await this._initializeHistoryRecovery();
      this._connect();
    } catch (err) {
      this._handleError(err as Error);
    }
  }

  private async _connect(): Promise<void> {
    if (this._stopped) return;

    // Each WS session gets a fresh reactive-reissue attempt. We reset here so
    // a reconnect after a terminal reissue failure can still try once on the
    // new session (in case the server-side credential state changed).
    this._reissueAttempted = false;
    this._inactive403Check = null;
    this._inactive403RetryAt = 0;
    // Same reset for the auth-failure flag used by the rapid-disconnect
    // classifier — a fresh connect attempt clears prior auth-failed signal.
    this._authFailedThisSession = false;

    // Clear reconnect timer — we're connecting now, so any pending reconnect is superseded.
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }

    // Clean up existing WebSocket to prevent reconnect cascade:
    // The server disconnects old WS when same device reconnects, which fires
    // the old close handler → _scheduleReconnect() → new _connect() → cascade.
    if (this._ws) {
      this._ws.removeAllListeners();
      try { this._ws.close(); } catch { /* already closed */ }
      this._ws = null;
    }

    this._setState("connecting");

    // Proactively reissue the device JWT if it's within 30 days of expiry.
    // Runs before the WS URL is built so the reissued token (if any) is used
    // for this connect.
    //
    // #743: a terminal verdict here (401/403 — device revoked or hard-deleted)
    // ABORTS the connect. Previously this return value was discarded and we
    // opened a WS with the credential we had just been told was dead; the
    // server denylisted it every time, once per supervisor relaunch, forever.
    // `auth_failed` has already been emitted + reported by this point, so the
    // consumer's terminal handling (bridge: clean exit(0); plugin lifecycle:
    // onTerminal) is what decides whether the process stops — we only make sure
    // we do not hammer the backend on the way there. No reconnect is scheduled:
    // retrying cannot change a terminal verdict.
    if (await this._maybeReissueDeviceJwt()) {
      // Paused by its owner (confirmed 403 + SUSPENDED): the suspended-wait
      // poll owns the reconnect. Stay "disconnected", never "error" — the
      // bridges stop on "error", and a pause must not stop anything.
      if (this._suspended) {
        console.log("[SecureChannel] device is paused — not connecting until it is resumed");
        return;
      }
      console.warn(
        "[SecureChannel] credentials are terminally dead — aborting connect (no WS attempt)",
      );
      // "error", NOT "disconnected": _setState starts the 30s HTTP poll
      // fallback on "disconnected" whenever a device JWT is present, which for
      // a dead credential polls forever — 120 req/h, an order of magnitude MORE
      // backend traffic than the once-per-relaunch WS attempt this removes.
      // "error" is the state that stops the fallback (see _setState).
      this._setState("error");
      return;
    }

    const wsUrl = this.config.apiUrl.replace(/^http/, "ws");
    // #836: report the build we are actually RUNNING. Agent clients never sent
    // this — 0 of 14 agent devices had ever reported a version — so "which build
    // is this agent on?" had no remote answer and #826 needed shell access to
    // the host to resolve. Diagnostic only; nothing branches on it.
    const url =
      `${wsUrl}/api/v1/ws?token=${encodeURIComponent(this._deviceJwt!)}` +
      `&device_id=${this._deviceId}` +
      `&client_version=${encodeURIComponent(buildClientVersion(this.config.clientVersion))}`;

    const ws = new WebSocket(url);
    this._ws = ws;
    const served = this._createServedGate();

    ws.on("open", async () => {
      try {
        this._lastWsOpenTime = Date.now();
        this._startPing(ws);
        this._startWakeDetector();

        // 2026-10-25: "open" is NOT "served". The backend's reject_connection
        // ACCEPTS the socket (so `open` fires) and only then sends
        // `connection_rejected` and closes. The REST work below used to start
        // immediately, so a paused device's rejected reconnect still ran
        // DR-pull -> 401 -> reissue -> 403 -> "device_revoked" and uninstalled
        // its own service. Wait until the server has either sent a real frame
        // or let the settle window pass without rejecting us.
        if (!(await served.wait())) {
          console.log("[SecureChannel] connection rejected before it was served — skipping connect-time work");
          return;
        }
        const aborted = (): boolean =>
          this._stopped || this._suspended || this._ws !== ws || served.rejected;
        if (aborted()) return;
        // Only a SERVED session resets the backoff — a rejected one is not a
        // successful connect.
        this._reconnectAttempt = 0;
        this._startPendingPoll();

        // Sync missed messages before declaring ready
        await this._syncMissedMessages();
        if (aborted()) return;
        // Pull from DR delivery queue — catches messages missed while offline (Signal-model)
        await this._pullDrDeliveryQueue();
        if (aborted()) return;
        await this._flushOutboundQueue();
        if (aborted()) return;

        this._setState("ready");

        // #1034: re-resolve the conversation→group map on EVERY connect.
        //
        // `_reconcileConversationGroups` already existed and already knew how to
        // fix this — but it was reachable ONLY from `_handleServerError`, on a
        // server `unknown group` rejection. That requires the agent to SEND
        // something. An agent whose map points at a deleted group never
        // constructs an outbound at all, so it is never rejected, so the repair
        // never runs.
        //
        // 🎯 A REPAIR THAT ONLY RUNS ON THE SEND PATH CANNOT FIX AN AGENT THAT
        // HAS NOTHING TO SAY. Hermes learned this as #991 and fired its resync
        // from the DETECT site too; this is the same lesson, one package over.
        //
        // wren, measured 2026-08-24: `groupId` and all three
        // `conversationGroupIds` entries pointed at 99faf707 — deleted, 285
        // messages, replaced by a new conversation a day earlier. Inbound
        // decrypted fine (its `mlsGroups` map had the live group), every message
        // was recorded, and not one reply was ever constructed. Two restarts and
        // a fresh inbound message all failed to repair it, because none of them
        // reaches the send path.
        //
        // Same discipline as the room reconcile below: best-effort,
        // fire-and-forget, never blocks ready, and keeps the existing map on any
        // failure. Called with no stale group, so it re-resolves and prunes
        // nothing.
        void this._reconcileConversationGroups().catch((err) => {
          console.warn("[SecureChannel] Conversation-group reconcile failed:", err);
        });

        // #629: reconcile local rooms against the server (best-effort, fire-and-
        // forget so it never blocks the ready path). Prunes disbanded/left rooms
        // that would otherwise replay dead MLS commit history every reconnect.
        // Chained, not parallel: the quarantine must run AFTER rooms are pruned
        // so it only ever sees genuine non-room orphans. Still fire-and-forget —
        // neither may block the ready path.
        void this._reconcileRoomsWithServer()
          .catch((err) =>
            console.warn(`[SecureChannel] room reconcile failed (ignored): ${err instanceof Error ? err.message : String(err)}`),
          )
          .then(() => this._quarantineOrphanedMlsGroups())
          .catch((err) =>
            console.warn(`[SecureChannel] MLS orphan quarantine failed (ignored): ${err instanceof Error ? err.message : String(err)}`),
          );

        // Initialize client-side policy scanning if enabled
        if (this.config.enableScanning) {
          this._scanEngine = new ScanEngine();
          await this._fetchScanRules();
        }

        // Sync A2A channels from server (handles missed WS events after restart)
        try {
          console.log(`[SecureChannel] Syncing A2A channels (hubAddress=${this._persisted?.hubAddress ?? "NOT SET"})`);
          await this.listA2AChannels();
          const a2aCount = Object.keys(this._persisted?.a2aChannels ?? {}).length;
          console.log(`[SecureChannel] A2A sync complete: ${a2aCount} channels`);
          this.emit("a2a_channels_synced", { count: a2aCount });

        } catch (err) {
          console.warn("[SecureChannel] A2A channel sync failed (non-fatal):", err);
        }

        // Initialize telemetry reporter for agent-side metrics
        // (hubId may not be set yet — hub_identity_sync event will late-init if needed)
        if (!this._telemetryReporter && this._persisted?.deviceJwt && this._persisted?.hubId) {
          this._telemetryReporter = new TelemetryReporter({
            apiBase: this.config.apiUrl,
            hubId: this._persisted.hubId,
            authHeader: `Bearer ${this._persisted.deviceJwt}`,
            agentName: this.config.agentName ?? this._persisted.hubAddress,
            agentVersion: this.config.agentVersion ?? "0.0.0",
            // __AV_VERSION__ is injected by esbuild from package.json at build time.
            // Falls back to "0.0.0-dev" in non-bundled contexts (tests).
            pluginVersion: typeof __AV_VERSION__ !== "undefined" ? __AV_VERSION__ : "0.0.0-dev",
          });
          this._telemetryReporter.startAutoFlush(30_000);
        }

        // Start Trust Gate token lifecycle
        this.startTrustTokenRefresh();

        // Sync A2A channels FIRST — members publish their own KP during sync fallback,
        // so the on-connect KP must come after to avoid overwriting it on the server.
        // (BUG-20260408-001: on-connect KP overwrote sync fallback KP, causing Welcome mismatch)
        await this.listA2AChannels().catch((err: unknown) => {
          console.warn("[SecureChannel] A2A channel sync on connect failed:", err);
        });

        // Publish a POOL of MLS KeyPackages so the agent can be added to many
        // rooms without starving — the backend consumes one KP per Welcome
        // fulfillment. Tops up to _KP_POOL_TARGET (the backend caps unconsumed
        // KPs to match). A reconnect that still holds its pool republishes
        // little or nothing.
        if (aborted()) return;
        await this._ensureKpPoolPublished();

        // #826: the connect-time publish above is not enough on its own.
        // KeyPackages expire after 7 days, and an agent that simply stays
        // connected past that ran to zero usable while looking healthy. Re-check
        // against the SERVER's expiry-aware count on a timer.
        this._startKpReplenishTimer();

        // MLS delivery queue: pull pending messages on connect + 30s heartbeat
        this._pullDeliveryQueue().catch((err: unknown) => {
          console.warn("[SecureChannel] Initial delivery pull failed:", err);
        });
        if (this._deliveryHeartbeat) clearInterval(this._deliveryHeartbeat);
        this._deliveryHeartbeat = setInterval(() => {
          this._pullDeliveryQueue().catch(() => {});
        }, 30_000);
        // Periodic A2A reconcile: self-heal channels whose real-time join-trigger
        // was missed while briefly disconnected (best-effort backend push).
        this._startA2aReconcileTimer();

        // MLS sync: request catch-up for 1:1 conversations
        for (const [convId, entry] of Object.entries(this._persisted?.mlsConversations ?? {})) {
          if (entry.mlsGroupId) {
            const mlsGroup = this._mlsGroups.get(`conv:${convId}`);
            ws.send(JSON.stringify({
              event: "mls_sync",
              data: {
                group_id: entry.mlsGroupId,
                last_epoch: mlsGroup ? Number(mlsGroup.epoch) : 0,
              },
            }));
          }
        }

        // MLS sync: request catch-up for each room with MLS group
        for (const [roomId, room] of Object.entries(this._persisted?.rooms ?? {})) {
          const mlsGroupId = (room as RoomState).mlsGroupId;
          if (mlsGroupId) {
            const mlsGroup = this._mlsGroups.get(roomId);
            const syncData: Record<string, unknown> = {
              group_id: mlsGroupId,
              last_epoch: mlsGroup ? Number(mlsGroup.epoch) : 0,
            };
            // Send last received timestamp for same-epoch catch-up
            const lastTs = (room as RoomState).lastMlsMessageTs;
            if (lastTs) {
              syncData.since_ts = lastTs;
            }
            ws.send(JSON.stringify({ event: "mls_sync", data: syncData }));
          }
        }

        // MLS sync: A2A channels
        for (const [channelId, entry] of Object.entries(this._persisted?.a2aChannels ?? {})) {
          if (entry.mlsGroupId) {
            const mlsGroup = this._mlsGroups.get(`a2a:${channelId}`);
            ws.send(JSON.stringify({
              event: "mls_sync",
              data: {
                group_id: entry.mlsGroupId,
                last_epoch: mlsGroup ? Number(mlsGroup.epoch) : 0,
              },
            }));
          }
        }

        this.emit("ready");
      } catch (openErr) {
        console.error("[SecureChannel] Error in WS open handler:", openErr);
        this.emit("error", openErr);
      }
    });

    ws.on("message", async (raw: WebSocket.Data) => {
      this._lastServerMessage = Date.now();
      this._lastWakeTick = Date.now();
      try {
        const data = JSON.parse(raw.toString());

        // The served gate (see the open handler): the server's FIRST frame on
        // a refused connection is `connection_rejected`; any other frame means
        // this session is being served.
        if (data?.event === "connection_rejected") served.markRejected();
        else served.markServed();

        // Diagnostic: log all non-ping events to trace A2A delivery
        if (data.event && data.event !== "ping" && data.event !== "typing") {
          // #718: this used to print `conv=` and nothing else for the events
          // that matter most. An `error` frame carries `detail` + `group_id`
          // and NEVER `conversation_id`, so every rejection rendered as a bare
          // `WS event: error conv=` — unattributable to the send that caused
          // it. 707 of them accumulated between 2026-07-02 and 2026-07-31 and
          // none could be joined to anything. Print whatever identifies the
          // frame, not just the one field this line happened to be built for.
          const d = data.data ?? {};
          const conv = (data.conversation_id || d.conversation_id || "").toString();
          const parts = [`conv=${conv.slice(0, 8)}`];
          if (d.group_id) parts.push(`group=${String(d.group_id).slice(0, 8)}`);
          if (d.detail) parts.push(`detail=${d.detail}`);
          console.log(`[SecureChannel] WS event: ${data.event} ${parts.join(" ")}`);
        }

        if (data.event === "ping") {
          ws.send(JSON.stringify({ event: "pong" }));
          return;
        }

        if (data.event === "device_revoked") {
          await this._handleDeviceRevoked();
          return;
        }

        if (data.event === "device_suspended") {
          await this._handleDeviceSuspended();
          return;
        }

        if (data.event === "connection_rejected") {
          await this._handleConnectionRejected(data);
          return;
        }

        if (data.event === "device_linked") {
          await this._handleDeviceLinked(data.data);
          return;
        }

        if (data.event === "resync_request") {
          await this._handleResyncRequest(data.data);
          return;
        }

        if (data.event === "resync_ack") {
          // Agent doesn't need to handle its own ack echo
          return;
        }

        if (data.event === "message") {
          // Dedup: skip messages already processed (prevents ratchet corruption
          // from double-delivery via direct WS forward + Redis pub/sub)
          const inMsgId = data.data?.message_id;
          if (inMsgId && this._seenMessageIds.has(inMsgId)) {
            return;
          }
          if (inMsgId) {
            this._seenMessageIds.add(inMsgId);
            if (this._seenMessageIds.size > SecureChannel.SEEN_MSG_MAX) {
              const first = this._seenMessageIds.values().next().value;
              if (first) this._seenMessageIds.delete(first);
            }
          }
          try {
            await this._handleIncomingMessage(data.data);
          } catch (msgErr) {
            console.error(
              `[SecureChannel] Message handler failed for conv ${data.data?.conversation_id?.slice(0, 8) ?? "?"}...:`,
              msgErr,
            );
          }
        }

        // --- MLS 1:1 message ---
        if (data.event === "message_mls") {
          const mlsMsgId = data.data?.message_id;
          if (mlsMsgId && this._seenMessageIds.has(mlsMsgId)) {
            return;
          }
          if (mlsMsgId) {
            this._seenMessageIds.add(mlsMsgId);
            if (this._seenMessageIds.size > SecureChannel.SEEN_MSG_MAX) {
              const first = this._seenMessageIds.values().next().value;
              if (first) this._seenMessageIds.delete(first);
            }
          }
          try {
            await this._handleMessageMLS(data.data);
          } catch (mlsMsgErr) {
            console.error(`[SecureChannel] MLS message handler failed:`, mlsMsgErr);
          }
        }

        if (data.event === "room_joined") {
          const d = data.data;
          this.joinRoom({
            roomId: d.room_id,
            name: d.room_name ?? d.name ?? "Room",
            members: (d.members || []).map((m: any) => ({
              deviceId: m.device_id,
              entityType: m.entity_type,
              displayName: m.display_name,
              identityPublicKey: m.identity_public_key,
              ephemeralPublicKey: m.ephemeral_public_key,
              ...(typeof m.tenant_id === "string" ? { tenantId: m.tenant_id } : {}),
            })),
            conversations: (d.conversations || []).map((c: any) => ({
              id: c.id,
              participantA: c.participant_a,
              participantB: c.participant_b,
            })),
            forceRekey: d.force_rekey === true,
          }).catch((err) => this.emit("error", err));
        }

        if (data.event === "room_message_mls") {
          const mlsMsgId = data.data?.message_id;
          if (mlsMsgId && this._seenMessageIds.has(mlsMsgId)) {
            return;
          }
          if (mlsMsgId) {
            this._seenMessageIds.add(mlsMsgId);
            if (this._seenMessageIds.size > SecureChannel.SEEN_MSG_MAX) {
              const first = this._seenMessageIds.values().next().value;
              if (first) this._seenMessageIds.delete(first);
            }
          }
          try {
            await this._handleRoomMessageMLS(data.data || data);
          } catch (mlsRmErr) {
            console.error("[SecureChannel] MLS room message handler failed:", mlsRmErr);
          }
        }

        if (data.event === "mls_commit") {
          try {
            await this._handleMlsCommit(data.data || data);
          } catch (mlsCommitErr) {
            console.error("[SecureChannel] MLS commit handler failed:", mlsCommitErr);
          }
        }

        if (data.event === "mls_welcome") {
          try {
            await this._handleMlsWelcome(data.data || data);
          } catch (mlsWelcomeErr) {
            console.error("[SecureChannel] MLS welcome handler failed:", mlsWelcomeErr);
          }
        }

        if (data.event === "mls_delivery") {
          // Lightweight ping — pull actual messages from delivery queue
          this._pullDeliveryQueue().catch((err: unknown) => {
            console.warn("[SecureChannel] Delivery pull on ping failed:", err);
          });
        }

        if (data.event === "dr_delivery") {
          // Signal-model DR delivery ping — pull DR messages from queue
          this._pullDrDeliveryQueue().catch((err: unknown) => {
            console.warn("[SecureChannel] DR delivery pull on ping failed:", err);
          });
        }

        if (data.event === "mls_welcome_requested") {
          // Someone (observer or rejoining member) needs a Welcome — trigger delivery pull
          // which includes A2A Welcome fulfillment
          console.log(`[SecureChannel] mls_welcome_requested for group ${(data.data?.group_id || "").slice(0, 8)} — pulling deliveries`);
          this._pullDeliveryQueue().catch((err: unknown) => {
            console.warn("[SecureChannel] Delivery pull on welcome request failed:", err);
          });
        }

        if (data.event === "mls_sync_response") {
          try {
            await this._handleMlsSyncResponse(data.data || data);
          } catch (mlsSyncErr) {
            console.error("[SecureChannel] MLS sync response handler failed:", mlsSyncErr);
          }
        }

        if (data.event === "room_participant_added") {
          const p = data.data;
          this.emit("room_participant_added", {
            roomId: p.room_id,
            participant: {
              deviceId: p.participant?.device_id ?? p.device_id,
              participantType: p.participant?.participant_type ?? p.participant_type ?? "human",
              displayName: p.participant?.display_name ?? p.display_name,
            },
          });
        }

        if (data.event === "room_participant_removed") {
          const p = data.data;
          this.emit("room_participant_removed", {
            roomId: p.room_id,
            participant: {
              deviceId: p.participant?.device_id ?? p.device_id,
              participantType: p.participant?.participant_type ?? p.participant_type ?? "human",
              displayName: p.participant?.display_name ?? p.display_name,
            },
          });
        }

        if (data.event === "room_hushed") {
          // #392 room hush (advisory). Forward to consumers (e.g. the native
          // bridge cooperatively holds room_say until hushed_until passes).
          this.emit("room_hushed", {
            roomId: data.data?.room_id,
            hushedUntil: data.data?.hushed_until ?? null,
          });
        }

        if (data.event === "room_advisory") {
          // #392 one-time no-secrets reminder (advisory).
          this.emit("room_advisory", {
            roomId: data.data?.room_id,
            kind: data.data?.kind,
            message: data.data?.message,
          });
        }

        if (data.event === "arm_requested") {
          // Slice 2 Plan C — owner requested worker arming for a room.
          this.emit("arm_requested", {
            roomId: data.data?.room_id,
            requestId: data.data?.request_id,
            workspaceLabel: data.data?.workspace_label ?? null,
          });
        }

        if (data.event === "disarm") {
          // Slice 2 Plan C — owner (or the backend) disarmed a room.
          this.emit("disarm", { roomId: data.data?.room_id });
        }

        if (data.event === "arming_snapshot") {
          // Slice 2 Plan C — full re-sync of currently-armed rooms (e.g. on reconnect).
          // Task 3: also pass through the device-level `work_allowed` grant
          // (Task 2's unified push) so the bridge can derive its fail-closed
          // `workAllowed()` getter — coerced to a strict boolean so a missing
          // field never accidentally reads truthy.
          //
          // #627: `gates_removed` is the owner's "Remove all gates" grant. Same
          // `=== true` idiom, but the asymmetry is worth stating because it is
          // what makes strictness non-negotiable here: a wrongly-false
          // workAllowed fails CLOSED (the agent just can't work), whereas a
          // wrongly-true gatesRemoved fails OPEN (unrestricted shell on the
          // owner's machine). Any value that is not literally `true` — a
          // truthy string from a hand-rolled client, a `1`, an object, or the
          // key being absent because the backend predates #627 — must land on
          // false. This is a passthrough only; the decision was made upstream
          // and the gate that acts on it lives in worker-permission.ts.
          this.emit("arming_snapshot", {
            workAllowed: data.data?.work_allowed === true,
            gatesRemoved: data.data?.gates_removed === true,
            roomIds: data.data?.room_ids ?? [],
          });
        }

        if (data.event === "policy_blocked") {
          this.emit("policy_blocked", data.data);
        }

        if (data.event === "message_held") {
          this.emit("message_held", data.data);
        }

        if (data.event === "policy_rejected") {
          this.emit("policy_rejected", data.data);
        }

        if (data.event === "scan_rules_updated") {
          await this._fetchScanRules();
          return;
        }

        // MCP proxy: backend relays an MCP request from a remote client
        if (data.event === "mcp_request") {
          const requestId = data.data?.request_id;
          const mcpPayload = data.data?.mcp_payload;
          if (requestId && mcpPayload && this._mcpServer) {
            try {
              // Create a synthetic HTTP request/response to forward to the MCP server
              const { createMcpProxyRequest } = await import("./mcp-proxy-helpers.js");
              const mcpResponse = await createMcpProxyRequest(this._mcpServer, mcpPayload);
              // Send response back via WS
              ws.send(JSON.stringify({
                event: "mcp_response",
                data: { request_id: requestId, mcp_payload: mcpResponse },
              }));
            } catch (err) {
              ws.send(JSON.stringify({
                event: "mcp_response",
                data: {
                  request_id: requestId,
                  mcp_payload: {
                    jsonrpc: "2.0",
                    id: mcpPayload?.id ?? null,
                    error: { code: -32603, message: String(err) },
                  },
                },
              }));
            }
          }
          return;
        }

        // hub_identity_sync fires on every WS connect — ensures hubId + agentRole are set
        if (data.event === "hub_identity_sync") {
          if (this._persisted && data.data?.hub_id) {
            const changed = this._persisted.hubId !== data.data.hub_id
              || this._persisted.agentRole !== (data.data.agent_role ?? "peer");
            this._persisted.hubAddress = data.data.hub_address;
            this._persisted.hubId = data.data.hub_id;
            this._persisted.agentHubId = data.data.hub_id;
            this._persisted.agentRole = (data.data.agent_role as "lead" | "peer") ?? "peer";
            if (changed) this._persistState();
            // #499 A1: (re)resolve our own display_name on every connect for the
            // DM identity preamble — picks up a rename and retries a prior
            // failed fetch (best-effort, fire-and-forget — never blocks connect).
            void this._resolveOwnDisplayName();

            // Late-init telemetry reporter if it wasn't created during open handler
            if (!this._telemetryReporter && this._persisted.deviceJwt && this._persisted.hubId) {
              this._telemetryReporter = new TelemetryReporter({
                apiBase: this.config.apiUrl,
                hubId: this._persisted.hubId,
                authHeader: `Bearer ${this._persisted.deviceJwt}`,
                agentName: this.config.agentName ?? this._persisted.hubAddress,
                agentVersion: this.config.agentVersion ?? "0.0.0",
                // __AV_VERSION__ is injected by esbuild from package.json at build time.
                // Falls back to "0.0.0-dev" in non-bundled contexts (tests).
                pluginVersion: typeof __AV_VERSION__ !== "undefined" ? __AV_VERSION__ : "0.0.0-dev",
              });
              this._telemetryReporter.startAutoFlush(30_000);
            }
          }
        }

        if (data.event === "hub_identity_assigned") {
          if (this._persisted) {
            this._persisted.hubAddress = data.data.hub_address;
            this._persisted.hubId = data.data.hub_id;
            this._persistState();
          }
          this.emit("hub_identity_assigned", data.data);
        }
        if (data.event === "hub_identity_role_changed") {
          if (this._persisted && data.data?.agent_role) {
            this._persisted.agentRole = data.data.agent_role as "lead" | "peer";
            this._persistState();
            console.log(`[SecureChannel] Agent role changed to: ${data.data.agent_role}`);
          }
          this.emit("hub_identity_role_changed", data.data);
        }
        // --- Shadow Mode WS events ---

        if (data.event === "shadow_config_sync") {
          if (this._persisted && data.data?.skills) {
            this._persisted.shadowSkills = data.data.skills;
            this._persistState();
            console.log(`[SecureChannel] Shadow config synced: ${Object.keys(data.data.skills).length} skill(s)`);
          }
        }

        if (data.event === "shadow_session_created") {
          if (this._persisted && data.data?.skill_name) {
            if (!this._persisted.shadowSkills) this._persisted.shadowSkills = {};
            this._persisted.shadowSkills[data.data.skill_name] = {
              sessionId: data.data.session_id,
              autonomyLevel: data.data.autonomy_level,
              decisionClass: data.data.decision_class,
            };
            this._persistState();
            console.log(`[SecureChannel] Shadow session created for skill: ${data.data.skill_name}`);
          }
          this.emit("shadow_session_created", data.data);
        }

        if (data.event === "shadow_session_graduated") {
          if (this._persisted?.shadowSkills && data.data?.skill_name) {
            if (data.data.autonomy_level === "autonomous") {
              delete this._persisted.shadowSkills[data.data.skill_name];
            } else {
              const entry = this._persisted.shadowSkills[data.data.skill_name];
              if (entry) entry.autonomyLevel = data.data.autonomy_level;
            }
            this._persistState();
            console.log(`[SecureChannel] Shadow session graduated: ${data.data.skill_name} → ${data.data.autonomy_level}`);
          }
          this.emit("shadow_session_graduated", data.data);
        }

        if (data.event === "shadow_session_deleted") {
          if (this._persisted?.shadowSkills && data.data?.skill_name) {
            delete this._persisted.shadowSkills[data.data.skill_name];
            this._persistState();
          }
          this.emit("shadow_session_deleted", data.data);
        }

        if (data.event === "hub_identity_removed") {
          if (this._persisted) {
            delete this._persisted.hubAddress;
            delete this._persisted.hubId;
            this._persistState();
          }
          this.emit("hub_identity_removed", data.data);
        }

        // --- A2A Channel WS events ---

        if (data.event === "a2a_channel_approved") {
          const channelData = data.data || data;
          const channelId = channelData.channel_id;
          const convId = channelData.conversation_id;

          // Determine role from participants or event data
          const myHub = this._persisted?.hubAddress || "";
          const myHubId = this._persisted?.hubId || "";
          const eventParticipants: any[] = channelData.participants ?? [];
          const myParticipant = eventParticipants.find(
            (p: any) => p.hub_id === myHubId || p.hub_address === myHub,
          );
          const existingEntry = this._persisted?.a2aChannels?.[channelId];
          const role: "creator" | "member" =
            existingEntry?.role ||
            myParticipant?.role ||
            (channelData.creator_hub_id === myHubId ? "creator" : "member");

          // Build participants list (peers only).
          //
          // Carries device_id through so _foundA2AGroup can target Welcomes
          // by device (backend ws.py mls_welcome handler reads target_device_id,
          // not target_hub_id — silent drop otherwise; see action_items
          // "CRITICAL: OC plugin emits mls_welcome with target_hub_id+welcome").
          const localParticipants = eventParticipants
            .filter((p: any) => p.hub_id !== myHubId && p.hub_address !== myHub)
            .map((p: any) => ({
              hubId: p.hub_id || "",
              hubAddress: p.hub_address || "",
              status: (p.status || "active") as "invited" | "active" | "left" | "removed",
              deviceId: p.device_id || null,
            }));

          // Store in persisted state
          if (this._persisted && convId) {
            if (!this._persisted.a2aChannels) this._persisted.a2aChannels = {};

            const existingNow = this._persisted.a2aChannels[channelId];

            // MERGE instead of overwrite — preserve existing MLS state
            this._persisted.a2aChannels[channelId] = {
              ...existingNow,
              channelId,
              conversationId: convId,
              role: existingNow?.role || role,
              topic: channelData.topic || existingNow?.topic || undefined,
              participants: localParticipants.length > 0
                ? localParticipants
                : existingNow?.participants ?? [],
            };

            await this._persistState();
          }
          this.emit("a2a_channel_approved", channelData);

          // --- MLS founding for creator agent ---
          if (this._persisted && convId && channelId && role === "creator") {
            try {
              await this._foundA2AGroup(channelId, convId, this._persisted.a2aChannels?.[channelId]?.participants ?? []);
            } catch (err) {
              console.warn(`[SecureChannel] A2A MLS init failed (non-fatal):`, err);
            }
          } else if (this._persisted && convId && channelId && role === "member") {
            // Member agent: publish KeyPackage and wait for creator's Welcome.
            // Do NOT call /init — that creates a separate group and races with the creator.
            try {
              const mgr = new MLSGroupManager();
              const identity = new TextEncoder().encode(this._persisted.deviceId);
              const kp = await mgr.generateKeyPackage(identity);
              this._recordPublishedKp(kp);
              const kpBytes = MLSGroupManager.serializeKeyPackage(kp.publicPackage);
              await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${this._deviceJwt}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  key_package: Buffer.from(kpBytes).toString("hex"),
                  device_id: this._persisted.deviceId,
                }),
              });
              console.log(
                `[SecureChannel] A2A ${channelId.slice(0, 8)} member published KeyPackage, awaiting Welcome from creator`,
              );
            } catch (err) {
              console.warn(`[SecureChannel] A2A member KeyPackage publish failed (non-fatal):`, err);
            }
          }

          // NOTE: onA2AChannelReady is now fired after MLS Welcome join (see _handleMlsWelcome)
          // to ensure the MLS group is ready before the seed message is sent.
        }

        if (data.event === "a2a_channel_rejected") {
          this.emit("a2a_channel_rejected", data.data || data);
        }

        if (data.event === "a2a_channel_revoked") {
          const channelData = data.data || data;
          const channelId = channelData.channel_id;
          // Remove from persisted state
          if (this._persisted?.a2aChannels?.[channelId]) {
            delete this._persisted.a2aChannels[channelId];
            await this._persistState();
          }
          this.emit("a2a_channel_revoked", channelData);
        }

        // --- Multi-agent A2A participant events ---

        if (data.event === "a2a_channel_invited") {
          // Another agent invited us to a channel — publish KeyPackage, await Welcome
          const channelData = data.data || data;
          const channelId = channelData.channel_id;
          const convId = channelData.conversation_id;
          console.log(`[SecureChannel] Received a2a_channel_invited for ${channelId?.slice(0, 8)}`);

          if (this._persisted && channelId) {
            if (!this._persisted.a2aChannels) this._persisted.a2aChannels = {};
            const participants = (channelData.participants ?? [])
              .filter((p: any) => p.hub_id !== (this._persisted?.hubId || "") && p.hub_address !== (this._persisted?.hubAddress || ""))
              .map((p: any) => ({
                hubId: p.hub_id || "",
                hubAddress: p.hub_address || "",
                status: (p.status || "active") as "invited" | "active" | "left" | "removed",
              }));

            this._persisted.a2aChannels[channelId] = {
              ...this._persisted.a2aChannels[channelId],
              channelId,
              conversationId: convId || this._persisted.a2aChannels[channelId]?.conversationId || "",
              role: "member",
              participants,
            };
            await this._persistState();

            // Publish KeyPackage so creator can send Welcome
            try {
              const mgr = new MLSGroupManager();
              const identity = new TextEncoder().encode(this._persisted!.deviceId);
              const kp = await mgr.generateKeyPackage(identity);
              this._recordPublishedKp(kp);
              const kpBytes = MLSGroupManager.serializeKeyPackage(kp.publicPackage);
              await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${this._deviceJwt}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  key_package: Buffer.from(kpBytes).toString("hex"),
                  device_id: this._persisted!.deviceId,
                }),
              });
              console.log(`[SecureChannel] Published KeyPackage for A2A invite ${channelId.slice(0, 8)}`);
            } catch (kpErr) {
              console.warn(`[SecureChannel] KeyPackage publish failed for invited channel:`, kpErr);
            }
          }
          this.emit("a2a_channel_invited", channelData);
        }

        if (data.event === "a2a_participant_joined") {
          const eventData = data.data || data;
          const channelId = eventData.channel_id;
          const participant = eventData.participant;
          if (this._persisted?.a2aChannels?.[channelId] && participant) {
            const entry = this._persisted.a2aChannels[channelId];
            if (!entry.participants) entry.participants = [];
            const existing = entry.participants.find(p => p.hubId === participant.hub_id || p.hubAddress === participant.hub_address);
            if (existing) {
              existing.status = "active";
              existing.hubId = participant.hub_id || existing.hubId;
              existing.hubAddress = participant.hub_address || existing.hubAddress;
            } else {
              entry.participants.push({
                hubId: participant.hub_id || "",
                hubAddress: participant.hub_address || "",
                status: "active",
              });
            }
            await this._persistState();
            console.log(`[SecureChannel] Participant joined A2A ${channelId.slice(0, 8)}: ${participant.hub_address}`);

            // If we're creator, send Welcome to new participant
            if (entry.role === "creator" && entry.mlsGroupId) {
              const mlsGroup = this._mlsGroups.get(`a2a:${channelId}`);
              if (mlsGroup?.isInitialized) {
                try {
                  // Sister fix to the _foundA2AGroup dispatch (see PR #236):
                  // backend mls_welcome reads target_device_id+payload, not
                  // target_hub_id+welcome — without the right field names
                  // the welcome silently drops, the new participant never
                  // joins the MLS group, and the channel quietly bricks.
                  // #1225: checked BEFORE the commit — an Add we cannot
                  // Welcome only burns an epoch and strands the participant.
                  const newDeviceId = (participant as any).device_id || null;
                  const a2aLabel = `A2A ${channelId.slice(0, 8)} participant_joined`;
                  if (!newDeviceId) {
                    console.warn(
                      `[SecureChannel] ${a2aLabel}: no device_id on the payload for ${participant.hub_address} — NOT adding (a Welcome could not be addressed)`,
                    );
                  } else if (!this._wsIsOpen()) {
                    console.warn(`[SecureChannel] ${a2aLabel}: socket is not OPEN — NOT advancing the group`);
                  } else {
                  // #1225: pre-flight (review S4) — a commit built on a stale
                  // epoch WILL be refused; do not build it, and do not spend a
                  // KeyPackage fetch on it.
                  const preHead = await this._readGroupHead(entry.mlsGroupId);
                  const inFlight = preHead.ok && preHead.epoch !== Number(mlsGroup.epoch)
                    ? null
                    : this._reserveCommit(entry.mlsGroupId, a2aLabel);
                  if (preHead.ok && preHead.epoch !== Number(mlsGroup.epoch)) {
                    console.warn(
                      `[SecureChannel] ${a2aLabel}: server head is at epoch ${preHead.epoch}, we are at ${Number(mlsGroup.epoch)} — ` +
                        `NOT committing (it would be refused); resync catches up first`,
                    );
                    await this._catchUpAfterPreflightSkip(entry.mlsGroupId, preHead.epoch, Number(mlsGroup.epoch), a2aLabel);
                  } else if (inFlight) {
                  try {
                  const kpRes = await fetch(
                    `${this.config.apiUrl}/api/v1/mls/key-packages?hub_id=${encodeURIComponent(participant.hub_id)}`,
                    { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
                  );
                  if (kpRes.ok) {
                    const kpData = await kpRes.json();
                    if (kpData.key_package) {
                      const kpBytes = new Uint8Array(Buffer.from(kpData.key_package, "hex"));
                      const memberKp = MLSGroupManager.deserializeKeyPackage(kpBytes);
                      // #1225: snapshot, commit, then ask the server (#1184) before
                      // anything is minted from or persisted at the new epoch.
                      const stateBefore = mlsGroup.exportState();
                      const { commit, welcome } = await mlsGroup.addMembers([memberKp]);
                      const committedEpoch = Number(mlsGroup.epoch);
                      if (!welcome) {
                        mlsGroup.importState(stateBefore);
                        console.warn(`[SecureChannel] ${a2aLabel}: addMembers produced no Welcome — rolled back`);
                      } else {
                        const verdict = await this._sendCommitConfirmed({
                          mlsGroup,
                          mlsGroupId: entry.mlsGroupId,
                          label: a2aLabel,
                          commit,
                          epoch: committedEpoch,
                          parent: preHead.ok ? preHead.digest : undefined,
                          memberDeviceIds: this._commitRoster(
                            (entry.participants ?? []).map((p) => p.deviceId),
                            [newDeviceId],
                          ),
                          stateBefore,
                          inFlight,
                        });
                        if (verdict === "confirmed") {
                          const sent = await this._sendFrameConfirmed({
                            event: "mls_welcome",
                            data: {
                              group_id: entry.mlsGroupId,
                              payload: Buffer.from(welcome).toString("hex"),
                              target_device_id: newDeviceId,
                              a2a_channel_id: channelId,
                              target_hub_id: participant.hub_id, // observability only
                            },
                          }, `${a2aLabel} welcome`);
                          // The head positively showed our commit: persist it.
                          await saveMlsState(this.config.dataDir, entry.mlsGroupId, JSON.stringify(mlsGroup.exportState()));
                          if (sent) {
                            console.log(`[SecureChannel] Sent MLS Welcome to new participant ${participant.hub_address} (device ${String(newDeviceId).slice(0, 8)})`);
                          } else {
                            console.warn(`[SecureChannel] ${a2aLabel}: Welcome to ${String(newDeviceId).slice(0, 8)} was NOT written — the participant will request one`);
                          }
                        }
                      }
                    }
                  }
                  } finally {
                    await this._releaseCommit(entry.mlsGroupId, inFlight, a2aLabel);
                  }
                  }
                  }
                } catch (welcomeErr) {
                  console.warn(`[SecureChannel] Welcome to new participant failed:`, welcomeErr);
                }
              }
            }
          }
          this.emit("a2a_participant_joined", eventData);
        }

        if (data.event === "a2a_participant_left" || data.event === "a2a_participant_removed") {
          const eventData = data.data || data;
          const channelId = eventData.channel_id;
          const participantHubId = eventData.hub_id || eventData.participant?.hub_id;
          if (this._persisted?.a2aChannels?.[channelId] && participantHubId) {
            const entry = this._persisted.a2aChannels[channelId];
            const p = entry.participants?.find(pp => pp.hubId === participantHubId);
            if (p) {
              p.status = data.event === "a2a_participant_left" ? "left" : "removed";
              await this._persistState();
              console.log(`[SecureChannel] Participant ${data.event.replace("a2a_participant_", "")} A2A ${channelId.slice(0, 8)}: ${p.hubAddress}`);
            }
          }
          this.emit(data.event, eventData);
        }

        if (data.event === "a2a_agent_removed") {
          // Our agent was kicked from the channel
          const eventData = data.data || data;
          const channelId = eventData.channel_id;
          if (this._persisted?.a2aChannels?.[channelId]) {
            // Clean up MLS group
            const entry = this._persisted.a2aChannels[channelId];
            if (entry.mlsGroupId) {
              this._mlsGroups.delete(`a2a:${channelId}`);
              await deleteMlsState(this.config.dataDir, entry.mlsGroupId);
            }
            delete this._persisted.a2aChannels[channelId];
            await this._persistState();
            console.log(`[SecureChannel] Removed from A2A channel ${channelId.slice(0, 8)}`);
          }
          this.emit("a2a_agent_removed", eventData);
        }

        if (data.event === "a2a_creator_transferred") {
          const eventData = data.data || data;
          const channelId = eventData.channel_id;
          const newCreatorHubId = eventData.new_creator_hub_id;
          if (this._persisted?.a2aChannels?.[channelId]) {
            const myHubId = this._persisted.hubId || "";
            this._persisted.a2aChannels[channelId].role =
              newCreatorHubId === myHubId ? "creator" : "member";
            await this._persistState();
            console.log(`[SecureChannel] Creator transferred for A2A ${channelId.slice(0, 8)}: ${newCreatorHubId === myHubId ? "we are now creator" : "we are member"}`);
          }
          this.emit("a2a_creator_transferred", eventData);
        }

        // Handle server error responses (prevents silent failures)
        if (data.event === "error") {
          const detail = (data.data?.detail || data.detail || "Unknown server error");
          // #718: the correlated other half of `[deliver] … result=sent`. A
          // frame we already reported as dispatched has been REFUSED, so the
          // message is gone. Emit it on the `[deliver]` prefix and carry the
          // group id, so "did this message get delivered?" is answerable from
          // the log instead of requiring the two halves to be spotted 50ms
          // apart under different prefixes.
          const rejectedGroup = String(data.data?.group_id ?? "").slice(0, 8);
          console.error(
            `[deliver] delivery_result=REJECTED ` +
              `group=${rejectedGroup || "unknown"} detail=${detail}`,
          );
          console.error(`[SecureChannel] Server error: ${detail}`);
          // #1184: a `… commit rejected` frame for a group with our commit in
          // flight is the server's verdict on it. Recorded BEFORE anything
          // that can await, so the confirm loop sees it on its next check.
          this._noteCommitRefusal(data.data || data);
          // An in-band error naming a dead resource must PRUNE that resource.
          // Emitting alone left the dead group in state to be re-written forever.
          await this._handleServerError(data.data || data);
          this.emit("error", new Error(`Server: ${detail}`));
        }

        if (data.event === "a2a_message_mls") {
          // Dedup
          const msgId = (data.data || data).message_id;
          if (msgId && this._a2aSeenMessageIds.has(msgId)) return;
          if (msgId) {
            this._a2aSeenMessageIds.add(msgId);
            if (this._a2aSeenMessageIds.size > SecureChannel.A2A_SEEN_MAX) {
              const first = this._a2aSeenMessageIds.values().next().value;
              if (first) this._a2aSeenMessageIds.delete(first);
            }
          }
          await this._handleA2AMessageMLS(data.data || data);
        }
      } catch (err) {
        this.emit("error", err);
      }
    });

    ws.on("close", (code?: number) => {
      // Capture the close code so the rapid-disconnect classifier in
      // _scheduleReconnect can distinguish auth rejection (4001) and the
      // reserved "session_replaced" (4000) from other disconnects.
      this._lastCloseCode = typeof code === "number" ? code : null;
      // A socket that closes before it was served was never served.
      served.markRejected();
      this._stopPing();
      this._stopWakeDetector();
      this._stopPendingPoll();
      if (this._deliveryHeartbeat) { clearInterval(this._deliveryHeartbeat); this._deliveryHeartbeat = null; }
      this._stopA2aReconcileTimer();
      this._stopKpReplenishTimer();
      if (this._stopped) return;
      this._setState("disconnected");
      this._scheduleReconnect();
    });

    ws.on("error", (err: Error) => {
      this.emit("error", err);
      // The close event will fire after this and handle reconnection
    });
  }

  /**
   * Handle an incoming encrypted message from a specific conversation.
   * Decrypts using the appropriate session ratchet, emits to the agent,
   * and relays as sync messages to sibling sessions.
   */
  /**
   * Handle an incoming MLS-encrypted 1:1 message.
   * Decrypts via the conversation's MLSGroupManager and emits the plaintext.
   */
  private async _handleMessageMLS(data: any): Promise<void> {
    const convId = data.conversation_id;
    const convGroupId = data.conversation_group_id;
    const groupId = data.group_id;
    const senderDeviceId = data.sender_device_id;

    // #1013: every inbound frame reports the epoch the group was at when it
    // was produced — a lower bound on where the group is now.
    this._noteGroupEpoch(groupId, data.epoch);

    // Skip own echo
    if (senderDeviceId === this._deviceId) return;

    // Find MLS group: shared 1:1 group first, then per-conversation, then by group_id search
    let mgr: MLSGroupManager | undefined;
    let mgrKey: string | undefined;

    if (convGroupId) {
      mgrKey = `1to1-group:${convGroupId}`;
      mgr = this._mlsGroups.get(mgrKey);
    }
    if (!mgr && convId) {
      mgrKey = `conv:${convId}`;
      mgr = this._mlsGroups.get(mgrKey);
    }
    if (!mgr && groupId) {
      // Search shared groups by mlsGroupId
      for (const [gid, entry] of Object.entries(this._persisted?.mlsGroups ?? {})) {
        if (entry.mlsGroupId === groupId) {
          mgrKey = `1to1-group:${gid}`;
          mgr = this._mlsGroups.get(mgrKey);
          break;
        }
      }
    }
    if (!mgr && groupId) {
      // Search legacy per-conversation groups by mlsGroupId
      for (const [cid, entry] of Object.entries(this._persisted?.mlsConversations ?? {})) {
        if (entry.mlsGroupId === groupId) {
          mgrKey = `conv:${cid}`;
          mgr = this._mlsGroups.get(mgrKey);
          break;
        }
      }
    }

    if (!mgr) {
      console.warn(`[SecureChannel] No MLS group for message_mls (conv=${convId?.slice(0, 8)}, group=${groupId?.slice(0, 8)})`);
      // Self-heal (BUG-20260604-001): a late-added member receives group
      // ciphertext it can't decrypt because it never got a Welcome. Ask the
      // committer to re-Welcome us (deduped). Without this the row is dropped
      // forever and the member stays silent.
      if (groupId) void this._requestWelcomeSelfHeal(groupId);
      return;
    }

    try {
      const cipherBytes = new Uint8Array(Buffer.from(data.payload, "hex"));
      const result = await mgr.decrypt(cipherBytes);
      // A successful decrypt proves the group is live: clear the run, as the
      // commit counter does at `_onCommitFailure`. Before the handshake return
      // below — a handshake is also a successful decrypt.
      if (mgrKey) this._mlsDecryptFailCounts.delete(mgrKey);

      // Save updated MLS state
      const mlsGroupId = (convGroupId ? this._persisted?.mlsGroups?.[convGroupId]?.mlsGroupId : null)
        ?? this._persisted?.mlsConversations?.[convId]?.mlsGroupId
        ?? groupId;
      if (mlsGroupId) {
        await saveMlsState(this.config.dataDir, mlsGroupId, JSON.stringify(mgr.exportState()));
      }

      if (result.isHandshake) return; // commit processing — no plaintext

      const plaintext = new TextDecoder().decode(result.plaintext);

      // Update last message timestamp for sync
      if (data.created_at) {
        this._persisted!.lastMessageTimestamp = data.created_at;
      }

      // Parse envelope
      let text = plaintext;
      let messageType = "text";
      let topicId: string | undefined = data.topic_id;
      // #1139 — attachments were parsed ONLY on the Double Ratchet path, so a
      // file sent over MLS reached nobody. hermes drops DR entirely, which is
      // why no single transport could serve every client family.
      let mlsAttachment: any = null;
      let decisionReply: DecisionResponse | undefined;
      try {
        const parsed = JSON.parse(plaintext);
        text = envelopeText(parsed, plaintext);
        messageType = parsed.type ?? "text";
        topicId = parsed.topicId ?? topicId;
        if (parsed.attachment) mlsAttachment = parsed.attachment;
        // The owner's answer to a decision_request rides in `decision`; the
        // text alone ("Selected: x") is all the agent got before, so
        // waitForDecision() never resolved on this path.
        decisionReply = decisionReplyFromEnvelope(parsed);
        if (decisionReply) messageType = "decision_response";
      } catch {
        // plain string
      }

      // #1416: the conversation group this 1:1 message belongs to.
      const dmGroupId: string | undefined = convGroupId
        ?? (mgrKey?.startsWith("1to1-group:") ? mgrKey.slice("1to1-group:".length) : undefined);

      // Handle history catchup request (from new devices joining 1:1 shared group)
      if (messageType === "history_catchup_request") {
        try {
          const request = JSON.parse(plaintext);
          if (typeof request.requesting_device_id !== "string") return;
          // #1416: answer from THIS group's history only.
          const filtered = this._historyForCatchup("dm", undefined, request.since_ts, {
            groupId: dmGroupId,
            conversationId: convId,
          });

          const responsePayload = JSON.stringify({
            type: "history_catchup_response",
            target_device_id: request.requesting_device_id,
            messages: filtered.map((e: any) => ({
              sender: e.sender,
              sender_name: e.sender === "agent" ? (this.config.agentName ?? "Agent") : "Owner",
              text: e.text,
              ts: e.ts,
            })),
            no_history: filtered.length === 0,
          });

          // Send via shared MLS group
          // `mgrKey` is assigned immediately before `mgr` in every resolution
          // branch above, so naming it here narrows the type without changing
          // reachability.
          if (mgr && mgrKey && mlsGroupId) {
            const responseBytes = new TextEncoder().encode(responsePayload);
            const cipher = await this._encryptWithCatchUp(
              mgr, mgrKey, mlsGroupId,
              `history catch-up ${mlsGroupId.slice(0, 8)}`, responseBytes,
            );
            await saveMlsState(this.config.dataDir, mlsGroupId, JSON.stringify(mgr.exportState()));
            if (this._ws) {
              this._ws.send(JSON.stringify({
                event: "message_mls",
                data: {
                  group_id: mlsGroupId,
                  epoch: Number(mgr.epoch),
                  payload: Buffer.from(cipher).toString("hex"),
                  message_type: "history_catchup_response",
                },
              }));
            }
            console.log(`[SecureChannel] Sent 1:1 history catchup: ${filtered.length} messages to ${request.requesting_device_id?.slice(0, 8)}`);
          }
        } catch (catchupErr) {
          console.warn("[SecureChannel] 1:1 history catchup failed:", catchupErr);
        }
        return; // Don't emit to agent
      }

      // Skip history catchup responses (for web clients only)
      if (messageType === "history_catchup_response") return;

      // Belt-and-suspenders (worker-mode removal P2): the 1:1 MLS path structurally
      // carries only 1:1 owner conversations. Room/A2A routing happens UPSTREAM at
      // the MLS-delivery dispatcher (~_pullDeliveryQueue), which routes rows with a
      // server `room_id`/`a2a_channel_id` to _handleRoomMessageMLS/_handleA2AMessageMLS
      // BEFORE this handler and threads only conversation_id/conversation_group_id
      // through — so `data.room_id`/`data.a2a_channel_id` are normally absent here
      // (the two disjuncts below are defensive and virtually never fire). The REAL
      // belt on this path is the LOCAL `_isNonDmConversation` (rooms + a2aChannels,
      // either id): if a room/A2A conv ever reached here it would emit as a no-roomId
      // `message`, which the native worker tool-gate treats as an owner DM (tool-
      // enabled, bypassing arming) — a prompt-injection -> tool-execution path once
      // capability is intrinsic. Skip it BEFORE any session side effect so a to-be-
      // dropped message cannot flip session.activated.
      if (data.room_id || data.a2a_channel_id || this._isNonDmConversation(convId, convGroupId)) {
        console.warn(`[SecureChannel] Dropped 1:1 MLS emit for non-DM conv ${(convId ?? convGroupId ?? "?").slice(0, 8)} (room/A2A guard)`);
        return;
      }

      // Activate the DR session for this conversation so send() includes it.
      // The send() loop iterates this._sessions and checks activated flag —
      // without this, MLS-received messages wouldn't trigger replies.
      if (convId) {
        const session = this._sessions.get(convId);
        if (session && !session.activated) {
          session.activated = true;
          if (this._persisted?.sessions?.[convId]) {
            this._persisted.sessions[convId].activated = true;
          }
          console.log(`[SecureChannel] Session ${convId.slice(0, 8)} activated via MLS message`);
        }
      }

      // #1416: remember this message's topic for ITS group, as the DR path
      // does. Only when one is present — the server does not store a topic on
      // MLS rows, so most MLS messages carry none and must not clear a topic
      // learned over DR.
      if (topicId) {
        this._rememberIncomingTopic(convId, dmGroupId, topicId);
      }

      // #1139 — fold any attachment into the text before it reaches the agent,
      // exactly as the Double Ratchet path does.
      if (mlsAttachment) {
        text = await this._augmentWithAttachment(mlsAttachment, text);
      }

      await this._captureHistoryApplication(
        "owner",
        plaintext,
        "dm",
        topicId,
        data.created_at,
        messageType,
        { groupId: dmGroupId, conversationId: convId },
      );

      // #1418: a frame that names only its group is still that group's
      // person's message — answer into their conversation, not the primary's.
      const dmConvId: string | undefined = convId ?? this._conversationOfGroup(dmGroupId);
      this._noteInboundDm(dmConvId, dmGroupId, { createdAt: data.created_at, senderDeviceId });
      // A group nothing maps: learn its conversation (fire-and-forget).
      this._reconcileForUnmappedGroup(dmGroupId);

      // Emit to agent
      const metadata = {
        messageId: data.message_id ?? `mls_${Date.now()}`,
        // A frame on a KNOWN group that no conversation maps yet is not the
        // primary person's: leave the conversation unnamed rather than hand
        // the reply to someone else; `conversationGroupId` still places it.
        conversationId: dmConvId ?? (dmGroupId ? undefined : this._primaryConversationId),
        // #1418: the group this message decrypted under — attested, so a turn
        // answers here even when the local map is stale.
        conversationGroupId: dmGroupId,
        timestamp: data.created_at ?? new Date().toISOString(),
        topicId,
        // #621: positive attestation that this decrypted under the OWNER'S 1:1
        // material (MLS 1to1-group map, or a Double Ratchet session), NOT merely
        // that a roomId was absent. The bridge's full-access DM lane requires it.
        ownerDm1to1: true,
        messageType,
        ...(decisionReply ? { decision: decisionReply } : {}),
      };
      this.emit("message", text, metadata);
      if (this.config.onMessage) {
        this.config.onMessage(text, metadata);
      }
    } catch (decryptErr) {
      // A frame from BELOW our epoch was sealed before we joined; forward secrecy
      // makes it permanently unreadable. That is MLS working, not divergence, and
      // counting it toward recovery re-keys a healthy group (+2 epochs per member).
      // Same rule the commit counter has used since #920, classified on EPOCHS,
      // never on the error text (see mls-commit-classify.ts). Unknown still counts.
      if (classifyCommitFailure(data.epoch, mgr?.epoch) === "already-applied") {
        console.debug(`[SecureChannel] MLS 1:1 message at epoch ${String(data.epoch)} predates our epoch ${String(mgr?.epoch)} — cannot decrypt, ignoring`);
        return;
      }
      const failKey = mgrKey || `unknown:${groupId}`;
      const count = (this._mlsDecryptFailCounts.get(failKey) ?? 0) + 1;
      this._mlsDecryptFailCounts.set(failKey, count);
      console.error(`[SecureChannel] MLS 1:1 decrypt failed (${count}/${SecureChannel.MAX_MLS_DECRYPT_FAILS}) for ${failKey}:`, decryptErr);
      if (count >= SecureChannel.MAX_MLS_DECRYPT_FAILS) {
        const recoveryGroupId = (convGroupId ? this._persisted?.mlsGroups?.[convGroupId]?.mlsGroupId : null)
          ?? this._persisted?.mlsConversations?.[convId]?.mlsGroupId
          ?? groupId;
        if (recoveryGroupId && failKey) {
          // #440: catch up a behind member via /sync replay before re-joining.
          if (!(await this._resyncOnDivergence(failKey, recoveryGroupId))) {
            await this._triggerMlsEpochRecovery(failKey, recoveryGroupId);
          }
        }
      }
    }
  }

  private async _handleIncomingMessage(msgData: {
    message_id: string;
    conversation_id: string;
    sender_device_id: string;
    header_blob: string;
    ciphertext: string;
    created_at: string;
    message_group_id?: string;
    topic_id?: string;
    span_id?: string;
    parent_span_id?: string;
    message_type?: string;
    priority?: string;
    envelope_version?: string;
  }): Promise<void> {
    // Don't decrypt our own messages
    if (msgData.sender_device_id === this._deviceId) return;

    // Dedup: skip if this message was already processed during sync
    if (this._syncMessageIds?.has(msgData.message_id)) {
      return;
    }

    const convId = msgData.conversation_id;
    const session = this._sessions.get(convId);
    if (!session) {
      // No session for this conversation — trigger resync to establish one
      console.warn(
        `[SecureChannel] No session for conversation ${convId}, requesting resync`,
      );
      const RESYNC_COOLDOWN_MS = 5 * 60 * 1000;
      const lastResync = this._lastResyncRequest.get(convId) ?? 0;
      if (
        Date.now() - lastResync > RESYNC_COOLDOWN_MS &&
        this._ws &&
        this._persisted
      ) {
        this._lastResyncRequest.set(convId, Date.now());
        this._ws.send(
          JSON.stringify({
            event: "resync_request",
            data: {
              conversation_id: convId,
              reason: "no_session",
              identity_public_key: this._persisted.identityKeypair.publicKey,
              ephemeral_public_key:
                this._persisted.ephemeralKeypair.publicKey,
              epoch: this._persisted.sessions[convId]?.epoch,
            },
          }),
        );
        this.emit("resync_requested", {
          conversationId: convId,
          reason: "no_session",
        });
      }
      return;
    }

    const encrypted = transportToEncryptedMessage({
      header_blob: msgData.header_blob,
      ciphertext: msgData.ciphertext,
    });

    const ratchetSnapshot = session.ratchet.serialize();
    let plaintext: string;
    try {
      plaintext = session.ratchet.decrypt(encrypted);
    } catch (decryptErr) {
      console.error(`[SecureChannel] Decrypt failed for conv ${convId.slice(0, 8)}... — restoring ratchet:`, decryptErr);
      try {
        session.ratchet = DoubleRatchet.deserialize(ratchetSnapshot);
      } catch (restoreErr) {
        console.error("[SecureChannel] Ratchet restore failed:", restoreErr);
      }

      // Initiate resync if within cooldown window
      const RESYNC_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes
      const lastResync = this._lastResyncRequest.get(convId) ?? 0;
      if (Date.now() - lastResync > RESYNC_COOLDOWN_MS && this._ws && this._persisted) {
        this._lastResyncRequest.set(convId, Date.now());
        const idPubHex = this._persisted.identityKeypair.publicKey;
        const ephPubHex = this._persisted.ephemeralKeypair.publicKey;
        this._ws.send(
          JSON.stringify({
            event: "resync_request",
            data: {
              conversation_id: convId,
              reason: "decrypt_failure",
              identity_public_key: idPubHex,
              ephemeral_public_key: ephPubHex,
              epoch: this._persisted?.sessions[convId]?.epoch,
            },
          }),
        );
        console.log(`[SecureChannel] Sent resync_request for conv ${convId.slice(0, 8)}...`);
        this.emit("resync_requested", { conversationId: convId, reason: "decrypt_failure" });
      }
      return;
    }

    // ACK delivery to server
    this._sendAck(msgData.message_id);

    // Mark session as activated on first owner message — persist immediately
    // Also activate ALL sibling sessions so agent fans out replies to all owner devices
    if (!session.activated) {
      session.activated = true;
      console.log(`[SecureChannel] Session ${convId.slice(0, 8)}... activated by first owner message`);
      if (this._persisted?.sessions[convId]) {
        this._persisted.sessions[convId].activated = true;
      }
      this._activateSiblings(convId);
      await this._persistState();
    }

    // Parse structured payload (new format) or treat as raw text (legacy)
    let messageText: string;
    let messageType: string;
    let attachmentInfo: any = null;
    let decisionReply: DecisionResponse | undefined;
    try {
      const parsed = JSON.parse(plaintext);
      messageType = parsed.type || "message";
      messageText = envelopeText(parsed, plaintext);
      if (parsed.attachment) {
        attachmentInfo = parsed.attachment;
      }
      decisionReply = decisionReplyFromEnvelope(parsed);
    } catch {
      // Legacy plain string format
      messageType = "message";
      messageText = plaintext;
    }

    if (messageType === "session_init") {
      // New device session activated — replay message history
      console.log(`[SecureChannel] session_init received for ${convId.slice(0, 8)}..., replaying history`);
      await this._replayHistoryToSession(convId);
      await this._persistState();
      return;
    }

    // --- Workspace file operations (no scan — these are structured commands) ---
    if (messageType === "workspace_file_upload") {
      try {
        const parsed = JSON.parse(plaintext);
        const workspaceDir = this._resolveWorkspaceDir();
        const { handleWorkspaceUpload } = await import("./workspace-handlers.js");
        const result = await handleWorkspaceUpload(parsed, workspaceDir);
        await this._sendStructuredReply(convId, { type: "workspace_file_ack", filename: parsed.filename, ...result });
      } catch (err: any) {
        await this._sendStructuredReply(convId, { type: "workspace_file_ack", status: "error", error: err.message });
      }
      return;
    }
    if (messageType === "workspace_file_list") {
      try {
        const workspaceDir = this._resolveWorkspaceDir();
        const { handleWorkspaceList } = await import("./workspace-handlers.js");
        const result = await handleWorkspaceList(workspaceDir);
        await this._sendStructuredReply(convId, { type: "workspace_file_list_response", ...result });
      } catch (err: any) {
        await this._sendStructuredReply(convId, { type: "workspace_file_list_response", files: [], error: err.message });
      }
      return;
    }
    if (messageType === "workspace_file_read") {
      try {
        const parsed = JSON.parse(plaintext);
        const workspaceDir = this._resolveWorkspaceDir();
        const { handleWorkspaceRead } = await import("./workspace-handlers.js");
        const result = await handleWorkspaceRead(parsed, workspaceDir);
        await this._sendStructuredReply(convId, { type: "workspace_file_read_response", ...result });
      } catch (err: any) {
        await this._sendStructuredReply(convId, { type: "workspace_file_read_response", error: err.message });
      }
      return;
    }

    // Inbound scan — after decryption
    if (this._scanEngine) {
      const scanResult = this._scanEngine.scanInbound(messageText);
      if (scanResult.status === "blocked") {
        this.emit("scan_blocked", { direction: "inbound", violations: scanResult.violations });
        return; // drop the message
      }
    }

    // A decision answer is emitted like a message (the agent still reads its
    // text) — `decision_response` envelopes used to be dropped on this path.
    if (messageType === "message" || (messageType === "decision_response" && decisionReply)) {
      const topicId = msgData.topic_id;

      // Track last inbound topic so replies default to the correct channel —
      // for THIS conversation's group only (#1416).
      this._rememberIncomingTopic(convId, undefined, topicId);

      // Handle attachment: download, decrypt, save to disk
      let attachData: AttachmentData | undefined;
      if (attachmentInfo) {
        try {
          const { filePath, decrypted } = await this._downloadAndDecryptAttachment(attachmentInfo);
          attachData = {
            filename: attachmentInfo.filename,
            mime: attachmentInfo.mime,
            size: decrypted.length,
            filePath,
          };

          // For images: include base64 so LLM vision can see the content
          if (attachmentInfo.mime.startsWith("image/")) {
            attachData.base64 = `data:${attachmentInfo.mime};base64,${bytesToBase64(decrypted)}`;
          }

          // For text-based files: extract content inline
          const textMimes = ["text/", "application/json", "application/xml", "application/csv"];
          if (textMimes.some((m) => attachmentInfo.mime.startsWith(m))) {
            attachData.textContent = new TextDecoder().decode(decrypted);
          }
        } catch (err) {
          console.error(`[SecureChannel] Failed to download attachment:`, err);
        }
      }

      // Store only normalized application text for cross-device replay.
      await this._captureHistoryApplication(
        "owner",
        plaintext,
        "dm",
        topicId,
        msgData.created_at,
        messageType,
        { conversationId: convId },
      );

      // Build display text with attachment content
      let emitText = messageText;
      if (attachData) {
        if (attachData.textContent) {
          emitText = `[Attachment: ${attachData.filename} (${attachData.mime}) saved to ${attachData.filePath}]\n---\n${attachData.textContent}\n---\n\n${messageText}`;
        } else if (attachData.base64) {
          emitText = `[Image attachment: ${attachData.filename} saved to ${attachData.filePath}]\nUse your Read tool to view this image file.\n\n${messageText}`;
        } else {
          emitText = `[Attachment: ${attachData.filename} saved to ${attachData.filePath}]\n\n${messageText}`;
        }
      }

      // Resolve room membership — check if this conversation belongs to a room
      const roomId = this._roomIdForConversation(convId);

      // Update room context when a room message arrives (sticky — never cleared by 1:1 traffic)
      if (roomId) this._lastInboundRoomId = roomId;

      // Emit to agent developer
      const metadata: MessageMetadata = {
        messageId: msgData.message_id,
        conversationId: convId,
        timestamp: msgData.created_at,
        topicId,
        attachment: attachData,
        spanId: msgData.span_id,
        parentSpanId: msgData.parent_span_id,
        // A decision answer only counts from the owner's 1:1 — never a room.
        messageType: decisionReply && !roomId ? "decision_response" : (msgData.message_type ?? "text"),
        priority: msgData.priority ?? "normal",
        envelopeVersion: msgData.envelope_version ?? "1.0.0",
        roomId,
        ...(decisionReply && !roomId ? { decision: decisionReply } : {}),
        // #1223 — the pre-fold text. This DR `message` path also sets `roomId`
        // and folds an attachment into `emitText`, so without this a room
        // message that reaches the gate here would fall back to the folded
        // text (the same bug the MLS room path fixes). `messageText` is the
        // envelope text before the fold; `emitText` is the folded copy.
        caption: messageText,
      };

      // #1418: the person involved in what the agent says next — 1:1 only.
      if (!roomId) {
        this._noteInboundDm(convId, undefined, {
          createdAt: msgData.created_at,
          senderDeviceId: msgData.sender_device_id,
        });
        this._reconcileForUnmappedConversation(convId);
      }
      this.emit("message", emitText, metadata);
      Promise.resolve(this.config.onMessage?.(emitText, metadata)).catch((err: unknown) => {
        console.error("[SecureChannel] onMessage callback error:", err);
      });

      // Relay to sibling sessions as sync messages
      await this._relaySyncToSiblings(convId, session.ownerDeviceId, messageText, topicId);
    }
    // If type === "sync", agent ignores it (sync is for owner devices only)

    // Track last message timestamp for offline sync
    if (this._persisted) {
      this._persisted.lastMessageTimestamp = msgData.created_at;
    }

    // Persist all ratchet states after decrypt
    await this._persistState();
  }

  /**
   * Download an encrypted attachment blob, decrypt it, verify integrity,
   * and save the plaintext file to disk.
   */
  /** Where an attachment may be written — always directly inside `attachDir` (#1143).
   *
   * The filename arrives INSIDE the envelope and is chosen by whoever sent the
   * message. `join(attachDir, info.filename)` resolves `..` normally, so
   * `../../agentvault.json` wrote over the device credential store. Only the
   * final path component is honoured, and anything that is not a usable name
   * falls back to a fixed one.
   */
  private _safeAttachmentPath(attachDir: string, filename: string): string {
    const raw = String(filename ?? "");
    // Split on BOTH separators: a POSIX `basename` leaves `..\..\x` intact on
    // a Windows-authored name, which is still a traversal on some hosts.
    const last = raw.split(/[\\/]/).pop() ?? "";
    const cleaned = last.replace(/\0/g, "").trim();
    const safe = cleaned === "" || cleaned === "." || cleaned === ".." ? "attachment.bin" : cleaned;
    return join(attachDir, safe);
  }

  /** Download an attachment and fold it into the text the agent sees (#1139).
   *
   * Extracted so the MLS receive path can use it too. It used to live inline in
   * `_handleIncomingMessage`, which meant attachments worked on the Double
   * Ratchet path ONLY — and hermes drops DR by design (`secure_channel.py:594`,
   * "DR fallback is logged-and-dropped"), so no single transport could serve
   * both client families. That is why an attachment reached ohtani and never
   * reached smartcoder2 on 2026-08-31.
   *
   * Returns the text unchanged when there is no attachment or the fetch fails —
   * but a failure is LOGGED and announced in the text, never silent.
   */
  private async _augmentWithAttachment(
    attachmentInfo: any,
    baseText: string,
    roomId?: string,
    senderDeviceId?: string,
  ): Promise<string> {
    if (!attachmentInfo) return baseText;
    try {
      const { filePath, decrypted } = await this._downloadAndDecryptAttachment(attachmentInfo);
      const mime = String(attachmentInfo.mime ?? "application/octet-stream");
      const name = String(attachmentInfo.filename ?? "attachment");
      // #1181: remember it for the room's next attachment-less turn.
      if (roomId) this._recentRoomAttachments.set(roomId, { filePath, name, at: Date.now(), senderDeviceId });
      const textMimes = ["text/", "application/json", "application/xml", "application/csv"];
      if (textMimes.some((m) => mime.startsWith(m))) {
        const content = new TextDecoder().decode(decrypted);
        return `[Attachment: ${name} (${mime}) saved to ${filePath}]\n---\n${content}\n---\n\n${baseText}`;
      }
      if (mime.startsWith("image/")) {
        return `[Image attachment: ${name} saved to ${filePath}]\nUse your Read tool to view this image file.\n\n${baseText}`;
      }
      return `[Attachment: ${name} saved to ${filePath}]\n\n${baseText}`;
    } catch (err) {
      console.error("[SecureChannel] Failed to download attachment:", err);
      return `[Attachment: ${String(attachmentInfo.filename ?? "file")} was sent but could not be retrieved: ${err}]\n\n${baseText}`;
    }
  }

  private async _downloadAndDecryptAttachment(info: {
    blobId: string;
    blobUrl: string;
    fileKey: string;
    fileNonce: string;
    digest: string;
    filename: string;
    mime: string;
    size: number;
  }): Promise<{ filePath: string; decrypted: Uint8Array }> {
    const attachDir = join(this.config.dataDir, "attachments");
    await mkdir(attachDir, { recursive: true });

    // Download encrypted blob from API.
    //
    // #1152 — `device_id` is REQUIRED for a room attachment and ignored for a
    // 1:1 one. Rooms are routinely cross-tenant, so the server authorizes a
    // room blob by room membership rather than tenant equality, and it cannot
    // infer which device is asking. Sent unconditionally: a conditional would
    // need to know whether this blob belongs to a room, which is exactly the
    // fact the caller does not have here.
    const sep = info.blobUrl.includes("?") ? "&" : "?";
    const url = `${this.config.apiUrl}${info.blobUrl}${sep}device_id=${encodeURIComponent(this._deviceId ?? "")}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${this._deviceJwt}` },
    });

    if (!res.ok) {
      throw new Error(`Attachment download failed: ${res.status}`);
    }

    const buffer = await res.arrayBuffer();
    const encryptedData = new Uint8Array(buffer);

    // Verify integrity
    const digest = computeFileDigest(encryptedData);
    if (digest !== info.digest) {
      throw new Error("Attachment digest mismatch — possible tampering");
    }

    // Decrypt
    const fileKey = base64ToBytes(info.fileKey);
    const fileNonce = base64ToBytes(info.fileNonce);
    const decrypted = decryptFile(encryptedData, fileKey, fileNonce);

    // Save to disk. #1143 — the filename is sender-controlled; never join it raw.
    const filePath = this._safeAttachmentPath(attachDir, info.filename);
    await writeFile(filePath, decrypted);
    console.log(`[SecureChannel] Attachment saved: ${filePath} (${decrypted.length} bytes)`);

    return { filePath, decrypted };
  }

  /**
   * Upload an attachment file: encrypt, upload to server, return metadata
   * for inclusion in the message envelope.
   */
  private async _uploadAttachment(
    filePath: string,
    conversationId: string,
  ): Promise<{
    blobId: string;
    blobUrl: string;
    fileKey: string;
    fileNonce: string;
    digest: string;
    filename: string;
    mime: string;
    size: number;
  }> {
    const data = await readFile(filePath);
    const plainData = new Uint8Array(data);

    const result = encryptFile(plainData);

    // Upload encrypted blob via multipart form
    const { Blob: NodeBlob, FormData: NodeFormData } = await import("node:buffer").then(
      () => globalThis,
    );
    const formData = new FormData();
    formData.append("conversation_id", conversationId);
    formData.append(
      "file",
      new Blob([result.encryptedData.buffer as ArrayBuffer], { type: "application/octet-stream" }),
      "attachment.bin",
    );

    const res = await fetch(`${this.config.apiUrl}/api/v1/attachments/upload`, {
      method: "POST",
      headers: { Authorization: `Bearer ${this._deviceJwt}` },
      body: formData,
    });

    if (!res.ok) {
      const detail = await res.text();
      throw new Error(`Attachment upload failed (${res.status}): ${detail}`);
    }

    const resp: any = await res.json();
    const filename = filePath.split("/").pop() || "file";

    return {
      blobId: resp.blob_id,
      blobUrl: resp.blob_url,
      fileKey: bytesToBase64(result.fileKey),
      fileNonce: bytesToBase64(result.fileNonce),
      digest: result.digest,
      filename,
      mime: "application/octet-stream",
      size: plainData.length,
    };
  }

  /**
   * Send a message with an attached file. Encrypts the file, uploads it,
   * then sends the envelope with attachment metadata via Double Ratchet.
   */
  async sendWithAttachment(
    plaintext: string,
    filePath: string,
    options?: { topicId?: string } & OwnerRouting,
  ): Promise<void> {
    if (this._state !== "ready" || this._sessions.size === 0 || !this._ws) {
      throw new Error("Channel is not ready");
    }

    const messageGroupId = randomUUID();
    const pendingWsSends: string[] = [];
    const frameHistoryContexts: string[] = [];

    // #1418: the blob is uploaded under the conversation of the person who
    // receives it (it used to be the PRIMARY conversation's, whoever asked),
    // and only that person's sessions get the envelope.
    const buckets = this._ownerFileRecipients(options);
    if (buckets.length === 0) {
      // #1418: reaching nobody is a failure, not a quiet success — deliver()
      // turns this into ok:false so the caller knows the file did not go.
      console.warn("[SecureChannel] sendWithAttachment(): no live session for the person involved — nothing sent");
      throw new Error("No conversation is ready to receive this file");
    }
    for (const bucket of buckets) {
      const attachMeta = await this._uploadAttachment(filePath, bucket.uploadConvId);

      for (const convId of bucket.convIds) {
        const session = this._sessions.get(convId)!;

        // #1416: each conversation's frame (and the envelope inside it) carries
        // the topic of that conversation's own group.
        // No last-incoming fallback here: this path never had one.
        const topicId = this._topicForFrame(undefined, convId, options?.topicId, false, false);
        const envelope = JSON.stringify({
          type: "message",
          text: plaintext,
          topicId,
          attachment: attachMeta,
        });

        const encrypted = session.ratchet.encrypt(envelope);
        const transport = encryptedMessageToTransport(encrypted);

        pendingWsSends.push(
          JSON.stringify({
            event: "message",
            data: {
              conversation_id: convId,
              header_blob: transport.header_blob,
              ciphertext: transport.ciphertext,
              message_group_id: messageGroupId,
              topic_id: topicId,
            },
          }),
        );
        frameHistoryContexts.push(this._dmHistoryContext(topicId, undefined, convId));
      }
    }

    // Persist ratchet states BEFORE sending over WS to avoid desync on crash
    await this._persistState();

    let deliveredFrames = 0;
    let historyContext: string | undefined;
    for (const [i, frame] of pendingWsSends.entries()) {
      if (this._sendApplicationFrame(frame)) {
        if (deliveredFrames === 0) historyContext = frameHistoryContexts[i];
        deliveredFrames += 1;
      }
    }
    if (deliveredFrames > 0) {
      await this._appendHistory("agent", plaintext, historyContext ?? this._historyContext("dm"), undefined, messageGroupId);
    }
  }

  /**
   * Relay an owner's message to all sibling sessions as encrypted sync messages.
   * This allows all owner devices to see messages from any single device.
   */
  private async _relaySyncToSiblings(
    sourceConvId: string,
    senderOwnerDeviceId: string,
    messageText: string,
    topicId?: string,
  ): Promise<void> {
    if (!this._ws || this._sessions.size <= 1) return;

    // Build set of room conversation IDs to exclude from sync relay
    // (room pairwise sessions should NOT receive 1:1 sync messages)
    const roomConvIds = new Set<string>();
    if (this._persisted?.rooms) {
      for (const room of Object.values(this._persisted.rooms)) {
        for (const cid of room.conversationIds) {
          roomConvIds.add(cid);
        }
      }
    }

    const syncPayload = JSON.stringify({
      type: "sync",
      sender: senderOwnerDeviceId,
      text: messageText,
      ts: new Date().toISOString(),
      topicId,
    });

    const pendingWsSends: string[] = [];

    // #1416: a "sibling" is another device of the SAME person — the same
    // conversation group. Since #1399 another person's 1:1 with this agent is
    // a different group; relaying the owner's words there would show person
    // A's message in person B's chat. Fails CLOSED: a sibling is relayed to
    // only when both groups are known and equal. An agent that knows no
    // groups at all keeps the old behaviour (it has only one person).
    const sourceGid = this._sessionGroupIds.get(sourceConvId);
    const knowsGroups = this._sessionGroupIds.size > 0;

    for (const [siblingConvId, siblingSession] of this._sessions) {
      if (siblingConvId === sourceConvId) continue;
      // Don't relay to sessions where owner hasn't sent their first message yet
      if (!siblingSession.activated) continue;
      const siblingGid = this._sessionGroupIds.get(siblingConvId);
      if (knowsGroups && !(sourceGid && siblingGid && siblingGid === sourceGid)) continue;
      // Skip room pairwise sessions — those use sendToRoom()
      if (roomConvIds.has(siblingConvId)) continue;

      const syncEncrypted = siblingSession.ratchet.encrypt(syncPayload);
      const syncTransport = encryptedMessageToTransport(syncEncrypted);

      pendingWsSends.push(
        JSON.stringify({
          event: "message",
          data: {
            conversation_id: siblingConvId,
            header_blob: syncTransport.header_blob,
            ciphertext: syncTransport.ciphertext,
          },
        }),
      );
    }

    // Persist ratchet states BEFORE sending over WS to avoid desync on crash
    await this._persistState();

    for (const frame of pendingWsSends) {
      this._ws.send(frame);
    }
  }

  /**
   * Resolve the agent's workspace directory.
   * Looks for OpenClaw workspace config, falls back to default path.
   */
  private _resolveWorkspaceDir(): string {
    const homedir = osHomedir();
    const agentName = this.config.agentName;

    // Lead agent uses root OpenClaw workspace
    if (this._persisted?.agentRole === "lead") {
      return join(homedir, ".openclaw", "workspace");
    }

    // Try to read OpenClaw config for workspace path
    try {
      const configPath = join(homedir, ".openclaw", "openclaw.json");
      const raw = readFileSync(configPath, "utf-8");
      const config = JSON.parse(raw);
      const agents = config?.agents?.list;
      if (Array.isArray(agents) && agentName) {
        // Find the agent matching this account by id or name
        for (const agent of agents) {
          if ((agent.id === agentName || agent.name === agentName) &&
              agent.workspace && typeof agent.workspace === "string") {
            return agent.workspace;
          }
        }
      }
    } catch {
      // Config not found or unreadable — fall through to default
    }

    // Fallback: OpenClaw convention is ~/.openclaw/workspace-<name>/
    if (agentName && agentName !== "CLI Agent" && agentName !== "OpenClaw Agent") {
      return join(homedir, ".openclaw", `workspace-${agentName}`);
    }

    // Last resort: default workspace
    return join(homedir, ".openclaw", "workspace");
  }

  /**
   * Send a structured JSON reply to a specific conversation.
   * Encrypts the payload via the conversation's ratchet and sends via WebSocket.
   */
  private async _sendStructuredReply(convId: string, payload: Record<string, unknown>): Promise<void> {
    const session = this._sessions.get(convId);
    if (!session || !this._ws) {
      console.warn(`[SecureChannel] Cannot send structured reply — no session for ${convId.slice(0, 8)}...`);
      return;
    }

    const plaintext = JSON.stringify(payload);
    const encrypted = session.ratchet.encrypt(plaintext);
    const transport = encryptedMessageToTransport(encrypted);

    // Persist ratchet state BEFORE sending over WS to avoid desync on crash
    await this._persistState();

    this._ws.send(
      JSON.stringify({
        event: "message",
        data: {
          conversation_id: convId,
          header_blob: transport.header_blob,
          ciphertext: transport.ciphertext,
        },
      }),
    );
  }

  /**
   * Send stored message history to a newly-activated session.
   * Batches all history into a single encrypted message.
   */
  private async _replayHistoryToSession(convId: string): Promise<void> {
    const session = this._sessions.get(convId);
    if (!session || !session.activated || !this._ws) return;

    // #1416: replay only this conversation's group's history.
    const history = this._selectHistoryContext("dm", undefined, { conversationId: convId });
    if (history.length === 0) {
      console.log(`[SecureChannel] No history to replay for ${convId.slice(0, 8)}...`);
      return;
    }

    console.log(
      `[SecureChannel] Replaying ${history.length} messages to session ${convId.slice(0, 8)}...`,
    );

    const replayPayload = JSON.stringify({
      type: "history_replay",
      messages: history,
    });

    const encrypted = session.ratchet.encrypt(replayPayload);
    const transport = encryptedMessageToTransport(encrypted);

    // Persist ratchet state BEFORE sending over WS to avoid desync on crash
    await this._persistState();

    this._ws.send(
      JSON.stringify({
        event: "message",
        data: {
          conversation_id: convId,
          header_blob: transport.header_blob,
          ciphertext: transport.ciphertext,
        },
      }),
    );
  }

  /**
   * Handle a device_linked event: a new owner device has joined.
   * Fetches the new device's public keys, performs X3DH, and initializes
   * a new ratchet session.
   */
  private async _handleDeviceLinked(event: {
    conversation_id: string;
    owner_device_id: string;
    group_id?: string;
    owner_identity_public_key?: string;
    owner_ephemeral_public_key?: string;
    owner_hub_id?: string;
  }): Promise<void> {
    console.log(
      `[SecureChannel] New owner device linked: ${event.owner_device_id.slice(0, 8)}...`,
    );

    try {
      // #1416/#1418: learn which group the new conversation belongs to FIRST —
      // even an event we cannot open a session for says whose group that is,
      // and without it the group would look like nobody's (or somebody new).
      // Only when unknown — the reconcile (#1034) owns corrections.
      if (event.group_id && !this._sessionGroupIds.has(event.conversation_id)) {
        this._sessionGroupIds.set(event.conversation_id, event.group_id);
        this._persisted!.conversationGroupIds = Object.fromEntries(this._sessionGroupIds);
      }

      if (!event.owner_identity_public_key) {
        console.error(
          `[SecureChannel] device_linked event missing owner keys for conv ${event.conversation_id.slice(0, 8)}...`,
        );
        await this._persistState();
        return;
      }

      const identity = this._persisted!.identityKeypair;
      const ephemeral = this._persisted!.ephemeralKeypair;

      // Perform X3DH as responder with the new owner device
      const sharedSecret = performX3DH({
        myIdentityPrivate: hexToBytes(identity.privateKey),
        myEphemeralPrivate: hexToBytes(ephemeral.privateKey),
        theirIdentityPublic: hexToBytes(event.owner_identity_public_key),
        theirEphemeralPublic: hexToBytes(
          event.owner_ephemeral_public_key ?? event.owner_identity_public_key,
        ),
        isInitiator: false,
      });

      // Initialize ratchet as receiver
      const ratchet = DoubleRatchet.initReceiver(sharedSecret, {
        publicKey: hexToBytes(identity.publicKey),
        privateKey: hexToBytes(identity.privateKey),
        keyType: "ed25519",
      }, hexToBytes(event.owner_identity_public_key));

      this._sessions.set(event.conversation_id, {
        ownerDeviceId: event.owner_device_id,
        ratchet,
        activated: false, // Wait for owner's first message
        epoch: 1,
      });

      // Persist
      this._persisted!.sessions[event.conversation_id] = {
        ownerDeviceId: event.owner_device_id,
        ratchetState: ratchet.serialize(),
        activated: false,
        epoch: 1,
      };
      // Persist owner hub_id if provided
      if (event.owner_hub_id) {
        this._persisted!.ownerHubId = event.owner_hub_id;
      }
      // (#1416: the conversation's group was recorded above.)
      await this._persistState();

      console.log(
        `[SecureChannel] Session initialized for device ${event.owner_device_id.slice(0, 8)}... (conv ${event.conversation_id.slice(0, 8)}...)`,
      );
    } catch (err) {
      console.error(
        `[SecureChannel] Failed to handle device_linked:`,
        err,
      );
      this.emit("error", err);
    }
  }

  /**
   * Handle a resync_request from the owner (owner-initiated ratchet re-establishment).
   * Re-derives shared secret via X3DH as responder, initializes fresh receiver ratchet,
   * and sends resync_ack back with agent's public keys.
   */
  private async _handleResyncRequest(data: {
    conversation_id: string;
    reason?: string;
    identity_public_key: string;
    ephemeral_public_key: string;
    sender_device_id?: string;
  }): Promise<void> {
    const convId = data.conversation_id;
    console.log(
      `[SecureChannel] Received resync_request for conv ${convId.slice(0, 8)}... (reason: ${data.reason ?? "unknown"})`,
    );

    try {
      if (!this._persisted) {
        console.error("[SecureChannel] Cannot handle resync — no persisted state");
        return;
      }

      const identity = this._persisted.identityKeypair;
      const ephemeral = this._persisted.ephemeralKeypair;

      // Perform X3DH as responder (agent is always responder in 1:1)
      const sharedSecret = performX3DH({
        myIdentityPrivate: hexToBytes(identity.privateKey),
        myEphemeralPrivate: hexToBytes(ephemeral.privateKey),
        theirIdentityPublic: hexToBytes(data.identity_public_key),
        theirEphemeralPublic: hexToBytes(data.ephemeral_public_key),
        isInitiator: false,
      });

      // Initialize fresh receiver ratchet
      const ratchet = DoubleRatchet.initReceiver(sharedSecret, {
        publicKey: hexToBytes(identity.publicKey),
        privateKey: hexToBytes(identity.privateKey),
        keyType: "ed25519",
      }, hexToBytes(data.identity_public_key));

      // Update session
      const existingSession = this._sessions.get(convId);
      const existingPersisted = this._persisted.sessions[convId];
      const ownerDeviceId = data.sender_device_id ?? existingSession?.ownerDeviceId ?? "";
      const newEpoch = ((existingPersisted?.epoch ?? existingSession?.epoch ?? 0) + 1);
      this._sessions.set(convId, {
        ownerDeviceId,
        ratchet,
        activated: false, // Wait for owner's encrypted session_init
        epoch: newEpoch,
      });

      // Persist
      this._persisted.sessions[convId] = {
        ownerDeviceId,
        ratchetState: ratchet.serialize(),
        activated: false,
        epoch: newEpoch,
      };
      await this._persistState();

      // Send resync_ack with agent's public keys
      if (this._ws) {
        this._ws.send(
          JSON.stringify({
            event: "resync_ack",
            data: {
              conversation_id: convId,
              identity_public_key: identity.publicKey,
              ephemeral_public_key: ephemeral.publicKey,
              epoch: newEpoch,
            },
          }),
        );
      }

      console.log(
        `[SecureChannel] Resync complete for conv ${convId.slice(0, 8)}... — waiting for owner session_init`,
      );
      this.emit("resync_completed", { conversationId: convId });
    } catch (err) {
      console.error(`[SecureChannel] Resync failed for conv ${convId.slice(0, 8)}...:`, err);
      this.emit("error", err);
    }
  }

  // _sendRoomResyncIfCooled removed — rooms are MLS-only now
  // _handleRoomMessage removed — rooms are MLS-only now

  /**
   * Handle credential protocol messages (grant, revoke, request).
   * These are intercepted before reaching the agent's onMessage callback.
   */
  private _handleCredentialMessage(
    roomId: string,
    messageType: string,
    plaintext: string,
    messageId?: string,
  ): void {
    try {
      const payload = JSON.parse(plaintext);

      if (messageType === "credential_grant") {
        const grant = payload as CredentialGrantPayload;

        // Replay prevention
        if (grant.nonce && !this._credentialStore.checkNonce(roomId, grant.nonce)) {
          console.warn(`[SecureChannel] Credential grant replay detected for room ${roomId.slice(0, 8)}..., ignoring`);
          return;
        }

        const keys: string[] = [];
        for (const cred of grant.credentials || []) {
          this._credentialStore.grant(roomId, {
            key: cred.key,
            value: cred.value,
            type: cred.type,
            scope: cred.scope,
            grantedAt: grant.timestamp || new Date().toISOString(),
            agreementId: grant.agreement_id,
            roomId,
          });
          keys.push(cred.key);
        }

        // Log without values
        console.log(
          `[SecureChannel] Credentials granted for room ${roomId.slice(0, 8)}...: ${keys.join(", ")} (${keys.length} keys)`,
        );

        // Send ACK back to room
        this._sendCredentialAck(roomId, grant.agreement_id, keys, "stored");

        this.emit("credentials_granted", { roomId, keys, agreementId: grant.agreement_id });
      } else if (messageType === "credential_revoke") {
        const revoke = payload as CredentialRevokePayload;
        const revoked: string[] = [];

        for (const key of revoke.credential_keys || []) {
          if (this._credentialStore.revoke(roomId, key)) {
            revoked.push(key);
          }
        }

        console.log(
          `[SecureChannel] Credentials revoked for room ${roomId.slice(0, 8)}...: ${revoked.join(", ")} (reason: ${revoke.reason || "none"})`,
        );

        this._sendCredentialAck(roomId, revoke.agreement_id, revoked, "revoked");

        this.emit("credentials_revoked", { roomId, keys: revoked, agreementId: revoke.agreement_id });
      }
      // credential_request is from agent → owner, not handled here (it originates from us)
    } catch (err) {
      console.error("[SecureChannel] Error handling credential message:", err);
    }

    // ACK the message itself
    if (messageId) {
      this._sendAck(messageId);
    }
  }

  /**
   * Send a credential_ack back to a room.
   */
  private _sendCredentialAck(roomId: string, agreementId: string, keys: string[], ackStatus: "stored" | "revoked"): void {
    const ack = JSON.stringify({
      type: "credential_ack",
      agreement_id: agreementId,
      acknowledged_keys: keys,
      status: ackStatus,
      timestamp: new Date().toISOString(),
    });

    // Send as room message using existing send path
    this.sendToRoom(roomId, ack, { messageType: "credential_ack" }).catch((err) => {
      console.warn("[SecureChannel] Failed to send credential ACK:", err);
    });
  }

  // --- Public credential accessors ---

  /** Get a specific renter credential for a room. */
  getCredential(roomId: string, key: string): import("./credential-store.js").RenterCredential | undefined {
    return this._credentialStore.get(roomId, key);
  }

  /** Get all renter credentials for a room (includes values — for agent context). */
  getCredentials(roomId: string): import("./credential-store.js").RenterCredential[] {
    return this._credentialStore.getAll(roomId);
  }

  /** Get credential key→value map for a room (for context injection). */
  getCredentialMap(roomId: string): Record<string, string> {
    return this._credentialStore.getCredentialMap(roomId);
  }

  /** Check if a specific credential exists for a room. */
  hasCredential(roomId: string, key: string): boolean {
    return this._credentialStore.has(roomId, key);
  }

  /** Purge all credentials for a room (call on rental end). */
  purgeRoomCredentials(roomId: string): void {
    this._credentialStore.purgeForRoom(roomId);
  }

  // _findConversationForSender removed — rooms are MLS-only now

  // ---------------------------------------------------------------------------
  // MLS room message handlers
  // ---------------------------------------------------------------------------

  /**
   * #1014: hand a room-framed frame to the handler that owns its group family.
   *
   * Returns true when the frame was routed. Routing means "give it to the right
   * handler" — NOT "decrypt it as a room message". Decrypting under the wrong
   * group is how MLS state gets corrupted, so an unplaceable group is left for
   * the caller to self-heal rather than guessed at.
   */
  private async _routeNonRoomGroupMessage(data: any, groupId: string): Promise<boolean> {
    const target = this._resolveGroupFamily(groupId);
    // "room" is not re-routed: the caller already tried that path and failed,
    // so a room match here would mean the room's manager is missing, which the
    // room path itself handles by requesting a Welcome.
    if (!target || target.family === "room") return false;

    console.log(
      `[SecureChannel] Room-framed message belongs to ${target.label} — ` +
        `routing to the handler that owns it (#1014)`,
    );

    switch (target.family) {
      case "shared1to1":
        await this._handleMessageMLS({ ...data, conversation_group_id: target.id });
        return true;
      case "conversation":
        await this._handleMessageMLS({ ...data, conversation_id: target.id });
        return true;
      case "a2a":
        await this._handleA2AMessageMLS({ ...data, a2a_channel_id: target.id });
        return true;
    }
  }

  private async _handleRoomMessageMLS(data: any): Promise<void> {
    const groupId = data.group_id;
    const senderDeviceId = data.sender_device_id;

    // #1013: every inbound frame reports the epoch the group was at when it
    // was produced — a lower bound on where the group is now.
    this._noteGroupEpoch(groupId, data.epoch);

    // Don't decrypt our own messages
    if (senderDeviceId === this._deviceId) return;

    // Find room by MLS group ID
    let roomId: string | undefined;
    for (const [rid, room] of Object.entries(this._persisted?.rooms ?? {})) {
      if ((room as RoomState).mlsGroupId === groupId) {
        roomId = rid;
        break;
      }
    }

    if (!roomId) {
      // Try matching by room_id directly (delivery queue includes room_id)
      if (data.room_id && this._persisted?.rooms?.[data.room_id]) {
        roomId = data.room_id as string;
        console.log(`[SecureChannel] Room ${roomId.slice(0, 8)} matched by room_id (group_id ${groupId?.slice(0, 8)} mismatch)`);
      } else {
        // #1014: this frame may not be a room message at all. `mls_sync` replay
        // framed EVERY non-commit message as `room_message_mls` regardless of
        // group family, so a 1:1 or A2A replay arrived here with no room_id,
        // matched nothing, and was dropped with a warn — the whole 1:1 catch-up
        // path was dead. The server now frames by family
        // (`mls_replay_framing.py`), but an agent runs against whatever backend
        // is deployed, so re-route here too.
        if (await this._routeNonRoomGroupMessage(data, groupId)) return;

        console.warn(`[SecureChannel] No room found for MLS group ${groupId?.slice(0, 8)}`);
        // #1014: and self-heal, the way `_handleMessageMLS` already does from
        // its own unresolved-group exit. The function was one call away and had
        // never been wired to this one.
        if (groupId) void this._requestWelcomeSelfHeal(groupId);
        return;
      }
    }

    // At this point roomId is guaranteed defined (guarded above)
    const resolvedRoomId: string = roomId as string;

    // Track room context so HTTP /send and OpenClaw tools auto-route to this room
    this._lastInboundRoomId = resolvedRoomId;

    const mlsGroup = this._mlsGroups.get(resolvedRoomId);
    console.log(`[SecureChannel] MLS decrypt attempt room=${resolvedRoomId.slice(0, 8)} group=${groupId?.slice(0, 8)} loaded=${!!mlsGroup} init=${mlsGroup?.isInitialized} mlsGroupsKeys=[${Array.from(this._mlsGroups.keys()).join(',')}]`);
    if (!mlsGroup?.isInitialized) {
      console.warn(`[SecureChannel] MLS group not loaded for room ${resolvedRoomId.slice(0, 8)}, requesting Welcome`);
      // MLS-only rooms: no DR fallback. Request a Welcome from the group creator.
      // Publish a fresh KeyPackage first so the creator can encrypt the Welcome to us.
      // Use sync lock (per groupId) to prevent concurrent KP publish races across agents.
      const roomLockId = `room-kp-${groupId}`;
      if (!await acquireA2ASyncLock(this.config.dataDir, roomLockId)) {
        console.log(`[SecureChannel] Room KP publish locked for group ${groupId?.slice(0, 8)}, skipping`);
        return;
      }
      try {
        const { MLSGroupManager: MLS } = await import("@agentvault/crypto");
        const mgr = new MLS();
        const kp = await mgr.generateKeyPackage(new TextEncoder().encode(this._deviceId!));
        const kpBytes = MLS.serializeKeyPackage(kp.publicPackage);
        const kpHex = Buffer.from(kpBytes).toString("hex");
        await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
          method: "POST",
          headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
          body: JSON.stringify({ key_package: kpHex, device_id: this._deviceId }),
        });
        // Store the key package bundle for joinFromWelcome later
        this._recordPublishedKp(kp);
        // Persist KP bundle so the Welcome handler can find it even after restart
        await savePendingKpBundle(this.config.dataDir, groupId!, kp, MLSGroupManager.serializeKeyPackage);
        // Request Welcome from creator
        await fetch(`${this.config.apiUrl}/api/v1/mls/groups/${groupId}/request-welcome`, {
          method: "POST",
          headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
          body: JSON.stringify({ device_id: this._deviceId }),
        });
        console.log(`[SecureChannel] Welcome requested for room ${resolvedRoomId.slice(0, 8)} group ${groupId?.slice(0, 8)}`);
      } catch (reqErr) {
        console.warn(`[SecureChannel] Welcome request failed for room ${resolvedRoomId.slice(0, 8)}:`, reqErr);
        await releaseA2ASyncLock(this.config.dataDir, roomLockId).catch(() => {});
      }
      return;
    }

    try {
      const ciphertext = Buffer.from(data.payload, "hex");
      const result = await mlsGroup.decrypt(new Uint8Array(ciphertext));
      // A successful decrypt proves the group is live: clear the run (the commit
      // counter already does this). Before the handshake return below.
      this._mlsDecryptFailCounts.delete(resolvedRoomId);

      // Persist updated MLS state
      await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mlsGroup.exportState()));

      if (result.isHandshake || !result.plaintext) {
        // Handshake message (commit/proposal) — no application data
        return;
      }

      const rawPlaintext = new TextDecoder().decode(result.plaintext);

      // Parse structured message envelope (same as pairwise/SK handlers)
      let messageText: string;
      let messageType: string;
      // #1152 — attachments were parsed on BOTH 1:1 paths and neither room one,
      // so a file sent to a room reached no agent. It went unnoticed because
      // rooms could not SEND an attachment at all (the pane posted a room id
      // where the API wanted a conversation id), which made the gap untestable
      // from either end. That is the same shape as #1139: an enumeration of
      // handlers that has to be kept in step by hand, and drifts.
      let roomAttachment: any = null;
      try {
        const parsed = JSON.parse(rawPlaintext);
        messageType = parsed.type || "message";
        messageText = envelopeText(parsed, rawPlaintext);
        if (parsed.attachment) roomAttachment = parsed.attachment;
      } catch {
        messageType = "message";
        messageText = rawPlaintext;
      }

      // Intercept credential protocol messages — handled internally
      if (CREDENTIAL_MESSAGE_TYPES.has(messageType)) {
        this._handleCredentialMessage(resolvedRoomId, messageType, rawPlaintext, data.message_id);
        if (data.created_at && this._persisted) {
          this._persisted.lastMessageTimestamp = data.created_at;
        }
        await this._persistState();
        return;
      }

      // Intercept history catch-up protocol messages
      if (CATCHUP_MESSAGE_TYPES.has(messageType)) {
        if (messageType === "history_catchup_request") {
          await this._handleHistoryCatchupRequest(resolvedRoomId, rawPlaintext, data.message_id);
        }
        // history_catchup_response is for web clients — agent ignores
        return;
      }

      // Only pass user-authored messages to the agent
      if (!ROOM_AGENT_TYPES.has(messageType)) {
        return;
      }

      if (data.message_id) {
        this._sendAck(data.message_id);
      }

      // Advance sync cursors
      if (data.created_at && this._persisted) {
        this._persisted.lastMessageTimestamp = data.created_at;
        // Track per-room MLS timestamp for same-epoch sync catch-up
        const roomState = this._persisted.rooms?.[resolvedRoomId];
        if (roomState) {
          (roomState as any).lastMlsMessageTs = data.created_at;
        }
      }

      await this._persistState();

      // GH #366: ensure the room roster is COMPLETE before surfacing the message.
      // The local roster is a join-time snapshot that can be missing members who
      // joined concurrently — so the first message gets an incomplete roster and
      // the agent frames a group room as a 1:1 DM. Refresh from the backend when
      // either (a) we haven't refreshed this room yet this process, or (b) the
      // sender isn't in the local roster (a member we don't know about). The
      // _roomRosterRefreshed flag keeps this to ~once per room (no refetch storm).
      {
        const known = this._persisted?.rooms?.[resolvedRoomId]?.members ?? [];
        const senderKnown = known.some((m: any) => m.deviceId === senderDeviceId);
        if (!this._roomRosterRefreshed.has(resolvedRoomId) || !senderKnown) {
          await this._refreshRoomRoster(resolvedRoomId);
        }
      }

      // #1152 — download, verify, decrypt and save, then fold the result into
      // the text, exactly as the 1:1 MLS path does. Placed BEFORE the history
      // append, the `room_message` emit and the sender prefix, so every consumer
      // of `messageText` below sees the same augmented value rather than three
      // slightly different ones.
      //
      // #1161 — but AFTER the protocol's identity-free half of the reply gate.
      // A caption with no mention token is addressed to nobody (#1107): no
      // consumer will answer, so nobody needs the file yet. Measured 09-03, room
      // 007: one bare file → seven downloads for the six agents that ignored it.
      // Defer it and fold it into the room's next @-addressed turn instead. Any
      // token at all fails OPEN here (fetch) — only the consumer can resolve
      // whether the name is its own, and starving the addressed agent is worse
      // than an extra download. `messageText` is still the raw caption here.
      //
      // #1223 — keep that raw caption. Every consumer's @ gate must decide on
      // what the sender TYPED, not on the folded file: a pasted chat log or a
      // source file with `@app.route` in it must not make an agent owe a
      // reply to a message whose caption addressed someone else. Carried as
      // `caption` on the emit and on `MessageMetadata` below.
      const caption = messageText;
      const addressed = parseMentions(messageText).length > 0;
      // #1459 — who sent each file whose content (or path) is folded into this
      // turn. A file is the sender's words as much as a message is, so a turn
      // carrying another member's file must not run with owner power.
      // `null` = a sender this process never learned (fail closed).
      const foldedFrom: (string | null)[] = [];
      if (roomAttachment && !addressed) {
        this._deferRoomAttachment(resolvedRoomId, roomAttachment, senderDeviceId);
      } else {
        const deferred = addressed ? this._takeDeferredRoomAttachments(resolvedRoomId) : [];
        for (const d of deferred) {
          messageText = await this._augmentWithAttachment(d.info, messageText, resolvedRoomId, d.senderDeviceId);
          foldedFrom.push(d.senderDeviceId ?? null);
        }
        if (roomAttachment) {
          messageText = await this._augmentWithAttachment(roomAttachment, messageText, resolvedRoomId, senderDeviceId);
          foldedFrom.push(senderDeviceId ?? null);
        } else if (deferred.length === 0) {
          const noted = this._withRecentRoomAttachmentNote(resolvedRoomId, messageText);
          messageText = noted.text;
          if (noted.from !== undefined) foldedFrom.push(noted.from);
        }
      }

      // Only a known human roster entry may grant room-tool authority. Missing
      // or unrecognized entity types remain unknown even if roster refresh failed.
      const roomMembers = this._persisted?.rooms?.[resolvedRoomId]?.members ?? [];
      const senderMember = roomMembers.find((m: any) => m.deviceId === senderDeviceId);
      const senderIsAgent = senderMember?.entityType === "agent" ? true
        : senderMember?.entityType === "owner" || senderMember?.entityType === "human" ? false
        : undefined;
      // #1459 — a human is not necessarily THIS agent's owner: a room can hold
      // another account's people. Owner-only power (a bridge's shell/write
      // lane) needs the sender's account to be this agent's own. The server
      // reports each member device's real account (`member_device_tenants`);
      // a member without one — an older server, a failed lookup — is not the
      // owner.
      const ownTenantId = this._deviceJwt ? this._decodeTenantIdForRecovery(this._deviceJwt) : null;
      const isOwnerMember = (deviceId: string | null | undefined): boolean => {
        const member = deviceId ? roomMembers.find((m: any) => m.deviceId === deviceId) : undefined;
        return (member?.entityType === "owner" || member?.entityType === "human")
          && ownTenantId !== null
          && typeof member?.tenantId === "string"
          && member.tenantId.toLowerCase() === ownTenantId;
      };
      const senderIsOwner = senderIsAgent === false && isOwnerMember(senderDeviceId);
      // #1459 — true when this turn carries a file from anyone but this
      // agent's own owner (another account, an agent, or a sender unknown).
      const foreignContent = foldedFrom.some((from) => !isOwnerMember(from));

      const metadata: MessageMetadata = {
        messageId: data.message_id ?? "",
        conversationId: "",
        timestamp: data.created_at ?? new Date().toISOString(),
        messageType: messageType,
        roomId: resolvedRoomId,
        senderDeviceId,
        senderIsAgent,
        senderIsOwner,
        foreignContent,
        senderName: senderMember?.displayName || "unknown",
        roomName: this._persisted?.rooms?.[resolvedRoomId]?.name,
        caption,
      };

      // Persist normalized room application text for contextual memory.
      const senderLabel = senderMember?.displayName || "someone";
      await this._captureHistoryApplication(
        "owner",
        rawPlaintext,
        "room",
        resolvedRoomId,
        data.created_at,
        messageType,
      );

      this.emit("room_message", {
        roomId: resolvedRoomId,
        senderDeviceId,
        senderName: senderLabel,
        plaintext: messageText,
        // #1223 — what the sender typed, before the attachment fold. The
        // bridges gate on this (falling back to `plaintext` when absent).
        caption,
        messageType,
        timestamp: data.created_at ?? new Date().toISOString(),
        // Bridges require an explicit human classification for room tools.
        // Reply policy separately uses this field for agent-loop prevention.
        senderIsAgent,
        // #1459 — owner-only power keys on these, never on senderIsAgent.
        senderIsOwner,
        foreignContent,
      });

      // Prefix message with sender name so the LLM knows who said it
      const contextualMessage = senderIsAgent
        ? messageText  // Don't prefix agent-to-agent messages
        : `[${senderLabel}]: ${messageText}`;

      Promise.resolve(this.config.onMessage?.(contextualMessage, metadata)).catch((err: unknown) => {
        console.error("[SecureChannel] onMessage callback error (MLS):", err);
      });
    } catch (err) {
      // Below our epoch = sealed before we joined = unreadable by forward secrecy.
      // Not divergence; do not count it (see the 1:1 handler and #920).
      if (classifyCommitFailure(data.epoch, mlsGroup.epoch) === "already-applied") {
        console.debug(`[SecureChannel] MLS room message at epoch ${String(data.epoch)} predates our epoch ${String(mlsGroup.epoch)} for ${resolvedRoomId.slice(0, 8)} — cannot decrypt, ignoring`);
        return;
      }
      const failKey = resolvedRoomId;
      const count = (this._mlsDecryptFailCounts.get(failKey) ?? 0) + 1;
      this._mlsDecryptFailCounts.set(failKey, count);
      console.error(`[SecureChannel] MLS room decrypt failed (${count}/${SecureChannel.MAX_MLS_DECRYPT_FAILS}) for ${resolvedRoomId.slice(0, 8)}:`, err);
      if (count >= SecureChannel.MAX_MLS_DECRYPT_FAILS && groupId) {
        // #440: an existing member that fell behind catches up by replaying the
        // missed commits via /sync FIRST; only fall back to re-join if it can't.
        if (!(await this._resyncOnDivergence(resolvedRoomId, groupId))) {
          await this._triggerMlsEpochRecovery(resolvedRoomId, groupId);
        }
      }
    }
  }

  /**
   * Handle a history catch-up request from a device that just rejoined a room.
   * Only the elected agent (smallest device_id among agents) responds.
   */
  private async _handleHistoryCatchupRequest(
    roomId: string,
    requestText: string,
    messageId?: string,
  ): Promise<void> {
    let request: { since_ts?: string | null; requesting_device_id?: string; retry?: boolean };
    try {
      request = JSON.parse(requestText);
    } catch {
      return;
    }
    if (typeof request.requesting_device_id !== "string") return;

    // Agent election: only the lexicographically smallest agent device_id responds
    const roomMembers = this._persisted?.rooms?.[roomId]?.members ?? [];
    const agentDeviceIds = roomMembers
      .filter((m: RoomMemberInfo) => m.entityType !== "owner" && m.entityType !== "human")
      .map((m: RoomMemberInfo) => m.deviceId)
      .sort();

    const isElected = agentDeviceIds.length > 0 && agentDeviceIds[0] === this._deviceId;

    if (!isElected && !request.retry) {
      console.log(
        `[SecureChannel] History catchup: not elected (elected=${agentDeviceIds[0]?.slice(0, 8)}), ignoring`,
      );
      return;
    }

    // On retry, any agent with history responds
    let filtered: HistoryEntry[];
    try {
      filtered = this._historyForCatchup("room", roomId, request.since_ts);
    } catch {
      return;
    }

    console.log(
      `[SecureChannel] History catchup for room ${roomId.slice(0, 8)}: ` +
      `${filtered.length} messages since ${request.since_ts || "beginning"} ` +
      `(total history: ${this.getRoomHistory(roomId, 100).length})`,
    );

    // Build response
    const responsePayload = JSON.stringify({
      type: "history_catchup_response",
      target_device_id: request.requesting_device_id,
      messages: filtered.map((e) => ({
        sender: e.sender,
        sender_name: e.sender === "agent" ? (this.config.agentName ?? "Agent") : "Owner",
        text: e.text,
        ts: e.ts,
      })),
      no_history: filtered.length === 0,
    });

    // Send via MLS
    try {
      await this.sendToRoom(roomId, responsePayload, { messageType: "history_catchup_response" });
      console.log(
        `[SecureChannel] Sent history catchup response: ${filtered.length} messages to ${request.requesting_device_id?.slice(0, 8)}`,
      );
    } catch (sendErr) {
      console.warn(`[SecureChannel] Failed to send history catchup response:`, sendErr);
    }
  }

  /**
   * Apply an inbound MLS commit to whichever group it addresses.
   *
   * Returns TRUE when the commit was applied, buffered, or handed to the
   * failure ladder — i.e. when this agent has taken responsibility for it and
   * the delivery-queue row may safely be acked. Returns FALSE when the commit
   * names a group we hold no state for at all, so the caller must NACK and let
   * the row survive.
   *
   * #1012: this used to loop `rooms` then `a2aChannels` and then END. A shared
   * 1:1 commit (`mlsGroups`) and a legacy per-conversation commit
   * (`mlsConversations`) matched neither. The call fell off the bottom with no
   * log, no buffer and no error — and because it did not THROW,
   * `_pullDeliveryQueue` acked the row and the server deleted it. An MLS commit
   * cannot be regenerated, so the agent stayed at epoch N while the group moved
   * to N+1 and every later send() encrypted at an epoch nobody could decrypt.
   *
   * The families are enumerated in ONE list resolved by ONE loop, because two
   * hand-copied loops are exactly how the 1:1 path fell two families behind.
   */
  private async _handleMlsCommit(data: any): Promise<boolean> {
    const groupId = data.group_id;
    const epoch = typeof data.epoch === "number" ? data.epoch : 0;

    // #1013: every inbound frame reports the epoch the group was at when it
    // was produced — a lower bound on where the group is now.
    this._noteGroupEpoch(groupId, data.epoch);

    // #1184: a commit of ours for this group is awaiting the server's verdict.
    // Hold this one; it is replayed against whichever state the verdict leaves.
    const held = this._commitInFlight.get(groupId);
    if (held) {
      held.frames.push(data);
      console.log(`[SecureChannel] MLS commit for ${groupId.slice(0, 8)} held (epoch=${epoch}) — our own commit is awaiting confirmation (#1184)`);
      return true;
    }

    const target = this._resolveGroupFamily(groupId);
    if (target) {
      const { managerKey: groupKey, label } = target;
      const mlsGroup = this._mlsGroups.get(groupKey);

      if (!mlsGroup?.isInitialized) {
        // Buffer until the Welcome arrives — `_applyBufferedMlsCommits` replays
        // it on join. We hold the bytes, so acking the row is safe.
        this._bufferMlsCommit(groupId, epoch, data);
        console.log(`[SecureChannel] Buffered MLS commit for ${label} (epoch=${epoch}, group not initialized)`);
        return true;
      }

      try {
        const commitBytes = new Uint8Array(Buffer.from(data.payload, "hex"));
        await mlsGroup.processCommit(commitBytes);
        await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mlsGroup.exportState()));
        console.log(`[SecureChannel] MLS commit processed for ${label} (epoch=${mlsGroup.epoch})`);
        // #629: clear strikes ONLY on a clean apply. This used to sit after the
        // try/catch, where it also wiped the strike the catch had just
        // recorded — so "3 CONSECUTIVE failures" could never be reached by
        // consecutive failures and the resync ladder was unreachable
        // (loopita: 800 commit failures, 0 resyncs).
        this._mlsCommitFailCounts.delete(groupKey);
      } catch (err: any) {
        // #920: `data.epoch` RAW, not the local `epoch` above — that one
        // coerces a non-number to 0, which would read as "behind the group"
        // and silence a real failure.
        //
        // Still `true`: `_onCommitFailure` owns the strike counter and the
        // resync/recovery ladder (#629, #474). Nacking here as well would run a
        // second, untuned retry loop against the same failure.
        await this._onCommitFailure(
          groupKey, groupId, err, label,
          data.epoch, mlsGroup.epoch,
        );
      }
      return true;
    }

    // #1012: no family claimed it. Keep the bytes so a Welcome arriving later
    // can still replay them, ask to be re-Welcomed the way `_handleMessageMLS`
    // already does from its own unresolved-group exit (`:4930`), and report
    // UNHANDLED so the caller nacks. A nacked row is retried and dead-letters
    // after 5 attempts — visible. An acked row is gone.
    this._bufferMlsCommit(groupId, epoch, data);
    console.warn(
      `[SecureChannel] MLS commit for UNKNOWN group ${String(groupId).slice(0, 8)} ` +
        `(epoch=${epoch}) — buffered, requesting a Welcome, NOT acked`,
    );
    if (groupId) void this._requestWelcomeSelfHeal(groupId);
    return false;
  }

  /** Buffer an MLS commit for replay after Welcome join. Max 50 per group. */
  private _bufferMlsCommit(groupId: string, epoch: number, data: any): void {
    let buf = this._pendingMlsCommits.get(groupId);
    if (!buf) {
      buf = [];
      this._pendingMlsCommits.set(groupId, buf);
    }
    // Dedup by epoch
    if (!buf.some(c => c.epoch === epoch)) {
      buf.push({ epoch, data });
      buf.sort((a, b) => a.epoch - b.epoch);
      // Cap buffer size to prevent memory leak
      if (buf.length > 50) buf.splice(0, buf.length - 50);
    }
  }

  /** Apply buffered MLS commits after Welcome join. */
  private async _applyBufferedMlsCommits(groupId: string, mgr: MLSGroupManager): Promise<void> {
    const buf = this._pendingMlsCommits.get(groupId);
    if (!buf || buf.length === 0) return;
    console.log(`[SecureChannel] Applying ${buf.length} buffered MLS commits for group ${groupId.slice(0, 8)}`);
    let applied = 0;
    for (const entry of buf) {
      if (entry.epoch <= mgr.epoch) continue; // already past this epoch
      try {
        const commitBytes = new Uint8Array(Buffer.from(entry.data.payload, "hex"));
        await mgr.processCommit(commitBytes);
        applied++;
      } catch (err: any) {
        console.warn(`[SecureChannel] Buffered commit epoch=${entry.epoch} failed: ${err?.message}`);
        break; // Stop on first failure — remaining commits depend on this one
      }
    }
    this._pendingMlsCommits.delete(groupId);
    if (applied > 0) {
      await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
      console.log(`[SecureChannel] Applied ${applied} buffered commits, now epoch=${mgr.epoch}`);
    }
  }

  /**
   * MLS epoch recovery for shared 1:1 groups.
   * Clears local state, publishes fresh KeyPackage, and requests Welcome.
   */
  /**
   * Self-heal for a group message we received but have NO MLS group state for
   * (BUG-20260604-001): a member added to a live room after founding gets group
   * ciphertext it can't decrypt because it never received a Welcome. Publish a
   * fresh KeyPackage + request a Welcome so the committer re-runs addMembers and
   * sends us one. Deduped to a 30s window per group so a burst of undecryptable
   * rows doesn't spam request-welcome. Fire-and-forget.
   */
  /**
   * #474 — a failed `processCommit` is DIVERGENCE, and divergence must
   * self-heal. It previously did neither: the room stayed broken forever.
   *
   * Two swallow paths existed, and only the second was ever reported:
   *
   *   1. `isOldEpoch` substring-matched the error text for "epoch" / "old" /
   *      "Validation" and discarded deliberately. `ValidationError: Desired gen
   *      in the past` contains "Validation", so the LOUDEST production failure
   *      was filed as benign — 1,439 occurrences on one agent, all thrown away
   *      by a string match.
   *   2. Everything else re-threw to the WS dispatcher, which logs and drops.
   *
   * Result: ~1,600 commit failures across 13 rooms and ZERO resyncs. Classifying
   * divergence by grepping an error message is the root of it — error text is
   * not a protocol, and the one case that must never be silent was the one that
   * happened to contain the magic word.
   *
   * So: no classification. COUNT, and after MAX_MLS_DECRYPT_FAILS consecutive
   * failures run the same ladder the decrypt path has used since #440 — replay
   * the missed commits via /sync, and fall back to re-join only if that cannot
   * catch up. Counting rather than resyncing on first failure matters: a
   * genuinely stale commit is normal, and resyncing on each one would have
   * fired ~1,600 Welcome requests.
   *
   * Never re-throws. The caller's only handler logs and drops, so throwing here
   * is indistinguishable from swallowing — it just moves the swallow one frame up.
   */
  private async _onCommitFailure(
    groupKey: string,
    mlsGroupId: string,
    err: unknown,
    label: string,
    messageEpoch?: unknown,
    groupEpoch?: unknown,
  ): Promise<void> {
    // #920: a commit the group is ALREADY PAST is not a failure. On a first
    // room join the Welcome advances the agent to the new epoch, so the 0->1
    // commit pulled afterwards from the delivery queue has nothing left to do.
    // Logging it as `MLS commit failed (1/3)` with a stack trace sends someone
    // debugging a working room down a dead end, and — worse — spends the alarm
    // budget, so a REAL epoch divergence arrives looking like the line everyone
    // has learned to ignore.
    //
    // Classified on EPOCHS, never on the error text. See the warning in
    // mls-commit-classify.ts: the previous attempt matched the message string
    // and swallowed 1,439 real failures. Anything unknown still counts.
    if (classifyCommitFailure(messageEpoch, groupEpoch) === "already-applied") {
      console.debug(
        `[SecureChannel] Skipping already-applied MLS commit for ${label} ` +
        `(commit epoch ${String(messageEpoch)} is behind group epoch ${String(groupEpoch)})`,
      );
      return;
    }

    const count = (this._mlsCommitFailCounts.get(groupKey) ?? 0) + 1;
    this._mlsCommitFailCounts.set(groupKey, count);
    console.error(
      `[SecureChannel] MLS commit failed (${count}/${SecureChannel.MAX_MLS_DECRYPT_FAILS}) for ${label}:`,
      err,
    );
    if (count < SecureChannel.MAX_MLS_DECRYPT_FAILS) return;

    console.warn(`[SecureChannel] MLS commit resync triggered for ${label} — ${count} consecutive failures`);
    try {
      if (!(await this._resyncOnDivergence(groupKey, mlsGroupId))) {
        await this._triggerMlsEpochRecovery(groupKey, mlsGroupId);
      }
      this._mlsCommitFailCounts.delete(groupKey);
    } catch (recoverErr) {
      // Leave the count in place so the next commit retries recovery.
      console.error(`[SecureChannel] MLS commit resync FAILED for ${label}:`, recoverErr);
    }
  }

  private async _requestWelcomeSelfHeal(groupId: string): Promise<void> {
    const now = Date.now();
    const last = this._welcomeSelfHealInflight.get(groupId) ?? 0;
    if (now - last < 30_000) return; // within dedup window
    this._welcomeSelfHealInflight.set(groupId, now);
    try {
      // Publish a fresh KeyPackage so the committer's addMembers uses a current one.
      const mgr = new MLSGroupManager();
      const identity = new TextEncoder().encode(this._deviceId!);
      const kp = await mgr.generateKeyPackage(identity);
      const kpBytes = MLSGroupManager.serializeKeyPackage(kp.publicPackage);
      const kpHex = Buffer.from(kpBytes).toString("hex");
      await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
        method: "POST",
        headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
        body: JSON.stringify({ key_package: kpHex, device_id: this._deviceId }),
      });
      this._recordPublishedKp(kp);
      await savePendingKpBundle(this.config.dataDir, groupId, kp, MLSGroupManager.serializeKeyPackage);

      await fetch(`${this.config.apiUrl}/api/v1/mls/groups/${groupId}/request-welcome`, {
        method: "POST",
        headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: this._deviceId }),
      });
      console.log(`[SecureChannel] Welcome self-heal requested for group ${groupId.slice(0, 8)} (no local MLS state)`);
    } catch (err) {
      // Fire-and-forget: clear inflight so a later row can retry.
      this._welcomeSelfHealInflight.delete(groupId);
      console.warn(`[SecureChannel] Welcome self-heal failed for ${groupId.slice(0, 8)}:`, err);
    }
  }

  private async _triggerMlsEpochRecovery(groupKey: string, mlsGroupId: string): Promise<void> {
    console.warn(`[SecureChannel] MLS epoch recovery for ${groupKey} (${mlsGroupId.slice(0, 8)})`);
    // Clear in-memory and persisted state
    this._mlsGroups.delete(groupKey);
    this._mlsDecryptFailCounts.delete(groupKey);

    // Remove persisted MLS state file.
    //
    // This used to unlink `${dataDir}/.mls-state/${mlsGroupId}.json` by hand.
    // `mls-state.ts` writes state FLAT in dataDir as `mls-<groupId>.json`
    // (`mlsFileName`), and no `.mls-state/` directory has ever existed —
    // checked across every agent on a live host, 2026-09-04. So the unlink
    // raised ENOENT every time, `.catch(() => {})` ate it, and recovery
    // cleared only the in-memory map: `loadMlsState` handed the very state
    // recovery had rejected straight back on the next start.
    //
    // This was the only one of eight deletion sites that built its own path.
    // The other seven already call the helper.
    await deleteMlsState(this.config.dataDir, mlsGroupId);

    // Publish fresh KeyPackage
    try {
      const mgr = new MLSGroupManager();
      const identity = new TextEncoder().encode(this._deviceId!);
      const kp = await mgr.generateKeyPackage(identity);
      const kpBytes = MLSGroupManager.serializeKeyPackage(kp.publicPackage);
      const kpHex = Buffer.from(kpBytes).toString("hex");
      await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
        method: "POST",
        headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
        body: JSON.stringify({ key_package: kpHex, device_id: this._deviceId }),
      });
      this._recordPublishedKp(kp);
      await savePendingKpBundle(this.config.dataDir, mlsGroupId, kp, MLSGroupManager.serializeKeyPackage);

      // Request Welcome
      await fetch(`${this.config.apiUrl}/api/v1/mls/groups/${mlsGroupId}/request-welcome`, {
        method: "POST",
        headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
        body: JSON.stringify({ device_id: this._deviceId }),
      });
      console.log(`[SecureChannel] Epoch recovery: Welcome requested for group ${mlsGroupId.slice(0, 8)}`);
    } catch (err) {
      console.error(`[SecureChannel] Epoch recovery failed for ${mlsGroupId.slice(0, 8)}:`, err);
    }
  }

  /**
   * #440 resync-on-divergence — catch up an EXISTING member that has fallen
   * BEHIND the group's epoch by FETCHING the missed commits and APPLYING them in
   * epoch order, INSTEAD of tearing down local state and re-joining (the
   * welcome / re-admit path in `_triggerMlsEpochRecovery`, which is for a member
   * that was never in the group). When an existing member lags, re-admit churns
   * the epoch and the room "goes dark"; replaying the missed commits re-converges
   * in place with no re-key.
   *
   * Mirrors the proven `Creator.resyncToCurrentEpoch` L2-gate blueprint:
   *   1. DETECT: our local manager exists + is initialized → local_epoch known.
   *   2. FETCH:  GET /mls/groups/{groupId}/sync?last_epoch=<localEpoch>
   *              → { action: "replay", messages: [...commits...] }
   *              (or { action: "rejoin" } when too far behind to replay).
   *   3. APPLY:  processCommit each returned commit, in epoch order.
   *
   * Returns true ONLY when the member caught up via replay (caller then SKIPS
   * epoch recovery). Returns false for action="rejoin", no local manager, an
   * empty replay, or any failure — in which case the caller falls back to
   * `_triggerMlsEpochRecovery` exactly as before (no behavior change on that path).
   *
   * @param groupKey the `_mlsGroups` map key for the member's manager (roomId,
   *   `1to1-group:<gid>`, `a2a:<channelId>`, …) — also the decrypt-fail-count key.
   * @param groupId  the server-side MLS group id used in the /sync URL.
   */
  /**
   * #1015 — the ONLY place this package fulfils a Welcome request.
   *
   * Add the members, transmit, and record `fulfilled` **only for the targets
   * whose frames actually went out**. Every one of the five sites that used to
   * do this by hand followed the same sequence and had the same three holes:
   * `this._ws` null (both sends skipped by an `if`), a CLOSING socket (`send`
   * returns without throwing), and a fulfil POST wrapped in `.catch(() => {})`.
   *
   * A fulfilled request is never re-requested. So a Welcome that evaporated
   * left the requester locked out, the fulfiller at epoch N+1, everyone else at
   * N, and the bridging commit untransmitted and impossible to regenerate — a
   * permanently wedged group in which every signal read success.
   *
   * Returns the number of requests actually fulfilled.
   */
  /**
   * #1226: the roster to submit on a commit frame — the caller's known members,
   * the devices being admitted, and ALWAYS ourselves — deduplicated, UUIDs
   * only. Self is non-negotiable: once a roster is non-empty the server
   * requires the committer in it (#469), and our own head read
   * (`assert_group_member`) requires it too. So with no device id of our own
   * this returns `[]` and the frame carries NO roster (the pre-#1226 shape)
   * rather than a roster that locks us out. Non-UUID entries are dropped:
   * `known` can come off an inbound Welcome frame, and a malformed id in the
   * outbound frame is refused by the server (`MEMBER_DEVICE_IDS_MALFORMED`).
   * @internal exposed for tests
   */
  _commitRoster(known: readonly unknown[] | undefined, targets: readonly unknown[]): string[] {
    if (!this._deviceId) return [];
    const out: string[] = [];
    const seen = new Set<string>();
    for (const id of [...(known ?? []), ...targets, this._deviceId]) {
      if (typeof id !== "string" || !UUID_RE.test(id) || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
    return out;
  }

  private async _fulfilWelcomeRequests(opts: {
    mlsGroup: any;
    mlsGroupId: string;
    label: string;
    /** Linkage added to the mls_welcome frame: room_id / conversation_group_id / a2a_channel_id. */
    welcomeFields: Record<string, string>;
    targets: Array<{ requestId: string; deviceId: string; keyPackage: any }>;
    /**
     * #1226: the roster the caller holds for its family (room members, the
     * shared group's member list, the 1:1 owner device). Sent on every commit
     * frame as `member_device_ids`, unioned with the targets and ourselves —
     * see `_commitRoster`. Absent/empty is allowed; self + targets still go.
     */
    memberDeviceIds?: string[];
    /**
     * #1429: the head `_welcomeCommitPreflight` read just before the caller
     * claimed KeyPackages. Used as this run's pre-flight instead of reading
     * again; the check below is still applied to it. Absent -> read here.
     */
    preHead?: Awaited<ReturnType<SecureChannel["_readGroupHead"]>>;
  }): Promise<number> {
    const { mlsGroup, mlsGroupId, label, welcomeFields, targets } = opts;
    if (targets.length === 0) return 0;
    // #1226: this frame is the ONLY roster writer for a group the plugin
    // founded — `POST /rooms/{id}/init` returns an existing group without
    // merging, and the plugin only calls it before it knows the group id. A
    // frame without `member_device_ids` leaves the server roster empty, and an
    // empty roster 403s our own head read (`assert_group_member` fails closed),
    // which is what made every #1184 verdict on such a group "unverifiable".
    // The server does (prior ∪ submitted) ∩ participants, so over-sending
    // cannot inject or wipe; what matters is that SELF is in it, or the next
    // commit fails the #469 committer check.
    // Two rosters: the batch's LAST commit (the add) names the targets; any
    // earlier commit (the remove of a stale leaf, #1170) names only what we
    // already know plus ourselves. Naming the joiners on the remove commit
    // would put them in the server roster BEFORE they hold a leaf — if the add
    // commit is then refused and rolled back, they would be queued ciphertext
    // they cannot decrypt (review S4). The first frame still carries self, so
    // the head becomes readable from the first commit either way.
    const rosterBefore = this._commitRoster(opts.memberDeviceIds, []);
    const rosterAfter = this._commitRoster(opts.memberDeviceIds, targets.map((t) => t.deviceId));

    // Refuse to advance the group at all when we cannot transmit. `addMembers`
    // advances the local epoch, and an MLS commit cannot be regenerated.
    if (!this._wsIsOpen()) {
      console.warn(
        `[SecureChannel] ${label}: socket is not OPEN — NOT advancing the group; ` +
          `${targets.length} request(s) stay pending for the next pull`,
      );
      return 0;
    }

    // The rollback target, captured before the commit.
    const snapshot = mlsGroup.exportState();

    // `reAdmitMembers`, not `addMembers` (#1170). A device that lost its LOCAL
    // MLS state still holds its LEAF in the group, so `addMembers` refuses it
    // with "already in the group" and the whole rescue fails — observed live
    // 2026-09-03 rescuing room 007, where all three stuck devices were still
    // members. `reAdmitMembers` subsumes the plain case: one commit for pure
    // adds, two (all removes, then all adds) when a leaf is stale. That split
    // is not a style choice — 2 removes + 2 adds in ONE commit breaks a
    // REMAINING member (`OpenError at applyUpdatePathSecret`), measured in
    // #1093 and pinned by a crypto test.
    // #1177: the parent of the first commit is the server's head as of now;
    // each later commit is built on the one we just sent. Read BEFORE the
    // group advances locally. A read that fails (403 on an empty-roster room,
    // which is 79 of 80 in production; 5xx; network) sends the commit with no
    // parent — the server skips the check for an absent field — and does NOT
    // skip the pre-flight below, which has nothing to compare against.
    const preHead = opts.preHead ?? (await this._readGroupHead(mlsGroupId));
    // #1184: if the server is already somewhere else, this commit WILL be
    // refused (the WS handler checks `expected_epoch = epoch - 1`), and before
    // #1184 that refusal forked us. Do not build it. The requests stay pending;
    // the group catches up through the normal divergence path.
    if (preHead.ok && preHead.epoch !== Number(mlsGroup.epoch)) {
      console.warn(
        `[SecureChannel] ${label}: server head is at epoch ${preHead.epoch}, we are at ` +
          `${Number(mlsGroup.epoch)} — NOT committing (it would be refused, #1184); ` +
          `${targets.length} request(s) stay pending`,
      );
      await this._catchUpAfterPreflightSkip(mlsGroupId, preHead.epoch, Number(mlsGroup.epoch), label);
      return 0;
    }
    let parent = preHead.ok ? preHead.digest : undefined;

    // #1184: from here until the verdict, socket-delivered commits for this
    // group are held (see `_commitInFlight`) and replayed in `finally`, and a
    // `… commit rejected` error frame naming this group is recorded as the
    // server's refusal (`_noteCommitRefusal`).
    // #1225 (review F1): one commit run per group at a time — a second run
    // would clobber this record (refusal correlation + the socket hold).
    if (this._commitInFlight.has(mlsGroupId)) {
      console.warn(
        `[SecureChannel] ${label}: another commit for this group is in flight — NOT advancing; ` +
          `${targets.length} request(s) stay pending for the next pull`,
      );
      return 0;
    }
    const inFlight: CommitInFlight = { frames: [], sent: [] };
    this._commitInFlight.set(mlsGroupId, inFlight);
    try {
    const { commits, welcome } = await mlsGroup.reAdmitMembers(targets.map((t) => t.keyPackage));

    let sentCommits = 0;
    // Index in `inFlight.sent` of the first commit the head poll showed does
    // not hold; -1 while none. A refusal FRAME can move it earlier or set it
    // later — see `cutFromRefusal` below.
    let refusedAt = -1;
    let verdict: "confirmed" | "conflict" | "unconfirmed" | "unverifiable" | "unsent" = "confirmed";
    for (const c of commits) {
      const ours = this._commitDigest(c.commit);
      // Registered BEFORE the send so a refusal that arrives while the send
      // resolves finds the commit it names. `stateBefore` is the state as of
      // the previous commit (from crypto — after two commits the manager
      // itself only holds the last one), or the pre-batch snapshot.
      inFlight.sent.push({
        epoch: Number(c.epoch),
        digest: ours,
        verified: false,
        stateBefore: sentCommits === 0 ? snapshot : commits[sentCommits - 1].state,
      });
      const ok = await this._sendFrameConfirmed(
        {
          event: "mls_commit",
          data: {
            group_id: mlsGroupId,
            // The epoch this commit PRODUCED. After two commits the manager
            // only knows the last one, which is why the result carries them.
            epoch: Number(c.epoch),
            payload: Buffer.from(c.commit).toString("hex"),
            ...(parent ? { parent_digest: parent } : {}),
            ...((sentCommits === commits.length - 1 ? rosterAfter : rosterBefore).length > 0
              ? { member_device_ids: sentCommits === commits.length - 1 ? rosterAfter : rosterBefore }
              : {}),
          },
        },
        `${label} commit ${sentCommits + 1}/${commits.length}`,
      );
      if (!ok) {
        verdict = "unsent";
        inFlight.sent.pop(); // never written: no refusal can name it
        break;
      }
      // #1184: the socket took it; now the SERVER has to say so. Only a head
      // showing our digest lets the next commit build on it, or a Welcome be
      // minted from it.
      verdict = await this._confirmCommitAtHead(mlsGroupId, Number(c.epoch), ours, inFlight, label);
      if (verdict === "confirmed") {
        inFlight.sent[sentCommits].verified = true;
      } else if (verdict === "unverifiable") {
        // Not confirmed, not refused: the head could not be read on ANY try
        // (the empty-roster 403, or an outage covering the window) and the
        // server, which had the whole window to refuse on the socket, did not.
        // This is the pre-#1184 behaviour, kept on purpose: refusing here
        // would make every rescue on the 79 empty-roster rooms fail forever —
        // a rescue that cannot run is the 2026-09-04 lockout (#1177).
        console.warn(
          `[SecureChannel] ${label}: commit ${sentCommits + 1}/${commits.length} at epoch ${Number(c.epoch)} ` +
            `could not be verified and was not refused — proceeding UNCHECKED (#1184)`,
        );
      } else {
        refusedAt = sentCommits;
        break;
      }
      parent = ours;
      sentCommits++;
    }

    // #1184: where to cut. The head poll's verdict names a commit; a refusal
    // FRAME (`inFlight.refusal`) names the first commit of ours the server
    // shows did not land, and it is honoured whenever it arrives — inside the
    // window or after it, right up to the fulfil POST, which is the step that
    // locks a requester out. Whichever is earlier wins: everything from the
    // cut on describes a fork nobody else will ever hold.
    const cutFromRefusal = (): number => {
      const r = inFlight.refusal;
      if (r === undefined) return refusedAt;
      return refusedAt === -1 ? r.from : Math.min(refusedAt, r.from);
    };
    // Roll back to the state BEFORE commit `cut`: the pre-batch snapshot when
    // it is the first, else the state after the previous commit. Rolling back
    // PAST a commit the server holds (removes landed, adds refused, #1170)
    // would leave us at N with the server at N+2 and no way to reach N+1 —
    // the committer is excluded from its own fan-out. One behind is what
    // replay repairs; ahead is what cannot be (#972). `persisted` says a later
    // state already reached the disk and must be overwritten.
    const rollBackTo = async (cut: number, stage: string, persisted: boolean): Promise<void> => {
      const why = inFlight.refusal?.detail ?? verdict.toUpperCase();
      mlsGroup.importState(inFlight.sent[cut].stateBefore);
      sentCommits = cut;
      console.warn(
        `[SecureChannel] ${label}: commit ${cut + 1}/${commits.length} ${stage} — "${why}" — ` +
          `rolled back to epoch ${Number(mlsGroup.epoch)}` +
          (cut === 0 ? ", nothing" : ` (after commit ${cut}), only that`) +
          ` persisted, ${targets.length} request(s) stay pending`,
      );
      if (persisted) await saveMlsState(this.config.dataDir, mlsGroupId, JSON.stringify(mlsGroup.exportState()));
    };

    let cut = cutFromRefusal();
    if (cut !== -1) {
      await rollBackTo(cut, "was refused by the server", false);
      if (sentCommits === 0) return 0;
      // The commit(s) before the cut stand; persist exactly that and fall
      // through to the partial-batch outcome below.
    }

    if (sentCommits === 0) {
      // #972: a client epoch AHEAD of the server is unrecoverable, while being
      // behind is repaired by replay. If the commit did not go out, the safe
      // direction is to be exactly where we started.
      mlsGroup.importState(snapshot);
      console.warn(
        `[SecureChannel] ${label}: commit was NOT written — rolled back to epoch ` +
          `${Number(mlsGroup.epoch)}, nothing fulfilled, requests stay pending`,
      );
      return 0;
    }

    // At least one commit is on the wire, so the group has moved and our
    // persisted state must move with it — even if a later commit or a Welcome
    // below fails.
    await saveMlsState(this.config.dataDir, mlsGroupId, JSON.stringify(mlsGroup.exportState()));

    // The refusal may have landed while that write was in flight.
    cut = cutFromRefusal();
    if (cut !== -1 && cut < sentCommits) {
      await rollBackTo(cut, "was refused by the server after the window (during persist)", true);
      return 0;
    }

    if (sentCommits < commits.length) {
      // The removes landed and the adds did not (refused by the server, or
      // never written). NOT recoverable by rollback — the group has already
      // moved for everyone else. Leaving the requests pending is the only
      // honest outcome: the next pull re-reads the roster and tries again
      // from wherever the group now is (the pre-flight above skips while the
      // server is ahead of us; a decrypt failure runs the divergence resync).
      console.warn(
        `[SecureChannel] ${label}: only ${sentCommits}/${commits.length} commits were ` +
          `confirmed — requests stay PENDING for the next pull`,
      );
      return 0;
    }

    if (!welcome) {
      // Nothing to hand the joiners. The commit stands, but marking them
      // fulfilled would lock them out of a group they were never given.
      console.warn(
        `[SecureChannel] ${label}: addMembers produced NO Welcome — ` +
          `${targets.length} request(s) left pending rather than marked fulfilled`,
      );
      return 0;
    }

    let fulfilled = 0;
    for (const t of targets) {
      const sent = await this._sendFrameConfirmed(
        {
          event: "mls_welcome",
          data: {
            target_device_id: t.deviceId,
            group_id: mlsGroupId,
            ...welcomeFields,
            payload: Buffer.from(welcome).toString("hex"),
          },
        },
        `${label} welcome→${t.deviceId.slice(0, 8)}`,
      );
      if (!sent) {
        console.warn(
          `[SecureChannel] ${label}: Welcome for ${t.deviceId.slice(0, 8)} was NOT ` +
            `written — leaving the request PENDING so the next pull retries it`,
        );
        continue;
      }
      // Last check before the step that locks the requester out. A Welcome
      // already on the wire cannot be recalled; a request left PENDING is
      // retried from a real state on the next pull.
      cut = cutFromRefusal();
      if (cut !== -1 && cut < sentCommits) {
        await rollBackTo(cut, "was refused by the server after the window (during the Welcome)", true);
        return fulfilled;
      }
      if (await this._postFulfilWelcome(mlsGroupId, t.requestId, `${label} ${t.deviceId.slice(0, 8)}`)) {
        fulfilled++;
      }
    }
    return fulfilled;
    } finally {
      this._commitInFlight.delete(mlsGroupId);
      for (const frame of inFlight.frames) {
        try {
          await this._handleMlsCommit(frame);
        } catch (err) {
          console.error(`[SecureChannel] ${label}: replaying a held MLS commit failed:`, err);
        }
      }
    }
  }

  /**
   * Resolve an MLS group id to the family that owns it.
   *
   * There are four: rooms, A2A channels, shared 1:1 groups, and the legacy
   * per-conversation groups. They all store the MLS group id under
   * `mlsGroupId`; they differ only in the key their live `MLSGroupManager` sits
   * under in `_mlsGroups`.
   *
   * ONE resolver, because hand-copying this enumeration is what produced
   * #1012 (`_handleMlsCommit` handled two families of four), #1014 (a
   * room-framed 1:1 replay matched nothing) and #1016 (a rejoin dropped only
   * the room manager). Three bugs, one missing abstraction.
   */
  private _resolveGroupFamily(mlsGroupId: string): {
    family: "room" | "a2a" | "shared1to1" | "conversation";
    id: string;
    managerKey: string;
    label: string;
  } | null {
    if (!mlsGroupId) return null;

    const families: Array<{
      family: "room" | "a2a" | "shared1to1" | "conversation";
      entries: Record<string, any> | undefined;
      managerKey: (id: string) => string;
      label: string;
    }> = [
      { family: "room", entries: this._persisted?.rooms, managerKey: (id) => id, label: "room" },
      { family: "a2a", entries: this._persisted?.a2aChannels, managerKey: (id) => `a2a:${id}`, label: "A2A" },
      { family: "shared1to1", entries: this._persisted?.mlsGroups, managerKey: (id) => `1to1-group:${id}`, label: "1:1 group" },
      { family: "conversation", entries: this._persisted?.mlsConversations, managerKey: (id) => `conv:${id}`, label: "conversation" },
    ];

    for (const f of families) {
      for (const [id, entry] of Object.entries(f.entries ?? {})) {
        if ((entry as any)?.mlsGroupId !== mlsGroupId) continue;
        return {
          family: f.family,
          id,
          managerKey: f.managerKey(id),
          label: `${f.label} ${id.slice(0, 8)}`,
        };
      }
    }
    return null;
  }

  /** #1015: is the socket in a state that can actually take a write? */
  private _wsIsOpen(): boolean {
    return this._ws?.readyState === 1; // 1 === WebSocket.OPEN
  }

  /**
   * #1429 review: the cheap checks `_fulfilWelcomeRequests` makes, run BEFORE a
   * pending-Welcome site claims KeyPackages — the order #1225 S4 gave the A2A
   * path. `GET /key-packages/batch` CONSUMES; the room, shared-1:1 and conv
   * sites used to claim one per pending device and only then find the socket
   * closed, another commit in flight, or the server head elsewhere — burning a
   * package per device per failed pull (and the shared/conv sites have no
   * cooldown). `_fulfilWelcomeRequests` keeps its own checks: this is a gate on
   * spending, not a replacement for the guard at the point of commit.
   */
  /**
   * Returns the head it read (handed on to `_fulfilWelcomeRequests` as its
   * pre-flight, so a pull still reads the head once before committing), or
   * `null` when nothing should be claimed this pull.
   */
  private async _welcomeCommitPreflight(
    mlsGroupId: string,
    mlsGroup: any,
    label: string,
  ): Promise<Awaited<ReturnType<SecureChannel["_readGroupHead"]>> | null> {
    if (!this._wsIsOpen()) {
      console.warn(`[SecureChannel] ${label}: socket is not OPEN — not claiming KeyPackages; requests stay pending for the next pull`);
      return null;
    }
    if (this._commitInFlight.has(mlsGroupId)) {
      console.warn(`[SecureChannel] ${label}: another commit for this group is in flight — not claiming KeyPackages this pull`);
      return null;
    }
    const head = await this._readGroupHead(mlsGroupId);
    if (head.ok && head.epoch !== Number(mlsGroup.epoch)) {
      console.warn(
        `[SecureChannel] ${label}: server head is at epoch ${head.epoch}, we are at ${Number(mlsGroup.epoch)} — ` +
          `not claiming KeyPackages (the commit would be refused, #1184); requests stay pending`,
      );
      await this._catchUpAfterPreflightSkip(mlsGroupId, head.epoch, Number(mlsGroup.epoch), label);
      return null;
    }
    return head;
  }

  /** SHA-256 hex of a commit's bytes — its identity on the server (#1175/#1177). */
  /**
   * #1225: send ONE commit over the socket and wait for the server's verdict,
   * with the #1184 machinery — an in-flight record (so a `… commit rejected`
   * frame is correlated to it and a concurrent commit for the group is held
   * and replayed), the head poll, and rollback. The three A2A commit sites
   * (`a2a_participant_joined`, the `/distribute` WS fallback, `_foundA2AGroup`)
   * used a raw `ws.send` and treated "the socket took it" as "the server
   * stored it" — two of them then PERSISTED a state the server never saw.
   *
   * Returns the verdict. On `refused` / `unsent` the manager has been restored
   * to `stateBefore` and nothing must be persisted or minted from it.
   * `unverifiable` (head unreadable on every try AND no refusal) proceeds
   * UNCHECKED, the same deliberate choice as the fulfil site.
   */
  /**
   * #1225 (review F1): reserve the single in-flight slot for a group. The
   * record is what `_noteCommitRefusal` correlates a refusal to and what
   * `_handleMlsCommit` holds concurrent socket commits behind; a second run on
   * the same group would clobber both (measured: two overlapping joins → two
   * commits claiming one epoch, one Welcome instead of two, the manager AHEAD
   * of the server). Taken BEFORE the commit is generated; released by
   * `_releaseCommit` after the last step that depends on it.
   */
  /**
   * A pre-flight skip must not be a dead end. The skip happens because the
   * server's head is somewhere else; before this the group "caught up through
   * the normal divergence path", which only runs when a message for the group
   * arrives — so a group nobody writes to stayed diverged and its pending
   * Welcome requests were never fulfilled (named in PR #1227). When the server
   * is AHEAD, replay the missing commits now so the next pull can commit; when
   * WE are ahead there is nothing to replay (the fork-ahead direction, #972).
   */
  private async _catchUpAfterPreflightSkip(mlsGroupId: string, serverEpoch: number, localEpoch: number, label: string): Promise<void> {
    if (serverEpoch < localEpoch) {
      console.warn(`[SecureChannel] ${label}: we are AHEAD of the server (${localEpoch} > ${serverEpoch}) — nothing to replay`);
      return;
    }
    const fam = this._resolveGroupFamily(mlsGroupId);
    if (!fam) return;
    try {
      const caughtUp = await this._resyncOnDivergence(fam.managerKey, mlsGroupId);
      console.log(`[SecureChannel] ${label}: catch-up after pre-flight skip ${caughtUp ? "replayed the missing commits" : "could not replay"}`);
    } catch (err) {
      console.warn(`[SecureChannel] ${label}: catch-up after pre-flight skip failed:`, err);
    }
  }

  private _reserveCommit(groupId: string, label: string): CommitInFlight | null {
    if (this._commitInFlight.has(groupId)) {
      console.warn(
        `[SecureChannel] ${label}: another commit for group ${groupId.slice(0, 8)} is in flight — ` +
          `NOT advancing the group (a Welcome can be requested again)`,
      );
      return null;
    }
    const rec: CommitInFlight = { frames: [], sent: [] };
    this._commitInFlight.set(groupId, rec);
    return rec;
  }

  /** Release the slot taken by `_reserveCommit` and replay the socket commits held meanwhile. */
  private async _releaseCommit(groupId: string, rec: CommitInFlight, label: string): Promise<void> {
    if (this._commitInFlight.get(groupId) === rec) this._commitInFlight.delete(groupId);
    for (const frame of rec.frames) {
      try {
        await this._handleMlsCommit(frame);
      } catch (err) {
        console.error(`[SecureChannel] ${label}: replaying a held MLS commit failed:`, err);
      }
    }
  }

  /**
   * #1225: send ONE commit over the socket and wait for the server's verdict
   * with the #1184 machinery (refusal frame correlated through the reserved
   * in-flight record, head poll). The three A2A commit sites
   * (`a2a_participant_joined`, the `/distribute` WS fallback, `_foundA2AGroup`)
   * used a raw `ws.send` and treated "the socket took it" as "the server
   * stored it" — two of them then PERSISTED a state the server never saw.
   *
   * Only `confirmed` proceeds. `unverifiable` (head unreadable on every try,
   * no refusal) is a ROLLBACK here, unlike the fulfil site: that site proceeds
   * UNCHECKED because 79 of 80 production rooms 403 the head read on an empty
   * roster; an A2A head read admits any active participant (observer
   * fallback), so five failed reads there mean an outage, not a routine 403.
   * On anything but `confirmed` the manager is back at `stateBefore`.
   */
  private async _sendCommitConfirmed(opts: {
    mlsGroup: any;
    mlsGroupId: string;
    label: string;
    commit: Uint8Array;
    /** The epoch this commit PRODUCED — captured right after it was generated, never a live read. */
    epoch: number;
    parent?: string;
    memberDeviceIds?: string[];
    /** `mlsGroup.exportState()` taken BEFORE the commit was generated. */
    stateBefore: any;
    /** The record from `_reserveCommit`; the caller releases it. */
    inFlight: CommitInFlight;
  }): Promise<"confirmed" | "refused" | "unsent"> {
    const { mlsGroup, mlsGroupId, label, commit, epoch, parent, memberDeviceIds, stateBefore, inFlight } = opts;
    const ours = this._commitDigest(commit);
    inFlight.sent.push({ epoch, digest: ours, verified: false, stateBefore });
    const ok = await this._sendFrameConfirmed(
      {
        event: "mls_commit",
        data: {
          group_id: mlsGroupId,
          epoch,
          payload: Buffer.from(commit).toString("hex"),
          ...(parent ? { parent_digest: parent } : {}),
          ...(memberDeviceIds && memberDeviceIds.length > 0 ? { member_device_ids: memberDeviceIds } : {}),
        },
      },
      `${label} commit`,
    );
    if (!ok) {
      inFlight.sent.pop(); // never written: no refusal can name it
      mlsGroup.importState(stateBefore);
      console.warn(
        `[SecureChannel] ${label}: commit at epoch ${epoch} was NOT written — ` +
          `rolled back to epoch ${Number(mlsGroup.epoch)}`,
      );
      return "unsent";
    }
    const verdict = await this._confirmCommitAtHead(mlsGroupId, epoch, ours, inFlight, label);
    if (inFlight.refusal === undefined && verdict === "confirmed") {
      inFlight.sent[inFlight.sent.length - 1].verified = true;
      return "confirmed";
    }
    const why = inFlight.refusal?.detail ?? verdict.toUpperCase();
    mlsGroup.importState(stateBefore);
    console.warn(
      `[SecureChannel] ${label}: commit at epoch ${epoch} was ${verdict === "unverifiable" ? "not verified (head unreadable)" : "refused by the server"} — "${why}" — ` +
        `rolled back to epoch ${Number(mlsGroup.epoch)}, nothing persisted`,
    );
    return "refused";
  }

  private _commitDigest(bytes: Uint8Array): string {
    return createHash("sha256").update(bytes).digest("hex");
  }

  /**
   * #1177: the digest of the commit at the server's head, or undefined.
   *
   * Undefined covers "no head yet" (a pruned group: epoch known, digest NULL —
   * 29 in production) AND "could not read" — deliberately the same value. The
   * commit is sent either way: the server skips the parent check for an
   * ABSENT field and refuses a MALFORMED one, so a read failure must degrade
   * to "unchecked", never to "blocked" (a rescue that cannot run is the
   * 2026-09-04 lockout) and never to `null`.
   */
  private async _readGroupHeadDigest(groupId: string): Promise<string | undefined> {
    const head = await this._readGroupHead(groupId);
    return head.ok ? head.digest : undefined;
  }

  /**
   * #1184: the server's head — its `current_epoch` and the digest of the commit
   * that produced it.
   *
   *   - `{ ok: true, epoch, digest? }`  the server described its head. `digest`
   *                                     is absent on a pruned group (NULL
   *                                     `last_commit_digest`).
   *   - `{ ok: false, why }`            the read failed: a non-2xx (the 403
   *                                     `assert_group_member` returns on an
   *                                     EMPTY roster — 79 of 80 production
   *                                     rooms — or a 5xx), a timeout, or a body
   *                                     with no `current_epoch`. Callers may
   *                                     retry; a failure is never a verdict.
   *
   * `current_epoch` is a required field of `MLSGroupResponse`, so "200 with
   * no head" is not a shape any deployed backend produces; it is folded into
   * `ok: false` rather than given a branch of its own (the first cut of #1184
   * had one, and it was the only branch the suite exercised).
   */
  private async _readGroupHead(
    groupId: string,
  ): Promise<{ ok: true; epoch: number; digest?: string } | { ok: false; why: string }> {
    try {
      const res = await fetch(`${this.config.apiUrl}/api/v1/mls/groups/${groupId}`, {
        headers: { Authorization: `Bearer ${this._deviceJwt}` },
        // The confirm loop holds `_deliveryPulling`; a black-holed read must
        // not hold it for undici's 300 s default. A timeout is a failed read.
        signal: AbortSignal.timeout(SecureChannel.HEAD_READ_TIMEOUT_MS),
      });
      if (!res.ok) return { ok: false, why: `HTTP ${res.status}` };
      const head = (await res.json()) as { current_epoch?: unknown; last_commit_digest?: unknown };
      if (typeof head?.current_epoch !== "number") return { ok: false, why: "no current_epoch in body" };
      const d = head.last_commit_digest;
      return { ok: true, epoch: head.current_epoch, digest: typeof d === "string" && /^[0-9a-f]{64}$/.test(d) ? d : undefined };
    } catch (err) {
      return { ok: false, why: err instanceof Error ? err.name : String(err) };
    }
  }

  /**
   * #1184: after each read of the head, how long to wait before the next.
   * The web's schedule (`lib/mls-commit-confirm.ts`) plus one immediate read:
   * five reads, ~3.75 s, no resend — an MLS commit cannot be regenerated. The
   * immediate read catches the common case where the server stored the frame
   * before the GET lands. Instance-level so tests can run the decision logic
   * without the clock; an empty schedule still reads once (`_confirmCommitAtHead`).
   */
  private _commitConfirmDelaysMs: number[] = [0, 250, 500, 1000, 2000];
  private static readonly HEAD_READ_TIMEOUT_MS = 5_000;

  /**
   * #1184: groups with a commit of OURS between "built" and "confirmed or rolled
   * back". Each record holds the `mls_commit` frames the socket delivered for
   * that group meanwhile — before the confirm loop that window was
   * microseconds; now it is up to ~3.75 s, and a commit applied to our
   * unconfirmed local state would be lost by the rollback (in memory) after
   * being persisted (on disk). Held frames are replayed, in order, once the
   * group is back on a real state. The record also carries the server's
   * REFUSAL, when one arrives on the socket (`_noteCommitRefusal`), and a
   * `wake` the confirm loop installs so a refusal ends its wait at once.
   */
  private _commitInFlight = new Map<string, CommitInFlight>();
  /** #1184: a pull was requested while one was running — run once more after. */
  private _deliveryPullRequested = false;

  /**
   * #1184: an `error` frame naming a group we have a commit in flight for, with
   * a `… commit rejected` detail, is the server's refusal of one of those
   * commits — every rejection the `mls_commit` WS handler sends (malformed
   * parent, PARENT_MISMATCH, EPOCH_CONFLICT, not-a-member, no-participants)
   * ends with those words and carries `group_id` (pinned by
   * `tests/test_1184_commit_reject_frame_contract.py`). It is sent on OUR
   * socket after the server processed OUR frame, so it is deterministic in a
   * way the head poll is not, and it is the only refusal signal on a group
   * whose head we cannot read (403).
   *
   * WHICH commit, and what else the frame proves (re-review of #1227):
   *   - EPOCH_CONFLICT carries `expected_epoch` (the epoch we claimed to build
   *     on) and `current_epoch` (the server's); PARENT_MISMATCH carries
   *     `current_epoch` (the head, which equalled our claim or the epoch check
   *     would have fired first) and `head_digest`. The refused commit is the
   *     one built on that epoch. A frame naming an epoch none of our in-flight
   *     commits claimed is a refusal of some EARLIER attempt — ignored.
   *   - the server's `current_epoch` bounds our earlier commits too: any of
   *     ours ABOVE it did not land.
   *   - `head_digest` at `current_epoch` is our commit at that epoch → that one
   *     positively LANDED; someone else's → ours at that epoch did not.
   *   - the un-numbered shapes refuse all of ours alike: the cut is the first
   *     commit the head poll has not positively confirmed.
   * Recorded as the index of the first commit shown not to hold; the fulfil
   * path honours it whenever it arrives. A frame for a group with nothing in
   * flight is somebody else's business.
   */
  private _noteCommitRefusal(payload: {
    detail?: unknown;
    group_id?: unknown;
    current_epoch?: unknown;
    expected_epoch?: unknown;
    head_digest?: unknown;
  }): void {
    const groupId = typeof payload?.group_id === "string" ? payload.group_id : undefined;
    const detail = typeof payload?.detail === "string" ? payload.detail : "";
    if (!groupId || !detail.endsWith("commit rejected")) return;
    const inFlight = this._commitInFlight.get(groupId);
    if (!inFlight) return;
    const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
    const current = num(payload.current_epoch);
    const claimed = num(payload.expected_epoch) ?? current;
    const headDigest = typeof payload.head_digest === "string" ? payload.head_digest : undefined;
    const sent = inFlight.sent;
    const tag = `[SecureChannel] refusal for ${groupId.slice(0, 8)} ("${detail}")`;

    let from = -1;
    if (claimed !== undefined) {
      from = sent.findIndex((c) => c.epoch === claimed + 1);
      if (from === -1) {
        console.warn(`${tag} names epoch ${claimed + 1}, which no commit in flight claimed — an earlier attempt's; ignored`);
        return;
      }
    }
    if (current !== undefined) {
      const above = sent.findIndex((c) => c.epoch > current && !c.verified);
      if (above !== -1 && (from === -1 || above < from)) from = above;
    }
    if (headDigest !== undefined && current !== undefined) {
      const at = sent.findIndex((c) => c.epoch === current);
      if (at !== -1) {
        if (sent[at].digest === headDigest) {
          sent[at].verified = true; // the head IS our commit — positively landed
        } else if (from === -1 || at < from) {
          from = at; // someone else holds that epoch — ours there did not land
        }
      }
    }
    if (from === -1) from = sent.findIndex((c) => !c.verified);
    if (from === -1 || sent[from].verified) {
      console.warn(`${tag} contradicts a head read that showed our commit — stale; ignored`);
      return;
    }
    if (inFlight.refusal !== undefined && inFlight.refusal.from <= from) return; // already cutting at or before this
    inFlight.refusal = { detail, from };
    inFlight.wake?.();
  }

  /**
   * #1184: a commit is CONFIRMED when the server's head shows OUR digest at OUR
   * epoch — never when the socket took the bytes (#1015 covers only that). The
   * server sends no ack for a stored commit, so, like the web, we read the
   * head back; but it DOES send a refusal on the socket (`_noteCommitRefusal`),
   * and that is checked first, before every read and during every wait.
   *
   *   confirmed     head.epoch === ours && head.digest === ours
   *   conflict      the server refused ours on the socket, OR the head is at
   *                 our epoch with ANOTHER digest, OR already past us
   *   unconfirmed   the head was READ (at least once) but never reached our
   *                 epoch within the window — the server described its state
   *                 and ours is not in it
   *   unverifiable  the head could not be read on ANY try (403 on an empty
   *                 roster, an outage) and no refusal arrived — or it is at
   *                 our epoch but keeps no digest (a pruned group). Nothing
   *                 can be decided on; the caller proceeds UNCHECKED (#1177's
   *                 rule: a rescue that cannot run is the 2026-09-04 lockout)
   *
   * The first cut returned `unverifiable` only for a body with no
   * `current_epoch`, which no deployed backend produces, and rolled back on
   * every real read failure — the opposite of the rule it stated.
   */
  private async _confirmCommitAtHead(
    groupId: string,
    epoch: number,
    ours: string,
    inFlight: CommitInFlight,
    label: string,
  ): Promise<"confirmed" | "conflict" | "unconfirmed" | "unverifiable"> {
    const refused = (): "conflict" | undefined => {
      if (inFlight.refusal === undefined) return undefined;
      console.warn(`[SecureChannel] ${label}: server refused our commit at epoch ${epoch} — "${inFlight.refusal.detail}"`);
      return "conflict";
    };
    let lastSeen: number | undefined;
    let described = false;
    let lastFailure = "";
    const delays = this._commitConfirmDelaysMs.length > 0 ? this._commitConfirmDelaysMs : [0];
    for (const delay of delays) {
      if (delay > 0) {
        // A refusal arriving during the wait ends it — no point sleeping out
        // the schedule once the server has answered.
        await new Promise<void>((resolve) => {
          const timer = setTimeout(() => { inFlight.wake = undefined; resolve(); }, delay);
          inFlight.wake = () => { clearTimeout(timer); inFlight.wake = undefined; resolve(); };
        });
      }
      const early = refused();
      if (early) return early;
      const head = await this._readGroupHead(groupId);
      const duringRead = refused();
      if (duringRead) return duringRead;
      if (!head.ok) { lastFailure = head.why; continue; } // read failed; try again
      described = true;
      lastSeen = head.epoch;
      if (head.epoch === epoch) {
        if (head.digest === ours) return "confirmed";
        if (head.digest === undefined) return "unverifiable";
        console.warn(
          `[SecureChannel] ${label}: head at epoch ${epoch} is NOT our commit ` +
            `(${head.digest.slice(0, 12)}… vs ours ${ours.slice(0, 12)}…) — ours was refused`,
        );
        return "conflict";
      }
      if (head.epoch > epoch) {
        console.warn(`[SecureChannel] ${label}: head already at epoch ${head.epoch}, past our ${epoch} — ours was refused`);
        return "conflict";
      }
      // behind: not stored (yet) — keep reading
    }
    const late = refused();
    if (late) return late;
    if (!described && !this._wsIsOpen()) {
      // An uncaught exception on the server's commit path sends no frame and
      // tears the socket down. With no positive signal either, "never ahead"
      // decides it: the refusal, if there was one, is lost — treat as refused.
      console.warn(
        `[SecureChannel] ${label}: the socket closed during the confirm window and the head could not be ` +
          `read — a refusal of commit at epoch ${epoch} may have been lost; treating as refused`,
      );
      return "unconfirmed";
    }
    if (!described) {
      console.warn(
        `[SecureChannel] ${label}: could not read the head on any of ${delays.length} tries ` +
          `(last: ${lastFailure}) and the server sent no refusal — cannot verify commit at epoch ${epoch}`,
      );
      return "unverifiable";
    }
    console.warn(
      `[SecureChannel] ${label}: commit at epoch ${epoch} never appeared at the head ` +
        `(last seen ${lastSeen}) — treating as refused`,
    );
    return "unconfirmed";
  }

  /**
   * #1015: write one frame and report whether the socket took it.
   *
   * `ws.send()` on a CLOSING or CLOSED socket returns WITHOUT throwing — the
   * error surfaces only through the callback, and every fulfilment site passed
   * none. So a Welcome could be "sent" into a dead socket and the requester
   * still marked `fulfilled`, which it never re-requests.
   */
  private _sendFrameConfirmed(frame: unknown, label: string): Promise<boolean> {
    const ws = this._ws;
    if (!ws || ws.readyState !== 1) {
      console.warn(
        `[SecureChannel] ${label}: socket is not OPEN ` +
          `(readyState=${ws?.readyState ?? "null"}) — frame NOT written`,
      );
      return Promise.resolve(false);
    }
    return new Promise<boolean>((resolve) => {
      try {
        ws.send(JSON.stringify(frame), (err?: Error) => {
          if (err) {
            console.error(`[SecureChannel] ${label}: socket refused the frame:`, err);
            resolve(false);
          } else {
            resolve(true);
          }
        });
      } catch (err) {
        console.error(`[SecureChannel] ${label}: send threw:`, err);
        resolve(false);
      }
    });
  }

  /**
   * #1015: record a Welcome as fulfilled — and say so when it fails.
   *
   * The batched shared-1:1 site posted this with `.catch(() => {})`, so an HTTP
   * failure here was invisible on top of the sends being invisible.
   */
  private async _postFulfilWelcome(
    mlsGroupId: string,
    requestId: string,
    label: string,
  ): Promise<boolean> {
    try {
      const res = await fetch(
        `${this.config.apiUrl}/api/v1/mls/groups/${mlsGroupId}/fulfill-welcome`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
          body: JSON.stringify({ request_id: requestId }),
        },
      );
      if (!res.ok) {
        console.warn(`[SecureChannel] ${label}: fulfill-welcome returned ${res.status}`);
        return false;
      }
      return true;
    } catch (err) {
      console.warn(`[SecureChannel] ${label}: fulfill-welcome failed:`, err);
      return false;
    }
  }

  /**
   * #1013: fold an epoch seen on an inbound frame into the running hint.
   *
   * Anything unusable (missing, non-numeric, negative, fractional) is ignored
   * rather than defaulted. A defaulted epoch here would be a guess, and a guess
   * that reads LOW silently disables the check it exists to drive.
   */
  private _noteGroupEpoch(mlsGroupId: unknown, epoch: unknown): void {
    if (typeof mlsGroupId !== "string" || !mlsGroupId) return;
    const current = this._observedGroupEpochs.get(mlsGroupId) ?? 0;
    const next = noteObservedEpoch(current, epoch);
    // Only ever upward. Epoch 0 tells us nothing, so it never seeds the hint.
    if (next > current) this._observedGroupEpochs.set(mlsGroupId, next);
  }

  /**
   * #1013 — the ONLY place this package encrypts an MLS application message.
   *
   * Catch up first when the group is known to have moved past us, then encrypt.
   * Hermes has done this since #440 and the browser since #996; the plugin —
   * the implementation that actually ships to customers — did not, so an agent
   * that missed one commit encrypted every later message at an epoch nobody
   * could read, while the server stored and relayed it and every signal read
   * 200.
   *
   * Funnelling every sender through one method is the point. A guard applied at
   * seven call sites decays to a guard applied at six: that is exactly how
   * `_handleMlsCommit` ended up handling two group families out of four (#1012).
   *
   * Best-effort by design. A catch-up that cannot replay — or that throws — must
   * NOT block the send; we fall through and encrypt at the local epoch, which is
   * precisely today's behaviour, so the worst case is no regression.
   */
  private async _encryptWithCatchUp(
    mlsGroup: any,
    groupKey: string,
    mlsGroupId: string,
    label: string,
    plaintextBytes: Uint8Array,
  ): Promise<Uint8Array> {
    const observed = this._observedGroupEpochs.get(mlsGroupId);
    if (observed !== undefined && needsPreSendResync(Number(mlsGroup.epoch), observed)) {
      console.warn(
        `[SecureChannel] ${label}: local epoch ${Number(mlsGroup.epoch)} is behind the ` +
          `observed group epoch ${observed} — catching up before encrypt (#1013)`,
      );
      try {
        if (!(await this._resyncOnDivergence(groupKey, mlsGroupId))) {
          console.warn(
            `[SecureChannel] ${label}: catch-up could not replay — sending at epoch ` +
              `${Number(mlsGroup.epoch)}. Peers past this epoch will not read it.`,
          );
        }
      } catch (err) {
        console.warn(`[SecureChannel] ${label}: catch-up failed:`, err);
      }
    }
    return await mlsGroup.encrypt(plaintextBytes);
  }

  private async _resyncOnDivergence(groupKey: string, groupId: string): Promise<boolean> {
    const mgr = this._mlsGroups.get(groupKey);
    if (!mgr?.isInitialized) return false; // no local state to catch up — fall back to rejoin
    const fromEpoch = Number(mgr.epoch);
    try {
      const res = await fetch(
        `${this.config.apiUrl}/api/v1/mls/groups/${groupId}/sync?last_epoch=${fromEpoch}`,
        { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
      );
      if (!res.ok) {
        console.warn(`[SecureChannel] Resync /sync failed for ${groupId.slice(0, 8)}: ${res.status}`);
        return false;
      }
      const body = (await res.json()) as { action?: string; messages?: any[] };
      if (body.action !== "replay") {
        // "rejoin" (gap > REJOIN_THRESHOLD) — too far behind to replay; let the
        // caller fall back to the welcome / re-admit path.
        console.log(`[SecureChannel] Resync action=${body.action} for ${groupId.slice(0, 8)} — falling back to rejoin`);
        return false;
      }
      const commits = (body.messages ?? [])
        .filter((m: any) => m.message_type === "commit")
        .sort((a: any, b: any) => Number(a.epoch) - Number(b.epoch));
      let applied = 0;
      for (const c of commits) {
        if (Number(c.epoch) <= Number(mgr.epoch)) continue; // already past this epoch
        await mgr.processCommit(new Uint8Array(Buffer.from(String(c.payload), "hex")));
        applied++;
      }
      if (applied === 0) {
        // #1084: NOT BEHIND. The server answered "replay" and held no commit
        // past our epoch, so this device is exactly current — the opposite of a
        // lockout. Returning false here sent the caller to
        // `_triggerMlsEpochRecovery`, which DELETES local MLS state, publishes a
        // fresh KeyPackage and requests a Welcome; the committer then re-admits
        // us (removeMember + addMembers), costing the group TWO epochs and
        // invalidating the state every other member just had.
        //
        // That is a loop, and it ran all day on 2026-08-27:
        //
        //   [SecureChannel] Resync for d0bce4cf: no new commits to apply (epoch=65)
        //
        // repeated for `loopita`, `ohtani` and `wowzer` while room d0bce4cf sat
        // at epoch 65 — each line followed by a re-key. Room 007 went 31 -> 65
        // in two hours with NO membership change, and every owner pane was
        // stale within a minute of being repaired.
        //
        // A decrypt failure at the current epoch is about ONE MESSAGE — an old
        // frame whose key was dropped for forward secrecy, a duplicate, or a
        // frame from before we joined. None of that is repaired by re-keying,
        // and re-keying breaks everyone else to fix nothing.
        //
        // Bounded, not absolute: a state that is genuinely corrupt while its
        // epoch counter happens to match must still escalate, so after
        // MAX_RESYNC_NOOPS consecutive no-op resyncs we fall through to the old
        // behaviour.
        const strikes = (this._resyncNoOpStrikes.get(groupKey) ?? 0) + 1;
        this._resyncNoOpStrikes.set(groupKey, strikes);
        if (strikes < SecureChannel.MAX_RESYNC_NOOPS) {
          // Clear the decrypt counter too: leaving it at the threshold makes the
          // very next undecryptable frame re-run this ladder immediately, which
          // is the same storm one level down.
          this._mlsDecryptFailCounts.delete(groupKey);
          console.log(
            `[SecureChannel] Resync for ${groupId.slice(0, 8)}: already current at epoch=${mgr.epoch} ` +
              `(strike ${strikes}/${SecureChannel.MAX_RESYNC_NOOPS}) — NOT re-keying (#1084)`,
          );
          return true;
        }
        this._resyncNoOpStrikes.delete(groupKey);
        console.warn(
          `[SecureChannel] Resync for ${groupId.slice(0, 8)}: still failing at epoch=${mgr.epoch} after ` +
            `${SecureChannel.MAX_RESYNC_NOOPS} no-op resyncs — escalating to re-key`,
        );
        return false;
      }
      await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
      this._mlsDecryptFailCounts.delete(groupKey);
      this._resyncNoOpStrikes.delete(groupKey);
      console.log(
        `[SecureChannel] Resync-on-divergence: applied ${applied} missed commit(s) for ${groupId.slice(0, 8)}, now epoch=${mgr.epoch}`,
      );
      return true;
    } catch (err) {
      console.warn(`[SecureChannel] Resync-on-divergence failed for ${groupId.slice(0, 8)}:`, err);
      return false;
    }
  }

  /**
   * Record a freshly-published KeyPackage bundle. Keeps it as the latest single
   * pending bundle (back-compat) AND appends it to the pending pool so a later
   * Welcome encrypted to it can still be matched even after a subsequent publish
   * overwrote `_pendingMlsKpBundle`. De-duped by public-key bytes; the pool is
   * bounded to `_KP_POOL_TARGET` (oldest dropped) so it can't grow unbounded.
   */
  private _recordPublishedKp(kp: KpBundle): void {
    this._pendingMlsKpBundle = kp;
    const idOf = (b: KpBundle) =>
      Buffer.from(MLSGroupManager.serializeKeyPackage(b.publicPackage)).toString("base64");
    const id = idOf(kp);
    if (!this._pendingKpBundles.some((b) => idOf(b) === id)) {
      this._pendingKpBundles.push(kp);
    }
    // #363: bound by _KP_POOL_RETENTION, NOT _KP_POOL_TARGET — see that constant.
    // The backend counts UNCONSUMED KeyPackages; this array counts ALL published
    // ones, so capping at the target shifted out bundles the backend could still
    // serve, making their Welcomes permanently unjoinable (#341).
    while (this._pendingKpBundles.length > SecureChannel._KP_POOL_RETENTION) {
      this._pendingKpBundles.shift();
    }
  }

  /** Drop a consumed bundle from the pending pool after a successful join. */
  private _removeKpFromPool(kp: KpBundle): void {
    const idx = this._pendingKpBundles.indexOf(kp);
    if (idx >= 0) this._pendingKpBundles.splice(idx, 1);
  }

  /**
   * Top the published KeyPackage pool back up to `_KP_POOL_TARGET`: generate +
   * publish one fresh KP per missing slot and record each into the pool only
   * after the backend accepts it. Called on connect (awaited) AND fire-and-forget
   * after every consumed Welcome join, so a long-lived connection that is added
   * to many rooms never drains the pool to empty (the starvation this fixes).
   *
   * Reentrancy-guarded so a connect-time fill and a post-join replenish (or two
   * concurrent welcomes) don't double-publish past target. The while-loop
   * re-checks the pool length each iteration, so a consume that lands mid-fill
   * is self-correcting. A rejected publish (non-2xx) is NOT recorded locally —
   * that keeps the local pool view consistent with the backend — and stops the
   * loop; the next trigger retries.
   */
  private async _ensureKpPoolPublished(): Promise<void> {
    if (this._kpPoolFilling) return;
    if (this._pendingKpBundles.length >= SecureChannel._KP_POOL_TARGET) return;
    this._kpPoolFilling = true;
    try {
      const identity = new TextEncoder().encode(this._deviceId!);
      let publishedCount = 0;
      while (this._pendingKpBundles.length < SecureChannel._KP_POOL_TARGET) {
        const mlsMgr = new MLSGroupManager();
        const kp = await mlsMgr.generateKeyPackage(identity);
        const kpHex = Buffer.from(
          MLSGroupManager.serializeKeyPackage(kp.publicPackage),
        ).toString("hex");
        const res = await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${this._deviceJwt}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ key_package: kpHex }),
        });
        if (!res.ok) {
          // Don't record a KP the backend rejected — the local pool must mirror
          // the backend's unconsumed set for try-each to be sound.
          console.warn(
            `[SecureChannel] KeyPackage publish rejected (status ${res.status}); pool=${this._pendingKpBundles.length}/${SecureChannel._KP_POOL_TARGET}, will retry on next trigger`,
          );
          break;
        }
        this._recordPublishedKp(kp);
        if (!this._mlsKeyPackage) this._mlsKeyPackage = kp;
        publishedCount++;
      }
      if (publishedCount > 0) {
        console.log(
          `[SecureChannel] MLS KeyPackage pool published (${publishedCount} new, pool=${this._pendingKpBundles.length}/${SecureChannel._KP_POOL_TARGET})`,
        );
      }
    } catch (kpErr) {
      console.warn("[SecureChannel] Failed to publish MLS KeyPackage pool:", kpErr);
    } finally {
      this._kpPoolFilling = false;
    }
  }

  /** Start the periodic KeyPackage replenish (#826). Idempotent. */
  private _startKpReplenishTimer(): void {
    this._stopKpReplenishTimer();
    this._kpReplenishTimer = setInterval(() => {
      void this._replenishKpPoolFromServer();
    }, SecureChannel._KP_REPLENISH_INTERVAL_MS);
  }

  private _stopKpReplenishTimer(): void {
    if (this._kpReplenishTimer) {
      clearInterval(this._kpReplenishTimer);
      this._kpReplenishTimer = null;
    }
  }

  /**
   * The server's EXPIRY-AWARE usable KeyPackage count, or null when unknown.
   *
   * `count_unconsumed` filters `expires_at > utcnow()`, which is the only count
   * that reflects what a peer can actually fetch. Returns null (not 0) on any
   * failure so callers can tell "server says empty" from "we could not ask" —
   * guessing 0 here would publish a fresh pool on every transient 503.
   */
  private async _fetchServerKpCount(): Promise<number | null> {
    try {
      // Ask how many will STILL be usable one full interval from now, not how
      // many are usable this instant. A now-cutoff can only ever report the
      // shortfall AFTER the KeyPackages have died, so the agent is un-addable
      // for up to `_KP_REPLENISH_INTERVAL_MS` at every expiry. Agents publish
      // their whole pool in one batch at connect, so all ten share an expiry
      // instant and the pool goes 10 → 0 at once (measured 2026-08-13: wren,
      // smartcode and smartcoder2 all expired at 2026-08-15 23:40 UTC exactly).
      // Older servers ignore the parameter and return the now-count, which is
      // the previous behaviour — degraded, never broken.
      const horizonSeconds = Math.floor(
        (SecureChannel._KP_REPLENISH_INTERVAL_MS * 2) / 1000,
      );
      const res = await fetch(
        `${this.config.apiUrl}/api/v1/mls/key-packages/count` +
          `?device_id=${this._deviceId}&renew_within_seconds=${horizonSeconds}`,
        { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
      );
      if (!res.ok) return null;
      const body: any = await res.json();
      const n = body?.count;
      return typeof n === "number" && Number.isFinite(n) ? n : null;
    } catch {
      return null;
    }
  }

  /**
   * Top the pool up against the SERVER's count (#826).
   *
   * ⚠️ DO NOT replace this with a periodic call to `_ensureKpPoolPublished()`.
   * That function early-returns on `_pendingKpBundles.length >= _KP_POOL_TARGET`,
   * and that array holds PRIVATE bundles which never expire locally — so on an
   * agent that has been connected longer than the KeyPackage lifetime it is
   * still full while the server has zero usable. Measured on prod 2026-08-09:
   * cortina and eclaude were online and green with 10 unconsumed / 0 usable,
   * unable to accept any new MLS 1:1. A timer over the local guard would have
   * run forever and changed nothing.
   *
   * Publishes only the shortfall, and never throws — this runs on a timer and
   * must not be able to take the channel down.
   */
  private async _replenishKpPoolFromServer(): Promise<void> {
    try {
      if (this._kpPoolFilling) return;
      const usable = await this._fetchServerKpCount();
      if (usable === null) return; // could not ask — never guess
      const shortfall = SecureChannel._KP_POOL_TARGET - usable;
      if (shortfall <= 0) return;

      this._kpPoolFilling = true;
      try {
        const identity = new TextEncoder().encode(this._deviceId!);
        let published = 0;
        for (let i = 0; i < shortfall; i++) {
          const mlsMgr = new MLSGroupManager();
          const kp = await mlsMgr.generateKeyPackage(identity);
          const kpHex = Buffer.from(
            MLSGroupManager.serializeKeyPackage(kp.publicPackage),
          ).toString("hex");
          const res = await fetch(`${this.config.apiUrl}/api/v1/mls/key-packages`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${this._deviceJwt}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ key_package: kpHex }),
          });
          if (!res.ok) {
            // Same rule as the connect-time path: never record a KP the backend
            // rejected, or the local pool stops mirroring the unconsumed set.
            console.warn(
              `[SecureChannel] KeyPackage replenish rejected (status ${res.status}) after ${published}/${shortfall}`,
            );
            break;
          }
          this._recordPublishedKp(kp);
          if (!this._mlsKeyPackage) this._mlsKeyPackage = kp;
          published++;
        }
        if (published > 0) {
          console.log(
            `[SecureChannel] MLS KeyPackage pool replenished (${published} new; server had ${usable}/${SecureChannel._KP_POOL_TARGET} usable)`,
          );
        }
      } finally {
        this._kpPoolFilling = false;
      }
    } catch (err) {
      console.warn("[SecureChannel] KeyPackage replenish failed:", err);
    }
  }

  private async _handleMlsWelcome(data: any): Promise<void> {
    const groupId = data.group_id;
    const conversationId = data.conversation_id;
    const conversationGroupId = data.conversation_group_id;
    const kpSource = this._pendingMlsKpBundle ? "pending" : this._mlsKeyPackage ? "connect" : (await hasPendingWelcome(this.config.dataDir, groupId)) ? "persisted" : "generated";
    console.log(`[SecureChannel] Received MLS welcome for group ${groupId?.slice(0, 8)}${conversationId ? ` conv=${conversationId.slice(0, 8)}` : ""} kpSource=${kpSource}`);

    try {
      const welcomeBytes = new Uint8Array(Buffer.from(data.payload, "hex"));

      // Try-each: the Welcome is HPKE-encrypted to exactly one of the KPs we
      // published, and the backend consumes published KPs newest-first (#508) so we can't
      // predict which one the room's creator was served. Build the candidate
      // set from the pending pool, the connect KP, and the persisted per-group
      // bundle (covers multi-instance gateways / restart), then trial-decrypt
      // against each until one joins. A per-candidate failure means "wrong KP",
      // not a broken Welcome — only ALL candidates failing is a real failure.
      const candidates: Array<KpBundle | null | undefined> = [
        ...this._pendingKpBundles,
        this._pendingMlsKpBundle,
        this._mlsKeyPackage,
      ];
      const persisted = await loadPendingKpBundle(this.config.dataDir, groupId);
      if (persisted) {
        candidates.push({
          publicPackage: MLSGroupManager.deserializeKeyPackage(persisted.publicPackageBytes),
          privatePackage: persisted.privatePackage,
        });
      }

      const candidateCount = candidates.filter(Boolean).length;
      const joined = await joinWithFirstMatchingKp(welcomeBytes, candidates);
      if (!joined) {
        console.error(`[SecureChannel] No matching KeyPackage found for Welcome — cannot join group ${groupId?.slice(0, 8)}. The Welcome is encrypted to a published KP's public key; none of our ${candidateCount} candidate bundle(s) decrypted it. Requesting a re-Welcome with a fresh KeyPackage.`);
        // #908: this branch used to return here while the message CLAIMED that
        // "the existing Welcome recovery mechanism will re-request". It did not.
        // _requestWelcomeSelfHeal had exactly one caller — the message_mls
        // handler — which only fires when group CIPHERTEXT arrives. A device
        // that never receives group traffic therefore sat outside the group
        // FOREVER: status ACTIVE, last_seen advancing, zero messages sent, and a
        // log line telling the operator recovery was already in hand.
        //
        // Found live 2026-08-16 (`localg`), after a bridge restart raced the
        // owner's Welcome. Re-requesting also republishes a fresh KeyPackage, so
        // the committer's next addMembers uses one whose private half THIS
        // process holds — re-requesting alone would just reproduce the failure.
        if (groupId) void this._requestWelcomeSelfHeal(groupId);
        return;
      }
      const mgr = joined.mgr;
      // The matched KeyPackage is single-use — drop it from the pool so a later
      // Welcome doesn't reuse it, then fire-and-forget a replenish so the pool
      // tops back up to target. Without this the pool drains monotonically on a
      // long-lived connection and the agent starves after _KP_POOL_TARGET joins.
      this._removeKpFromPool(joined.kp);
      void this._ensureKpPoolPublished().catch((err: unknown) => {
        console.warn("[SecureChannel] KeyPackage pool replenish after join failed:", err);
      });
      console.log(`[SecureChannel] Welcome joined: group=${groupId?.slice(0, 8)} kpSource=${kpSource} welcomeLen=${welcomeBytes.length} candidates=${candidateCount} poolRemaining=${this._pendingKpBundles.length}`);

      // Shared 1:1 group Welcome (has conversation_group_id).
      //
      // #621 fail-safe: `!data.room_id` is load-bearing. This branch used to win
      // on the mere PRESENCE of conversation_group_id, with the room branch
      // reachable only when it was absent — classification by absence again. A
      // room group arriving with a conversation_group_id would be registered
      // under `1to1-group:<id>` and become reachable by the group_id-only search
      // in the 1:1 handler, which is the one place room ciphertext must never
      // land. Not currently exploitable (ws.py's mls_welcome stores only
      // target_device_id, group_id and payload, so the field is server-derived
      // rather than attacker-controlled) — this makes it structurally impossible
      // rather than contingent on that staying true.
      if (conversationGroupId && !data.room_id) {
        const key = `1to1-group:${conversationGroupId}`;
        this._mlsGroups.set(key, mgr);
        await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
        await this._applyBufferedMlsCommits(groupId, mgr);
        if (this._persisted) {
          if (!this._persisted.mlsGroups) this._persisted.mlsGroups = {};
          this._persisted.mlsGroups[conversationGroupId] = {
            mlsGroupId: groupId,
            memberDeviceIds: data.member_device_ids,
          };
          await this._persistState();
        }
        console.log(`[SecureChannel] Joined shared 1:1 MLS group for group ${conversationGroupId.slice(0, 8)} via Welcome (epoch=${mgr.epoch})`);
        // #1418: a group no conversation maps is somebody this agent does not
        // know by conversation yet — a person until the next reconcile says
        // otherwise. Learn who, so routing can tell people apart.
        if (!this._isMappedGroup(conversationGroupId)) {
          this._welcomedUnmapped.set(conversationGroupId, Date.now());
        }
        this._reconcileForUnmappedGroup(conversationGroupId);
        // #952 — anything the agent tried to say before this Welcome landed is
        // parked; send it now, over MLS, in order.
        await this._flushMlsHold(conversationGroupId);
        return;
      }

      // Legacy per-conversation Welcome (backward compat)
      if (conversationId) {
        const key = `conv:${conversationId}`;
        this._mlsGroups.set(key, mgr);
        await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
        // Apply any commits that arrived before this Welcome
        await this._applyBufferedMlsCommits(groupId, mgr);
        // Persist the group ID in state for reconnect recovery
        if (this._persisted) {
          if (!this._persisted.mlsConversations) this._persisted.mlsConversations = {};
          this._persisted.mlsConversations[conversationId] = { mlsGroupId: groupId };
          await this._persistState();
        }
        console.log(`[SecureChannel] Joined 1:1 MLS group for conv ${conversationId.slice(0, 8)} via Welcome (epoch=${mgr.epoch})`);
        return;
      }

      // Room/A2A Welcome — match by group_id OR room_id from Welcome data
      const welcomeRoomId = data.room_id;
      for (const [roomId, room] of Object.entries(this._persisted?.rooms ?? {})) {
        if ((room as RoomState).mlsGroupId === groupId || roomId === welcomeRoomId) {
          // Update the persisted mlsGroupId in case it changed (e.g., nuclear reset)
          (room as RoomState).mlsGroupId = groupId;
          this._mlsGroups.set(roomId, mgr);
          await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
          await this._persistState();
          // Apply any commits that arrived before this Welcome
          await this._applyBufferedMlsCommits(groupId, mgr);
          console.log(`[SecureChannel] Joined MLS group for room ${roomId.slice(0, 8)} via Welcome (epoch=${mgr.epoch})`);
          // Clear pending KP bundle and release room KP lock after successful join
          this._pendingMlsKpBundle = undefined;
          await releaseA2ASyncLock(this.config.dataDir, `room-kp-${groupId}`).catch(() => {});
          return;
        }
      }

      // Fallback: a Welcome for a room we have NOT persisted yet. Happens when we
      // were added to an existing room and the room_joined that registers the room
      // raced behind (or was lost relative to) the Welcome. Without a room entry the
      // room-match loop above can't match, and every subsequent room message would be
      // dropped with "No room found for MLS group". Self-bootstrap a minimal room
      // entry from the Welcome so group→room mapping exists — mirrors the A2A
      // fallback below. A later room_joined enriches name/members. The backend now
      // sends the new device its own room_joined (PR #412); this is defense-in-depth
      // for the race where that delivery is missed.
      if (welcomeRoomId && this._persisted) {
        await this._registerRoomFromWelcome(
          welcomeRoomId,
          groupId,
          mgr,
          data.room_name as string | undefined,
        );
        this._pendingMlsKpBundle = undefined;
        await releaseA2ASyncLock(this.config.dataDir, `room-kp-${groupId}`).catch(() => {});
        console.log(
          `[SecureChannel] Registered room ${welcomeRoomId.slice(0, 8)} from unmatched Welcome (epoch=${mgr.epoch})`,
        );
        return;
      }

      // A2A channel Welcome — match by group_id or a2a_channel_id from the Welcome data
      const a2aChannelId = data.a2a_channel_id;
      for (const [channelId, entry] of Object.entries(this._persisted?.a2aChannels ?? {})) {
        if (entry.mlsGroupId === groupId || channelId === a2aChannelId) {
          entry.mlsGroupId = groupId; // ensure mapping is stored
          this._mlsGroups.set(`a2a:${channelId}`, mgr);
          await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
          await this._persistState();
          // Apply any commits that arrived before this Welcome
          await this._applyBufferedMlsCommits(groupId, mgr);
          // Clear pending lock and KP bundle on successful join
          await clearPendingWelcome(this.config.dataDir, groupId).catch(() => {});
          await releaseA2ASyncLock(this.config.dataDir, channelId).catch(() => {});
          this._pendingMlsKpBundle = undefined;
          console.log(`[SecureChannel] Joined MLS group for A2A ${channelId.slice(0, 8)} via Welcome (epoch=${mgr.epoch})`);

          // Fire onA2AChannelReady now that MLS group is joined and ready for messaging
          if (this.config.onA2AChannelReady && entry.conversationId) {
            const firstPeer = entry.participants?.find(p => p.status === "active" || p.status === "invited");
            this.config.onA2AChannelReady({
              channelId,
              peerHubAddress: firstPeer?.hubAddress || "",
              role: entry.role || "member",
              conversationId: entry.conversationId,
              topic: entry.topic || undefined,
            });
          }
          return;
        }
      }

      // Fallback: if a2a_channel_id provided but not in persisted state yet, store it
      if (a2aChannelId && this._persisted) {
        if (!this._persisted.a2aChannels) this._persisted.a2aChannels = {};
        if (!this._persisted.a2aChannels[a2aChannelId]) {
          this._persisted.a2aChannels[a2aChannelId] = {
            channelId: a2aChannelId,
            conversationId: "",
            mlsGroupId: groupId,
            participants: [],
          };
        } else {
          this._persisted.a2aChannels[a2aChannelId].mlsGroupId = groupId;
        }
        this._mlsGroups.set(`a2a:${a2aChannelId}`, mgr);
        await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
        await this._persistState();
        await releaseA2ASyncLock(this.config.dataDir, a2aChannelId).catch(() => {});
        console.log(`[SecureChannel] Joined MLS group for A2A ${a2aChannelId.slice(0, 8)} via Welcome (new channel, epoch=${mgr.epoch})`);
        return;
      }

      console.warn(`[SecureChannel] MLS welcome for unmatched group ${groupId?.slice(0, 8)} (no room/A2A/conv match)`);
    } catch (err) {
      // Release any A2A sync lock on failure so next cycle can retry
      const failedA2aId = data.a2a_channel_id;
      if (failedA2aId) await releaseA2ASyncLock(this.config.dataDir, failedA2aId).catch(() => {});
      console.error(`[SecureChannel] MLS welcome processing failed (kpSource=${kpSource}):`, err);
      // Re-throw so delivery queue nacks this message instead of silently acking
      throw err;
    }
  }

  /**
   * Self-bootstrap a room entry + MLS group mapping from a Welcome whose room is
   * not yet persisted. Happens when we were added to an EXISTING room and the
   * `room_joined` that registers the room raced behind (or was lost relative to)
   * the Welcome. Without a room entry the Welcome room-match loop can't match, and
   * every subsequent room message is dropped with "No room found for MLS group".
   * Mirrors the A2A self-bootstrap fallback. The backend now also sends the new
   * device its own `room_joined` (PR #412); this is defense-in-depth for the race
   * where that delivery is missed. A later `room_joined` enriches name/members.
   */
  private async _registerRoomFromWelcome(
    roomId: string,
    groupId: string,
    mgr: MLSGroupManager,
    roomName?: string,
  ): Promise<void> {
    if (!this._persisted) return;
    if (!this._persisted.rooms) this._persisted.rooms = {};
    const existing = this._persisted.rooms[roomId] as RoomState | undefined;
    this._persisted.rooms[roomId] = {
      roomId,
      name: existing?.name ?? (roomName ?? ""),
      conversationIds: existing?.conversationIds ?? [],
      members: existing?.members ?? [],
      mlsGroupId: groupId,
      lastMlsMessageTs: existing?.lastMlsMessageTs,
    };
    this._mlsGroups.set(roomId, mgr);
    await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mgr.exportState()));
    await this._persistState();
    await this._applyBufferedMlsCommits(groupId, mgr);
  }

  /**
   * Pull pending MLS messages from the delivery queue and process them.
   * Called on WS connect, on mls_delivery ping, and every 30s heartbeat.
   */
  private async _pullDeliveryQueue(): Promise<void> {
    if (!this._deviceId || !this._deviceJwt) return;
    if (this._deliveryPulling) {
      // #1184: a pull can now hold this flag for seconds (the commit confirm
      // loop). A `mls_delivery` ping or a reconnect landing meanwhile used to
      // be DROPPED — the message then waited for the 30 s heartbeat. Coalesce
      // instead: run one more pull when this one finishes.
      this._deliveryPullRequested = true;
      return;
    }
    this._deliveryPulling = true;

    try {
      const res = await fetch(
        `${this.config.apiUrl}/api/v1/mls/delivery?device_id=${this._deviceId}`,
        { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
      );
      if (!res.ok) {
        console.warn(`[SecureChannel] Delivery pull failed: ${res.status}`);
        return;
      }
      let { messages } = (await res.json()) as { messages: any[] };
      const hasMessages = messages && messages.length > 0;

      // Process delivery queue messages (if any)
      if (hasMessages) {
        // #467: Welcomes first (a commit cannot apply to a group we have not
        // joined), then the server's total order. See mls-delivery-order.ts —
        // the old type-grouped sort hoisted every commit above every
        // application message, so an application encrypted at an intermediate
        // epoch was decrypted after the group had already moved past it.
        messages = orderDeliveryBatch(messages);

        const ackedIds: string[] = [];
        const nackedIds: string[] = [];

        for (const msg of messages) {
          // #1012: a row is acked only when a handler took responsibility for
          // it. Reaching the bottom of the routing chain without throwing is
          // not the same thing as having handled the row.
          let handled = true;
          try {
            if (msg.sender_device_id === this._deviceId) {
              ackedIds.push(msg.queue_id);
              continue;
            }

            if (msg.message_type === "welcome") {
              await this._handleMlsWelcome({
                group_id: msg.group_id,
                sender_device_id: msg.sender_device_id,
                payload: msg.payload,
                room_id: msg.room_id,
                conversation_id: msg.conversation_id,
                conversation_group_id: msg.conversation_group_id,
                a2a_channel_id: msg.a2a_channel_id,
              });
            } else if (msg.message_type === "commit") {
              // #1012: the verdict decides ack vs nack. It used to be discarded.
              handled = await this._handleMlsCommit({
                group_id: msg.group_id,
                sender_device_id: msg.sender_device_id,
                epoch: msg.epoch,
                payload: msg.payload,
              });
            } else if (msg.message_type === "application") {
              // Route to the appropriate handler based on context
              if (msg.room_id) {
                await this._handleRoomMessageMLS({
                  message_id: msg.message_id,
                  group_id: msg.group_id,
                  sender_device_id: msg.sender_device_id,
                  epoch: msg.epoch,
                  payload: msg.payload,
                  room_id: msg.room_id,
                  created_at: msg.created_at,
                });
              } else if (msg.a2a_channel_id) {
                await this._handleA2AMessageMLS({
                  message_id: msg.message_id,
                  group_id: msg.group_id,
                  sender_device_id: msg.sender_device_id,
                  epoch: msg.epoch,
                  payload: msg.payload,
                  a2a_channel_id: msg.a2a_channel_id,
                  created_at: msg.created_at,
                });
              } else if (msg.conversation_id || msg.conversation_group_id) {
                await this._handleMessageMLS({
                  message_id: msg.message_id,
                  group_id: msg.group_id,
                  sender_device_id: msg.sender_device_id,
                  epoch: msg.epoch,
                  payload: msg.payload,
                  conversation_id: msg.conversation_id,
                  conversation_group_id: msg.conversation_group_id,
                  created_at: msg.created_at,
                });
              } else {
                // #1012: this if-chain used to exhaust silently. An application
                // row carrying none of room_id / a2a_channel_id /
                // conversation_id / conversation_group_id reached no handler
                // and was acked anyway.
                handled = false;
                console.warn(
                  `[SecureChannel] Delivery application row ${String(msg.message_id).slice(0, 8)} ` +
                    `names no room, A2A channel or conversation — NOT acked`,
                );
              }
            }
            if (handled) {
              ackedIds.push(msg.queue_id);
            } else {
              // Nacked, not acked: retry_count increments and the row
              // dead-letters after 5 attempts. Visible. An acked row is gone,
              // and an MLS commit cannot be regenerated.
              nackedIds.push(msg.queue_id);
            }
          } catch (err) {
            console.warn(`[SecureChannel] Delivery ${msg.message_type} processing failed:`, err);
            nackedIds.push(msg.queue_id);
            // Epoch recovery for MLS decrypt failures
            if (msg.group_id && (msg.message_type === "commit" || msg.message_type === "application")) {
              const failKey = msg.conversation_group_id ? `1to1-group:${msg.conversation_group_id}` : `group:${msg.group_id}`;
              // Below our epoch = unreadable by forward secrecy, not divergence.
              // Resolve the group by room id (rooms are keyed by roomId in
              // `_mlsGroups`) or by the 1:1 key; an unresolvable group counts,
              // as the classifier's unknown rule requires.
              const localMgr = (msg.room_id && this._mlsGroups.get(msg.room_id)) || this._mlsGroups.get(failKey);
              if (classifyCommitFailure(msg.epoch, localMgr?.epoch) === "already-applied") {
                console.debug(`[SecureChannel] Delivery ${msg.message_type} at epoch ${String(msg.epoch)} predates our epoch ${String(localMgr?.epoch)} — not counting toward recovery`);
                continue;
              }
              const count = (this._mlsDecryptFailCounts.get(failKey) ?? 0) + 1;
              this._mlsDecryptFailCounts.set(failKey, count);
              if (count >= SecureChannel.MAX_MLS_DECRYPT_FAILS) {
                await this._triggerMlsEpochRecovery(failKey, msg.group_id);
              }
            }
          }
        }

        // Batch nack failed messages (increments retry_count, dead-letter after 5)
        if (nackedIds.length > 0) {
          try {
            await fetch(`${this.config.apiUrl}/api/v1/mls/delivery/nack`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${this._deviceJwt}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ queue_ids: nackedIds }),
            });
          } catch { /* best effort */ }
        }

        // Batch ack
        if (ackedIds.length > 0) {
          try {
            await fetch(`${this.config.apiUrl}/api/v1/mls/delivery/ack`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${this._deviceJwt}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ queue_ids: ackedIds, device_id: this._deviceId }),
            });
            console.log(`[SecureChannel] Delivery acked ${ackedIds.length}/${messages.length}`);
          } catch (ackErr) {
            console.warn("[SecureChannel] Delivery ack failed:", ackErr);
          }
        }
      } // end hasMessages

      // Check for pending Welcome requests for rooms we have MLS state
      for (const [roomId, mlsGroup] of this._mlsGroups) {
        if (!mlsGroup?.isInitialized) continue;
        if (roomId.includes(":")) continue; // Skip non-room entries (conv:xxx, a2a:xxx)

        const roomState = this._persisted?.rooms?.[roomId];
        if (!roomState?.mlsGroupId) continue;

        try {
          const roomLabel = `room ${roomId.slice(0, 8)}`;
          const pendingRes = await fetch(
            `${this.config.apiUrl}/api/v1/mls/groups/${roomState.mlsGroupId}/pending-welcomes`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          if (!pendingRes.ok) {
            this._welcomeKpBackoff.noteFetchFailure(`pending-welcomes for ${roomLabel}`, pendingRes.status);
            continue;
          }
          this._welcomeKpBackoff.noteFetchOk(`pending-welcomes for ${roomLabel}`);
          let pending: PendingWelcomeRow[] = await pendingRes.json();
          // #1428: log the count only when the pending SET changes, and drop
          // backoffs for requests that are gone.
          if (this._welcomeKpBackoff.observePending(roomState.mlsGroupId, pending)) {
            console.log(`[SecureChannel] ${pending.length} pending Welcome requests for room ${roomId.slice(0, 8)}`);
          }
          if (pending.length === 0) continue;

          // #1428: a device whose KeyPackage the batch cannot serve is not
          // asked for again until the backoff lapses or the server says it has
          // one. Gated BEFORE the #1171 cooldown so a waiting device is neither
          // stamped nor re-logged as "re-admitted Ns ago" every pull.
          const nowKp = Date.now();
          const roomMlsGroupId = roomState.mlsGroupId;
          pending = pending.filter((r) =>
            this._welcomeKpBackoff.shouldAsk(roomMlsGroupId, r.requesting_device_id, r.key_package_available, roomLabel, nowKp),
          );
          if (pending.length === 0) continue;

          // #1171 — do not re-admit the same device again within the cooldown.
          //
          // The web client has had `FULFILL_COOLDOWN_MS` since #17; this path
          // never did, because the server returning `[]` was the only brake and
          // that was considered enough. It is not: on 2026-09-03 a nominated
          // agent re-admitted three devices that never consumed their Welcome,
          // saw the fresh requests, and went round nine times — epoch 139 -> 160
          // at +2 a round. A member that cannot converge will ask forever, so
          // the fulfiller has to be the one that stops.
          const nowMs = Date.now();
          const fresh = pending.filter((r) => {
            const key = `${roomState.mlsGroupId}:${r.requesting_device_id}`;
            const last = this._roomFulfilCooldown.get(key) ?? 0;
            if (nowMs - last < ROOM_FULFIL_COOLDOWN_MS) {
              console.log(
                `[SecureChannel] skipping ${r.requesting_device_id.slice(0, 8)} in room ` +
                  `${roomId.slice(0, 8)} — re-admitted ${Math.round((nowMs - last) / 1000)}s ago`,
              );
              return false;
            }
            return true;
          });
          if (fresh.length === 0) continue;
          // #1429: nothing is claimed (or stamped) for a commit that cannot go out.
          const roomPreHead = await this._welcomeCommitPreflight(roomState.mlsGroupId, mlsGroup, roomLabel);
          if (!roomPreHead) continue;
          for (const r of fresh) {
            this._roomFulfilCooldown.set(`${roomState.mlsGroupId}:${r.requesting_device_id}`, nowMs);
          }
          pending = fresh;

          // ONE commit for N requests (#1168). This site used to loop
          // `for (const req of pending)` with `targets: [one]` — "one commit
          // per request, as before" — while the shared-group site below has
          // batched all along. That difference is not cosmetic: a repair that
          // moves the epoch once per member races itself. Measured live on
          // 2026-08-28, room 007 ran epoch 71 -> 85 in seven seconds and the
          // Welcome minted for member n named an epoch that member n+1's commit
          // had already replaced, so six of seven were stale before they
          // finished joining (packages/crypto/src/mls-group.ts:431-455).
          //
          // It matters most in exactly the case nobody tested: several devices
          // stuck at once. One device is indistinguishable between the two
          // shapes, which is why looping survived this long.
          const deviceIds = pending.map((r) => r.requesting_device_id);
          const kpRes = await fetch(
            `${this.config.apiUrl}/api/v1/mls/key-packages/batch?device_ids=${deviceIds.join(",")}`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          if (!kpRes.ok) {
            this._welcomeKpBackoff.noteFetchFailure(`KeyPackage batch for ${roomLabel}`, kpRes.status);
            continue;
          }
          this._welcomeKpBackoff.noteFetchOk(`KeyPackage batch for ${roomLabel}`);
          const kpData: Record<string, string> = await kpRes.json();

          const roomTargets: Array<{ requestId: string; deviceId: string; keyPackage: any }> = [];
          for (const req of pending) {
            const kpHex = kpData[req.requesting_device_id];
            this._welcomeKpBackoff.recordKeyPackage(
              roomState.mlsGroupId, req.requesting_device_id, Boolean(kpHex), req.key_package_available, roomLabel,
            );
            if (!kpHex) {
              // Skip the one device, keep the batch. Refusing the whole rescue
              // because a single member published no KeyPackage is how one
              // stale enrollment strands everybody else. (#1428: the backoff
              // logged it once.)
              continue;
            }
            const kpBytes = new Uint8Array(Buffer.from(kpHex, "hex"));
            roomTargets.push({
              requestId: req.id,
              deviceId: req.requesting_device_id,
              keyPackage: MLSGroupManager.deserializeKeyPackage(kpBytes),
            });
          }
          if (roomTargets.length === 0) continue;

          try {
            // #1015: transmit-then-fulfil, with rollback if the commit never
            // reached the wire.
            const done = await this._fulfilWelcomeRequests({
              mlsGroup,
              mlsGroupId: roomState.mlsGroupId,
              label: `room ${roomId.slice(0, 8)}`,
              welcomeFields: { room_id: roomId },
              targets: roomTargets,
              memberDeviceIds: (roomState.members ?? []).map((m) => m.deviceId), // #1226
              preHead: roomPreHead, // #1429
            });
            if (done > 0) {
              console.log(
                `[SecureChannel] Batched Welcome for ${done}/${roomTargets.length} device(s) in room ${roomId.slice(0, 8)} (epoch=${mlsGroup.epoch})`,
              );
            }
          } catch (fulfillErr) {
            console.warn(`[SecureChannel] Batched Welcome failed for room ${roomId.slice(0, 8)}:`, fulfillErr);
          }
        } catch {
          // Non-critical — will retry on next pull
        }
      }

      // --- Shared 1:1 group Welcome fulfillment (batched) ---
      for (const [gid, entry] of Object.entries(this._persisted?.mlsGroups ?? {})) {
        if (!entry.mlsGroupId) continue;
        const mlsGroup = this._mlsGroups.get(`1to1-group:${gid}`);
        if (!mlsGroup?.isInitialized) continue;

        try {
          const sharedLabel = `shared group ${gid.slice(0, 8)}`;
          const pendingRes = await fetch(
            `${this.config.apiUrl}/api/v1/mls/groups/${entry.mlsGroupId}/pending-welcomes`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          if (!pendingRes.ok) {
            this._welcomeKpBackoff.noteFetchFailure(`pending-welcomes for ${sharedLabel}`, pendingRes.status);
            continue;
          }
          this._welcomeKpBackoff.noteFetchOk(`pending-welcomes for ${sharedLabel}`);
          const allPending: PendingWelcomeRow[] = await pendingRes.json();
          // #1428: the count is logged only when the pending set changes —
          // this line alone was 50,760 lines of loopita's stdout.
          if (this._welcomeKpBackoff.observePending(entry.mlsGroupId, allPending)) {
            console.log(`[SecureChannel] ${allPending.length} pending Welcome requests for shared group ${gid.slice(0, 8)}`);
          }
          const nowKp = Date.now();
          const pending = allPending.filter((r) =>
            this._welcomeKpBackoff.shouldAsk(entry.mlsGroupId, r.requesting_device_id, r.key_package_available, sharedLabel, nowKp),
          );
          if (pending.length === 0) continue;
          // #1429: nothing is claimed for a commit that cannot go out.
          const sharedPreHead = await this._welcomeCommitPreflight(entry.mlsGroupId, mlsGroup, sharedLabel);
          if (!sharedPreHead) continue;

          // Batch: fetch all KeyPackages at once
          const deviceIds = pending.map((r) => r.requesting_device_id);
          const kpRes = await fetch(
            `${this.config.apiUrl}/api/v1/mls/key-packages/batch?device_ids=${deviceIds.join(",")}`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          if (!kpRes.ok) {
            this._welcomeKpBackoff.noteFetchFailure(`KeyPackage batch for ${sharedLabel}`, kpRes.status);
            continue;
          }
          this._welcomeKpBackoff.noteFetchOk(`KeyPackage batch for ${sharedLabel}`);
          const kpData: Record<string, string> = await kpRes.json();

          // Collect valid KeyPackages for batched addMembers
          const validReqs: Array<{ req: typeof pending[0]; kp: any }> = [];
          for (const req of pending) {
            const kpHex = kpData[req.requesting_device_id];
            this._welcomeKpBackoff.recordKeyPackage(
              entry.mlsGroupId, req.requesting_device_id, Boolean(kpHex), req.key_package_available, sharedLabel,
            );
            if (!kpHex) continue; // #1428: logged once by the backoff
            const kpBytes = new Uint8Array(Buffer.from(kpHex, "hex"));
            validReqs.push({ req, kp: MLSGroupManager.deserializeKeyPackage(kpBytes) });
          }

          if (validReqs.length === 0) continue;

          // Single addMembers call with all KeyPackages — one commit, individual Welcomes
          const sharedTargets = validReqs.map((v) => ({
            requestId: v.req.id,
            deviceId: v.req.requesting_device_id,
            keyPackage: v.kp,
          }));
          try {
            // #1015: transmit-then-fulfil. The batched fulfil POST here was
            // `.catch(() => {})`, so an HTTP failure was invisible on top of
            // the sends being invisible.
            const done = await this._fulfilWelcomeRequests({
              mlsGroup,
              mlsGroupId: entry.mlsGroupId,
              label: `shared group ${gid.slice(0, 8)}`,
              welcomeFields: { conversation_group_id: gid },
              targets: sharedTargets,
              memberDeviceIds: entry.memberDeviceIds ?? [], // #1226
              preHead: sharedPreHead, // #1429
            });
            if (done > 0) {
              console.log(`[SecureChannel] Batched Welcome for ${done}/${validReqs.length} devices in shared group ${gid.slice(0, 8)} (epoch=${mlsGroup.epoch})`);
            }
          } catch (batchErr) {
            console.warn(`[SecureChannel] Batched Welcome failed for shared group ${gid.slice(0, 8)}, falling back to individual:`, batchErr);
            // Fallback: add one at a time
            for (const target of sharedTargets) {
              try {
                const done = await this._fulfilWelcomeRequests({
                  mlsGroup,
                  mlsGroupId: entry.mlsGroupId,
                  label: `shared group ${gid.slice(0, 8)}`,
                  welcomeFields: { conversation_group_id: gid },
                  targets: [target],
                  memberDeviceIds: entry.memberDeviceIds ?? [], // #1226
                });
                if (done > 0) {
                  console.log(`[SecureChannel] Individual Welcome for ${target.deviceId.slice(0, 8)} in shared group ${gid.slice(0, 8)}`);
                }
              } catch (indivErr) {
                console.warn(`[SecureChannel] Individual Welcome failed for ${target.deviceId.slice(0, 8)}:`, indivErr);
              }
            }
          }
        } catch {
          // Non-critical — will retry on next pull
        }
      }

      // --- 1:1 conversation Welcome fulfillment ---
      for (const [convId, convEntry] of Object.entries(this._persisted?.mlsConversations ?? {})) {
        if (!convEntry.mlsGroupId) continue;
        const mlsGroup = this._mlsGroups.get(`conv:${convId}`);
        if (!mlsGroup?.isInitialized) continue;

        try {
          const convLabel = `conv ${convId.slice(0, 8)}`;
          const pendingRes = await fetch(
            `${this.config.apiUrl}/api/v1/mls/groups/${convEntry.mlsGroupId}/pending-welcomes`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          if (!pendingRes.ok) {
            this._welcomeKpBackoff.noteFetchFailure(`pending-welcomes for ${convLabel}`, pendingRes.status);
            continue;
          }
          this._welcomeKpBackoff.noteFetchOk(`pending-welcomes for ${convLabel}`);
          const pending: PendingWelcomeRow[] = await pendingRes.json();
          if (this._welcomeKpBackoff.observePending(convEntry.mlsGroupId, pending)) {
            console.log(`[SecureChannel] ${pending.length} pending Welcome requests for conv ${convId.slice(0, 8)}`);
          }
          if (pending.length === 0) continue;

          for (const req of pending) {
            try {
              if (!this._welcomeKpBackoff.shouldAsk(
                convEntry.mlsGroupId, req.requesting_device_id, req.key_package_available, convLabel,
              )) continue;
              // #1429: nothing is claimed for a commit that cannot go out. A
              // failed pre-flight holds for every request in this group.
              const convPreHead = await this._welcomeCommitPreflight(convEntry.mlsGroupId, mlsGroup, convLabel);
              if (!convPreHead) break;
              const kpRes = await fetch(
                `${this.config.apiUrl}/api/v1/mls/key-packages/batch?device_ids=${req.requesting_device_id}`,
                { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
              );
              if (!kpRes.ok) {
                this._welcomeKpBackoff.noteFetchFailure(`KeyPackage batch for ${convLabel}`, kpRes.status);
                continue;
              }
              this._welcomeKpBackoff.noteFetchOk(`KeyPackage batch for ${convLabel}`);
              const kpData: Record<string, string> = await kpRes.json();
              const kpHex = kpData[req.requesting_device_id];
              this._welcomeKpBackoff.recordKeyPackage(
                convEntry.mlsGroupId, req.requesting_device_id, Boolean(kpHex), req.key_package_available, convLabel,
              );
              if (!kpHex) continue;

              const kpBytes = new Uint8Array(Buffer.from(kpHex, "hex"));
              const memberKp = MLSGroupManager.deserializeKeyPackage(kpBytes);

              // #1015: transmit-then-fulfil, with rollback if the commit never
              // reached the wire.
              const done = await this._fulfilWelcomeRequests({
                mlsGroup,
                mlsGroupId: convEntry.mlsGroupId,
                label: `conv ${convId.slice(0, 8)}`,
                welcomeFields: { conversation_id: convId },
                targets: [{
                  requestId: req.id,
                  deviceId: req.requesting_device_id,
                  keyPackage: memberKp,
                }],
                // #1226: a 1:1 roster is [self, owner] — the web's shape too.
                memberDeviceIds: [this._persisted?.sessions?.[convId]?.ownerDeviceId].filter(
                  (d): d is string => typeof d === "string",
                ),
                preHead: convPreHead, // #1429
              });
              if (done > 0) {
                console.log(`[SecureChannel] Fulfilled Welcome for ${req.requesting_device_id.slice(0, 8)} in conv ${convId.slice(0, 8)}`);
              }
            } catch (fulfillErr) {
              console.warn(`[SecureChannel] Conv Welcome fulfill failed for ${req.requesting_device_id.slice(0, 8)}:`, fulfillErr);
            }
          }
        } catch {
          // Non-critical — will retry on next pull
        }
      }

      // --- A2A channel Welcome fulfillment ---
      for (const [chId, chState] of Object.entries(this._persisted?.a2aChannels ?? {})) {
        // If creator has no mlsGroupId yet, found the group first
        if (!chState.mlsGroupId && chState.role === "creator") {
          try {
            console.log(`[SecureChannel] Creator founding A2A MLS group for ${chId.slice(0, 8)} (no mlsGroupId)`);
            await this._foundA2AGroup(chId, chState.conversationId, chState.participants ?? []);
          } catch (foundErr) {
            console.warn(`[SecureChannel] A2A MLS founding failed for ${chId.slice(0, 8)}:`, foundErr);
          }
        }
        if (!chState.mlsGroupId) continue;
        let mlsGroup = this._mlsGroups.get(`a2a:${chId}`);

        // Try to restore MLS state from disk if not in memory
        if (!mlsGroup?.isInitialized) {
          try {
            const savedState = await loadMlsState(this.config.dataDir, chState.mlsGroupId);
            if (savedState) {
              const mgr = new MLSGroupManager();
              mgr.importState(JSON.parse(savedState) as SerializedMLSGroupState);
              this._mlsGroups.set(`a2a:${chId}`, mgr);
              mlsGroup = mgr;
              console.log(`[SecureChannel] Restored A2A MLS state for ${chId.slice(0, 8)} during delivery pull`);
            }
          } catch { /* ignore restore failure */ }
        }

        // If still not initialized and we're the creator, found the group now
        if (!mlsGroup?.isInitialized && chState.role === "creator") {
          try {
            console.log(`[SecureChannel] Founding A2A MLS group for ${chId.slice(0, 8)} during delivery pull`);
            await this._foundA2AGroup(chId, chState.conversationId, chState.participants ?? []);
            mlsGroup = this._mlsGroups.get(`a2a:${chId}`);
          } catch (foundErr) {
            console.warn(`[SecureChannel] A2A MLS founding failed for ${chId.slice(0, 8)}:`, foundErr);
          }
        }

        if (!mlsGroup?.isInitialized) continue;

        // Only the creator instance should fulfill pending Welcome requests.
        // Without this guard, multiple instances restore the same MLS group from
        // disk and race on addMembers, producing duplicate commits that desync
        // other members' epoch state (BUG-20260408-002).
        if (chState.role !== "creator") continue;

        try {
          const a2aPendingLabel = `A2A ${chId.slice(0, 8)}`;
          const pendingRes = await fetch(
            `${this.config.apiUrl}/api/v1/mls/groups/${chState.mlsGroupId}/pending-welcomes`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          if (!pendingRes.ok) {
            this._welcomeKpBackoff.noteFetchFailure(`pending-welcomes for ${a2aPendingLabel}`, pendingRes.status);
            continue;
          }
          this._welcomeKpBackoff.noteFetchOk(`pending-welcomes for ${a2aPendingLabel}`);
          const pending: PendingWelcomeRow[] = await pendingRes.json();
          if (this._welcomeKpBackoff.observePending(chState.mlsGroupId, pending)) {
            console.log(`[SecureChannel] ${pending.length} pending A2A Welcome requests for channel ${chId.slice(0, 8)}`);
          }
          if (pending.length === 0) continue;

          for (const req of pending) {
            // #1428: checked BEFORE `_fulfilA2AWelcome`, which reads the head
            // and reserves the commit slot before it ever asks the batch.
            if (!this._welcomeKpBackoff.shouldAsk(
              chState.mlsGroupId, req.requesting_device_id, req.key_package_available, a2aPendingLabel,
            )) continue;
            try {
              const next = await this._fulfilA2AWelcome(chId, chState, mlsGroup, req);
              if (next === "stop") break;
            } catch (fulfillErr) {
              console.log(`[SecureChannel] A2A Welcome fulfill failed: ${fulfillErr instanceof Error ? fulfillErr.message : String(fulfillErr)}`);
            }
          }
        } catch {
          // Non-critical
        }
      }
    } catch (err) {
      console.warn("[SecureChannel] Delivery pull error:", err);
    } finally {
      this._deliveryPulling = false;
      if (this._deliveryPullRequested) {
        this._deliveryPullRequested = false;
        void this._pullDeliveryQueue().catch((err) => console.warn("[SecureChannel] Coalesced delivery pull error:", err));
      }
    }
  }

  /**
   * Pull pending DR (Double Ratchet) messages from the delivery queue.
   * Signal-model queue-first: called on WS connect and on dr_delivery ping.
   */
  private async _pullDrDeliveryQueue(): Promise<void> {
    if (!this._deviceId || !this._deviceJwt || this._drDeliveryPulling) return;
    this._drDeliveryPulling = true;

    try {
      // Uses authedFetch — DR delivery pull was one of the two endpoints
      // observed 401'ing post-expiry. One reissue+retry per WS session.
      const res = await this._authedFetch(
        `${this.config.apiUrl}/api/v1/dr/delivery?device_id=${this._deviceId}`,
      );
      if (!res.ok) {
        console.warn(`[SecureChannel] DR delivery pull failed: ${res.status}`);
        return;
      }
      const { messages } = (await res.json()) as { messages: any[] };
      if (!messages || messages.length === 0) return;

      const ackedIds: string[] = [];
      const nackedIds: string[] = [];

      for (const msg of messages) {
        try {
          // Skip own messages
          if (msg.sender_device_id === this._deviceId) {
            ackedIds.push(msg.queue_id);
            continue;
          }

          // Dedup: skip messages already processed
          if (msg.message_id && this._seenMessageIds.has(msg.message_id)) {
            ackedIds.push(msg.queue_id);
            continue;
          }

          // Room/A2A messages — skip (a non-DM conv delivered here would emit as a
          // no-roomId `message`, which the native worker tool-gate treats as an
          // owner DM: tool-enabled, bypassing arming). This endpoint is
          // `/dr/delivery` -> dr_delivery_service.pull_pending, which carries
          // `room_id` (populated from the conversation, mirroring the poll endpoint;
          // the DrDeliveryQueue is 1:1-only today so it is null in practice but
          // becomes a real server-field backstop for the cold-local-state window the
          // instant any room-backed conv ever DR-delivers). It carries NO
          // `a2a_channel_id` — A2A never rides the DR queue (MLS-only), so that
          // disjunct is a structurally-absent defensive check. `_isNonDmConversation`
          // (local rooms + a2aChannels) is the belt. Ack the queue_id so the server
          // does not redeliver the skipped message.
          if (msg.room_id || msg.a2a_channel_id || this._isNonDmConversation(msg.conversation_id, msg.conversation_group_id)) {
            ackedIds.push(msg.queue_id);
            continue;
          }

          if (!msg.header_blob || !msg.ciphertext) {
            ackedIds.push(msg.queue_id);
            continue;
          }

          const session = this._sessions.get(msg.conversation_id);
          if (!session) {
            console.warn(`[SecureChannel] DR delivery: no session for conv ${msg.conversation_id.slice(0, 8)}, skipping`);
            continue; // Don't ack — will retry when session exists
          }

          const encrypted = transportToEncryptedMessage({
            header_blob: msg.header_blob,
            ciphertext: msg.ciphertext,
          });

          const ratchetSnapshot = session.ratchet.serialize();
          let plaintext: string;
          try {
            plaintext = session.ratchet.decrypt(encrypted);
          } catch (decryptErr) {
            console.error(`[SecureChannel] DR delivery decrypt failed for conv ${msg.conversation_id.slice(0, 8)} — restoring ratchet:`, decryptErr);
            try { session.ratchet = DoubleRatchet.deserialize(ratchetSnapshot); } catch {}
            nackedIds.push(msg.queue_id);
            continue;
          }

          // Mark as seen
          if (msg.message_id) {
            this._seenMessageIds.add(msg.message_id);
            if (this._seenMessageIds.size > SecureChannel.SEEN_MSG_MAX) {
              const first = this._seenMessageIds.values().next().value;
              if (first) this._seenMessageIds.delete(first);
            }
          }

          this._sendAck(msg.message_id);

          // Activate session on first owner message
          if (!session.activated) {
            session.activated = true;
            console.log(`[SecureChannel] Session ${msg.conversation_id.slice(0, 8)}... activated via DR delivery`);
            if (this._persisted?.sessions[msg.conversation_id]) {
              this._persisted.sessions[msg.conversation_id].activated = true;
            }
            this._activateSiblings(msg.conversation_id);
            await this._persistState();
          }

          // Parse structured payload
          let messageText: string;
          let messageType: string;
          let decisionReply: DecisionResponse | undefined;
          try {
            const parsed = JSON.parse(plaintext);
            messageType = parsed.type || "message";
            messageText = envelopeText(parsed, plaintext);
            decisionReply = decisionReplyFromEnvelope(parsed);
          } catch {
            messageType = "message";
            messageText = plaintext;
          }

          if (messageType === "session_init") {
            console.log(`[SecureChannel] session_init received via DR delivery for ${msg.conversation_id.slice(0, 8)}...`);
            await this._replayHistoryToSession(msg.conversation_id);
            await this._persistState();
            ackedIds.push(msg.queue_id);
            continue;
          }

          if (messageType === "message" || (messageType === "decision_response" && decisionReply)) {
            const topicId = msg.topic_id;
            await this._appendHistory("owner", messageText, this._dmHistoryContext(topicId, undefined, msg.conversation_id), msg.created_at, msg.message_id);

            const metadata: MessageMetadata = {
              messageId: msg.message_id,
              conversationId: msg.conversation_id,
              timestamp: msg.created_at,
              topicId,
              // #621: positive attestation that this decrypted under the OWNER'S 1:1
              // material (MLS 1to1-group map, or a Double Ratchet session), NOT merely
              // that a roomId was absent. The bridge's full-access DM lane requires it.
              ownerDm1to1: true,
              ...(decisionReply ? { messageType: "decision_response", decision: decisionReply } : {}),
            };

            this._noteInboundDm(msg.conversation_id, undefined, { createdAt: msg.created_at, senderDeviceId: msg.sender_device_id }); // #1418

            this.emit("message", messageText, metadata);
            Promise.resolve(this.config.onMessage?.(messageText, metadata)).catch((err: unknown) => {
              console.error("[SecureChannel] onMessage callback error:", err);
            });
          }

          this._persisted!.lastMessageTimestamp = msg.created_at;
          ackedIds.push(msg.queue_id);
        } catch (err) {
          console.warn(`[SecureChannel] DR delivery processing failed for ${msg.message_id?.slice(0, 8)}:`, err);
          nackedIds.push(msg.queue_id);
        }
      }

      await this._persistState();

      // Batch nack
      if (nackedIds.length > 0) {
        try {
          await fetch(`${this.config.apiUrl}/api/v1/dr/delivery/nack`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${this._deviceJwt}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ queue_ids: nackedIds }),
          });
        } catch { /* best effort */ }
      }

      // Batch ack
      if (ackedIds.length > 0) {
        try {
          await fetch(`${this.config.apiUrl}/api/v1/dr/delivery/ack`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${this._deviceJwt}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ queue_ids: ackedIds, device_id: this._deviceId }),
          });
          console.log(`[SecureChannel] DR delivery acked ${ackedIds.length}/${messages.length}`);
        } catch (ackErr) {
          console.warn("[SecureChannel] DR delivery ack failed:", ackErr);
        }
      }
    } catch (err) {
      console.warn("[SecureChannel] DR delivery pull error:", err);
    } finally {
      this._drDeliveryPulling = false;
    }
  }

  private async _handleMlsSyncResponse(data: any): Promise<void> {
    const action = data.action;
    const groupId = data.group_id;

    if (action === "rejoin") {
      console.log(`[SecureChannel] MLS sync: re-join needed for group ${groupId?.slice(0, 8)}`);
      // #1016: this used to say "Delete old state, server will send fresh
      // Welcome". THE SERVER DOES NOT. `mls.py` returns
      // `{"action": "rejoin", "messages": []}` and nothing else, and the
      // `welcome` field declared on `MLSSyncResponse` is populated by no code
      // path. So the agent threw its group away and sat with no group and no
      // pending request — a mechanism for the `smartcode` stranding. Its only
      // escape was undecryptable ciphertext arriving, which never happens in a
      // room where it is the last party still speaking.
      if (groupId) {
        await deleteMlsState(this.config.dataDir, groupId);

        // #1016, second defect: this loop covered `rooms` ONLY, so for a shared
        // 1:1, legacy or A2A group the disk file went and the live manager
        // stayed — the agent kept encrypting against a group whose persisted
        // state was gone, and lost it entirely on the next restart.
        const target = this._resolveGroupFamily(groupId);
        if (target) {
          this._mlsGroups.delete(target.managerKey);
          console.log(`[SecureChannel] MLS sync rejoin: dropped local state for ${target.label}`);
        }

        // Ask for the Welcome nobody was going to send. Not knowing which
        // family the group belongs to is no reason to sit silent — that is the
        // stranding itself.
        void this._requestWelcomeSelfHeal(groupId);
      }
    } else if (action === "replay_complete") {
      console.log(`[SecureChannel] MLS sync complete for group ${groupId?.slice(0, 8)} (${data.count} messages replayed)`);
    }
  }

  /**
   * Handle an incoming MLS-encrypted A2A message.
   * The observer (owner device) decrypts as a regular MLS group member.
   */
  private async _handleA2AMessageMLS(data: any): Promise<void> {
    const groupId = data.group_id;

    // #1013: every inbound frame reports the epoch the group was at when it
    // was produced — a lower bound on where the group is now.
    this._noteGroupEpoch(groupId, data.epoch);
    // Accept either `channel_id` (legacy direct-event payload — e.g., when
    // dispatched from the WS handler at channel.ts:3375) or `a2a_channel_id`
    // (the field name used by _pullDeliveryQueue when it routes pulled
    // messages here at channel.ts:5127). Field-name divergence between the
    // two call sites silently dropped every inbound A2A application message
    // that came via the pull-delivery path — diagnosed live 2026-05-24
    // against a Hermes-to-OC A2A round-trip.
    const channelId = data.channel_id ?? data.a2a_channel_id;

    // Find A2A channel by channel ID or MLS group ID
    let a2aChannelId = channelId;
    if (!a2aChannelId) {
      for (const [cid, entry] of Object.entries(this._persisted?.a2aChannels ?? {})) {
        if (entry.mlsGroupId === groupId) {
          a2aChannelId = cid;
          break;
        }
      }
    }

    if (!a2aChannelId) {
      console.warn(`[SecureChannel] No A2A channel for MLS group ${groupId?.slice(0, 8)}`);
      return;
    }

    let mlsGroup = this._mlsGroups.get(`a2a:${a2aChannelId}`);

    // Try to restore MLS state from disk if not in memory (e.g. after restart)
    if (!mlsGroup?.isInitialized) {
      const entry = this._persisted?.a2aChannels?.[a2aChannelId];
      const mlsGroupId = entry?.mlsGroupId || groupId;
      if (mlsGroupId) {
        try {
          const savedState = await loadMlsState(this.config.dataDir, mlsGroupId);
          if (savedState) {
            const mgr = new MLSGroupManager();
            mgr.importState(JSON.parse(savedState) as SerializedMLSGroupState);
            this._mlsGroups.set(`a2a:${a2aChannelId}`, mgr);
            mlsGroup = mgr;
            console.log(`[SecureChannel] Restored A2A MLS state for ${a2aChannelId.slice(0, 8)} on message receive (epoch=${mgr.epoch})`);
          }
        } catch { /* ignore restore failure */ }
      }
    }

    if (!mlsGroup?.isInitialized) {
      console.warn(`[SecureChannel] MLS group not loaded for A2A ${a2aChannelId.slice(0, 8)}`);
      return;
    }

    try {
      const ciphertext = new Uint8Array(Buffer.from(data.payload, "hex"));
      const result = await mlsGroup.decrypt(ciphertext);
      // A successful decrypt proves the group is live: clear the run. Before the
      // handshake return below.
      this._mlsDecryptFailCounts.delete(`a2a:${a2aChannelId}`);

      // Persist updated state
      await saveMlsState(this.config.dataDir, groupId, JSON.stringify(mlsGroup.exportState()));

      if (result.isHandshake || !result.plaintext) {
        // Handshake message (commit/proposal) — no application data
        return;
      }

      const plaintext = new TextDecoder().decode(result.plaintext);
      const historyText = await this._captureHistoryApplication(
        "owner",
        plaintext,
        "a2a",
        a2aChannelId,
        data.created_at,
        // Backend transport values here are "a2a"/"a2a_message_mls", not
        // application envelope types. Parsed envelope.type still overrides.
        "message",
      );
      if (historyText === null) return;

      const entry = this._persisted?.a2aChannels?.[a2aChannelId];
      const a2aMsg: A2AMessage = {
        text: historyText,
        fromHubAddress: data.from_hub_address || data.hub_address || entry?.participants?.find(p => p.status === "active")?.hubAddress || "",
        channelId: a2aChannelId,
        conversationId: data.conversation_id || entry?.conversationId || "",
        parentSpanId: data.parent_span_id,
        timestamp: data.created_at || new Date().toISOString(),
      };

      console.log(`[SecureChannel] A2A message decrypted for ${a2aChannelId.slice(0, 8)} (epoch=${mlsGroup.epoch}, len=${historyText.length})`);
      this.emit("a2a_message", a2aMsg);
      if (this.config.onA2AMessage) {
        this.config.onA2AMessage(a2aMsg);
      }
    } catch (err) {
      // Below our epoch = unreadable by forward secrecy, not divergence (see #920).
      if (classifyCommitFailure(data.epoch, mlsGroup?.epoch) === "already-applied") {
        console.debug(`[SecureChannel] MLS A2A message at epoch ${String(data.epoch)} predates our epoch ${String(mlsGroup?.epoch)} for ${a2aChannelId.slice(0, 8)} — cannot decrypt, ignoring`);
        return;
      }
      const failKey = `a2a:${a2aChannelId}`;
      const count = (this._mlsDecryptFailCounts.get(failKey) ?? 0) + 1;
      this._mlsDecryptFailCounts.set(failKey, count);
      console.error(`[SecureChannel] MLS A2A decrypt failed (${count}/${SecureChannel.MAX_MLS_DECRYPT_FAILS}) for ${a2aChannelId.slice(0, 8)}:`, err);
      if (count >= SecureChannel.MAX_MLS_DECRYPT_FAILS && groupId) {
        // #440: catch up a behind member via /sync replay before re-joining.
        if (!(await this._resyncOnDivergence(failKey, groupId))) {
          await this._triggerMlsEpochRecovery(failKey, groupId);
        }
      }
    }
  }

  /**
   * #1225: fulfil ONE pending A2A Welcome request — the `/distribute` path.
   * Extracted from the delivery pull so the in-flight slot (review F1) is held
   * for exactly this request and released on every exit. Returns "stop" when
   * nothing else on this channel can succeed this pull (review F2).
   */
  private async _fulfilA2AWelcome(
    chId: string,
    chState: any,
    mlsGroup: any,
    req: PendingWelcomeRow,
  ): Promise<"next" | "stop"> {
    const a2aLabel = `A2A ${chId.slice(0, 8)}`;
    // Pre-flight (review S4): a commit built on a stale epoch WILL be refused —
    // do not build it and do not spend one of the requester's KeyPackages on it.
    const preHead = await this._readGroupHead(chState.mlsGroupId);
    if (preHead.ok && preHead.epoch !== Number(mlsGroup.epoch)) {
      console.warn(
        `[SecureChannel] ${a2aLabel}: server head is at epoch ${preHead.epoch}, we are at ${Number(mlsGroup.epoch)} — ` +
          `NOT committing (it would be refused); resync catches up first`,
          );
          await this._catchUpAfterPreflightSkip(chState.mlsGroupId, preHead.epoch, Number(mlsGroup.epoch), a2aLabel);
          return "stop";
    }
    const inFlight = this._reserveCommit(chState.mlsGroupId, a2aLabel);
    if (!inFlight) return "stop";
    try {
      const kpRes = await fetch(
        `${this.config.apiUrl}/api/v1/mls/key-packages/batch?device_ids=${req.requesting_device_id}`,
        { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
      );
      if (!kpRes.ok) {
        this._welcomeKpBackoff.noteFetchFailure(`KeyPackage batch for ${a2aLabel}`, kpRes.status);
        return "next";
      }
      this._welcomeKpBackoff.noteFetchOk(`KeyPackage batch for ${a2aLabel}`);
      // Batch endpoint returns {device_id: hex_string} dict, not array
      const kpData: Record<string, string> = await kpRes.json();
      const kpHex = kpData[req.requesting_device_id];
      this._welcomeKpBackoff.recordKeyPackage(
        chState.mlsGroupId, req.requesting_device_id, Boolean(kpHex), req.key_package_available, a2aLabel,
      );
      if (!kpHex) return "next";

      const kpBytes = new Uint8Array(Buffer.from(kpHex, "hex"));
      const memberKp = MLSGroupManager.deserializeKeyPackage(kpBytes);
      console.log(`[SecureChannel] A2A addMembers for ${req.requesting_device_id.slice(0, 8)} group=${chState.mlsGroupId.slice(0, 8)} epoch=${mlsGroup.epoch}`);
      // #1177: name the parent of whatever we commit next. The server
      // refuses a commit whose parent is not its head, under the same
      // lock as the epoch check — the epoch NUMBER alone let a fork in
      // on 2026-09-04. A head we cannot read is not evidence: the
      // commit still goes out without a parent (the server skips an
      // ABSENT check) rather than blocking the rescue on a read.
      let parentDigest = preHead.ok ? preHead.digest : undefined;
      let commit: Uint8Array;
      let welcome: Uint8Array | undefined;
      // #1225: the state to return to if the ADD commit is refused. For
      // the re-add path it is re-taken AFTER the remove commit is
      // published (that one is at the server's head and must stay).
      let preAddState = mlsGroup.exportState();
      try {
        const result = await mlsGroup.addMembers([memberKp]);
        commit = result.commit;
        welcome = result.welcome;
      } catch (addErr) {
        const errMsg = addErr instanceof Error ? addErr.message : String(addErr);
        if (errMsg.includes("already in the group")) {
          // Member was added before but Welcome was never delivered.
          // Remove and re-add to generate a fresh Welcome.
          console.log(`[SecureChannel] A2A member already in group — removing and re-adding ${req.requesting_device_id.slice(0, 8)}`);
          // #473: this recovery produces TWO commits — remove (N -> N+1)
          // then add (N+1 -> N+2) — and BOTH must reach the server in
          // that order. Publishing only the add left a hole no other
          // member could bridge: a device at epoch N has no N+1 step to
          // apply, so it forks. Masked in 2-party channels because the
          // only peer re-joins via the Welcome at N+2.
          //
          // Snapshot first: if the remove commit is rejected we must put
          // local state back to N. Left at N+1, the next pull would find
          // the member already gone locally, addMembers would SUCCEED,
          // and we would publish an add whose predecessor exists nowhere
          // — reproducing the very bug this fixes.
          const preRemoveState = JSON.stringify(mlsGroup.exportState());
          let removeCommitPublished = false;
          try {
            // Credential identities are the device UUID as UTF-8 (every
            // generateKeyPackage call site) — the same bytes addMembers
            // dedupes on, so this lookup and the "already in the group"
            // throw agree by construction.
            const identityBytes = new TextEncoder().encode(req.requesting_device_id);
            const leafIndex = mlsGroup.memberLeafIndex(identityBytes);
            if (leafIndex === null) {
              // addMembers said "already in the group" but the member has
              // no leaf. Committing anything here would guess at a victim.
              console.log(`[SecureChannel] A2A re-add: no leaf index for ${req.requesting_device_id.slice(0, 8)} — cannot remove`);
              return "next";
            }
            const removeResult = await mlsGroup.removeMember(leafIndex);

            // Publish the remove commit ALONE, before building the add.
            // No welcomes (a Welcome belongs to the add's epoch) and no
            // member_device_ids (the server unions rosters, so submitting
            // one here cannot shrink it — it would only risk a spurious
            // participant-validation rejection).
            try {
              const removeResp = await fetch(
                `${this.config.apiUrl}/api/v1/mls/groups/${chState.mlsGroupId}/distribute`,
                {
                  method: "POST",
                  headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
                  body: JSON.stringify({
                    group_id: chState.mlsGroupId,
                    device_id: this._deviceId,
                    commit: Buffer.from(removeResult.commit).toString("hex"),
                    commit_epoch: Number(mlsGroup.epoch),
                    expected_epoch: Number(mlsGroup.epoch) - 1, // #1225: exact-step, like the WS path
                    ...(parentDigest ? { parent_digest: parentDigest } : {}),
                  }),
                },
              );
              if (removeResp.ok) {
                removeCommitPublished = true;
                // #1225 (review S3): the remove IS at the server's head — persist
                // it now so a restart does not reload the pre-remove state.
                await saveMlsState(this.config.dataDir, chState.mlsGroupId, JSON.stringify(mlsGroup.exportState()));
                // The add is built on the remove WE just landed.
                parentDigest = this._commitDigest(removeResult.commit);
              } else {
                const detail = await removeResp.text().catch(() => "");
                console.warn(`[SecureChannel] A2A remove-commit distribute failed (${removeResp.status}): ${detail}`);
              }
            } catch (distErr) {
              console.warn(`[SecureChannel] A2A remove-commit distribute network error:`, distErr);
            }
            if (!removeCommitPublished) {
              // Server still at N. Rewind so the retry starts clean.
              mlsGroup.importState(JSON.parse(preRemoveState) as SerializedMLSGroupState);
              return "next";
            }
            // Past this point the server has the remove commit, so local
            // state must NOT be rewound — a later failure is resumable:
            // the next pull re-adds from N+1 with the chain intact.

            // Re-fetch a fresh KeyPackage (the old one was consumed by the failed add)
            const kpRes2 = await fetch(
              `${this.config.apiUrl}/api/v1/mls/key-packages/batch?device_ids=${req.requesting_device_id}`,
              { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
            );
            if (kpRes2.ok) {
              const kpData2: Record<string, string> = await kpRes2.json();
              const kpHex2 = kpData2[req.requesting_device_id];
              if (kpHex2) {
                const kpBytes2 = new Uint8Array(Buffer.from(kpHex2, "hex"));
                const memberKp2 = MLSGroupManager.deserializeKeyPackage(kpBytes2);
                preAddState = mlsGroup.exportState();
                const result2 = await mlsGroup.addMembers([memberKp2]);
                commit = result2.commit;
                welcome = result2.welcome;
              } else {
                console.log(`[SecureChannel] A2A re-add: no fresh KeyPackage for ${req.requesting_device_id.slice(0, 8)}`);
                return "next";
              }
            } else {
              console.log(`[SecureChannel] A2A re-add: KeyPackage fetch failed (${kpRes2.status})`);
              return "next";
            }
          } catch (readdErr) {
            console.log(`[SecureChannel] A2A remove+re-add FAILED: ${readdErr instanceof Error ? readdErr.message : String(readdErr)}`);
            if (!removeCommitPublished) {
              // Nothing reached the server — rewind so we don't strand a
              // local remove the group never saw.
              try {
                mlsGroup.importState(JSON.parse(preRemoveState) as SerializedMLSGroupState);
              } catch { /* best effort — next pull restores from disk */ }
            }
            return "next";
          }
        } else {
          console.log(`[SecureChannel] A2A addMembers FAILED: ${errMsg}`);
          return "next";
        }
      }

      if (!welcome) {
        console.warn(`[SecureChannel] A2A addMembers produced no Welcome for ${req.requesting_device_id.slice(0, 8)}`);
        return "next";
      }

      const commitHex = Buffer.from(commit).toString("hex");
      const welcomeHex = Buffer.from(welcome).toString("hex");
      let distributed = false;
      // #1225: only a TRANSPORT failure (network / 5xx) may fall through
      // to the socket. A 4xx is the server's verdict on THIS commit —
      // 409 EPOCH_CONFLICT / PARENT_MISMATCH, 403, 404 — and resending
      // a refused commit over WS was exactly the fork this issue names.
      let transportFailed = false;

      const updatedMembers = this._commitRoster([this._deviceId], [req.requesting_device_id]);
      // Prefer HTTP distribute (works regardless of WS state): a 200 is
      // "stored at the head under lock" — no head poll needed.
      try {
        const distResp = await fetch(
          `${this.config.apiUrl}/api/v1/mls/groups/${chState.mlsGroupId}/distribute`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${this._deviceJwt}`, "Content-Type": "application/json" },
            body: JSON.stringify({
              group_id: chState.mlsGroupId,
              device_id: this._deviceId,
              commit: commitHex,
              commit_epoch: Number(mlsGroup.epoch),
              welcomes: [{ target_device_id: req.requesting_device_id, payload: welcomeHex }],
              member_device_ids: updatedMembers,
              expected_epoch: Number(mlsGroup.epoch) - 1, // #1225: exact-step, like the WS path
              ...(parentDigest ? { parent_digest: parentDigest } : {}),
            }),
          },
        );
        if (distResp.ok) {
          distributed = true;
        } else {
          const detail = await distResp.text().catch(() => "");
          console.warn(`[SecureChannel] A2A distribute failed (${distResp.status}): ${detail}`);
          if (distResp.status >= 500) {
            transportFailed = true;
          } else if (distResp.status === 409) {
            // A lost race (EPOCH_CONFLICT / PARENT_MISMATCH): this commit did
            // not land, the next pull can genuinely win. Roll back, request
            // stays pending.
            mlsGroup.importState(preAddState);
            console.warn(
              `[SecureChannel] ${a2aLabel}: add commit lost the race (409) — ` +
                `rolled back to epoch ${Number(mlsGroup.epoch)}, request stays pending`,
            );
            return "next";
          } else {
            // 403 / 404 / 422: cannot succeed on a retry, and every attempt
            // consumes one of the requester's pooled KeyPackages (review F2).
            // Roll back and stop this channel's pending list for this pull.
            mlsGroup.importState(preAddState);
            console.error(
              `[SecureChannel] ${a2aLabel}: add commit refused with ${distResp.status} — ` +
                `rolled back to epoch ${Number(mlsGroup.epoch)}; not retrying this channel this pull`,
);
return "stop";
          }
        }
      } catch (distErr) {
        console.warn(`[SecureChannel] A2A distribute network error:`, distErr);
        transportFailed = true;
      }

      // Fallback to WS only for a transport failure. #1015: `this._ws`
      // being truthy is not evidence the frames went out; #1225: the
      // socket taking the frame is not evidence the server stored it —
      // ask (#1184) before minting a Welcome from the new epoch.
      if (!distributed && transportFailed && this._wsIsOpen()) {
        const verdict = await this._sendCommitConfirmed({
          mlsGroup,
          mlsGroupId: chState.mlsGroupId,
          label: a2aLabel,
          commit,
          epoch: Number(mlsGroup.epoch),
          parent: parentDigest,
          memberDeviceIds: updatedMembers,
          stateBefore: preAddState,
          inFlight,
        });
        if (verdict !== "confirmed") return "next"; // restored; request stays pending
        const welcomeSent = await this._sendFrameConfirmed({
          event: "mls_welcome",
          data: {
            target_device_id: req.requesting_device_id,
            group_id: chState.mlsGroupId,
            a2a_channel_id: chId,
            payload: welcomeHex,
          },
        }, `${a2aLabel} welcome`);
        if (!welcomeSent) {
          // The commit IS at the server's head: persist it so a restart
          // does not fork behind; the requester re-requests its Welcome.
          await saveMlsState(this.config.dataDir, chState.mlsGroupId, JSON.stringify(mlsGroup.exportState()));
          console.warn(`[SecureChannel] ${a2aLabel}: Welcome frame was NOT written — leaving the request pending`);
          return "next";
        }
        // The commit-less "membership patch" POST that used to follow is
        // gone: the frame carried `member_device_ids` (#1226).
        distributed = true;
      }

      if (!distributed) {
        console.warn(`[SecureChannel] Cannot deliver Welcome — HTTP distribute and WS both unavailable`);
        return "next"; // Don't mark as fulfilled if not delivered
      }

      const a2aFulfilled = await this._postFulfilWelcome(
        chState.mlsGroupId, req.id, `A2A ${chId.slice(0, 8)}`,
      );

      // The commit is out either way, so persist the advance regardless
      // — our state must match the group's.
      await saveMlsState(this.config.dataDir, chState.mlsGroupId, JSON.stringify(mlsGroup.exportState()));
      if (a2aFulfilled) {
        console.log(`[SecureChannel] Fulfilled A2A Welcome for ${req.requesting_device_id.slice(0, 8)} in channel ${chId.slice(0, 8)}`);
      }
      return "next";
    } finally {
      await this._releaseCommit(chState.mlsGroupId, inFlight, a2aLabel);
    }
  }

  /**
   * Found the MLS group for an A2A channel as the creator agent.
   * Calls /mls/a2a/{channelId}/init, creates the local MLS group, and sends
   * Welcome messages to all active/invited participants.
   * Extracted from the a2a_channel_approved WS handler so it can also be called
   * from the sync fallback in listA2AChannels.
   */
  private async _foundA2AGroup(
    channelId: string,
    convId: string,
    participants: Array<{ hubId: string; hubAddress: string; status: string; deviceId?: string | null }>,
  ): Promise<void> {
    if (!this._persisted || !this._deviceJwt) return;

    // Creator agent founds the MLS group
    const mlsInitRes = await fetch(
      `${this.config.apiUrl}/api/v1/mls/a2a/${channelId}/init`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${this._deviceJwt}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ member_device_ids: [this._persisted!.deviceId] }),
      },
    );

    if (!mlsInitRes.ok) {
      const detail = await mlsInitRes.text();
      throw new Error(`MLS A2A init failed (${mlsInitRes.status}): ${detail}`);
    }

    const mlsData = await mlsInitRes.json();
    const entry = this._persisted.a2aChannels?.[channelId];
    if (!entry) return;

    entry.mlsGroupId = mlsData.group_id;
    await this._persistState();
    console.log(
      `[SecureChannel] A2A ${channelId.slice(0, 8)} MLS group founded as creator (groupId=${mlsData.group_id.slice(0, 8)})`,
    );

    // Create MLS group and send Welcomes to active/invited participants
    try {
      const mgr = new MLSGroupManager();
      const identity = new TextEncoder().encode(this._persisted!.deviceId);
      const creatorKp = await mgr.generateKeyPackage(identity);
      const groupIdBytes = new TextEncoder().encode(mlsData.group_id);
      await mgr.createGroup(groupIdBytes, creatorKp);
      this._mlsGroups.set(`a2a:${channelId}`, mgr);
      await saveMlsState(this.config.dataDir, mlsData.group_id, JSON.stringify(mgr.exportState()));

      // Fetch KeyPackages for participants and send Welcomes
      // #1225: each Add is snapshot → commit → server verdict (#1184) → Welcome +
      // persist. Before, the commit went out raw and `saveMlsState` sat outside
      // the sent branch, so an advance the server never saw was persisted — a
      // fork AHEAD of the server (#972's direction).
      //
      // Welcome field names MUST match the backend handler (mls_welcome):
      //   target_device_id (NOT target_hub_id), payload (NOT welcome) — the
      //   backend drops the frame silently otherwise (2026-05-24 A2A discovery).
      for (const p of participants) {
        if (p.status !== "active" && p.status !== "invited") continue;
        const a2aLabel = `A2A ${channelId.slice(0, 8)} found→${p.hubAddress}`;
        try {
          if (!p.deviceId) {
            console.warn(
              `[SecureChannel] ${a2aLabel}: no deviceId — NOT adding (a Welcome could not be addressed). ` +
              `Backend a2a_channel_approved payload may be missing device_id for this participant.`,
            );
            continue;
          }
          if (!this._wsIsOpen()) {
            console.warn(`[SecureChannel] ${a2aLabel}: socket is not OPEN — NOT advancing the group`);
            continue;
          }
          // Review S2: the parent is the server's CURRENT head, re-read per
          // participant — never chained from a commit the server may not hold,
          // and never stale after a held commit was replayed.
          const head = await this._readGroupHead(mlsData.group_id);
          if (head.ok && head.epoch !== Number(mgr.epoch)) {
            console.warn(`[SecureChannel] ${a2aLabel}: server head is at epoch ${head.epoch}, we are at ${Number(mgr.epoch)} — NOT committing`);
            await this._catchUpAfterPreflightSkip(mlsData.group_id, head.epoch, Number(mgr.epoch), a2aLabel);
            continue;
          }
          const inFlight = this._reserveCommit(mlsData.group_id, a2aLabel);
          if (!inFlight) continue;
          try {
          const kpRes = await fetch(
            `${this.config.apiUrl}/api/v1/mls/key-packages?hub_id=${encodeURIComponent(p.hubId)}`,
            { headers: { Authorization: `Bearer ${this._deviceJwt}` } },
          );
          if (kpRes.ok) {
            const kpData = await kpRes.json();
            if (kpData.key_package) {
              const kpBytes = new Uint8Array(Buffer.from(kpData.key_package, "hex"));
              const memberKp = MLSGroupManager.deserializeKeyPackage(kpBytes);
              const stateBefore = mgr.exportState();
              const { commit, welcome } = await mgr.addMembers([memberKp]);
              const committedEpoch = Number(mgr.epoch);
              if (!welcome) {
                mgr.importState(stateBefore);
                console.warn(`[SecureChannel] ${a2aLabel}: addMembers produced no Welcome — rolled back`);
                continue;
              }
              const verdict = await this._sendCommitConfirmed({
                mlsGroup: mgr,
                mlsGroupId: mlsData.group_id,
                label: a2aLabel,
                commit,
                epoch: committedEpoch,
                parent: head.ok ? head.digest : undefined,
                memberDeviceIds: this._commitRoster(participants.map((q) => q.deviceId), [p.deviceId]),
                stateBefore,
                inFlight,
              });
              if (verdict !== "confirmed") continue; // restored; nothing persisted
              const sent = await this._sendFrameConfirmed({
                event: "mls_welcome",
                data: {
                  group_id: mlsData.group_id,
                  payload: Buffer.from(welcome).toString("hex"),
                  target_device_id: p.deviceId,
                  a2a_channel_id: channelId,
                  target_hub_id: p.hubId, // observability only — the backend ignores it
                },
              }, `${a2aLabel} welcome`);
              await saveMlsState(this.config.dataDir, mlsData.group_id, JSON.stringify(mgr.exportState()));
              if (sent) {
                console.log(`[SecureChannel] Sent MLS Welcome to ${p.hubAddress} (device ${p.deviceId.slice(0, 8)}) for A2A ${channelId.slice(0, 8)}`);
              } else {
                console.warn(`[SecureChannel] ${a2aLabel}: Welcome was NOT written — the participant will request one`);
              }
            }
          }
          } finally {
            await this._releaseCommit(mlsData.group_id, inFlight, a2aLabel);
          }
        } catch (kpErr) {
          console.warn(`[SecureChannel] Failed to Welcome participant ${p.hubAddress}:`, kpErr);
        }
      }
    } catch (mlsErr) {
      console.warn(`[SecureChannel] MLS group creation failed:`, mlsErr);
    }
  }

  /**
   * Paginated sync: fetch missed messages in pages of 200, up to 5 pages (1000 messages).
   * Tracks message IDs in _syncMessageIds to prevent duplicate processing from concurrent WS messages.
   */
  private async _syncMissedMessages(): Promise<void> {
    if (!this._persisted?.lastMessageTimestamp || !this._deviceJwt) return;

    this._syncMessageIds = new Set<string>();
    const MAX_PAGES = 5;
    const PAGE_SIZE = 200;
    let since = this._persisted.lastMessageTimestamp;
    let totalProcessed = 0;

    try {
      for (let page = 0; page < MAX_PAGES; page++) {
        const url = `${this.config.apiUrl}/api/v1/devices/${this._deviceId}/messages?since=${encodeURIComponent(since)}&limit=${PAGE_SIZE}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${this._deviceJwt}` },
        });

        if (!res.ok) break;

        const messages: Array<{
          id: string;
          conversation_id: string;
          sender_device_id: string;
          header_blob: string;
          ciphertext: string;
          created_at: string;
          topic_id?: string;
          room_id?: string;
        }> = await res.json();

        if (messages.length === 0) break;

        for (const msg of messages) {
          if (msg.sender_device_id === this._deviceId) continue;

          // Dedup: skip if already processed (check both sync and WS buffers)
          if (this._syncMessageIds.has(msg.id)) continue;
          if (this._seenMessageIds.has(msg.id)) continue;
          this._syncMessageIds.add(msg.id);
          // Also register in the WS dedup buffer to prevent double-delivery
          // when sync poll and WS events overlap
          if (this._seenMessageIds.size >= SecureChannel.SEEN_MSG_MAX) {
            const first = this._seenMessageIds.values().next().value;
            if (first) this._seenMessageIds.delete(first);
          }
          this._seenMessageIds.add(msg.id);

          // Legacy DR room messages in sync — skip (rooms are MLS-only now). Honors
          // server `room_id` first, then local rooms.
          // TODO(unify): sync guard could adopt _isNonDmConversation for a2a parity —
          // inert today (A2A is MLS-only; this endpoint carries no a2a_channel_id).
          let roomId = msg.room_id;
          if (!roomId && this._persisted?.rooms) {
            for (const room of Object.values(this._persisted.rooms)) {
              if (room.conversationIds?.includes(msg.conversation_id)) {
                roomId = room.roomId;
                break;
              }
            }
          }
          if (roomId) {
            this._persisted!.lastMessageTimestamp = msg.created_at;
            since = msg.created_at;
            continue;
          }

          const session = this._sessions.get(msg.conversation_id);
          if (!session) {
            // No session during sync — skip silently. Resync only triggers
            // on real-time messages when the owner's browser is connected.
            console.warn(
              `[SecureChannel] No session for conversation ${msg.conversation_id} during sync, skipping`,
            );
            continue;
          }

          try {
            const encrypted = transportToEncryptedMessage({
              header_blob: msg.header_blob,
              ciphertext: msg.ciphertext,
            });
            const ratchetSnapshot = session.ratchet.serialize();
            let plaintext: string;
            try {
              plaintext = session.ratchet.decrypt(encrypted);
            } catch (decryptErr) {
              console.error(`[SecureChannel] Sync decrypt failed for ${msg.conversation_id.slice(0, 8)}... — restoring ratchet:`, decryptErr);
              try { session.ratchet = DoubleRatchet.deserialize(ratchetSnapshot); } catch {}
              // Advance past the failing message to prevent infinite retry loop.
              this._persisted!.lastMessageTimestamp = msg.created_at;
              since = msg.created_at;
              continue;
            }

            this._sendAck(msg.id);

            if (!session.activated) {
              session.activated = true;
              console.log(`[SecureChannel] Session ${msg.conversation_id.slice(0, 8)}... activated during sync`);
              if (this._persisted?.sessions[msg.conversation_id]) {
                this._persisted.sessions[msg.conversation_id].activated = true;
              }
              this._activateSiblings(msg.conversation_id);
            }

            let messageText: string;
            let messageType: string;
            let decisionReply: DecisionResponse | undefined;
            try {
              const parsed = JSON.parse(plaintext);
              messageType = parsed.type || "message";
              messageText = envelopeText(parsed, plaintext);
              decisionReply = decisionReplyFromEnvelope(parsed);
            } catch {
              messageType = "message";
              messageText = plaintext;
            }

            if (messageType === "message" || (messageType === "decision_response" && decisionReply)) {
              const topicId = msg.topic_id;
              await this._appendHistory("owner", messageText, this._dmHistoryContext(topicId, undefined, msg.conversation_id), msg.created_at, msg.id);

              const metadata: MessageMetadata = {
                messageId: msg.id,
                conversationId: msg.conversation_id,
                timestamp: msg.created_at,
                topicId,
                // #621: positive attestation that this decrypted under the OWNER'S 1:1
                // material (MLS 1to1-group map, or a Double Ratchet session), NOT merely
                // that a roomId was absent. The bridge's full-access DM lane requires it.
                ownerDm1to1: true,
                ...(decisionReply ? { messageType: "decision_response", decision: decisionReply } : {}),
              };

              this._noteInboundDm(msg.conversation_id, undefined, { createdAt: msg.created_at, senderDeviceId: msg.sender_device_id }); // #1418

              this.emit("message", messageText, metadata);
              Promise.resolve(this.config.onMessage?.(messageText, metadata)).catch((err: unknown) => {
                console.error("[SecureChannel] onMessage callback error:", err);
              });
            }

            this._persisted!.lastMessageTimestamp = msg.created_at;
            since = msg.created_at;
            totalProcessed++;
          } catch (err) {
            console.warn(
              `[SecureChannel] Sync failed for msg ${msg.id.slice(0, 8)}... in conv ${msg.conversation_id.slice(0, 8)}...: ${String(err)}`,
            );
            // Advance past the failing message to prevent infinite retry loop.
            // The message is lost but the sync cursor moves forward.
            this._persisted!.lastMessageTimestamp = msg.created_at;
            since = msg.created_at;
            continue;
          }
        }

        await this._persistState();

        // If we got fewer than PAGE_SIZE, we've caught up
        if (messages.length < PAGE_SIZE) break;
      }

      if (totalProcessed > 0) {
        console.log(`[SecureChannel] Synced ${totalProcessed} missed messages`);
      }
    } catch {
      // Network error -- non-critical, will get messages via WS
    }

    this._syncMessageIds = null;
  }

  private _sendAck(messageId: string): void {
    this._pendingAcks.push(messageId);
    if (this._ackTimer) clearTimeout(this._ackTimer);
    this._ackTimer = setTimeout(() => this._flushAcks(), 500);
  }

  private _flushAcks(): void {
    if (this._pendingAcks.length === 0 || !this._ws) return;
    const batch = this._pendingAcks.splice(0, 50);
    this._ws.send(JSON.stringify({ event: "ack", data: { message_ids: batch } }));
  }

  private _flushOutboundQueue(): Promise<void> {
    if (this._outboundFlushPromise) return this._outboundFlushPromise;
    const run = this._flushOutboundQueueSerial();
    const tracked = run.finally(() => {
      if (this._outboundFlushPromise === tracked) this._outboundFlushPromise = null;
    });
    this._outboundFlushPromise = tracked;
    return tracked;
  }

  private async _flushOutboundQueueSerial(): Promise<void> {
    const queue = this._persisted?.outboundQueue;
    if (!queue || queue.length === 0 || !this._ws || this._ws.readyState !== WebSocket.OPEN) return;

    console.log(`[SecureChannel] Flushing ${queue.length} queued outbound messages`);
    while (queue.length > 0) {
      const msg = queue[0];
      try {
        // WebSocket#send may silently return on a closing socket. Check at the
        // exact delivery boundary, not merely when flush started.
        if (this._stopped || !this._ws || this._ws.readyState !== WebSocket.OPEN) break;
        const delivered = this._sendApplicationFrame(JSON.stringify({
            event: "message",
            data: {
              conversation_id: msg.convId,
              header_blob: msg.headerBlob,
              ciphertext: msg.ciphertext,
              message_group_id: msg.messageGroupId,
              topic_id: msg.topicId,
            },
          }));
        if (!delivered) break;
        if (msg.history) {
          await this._appendHistory(
            "agent",
            msg.history.text,
            msg.history.topicId,
            undefined,
            msg.history.id,
            false,
          );
        }
        // Publish history and the remaining queue in one atomic snapshot. The
        // current item stays live until its relay write succeeds; the tail is
        // never removed from either memory or disk ahead of its own send.
        const deliveredIndex = queue.indexOf(msg);
        if (deliveredIndex >= 0) queue.splice(deliveredIndex, 1);
        try {
          await this._saveHistoryState();
        } catch (error) {
          if (!queue.includes(msg)) queue.unshift(msg);
          throw error;
        }
      } catch (err) {
        console.warn(`[SecureChannel] Failed to flush message; current and tail remain queued: ${err}`);
        break; // Stop flushing on first failure
      }
    }
  }

  private _startPing(ws: WebSocket): void {
    this._stopPing(); // Clear any existing timers
    this._lastServerMessage = Date.now();
    this._pingTimer = setInterval(() => {
      if (ws.readyState !== WebSocket.OPEN) return;
      const silence = Date.now() - this._lastServerMessage;
      if (silence > SecureChannel.SILENCE_TIMEOUT_MS) {
        console.log(
          `[SecureChannel] No server data for ${Math.round(silence / 1000)}s — reconnecting stale WebSocket`,
        );
        ws.terminate(); // Forces close → _scheduleReconnect fires
      }
    }, SecureChannel.PING_INTERVAL_MS);
  }

  private _stopPing(): void {
    if (this._pingTimer) {
      clearInterval(this._pingTimer);
      this._pingTimer = null;
    }
  }

  private _startWakeDetector(): void {
    this._stopWakeDetector();
    this._lastWakeTick = Date.now();
    this._wakeDetectorTimer = setInterval(() => {
      const gap = Date.now() - this._lastWakeTick;
      this._lastWakeTick = Date.now();
      if (gap > 120_000 && this._ws) {
        console.log(
          `[SecureChannel] System wake detected (${Math.round(gap / 1000)}s gap) — forcing reconnect`,
        );
        this._ws.terminate();
      }
    }, 10_000);
  }

  private _stopWakeDetector(): void {
    if (this._wakeDetectorTimer) {
      clearInterval(this._wakeDetectorTimer);
      this._wakeDetectorTimer = null;
    }
  }

  private _startPendingPoll(): void {
    this._stopPendingPoll();
    this._pendingPollTimer = setInterval(() => {
      this._checkPendingMessages();
    }, PENDING_POLL_INTERVAL_MS);
  }

  private _stopPendingPoll(): void {
    if (this._pendingPollTimer) {
      clearInterval(this._pendingPollTimer);
      this._pendingPollTimer = null;
    }
  }

  private async _checkPendingMessages(): Promise<void> {
    // Don't poll while connecting (prevent hammering during reconnect)
    if (this._state === "connecting" || !this._deviceId || !this._deviceJwt) return;

    try {
      const url = `${this.config.apiUrl}/api/v1/devices/${this._deviceId}/pending`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${this._deviceJwt}` },
        signal: AbortSignal.timeout(10_000),
      });

      if (!resp.ok) return; // Silent fail — WS silence detector is backup

      const data = (await resp.json()) as { pending: number };

      if (data.pending > 0 && this._state !== "ready") {
        console.log(
          `[SecureChannel] Poll detected ${data.pending} pending messages — forcing reconnect`,
        );
        if (this._ws) {
          this._ws.terminate();
        } else {
          this._scheduleReconnect();
        }
      } else if (data.pending > 0 && this._state === "ready") {
        // WS claims ready but messages are pending — possible half-open connection
        const silence = Date.now() - this._lastServerMessage;
        if (silence > 180_000) {
          console.log(
            `[SecureChannel] Poll: ${data.pending} pending + ${Math.round(silence / 1000)}s silence — forcing reconnect`,
          );
          if (this._ws) this._ws.terminate();
        }
      }
    } catch {
      // Network error on poll — expected when offline, ignore silently
    }
  }

  private _scheduleReconnect(): void {
    if (this._stopped) return;
    // Paused by the owner: the suspended-wait poll owns reconnection.
    if (this._suspended) return;
    if (this._reconnectTimer) return; // Already scheduled

    // Detect rapid-disconnect cascade (e.g. two processes as same device).
    // If WS opened recently (< 10s) and we're disconnecting, count it.
    const sinceOpen = Date.now() - this._lastWsOpenTime;
    if (this._lastWsOpenTime > 0 && sinceOpen < 10_000) {
      this._rapidDisconnects++;
    } else {
      this._rapidDisconnects = 0;
    }

    if (this._rapidDisconnects >= 5) {
      console.error(
        `[SecureChannel] Detected rapid connect/disconnect loop (${this._rapidDisconnects} times in <10s each).`,
      );

      // Auth-failure branch: an `auth_failed` event was already emitted this
      // session (via proactive reissue or via _authedFetch's reactive 401
      // path). The disconnect cascade is caused by credential rejection, NOT
      // by a session collision — emit the correct cause and stop.
      if (this._authFailedThisSession) {
        console.log(
          "[SecureChannel] Auth-failed during repeated reconnects. Stopping; see prior auth_failed event.",
        );
        this._setState("error");
        this.emit("error", new Error("Reconnect loop detected — device credentials need attention (see auth_failed event)"));
        return;
      }

      const lastCode = this._lastCloseCode;

      // Auth-rejected branch: the server closes with 4001 ONLY when it could
      // not authenticate the socket (backend ws.py: close(4001, "Authentication
      // failed")). That is a credential problem, not a session collision —
      // report it as auth_failed (the documented terminal event) and stop.
      if (lastCode === 4001) {
        console.log(
          "[SecureChannel] Server rejected our credentials on every reconnect (close code 4001). Stopping; the device needs re-enrolling or a fresh token.",
        );
        this._authFailedThisSession = true;
        this._setState("error");
        this.emit("auth_failed", { reason: "ws_auth_rejected", deviceGone: false });
        return;
      }

      // Session-replacement branch: close code 4000 is reserved for a
      // server-driven "a newer session for this device took over" signal.
      // NOTE: the current backend never sends it — a replaced socket is
      // orphaned with no close frame (ws_manager.connect) — so this branch is
      // forward-compatible only. Emit a dedicated event and stop reconnecting.
      if (lastCode === 4000) {
        console.log(
          `[SecureChannel] Server reports our session was replaced by a newer one (close code ${lastCode}). Stopping reconnect.`,
        );
        this._setState("error");
        this.emit("session_replaced", { code: lastCode });
        return;
      }

      // Fallback: genuinely indeterminate cascade. Preserve the original
      // operator-facing message so the existing diagnosis path remains.
      console.error(
        `[SecureChannel] This usually means another process is already connected as this device.`,
      );
      console.error(
        `[SecureChannel] Stopping reconnection. Check for duplicate gateway processes or stale CLI sessions.`,
      );
      this._setState("error");
      this.emit("error", new Error("Reconnect loop detected — another process may be connected as this device"));
      return;
    }

    const delay = Math.min(
      RECONNECT_BASE_MS * Math.pow(2, this._reconnectAttempt),
      RECONNECT_MAX_MS,
    );
    this._reconnectAttempt++;

    console.log(`[SecureChannel] Scheduling reconnect in ${delay}ms (attempt ${this._reconnectAttempt})`);

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null; // Clear BEFORE calling _connect so future disconnects can schedule new reconnects
      if (!this._stopped) {
        this._connect();
      }
    }, delay);
  }

  private _setState(newState: ChannelState): void {
    if (this._state === newState) return;
    this._state = newState;
    this.emit("state", newState);
    this.config.onStateChange?.(newState);

    // Start/stop polling fallback based on connection state
    // Not while paused: the server will not serve a SUSPENDED device, so the
    // fallback could only spend 120 req/h being refused. The suspended-wait
    // poll owns that state.
    if (newState === "disconnected" && !this._pollFallbackTimer && this._deviceJwt && !this._suspended) {
      this._startPollFallback();
    }
    if (newState === "ready" || newState === "error" || this._stopped) {
      this._stopPollFallback();
    }
  }

  private _startPollFallback(): void {
    this._stopPollFallback();
    console.log("[SecureChannel] Starting HTTP poll fallback (WS is down)");

    let interval = SecureChannel.POLL_FALLBACK_INTERVAL_MS;

    const poll = async () => {
      if (this._state === "ready" || this._stopped) {
        this._stopPollFallback();
        return;
      }

      try {
        const since = this._persisted?.lastMessageTimestamp;
        if (!since || !this._deviceJwt) return;

        const url = `${this.config.apiUrl}/api/v1/devices/${this._deviceId}/messages?since=${encodeURIComponent(since)}&limit=200`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${this._deviceJwt}` },
        });

        if (!res.ok) return;

        const messages: Array<{
          id: string;
          conversation_id: string;
          sender_device_id: string;
          header_blob: string;
          ciphertext: string;
          created_at: string;
          topic_id?: string;
          room_id?: string;
        }> = await res.json();

        let foundMessages = false;

        for (const msg of messages) {
          if (msg.sender_device_id === this._deviceId) continue;

          const session = this._sessions.get(msg.conversation_id);
          if (!session) continue;

          // Room/A2A messages — skip (a non-DM conv delivered here would decrypt
          // and emit as a no-roomId 1:1 `message`, which the native worker tool-gate
          // treats as an owner DM: tool-enabled, bypassing arming). Honor the
          // AUTHORITATIVE server `room_id` FIRST — the devices/messages responder
          // populates it for room rows (conv_room_map) — then fall back to LOCAL
          // room/A2A membership (the poll endpoint carries no a2a_channel_id, so
          // A2A is caught via local a2aChannels). Mirrors the sync path's
          // server-field-first guard. Advance the poll cursor (lastMessageTimestamp)
          // so the skipped message isn't re-fetched next poll.
          if (msg.room_id || this._isNonDmConversation(msg.conversation_id)) {
            this._persisted!.lastMessageTimestamp = msg.created_at;
            continue;
          }

          try {
            const encrypted = transportToEncryptedMessage({
              header_blob: msg.header_blob,
              ciphertext: msg.ciphertext,
            });
            const ratchetSnapshot = session.ratchet.serialize();
            let plaintext: string;
            try {
              plaintext = session.ratchet.decrypt(encrypted);
            } catch (decryptErr) {
              console.error(`[SecureChannel] Room sync decrypt failed for ${msg.conversation_id.slice(0, 8)}... — restoring ratchet:`, decryptErr);
              try { session.ratchet = DoubleRatchet.deserialize(ratchetSnapshot); } catch {}
              continue;
            }

            if (!session.activated) {
              session.activated = true;
            }

            let messageText: string;
            let messageType: string;
            let decisionReply: DecisionResponse | undefined;
            try {
              const parsed = JSON.parse(plaintext);
              messageType = parsed.type || "message";
              messageText = envelopeText(parsed, plaintext);
              decisionReply = decisionReplyFromEnvelope(parsed);
            } catch {
              messageType = "message";
              messageText = plaintext;
            }

            if (messageType === "message" || (messageType === "decision_response" && decisionReply)) {
              const topicId = msg.topic_id;
              await this._appendHistory("owner", messageText, this._dmHistoryContext(topicId, undefined, msg.conversation_id), msg.created_at, msg.id);

              const metadata: MessageMetadata = {
                messageId: msg.id,
                conversationId: msg.conversation_id,
                timestamp: msg.created_at,
                topicId,
                // #621: positive attestation that this decrypted under the OWNER'S 1:1
                // material (MLS 1to1-group map, or a Double Ratchet session), NOT merely
                // that a roomId was absent. The bridge's full-access DM lane requires it.
                ownerDm1to1: true,
                ...(decisionReply ? { messageType: "decision_response", decision: decisionReply } : {}),
              };

              this._noteInboundDm(msg.conversation_id, undefined, { createdAt: msg.created_at, senderDeviceId: msg.sender_device_id }); // #1418

              this.emit("message", messageText, metadata);
              Promise.resolve(this.config.onMessage?.(messageText, metadata)).catch((err: unknown) => {
                console.error("[SecureChannel] onMessage callback error:", err);
              });
              foundMessages = true;
            }

            this._persisted!.lastMessageTimestamp = msg.created_at;
          } catch (err) {
            this.emit("error", err);
            break;
          }
        }

        if (messages.length > 0) {
          await this._persistState();
        }

        // Adaptive rate: faster when active, slower when idle
        const newInterval = foundMessages
          ? SecureChannel.POLL_FALLBACK_INTERVAL_MS
          : SecureChannel.POLL_FALLBACK_IDLE_MS;

        if (newInterval !== interval) {
          interval = newInterval;
          // Restart timer with new interval
          if (this._pollFallbackTimer) {
            clearInterval(this._pollFallbackTimer);
            this._pollFallbackTimer = setInterval(poll, interval);
          }
        }
      } catch {
        // Network error — try again next tick
      }
    };

    // Run first poll after a short delay, then on interval
    setTimeout(poll, 1_000);
    this._pollFallbackTimer = setInterval(poll, interval);
  }

  private _stopPollFallback(): void {
    if (this._pollFallbackTimer) {
      clearInterval(this._pollFallbackTimer);
      this._pollFallbackTimer = null;
      console.log("[SecureChannel] Stopped HTTP poll fallback");
    }
  }

  private _handleError(err: Error): void {
    this._setState("error");
    this.emit("error", err);
  }

  // Phase 2 — rolling-window timestamps of device_revoked self-heal reconnects,
  // used as a war cap so a takeover war or a server status/WS inconsistency can't
  // thrash forever.
  private _revokeRecoveries: number[] = [];
  private static readonly REVOKE_RECOVERY_MAX = 3;
  private static readonly REVOKE_RECOVERY_WINDOW_MS = 120_000;

  /**
   * Handle a WS `device_revoked` event.
   *
   * The signal is ambiguous: the server sends it both for a genuine owner revoke
   * AND for a transient/racy session-takeover of a device that is still ACTIVE
   * (the error text even reads "or another session has taken over" — loopita's
   * case, where the device stayed ACTIVE on the server). So we do NOT terminate
   * blindly. We ask the PUBLIC /devices/{id}/status endpoint (no JWT needed, so
   * it answers even when our device JWT is being rejected):
   *   - ACTIVE          -> reconnect in place (transient); stay online.
   *   - SUSPENDED       -> suspended-wait (owner paused it; resume later).
   *   - REVOKED/deleted -> terminal via `_terminalDeviceGone` (auth_failed with
   *                        deviceGone: the bridges exit and remove their service).
   *   - anything else / unreachable / 429 -> reconnect (inconclusive; a network blip shouldn't
   *                         permanently kill a healthy agent — the WS is down too,
   *                         so normal backoff handles it, and the war cap bounds
   *                         thrash if the device really is dead).
   *
   * A rolling-window cap (REVOKE_RECOVERY_MAX self-heals per WINDOW_MS) gives up
   * (terminal) on a repeating loop. Never clears credentials in any branch —
   * re-enrollment, which SHOULD wipe creds, goes through setup --force /
   * _forceReEnroll in start().
   */
  /**
   * Handle a WS `connection_rejected` event.
   *
   * The server accepts the WS upgrade, then — for a device it will not serve —
   * emits `{event:"connection_rejected", reason, retryable}` and closes with
   * code 4403. `retryable:false` (e.g. `device_revoked`) is a TERMINAL
   * credential failure: retrying can only reconnect, get rejected, and close
   * again. Left unhandled, that generic close drove the reconnect classifier
   * into "reconnect loop detected", which the bridge treats as a restartable
   * exit — so launchd/systemd relaunched us every ~30s to hammer the backend
   * with the same rejected handshake (empirically ~1150 rejects / 2h).
   *
   * So we route a non-retryable rejection to the SAME `auth_failed` surface the
   * proactive/reactive reissue paths use (see channel.ts emit sites). The
   * bridge wires `auth_failed` to a clean exit(0) (packages/claude-room-bridge
   * /src/bridge.ts), so launchd stops relaunching. Mirrors those emitters:
   * emit only + mark the session; the consumer decides how to stop. A
   * `retryable` rejection is transient — fall through to a normal reconnect.
   */
  private async _handleConnectionRejected(data: {
    reason?: string;
    retryable?: boolean;
  }): Promise<void> {
    // A paused agent reconnecting (e.g. after a restart) is rejected as
    // retryable `device_suspended`. Plain reconnecting would just be rejected
    // again until the rapid-disconnect classifier gave up for good — idle
    // and poll for the resume instead.
    if (data?.reason === "device_suspended") {
      this._enterSuspendedWait("connection_rejected");
      return;
    }
    if (data?.retryable === true) {
      this._scheduleReconnect();
      return;
    }
    this._authFailedThisSession = true;
    // #743: preserve the server's reason verbatim. Terminality is already
    // decided by `retryable` above, so this string is purely diagnostic — but
    // collapsing every reason to "device_revoked" made a hard-DELETED device
    // indistinguishable from a revoked one in the agent's own logs and in the
    // bridge's terminal message, and the two have different remedies (a revoked
    // device can be re-approved; a deleted one must re-enroll). Identifying the
    // looping prod device in #743 needed a server-side log dig for exactly this
    // reason. Unknown reasons pass through: terminality does not depend on
    // recognising the string, so a new server-side reason cannot silently
    // become non-terminal here.
    //
    // #913: an ABSENT reason now reports as "unknown" rather than being filled
    // in as "device_revoked". #743 fixed the collapse of RECOGNISED reasons and
    // left the missing case defaulting, and the two lines below disagreed about
    // the same value — the log said `unknown` while the event said
    // `device_revoked`. No consumer branches on this string today, so the change
    // is behaviour-neutral: every listener passes it straight to `terminal()`
    // for a message and an exit code.
    //
    // It stops being neutral for #913 item 2, self-uninstall on a revoked
    // device. That must key on `device_revoked`, and a reasonless
    // connection_rejected would otherwise uninstall a live customer's service
    // for a device that was never revoked. Inventing an identity for a missing
    // input and then acting on it is #919 exactly.
    const authReason: string = data?.reason ?? "unknown";
    // 2026-10-25: the server's `retryable:false` still decides that we STOP,
    // but whether the device is GONE — the verdict the bridges' self-uninstall
    // acts on — is confirmed against the status endpoint, never read off the
    // reason string. A denylist entry can outlive the state that put it there,
    // and "unreadable" must never remove a customer's service.
    const standing = await this._readDeviceStanding();
    if (standing.kind === "suspended") {
      this._authFailedThisSession = false;
      this._enterSuspendedWait("connection_rejected");
      return;
    }
    const deviceGone = standing.kind === "gone";
    console.warn(
      `[SecureChannel] connection_rejected (reason=${data?.reason ?? "unknown"}, retryable=false) — terminal; surfacing auth_failed (deviceGone=${deviceGone})`,
    );
    this.emit("auth_failed", { reason: authReason, deviceGone });
  }

  /**
   * How long an opened socket may stay silent before we treat it as served.
   * The backend's reject_connection sends `connection_rejected` immediately
   * after the accept, so a refused socket's first frame lands well inside
   * this; a served agent with a hub gets `hub_identity_sync` sooner still.
   */
  static readonly OPEN_SETTLE_MS = 1_500;

  /**
   * Per-socket "has the server agreed to serve this session?" latch. Settles
   * once: served on the first non-rejection frame or when the settle window
   * passes quietly; rejected on `connection_rejected` or a close first.
   * `rejected` stays readable afterwards so long open-handler work can bail.
   */
  private _createServedGate(): {
    wait(): Promise<boolean>;
    markServed(): void;
    markRejected(): void;
    readonly rejected: boolean;
  } {
    let verdict: boolean | null = null;
    let rejected = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const waiters: Array<(ok: boolean) => void> = [];
    const settle = (ok: boolean): void => {
      if (!ok) rejected = true;
      if (verdict !== null) return;
      verdict = ok;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      for (const w of waiters.splice(0)) w(ok);
    };
    return {
      wait: () => {
        if (verdict !== null) return Promise.resolve(verdict && !rejected);
        if (!timer) timer = setTimeout(() => settle(true), SecureChannel.OPEN_SETTLE_MS);
        return new Promise<boolean>((resolve) => waiters.push(resolve));
      },
      markServed: () => settle(true),
      markRejected: () => settle(false),
      get rejected() {
        return rejected;
      },
    };
  }

  // --- Owner pause (device SUSPENDED) — NON-final ---------------------------
  //
  // Suspension is reversible ("Agent active" off/on in the app). While
  // suspended the server will not serve this device (it rejects the handshake
  // with a retryable `device_suspended`), so reconnecting is pointless and,
  // through the rapid-disconnect classifier, used to end in a terminal error.
  // Instead: stay in "disconnected", poll the public status endpoint, and
  // reconnect as soon as the device is ACTIVE again. REVOKED ends the wait as
  // terminal; anything inconclusive (rate limit, network) keeps waiting.
  static readonly SUSPENDED_POLL_MS = 30_000;
  private _suspended = false;
  private _suspendedPollTimer: ReturnType<typeof setTimeout> | null = null;

  /** Handle a WS `device_suspended` frame (owner paused this agent). */
  private async _handleDeviceSuspended(): Promise<void> {
    this._enterSuspendedWait("device_suspended");
  }

  private _enterSuspendedWait(source: string): void {
    if (this._stopped) return;
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = null;
    }
    this._rapidDisconnects = 0;
    // A close that landed before the pause was known may have started the
    // HTTP poll fallback; a paused device has nothing to poll for.
    this._stopPollFallback();
    if (!this._suspended) {
      this._suspended = true;
      console.log(
        `[SecureChannel] Agent paused by its owner (${source}) — idling; will reconnect when resumed`,
      );
      this._setState("disconnected");
      this.emit("suspended", { source });
    }
    this._scheduleSuspendedPoll();
  }

  private _scheduleSuspendedPoll(): void {
    if (this._stopped || !this._suspended || this._suspendedPollTimer) return;
    this._suspendedPollTimer = setTimeout(() => {
      this._suspendedPollTimer = null;
      void this._pollWhileSuspended();
    }, SecureChannel.SUSPENDED_POLL_MS);
  }

  private _clearSuspendedWait(): void {
    this._suspended = false;
    if (this._suspendedPollTimer) {
      clearTimeout(this._suspendedPollTimer);
      this._suspendedPollTimer = null;
    }
  }

  private async _pollWhileSuspended(): Promise<void> {
    if (this._stopped || !this._suspended) return;
    const standing = await this._readDeviceStanding();
    if (this._stopped || !this._suspended) return;
    if (standing.kind === "active") {
      this._clearSuspendedWait();
      this._reconnectAttempt = 0;
      console.log("[SecureChannel] Agent resumed by its owner — reconnecting");
      this.emit("resumed");
      this._connect();
      return;
    }
    if (standing.kind === "gone") {
      // dr-evil P1/P1b/P2: this used to end in a bare `error` the bridges
      // ignore (REVOKED -> a zombie that neither exits nor uninstalls) or, for
      // a deleted row, poll the 404 forever. A confirmed verdict ends the wait
      // through the same surface as every other confirmed path.
      await this._terminalDeviceGone(standing.reason, "suspended_wait");
      return;
    }
    if (standing.kind === "unknown") {
      console.warn(`[SecureChannel] status check while paused inconclusive (${standing.why}); still waiting`);
    }
    this._scheduleSuspendedPoll();
  }

  /**
   * The device is CONFIRMED gone (status endpoint: REVOKED, or the row is
   * deleted). Stop for good through the documented terminal surface:
   * re_enroll report, `auth_failed{deviceGone:true}` (bridges exit and remove
   * their service), then the legacy `error("Device was revoked")` the OpenClaw
   * lifecycle and older listeners key on.
   */
  private async _terminalDeviceGone(
    reason: "device_revoked" | "device_deleted",
    source: string,
  ): Promise<void> {
    this._clearSuspendedWait();
    this._authFailedThisSession = true;
    console.warn(`[SecureChannel] ${source}: device confirmed gone (${reason}) — terminal`);
    await this._postAuthFailedToBackend(reason);
    this.emit("auth_failed", { reason, deviceGone: true });
    this._handleError(new Error("Device was revoked"));
  }

  private async _handleDeviceRevoked(): Promise<void> {
    const now = Date.now();
    this._revokeRecoveries = this._revokeRecoveries.filter(
      (t) => now - t < SecureChannel.REVOKE_RECOVERY_WINDOW_MS,
    );
    if (this._revokeRecoveries.length >= SecureChannel.REVOKE_RECOVERY_MAX) {
      console.warn(
        "[SecureChannel] device_revoked self-healed too many times in a short window — giving up (terminal)",
      );
      this._handleError(new Error("Device was revoked"));
      return;
    }

    const deviceId = this._deviceId ?? this._persisted?.deviceId;
    if (!deviceId) {
      this._handleError(new Error("Device was revoked"));
      return;
    }

    const standing = await this._readDeviceStanding();
    // Servers before `device_suspended` existed sent `device_revoked` for a
    // pause too. SUSPENDED is reversible — wait for the resume, never die.
    if (standing.kind === "suspended") {
      this._enterSuspendedWait("device_revoked");
      return;
    }
    // A CONFIRMED revoke/delete ends through the terminal surface the bridges
    // act on (auth_failed{deviceGone}). It used to end in a bare `error` they
    // ignore — a revoked, connected agent stayed running as a zombie.
    if (standing.kind === "gone") {
      await this._terminalDeviceGone(standing.reason, "device_revoked");
      return;
    }
    // ACTIVE, or inconclusive (network, 429, PENDING): lean to uptime. The war
    // cap above bounds a thrash if the device really is dead.
    if (standing.kind === "unknown") {
      console.warn(
        `[SecureChannel] device_revoked status check inconclusive (${standing.why}); reconnecting`,
      );
    }
    this._revokeRecoveries.push(now);
    console.log(
      "[SecureChannel] device_revoked but device not confirmed dead — reconnecting (self-heal)",
    );
    this._scheduleReconnect();
  }

  /**
   * Persist all ratchet session states to disk.
   * Syncs live ratchet states back into the persisted sessions map.
   */
  private async _persistState(): Promise<void> {
    if (!this._persisted) return;

    const hasOwnerSessions = this._sessions.size > 0;
    const hasA2AChannels = !!this._persisted.a2aChannels && Object.keys(this._persisted.a2aChannels).length > 0;

    if (!hasOwnerSessions && !hasA2AChannels) return;

    // Persist sticky room context for proactive send routing across restarts
    this._persisted.lastInboundRoomId = this._lastInboundRoomId;

    // Sync dedup sets to persisted state (capped to max size)
    this._persisted.seenMessageIds = [...this._seenMessageIds].slice(-SecureChannel.SEEN_MSG_MAX);
    this._persisted.seenA2AMessageIds = [...this._a2aSeenMessageIds].slice(-SecureChannel.A2A_SEEN_MAX);

    // Sync all live owner-agent ratchet states back to persisted (1:1 only — rooms use MLS)
    for (const [convId, session] of this._sessions) {
      this._persisted.sessions[convId] = {
        ownerDeviceId: session.ownerDeviceId,
        ratchetState: session.ratchet.serialize(),
        activated: session.activated,
      };
    }

    await this._queueStateSave(this._persisted);
    await backupState(this.config.dataDir);
  }
}

function historyErrorReason(error: unknown): string {
  let name = "Error";
  let status: unknown;
  try {
    if (typeof error === "object" && error !== null) {
      const candidate = Object.getPrototypeOf(error)?.constructor?.name;
      if (typeof candidate === "string" && /^[A-Za-z_$][A-Za-z0-9_$]{0,63}$/.test(candidate)) {
        name = candidate === "Object" ? "Error" : candidate;
      }
      status = Reflect.get(error, "status");
    }
  } catch {
    // Hostile values collapse to a generic error without exposing their text.
  }
  return typeof status === "number" && Number.isInteger(status) && status >= 100 && status <= 599
    ? `${name} (status ${status})`
    : name;
}
